/* 新規ゲーム追加ツール Web UI（ビルド不要の素の JS・日本語のみ） */
const app = document.getElementById('app');

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const currentUiLanguage = () => globalThis.VN_I18N?.lang === 'en' ? 'en' : 'ja';
const localizedText = (value, fallback = '') => {
    if (value && typeof value === 'object') {
        return value[currentUiLanguage()] ?? value.ja ?? value.en ?? fallback;
    }
    return value ?? fallback;
};

/**
 * 操作ボタンを 2 段で組み立てる（全画面共通の配置ルール・2026-07-25 指示）。
 *   1 行目 primary   … よく使う操作（画面移動・主要な実行）
 *   2 行目 secondary … たまにしか使わない操作（ファイルに保存・削除・取り下げなど）
 * よく使うボタンと横一列につながないことで、普段押さないボタンの誤操作を防ぐ。
 * 中身が空の行は出力しない（ボタンの無い行が余白だけ作らないようにする）。
 * @param {string|string[]} primary 1 行目に置くボタン HTML（falsy 要素は無視）
 * @param {string|string[]} secondary 2 行目に置くボタン HTML（falsy 要素は無視）
 * @param {{ style?: string }} [opts] 1 行目（無ければ 2 行目）へ付ける追加 style
 */
function actionRowsHtml(primary, secondary, opts = {}) {
    const join = (list) => (Array.isArray(list) ? list : [list]).filter(Boolean).join('');
    const main = join(primary);
    const sub = join(secondary);
    const style = opts.style ? ` style="${esc(opts.style)}"` : '';
    return `${main ? `<div class="actions"${style}>${main}</div>` : ''}`
        + `${sub ? `<div class="actions actions-sub"${main ? '' : style}>${sub}</div>` : ''}`;
}

// ── 利用者 API キー（ブラウザ localStorage 管理。計画書§16.4）──────
// キー等の機密はサーバやファイルに保存せず、このブラウザの localStorage にのみ置き、
// 全 API リクエストのヘッダで都度サーバへ渡す（サーバはメモリのみで使用・保存しない）。
const API_KEY_STORE = { openrouter: 'vn-api-key-openrouter', meshy: 'vn-api-key-meshy' };
const getApiKey = (service) => (localStorage.getItem(API_KEY_STORE[service]) ?? '').trim();

// OpenRouter の用途別モデル設定。API キーと同じくブラウザごとの設定であり、
// サーバの設定ファイルには書き込まない。テキストモデルの単価はOpenRouter APIの原値を
// 別キャッシュし、画面で再表示するときも四捨五入しない。画像モデルは課金単位がモデルごとに
// 異なるため、config/llm.json の実測値に基づく画像1枚あたりの推定コスト・生成時間を表示する。
const OPENROUTER_MODEL_SETTINGS_STORE = 'vn-openrouter-model-settings-v1';
const OPENROUTER_MODEL_PRICING_STORE = 'vn-openrouter-model-pricing-v1';
const OPENROUTER_MODEL_ID_KEYS = ['analysis', 'json', 'imageCost', 'imageSpeed', 'imageAdult'];
const OPENROUTER_IMAGE_ROLES = ['imageCost', 'imageSpeed', 'imageAdult'];
const validOpenRouterModelId = (value) => typeof value === 'string'
    && value.length <= 200
    && /^[A-Za-z0-9][A-Za-z0-9._-]*\/[A-Za-z0-9][A-Za-z0-9._:-]*$/.test(value);
// 引退したモデルIDの読み替え（同じモデルの後継スナップショットだけを載せる。ここに無い保存値は尊重する）。
// このブラウザの保存値は config の既定より優先されるため、放置すると新しい既定へ切り替わらない（2026-08-01）
const RETIRED_OPENROUTER_MODEL_IDS = {
    'deepseek/deepseek-v4-flash': 'deepseek/deepseek-v4-flash-0731',
};
const getStoredOpenRouterModelSettings = () => {
    try {
        const raw = JSON.parse(localStorage.getItem(OPENROUTER_MODEL_SETTINGS_STORE) ?? '{}');
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
        const out = {};
        for (const key of OPENROUTER_MODEL_ID_KEYS) {
            const value = typeof raw[key] === 'string' ? raw[key].trim() : '';
            if (validOpenRouterModelId(value)) out[key] = RETIRED_OPENROUTER_MODEL_IDS[value] ?? value;
        }
        if (OPENROUTER_IMAGE_ROLES.includes(raw.imageActiveRole)) out.imageActiveRole = raw.imageActiveRole;
        return out;
    } catch {
        return {};
    }
};
const saveStoredOpenRouterModelSettings = (value) => {
    const clean = {};
    for (const key of OPENROUTER_MODEL_ID_KEYS) {
        if (validOpenRouterModelId(value?.[key])) clean[key] = value[key].trim();
    }
    if (OPENROUTER_IMAGE_ROLES.includes(value?.imageActiveRole)) clean.imageActiveRole = value.imageActiveRole;
    localStorage.setItem(OPENROUTER_MODEL_SETTINGS_STORE, JSON.stringify(clean));
    return clean;
};
const getStoredOpenRouterPricing = () => {
    try {
        const value = JSON.parse(localStorage.getItem(OPENROUTER_MODEL_PRICING_STORE) ?? '{}');
        return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    } catch {
        return {};
    }
};
const saveStoredOpenRouterPricing = (value) => localStorage.setItem(OPENROUTER_MODEL_PRICING_STORE, JSON.stringify(value));

// 接続テスト成功の記録。キー本体は複製せず「長さ:末尾8文字」の指紋だけを保存し、
// キーを差し替えると指紋が一致しなくなって自動的に「未テスト」へ戻る。
// LLM 実費が発生する操作（プロジェクト作成→解析 等）の事前ゲートに使う
const API_VERIFIED_STORE = { openrouter: 'vn-api-verified-openrouter', meshy: 'vn-api-verified-meshy' };
const apiKeyFingerprint = (key) => `${key.length}:${key.slice(-8)}`;
const isApiKeyVerified = (service) => {
    const key = getApiKey(service);
    return !!key && localStorage.getItem(API_VERIFIED_STORE[service]) === apiKeyFingerprint(key);
};
const markApiKeyVerified = (service, key) => localStorage.setItem(API_VERIFIED_STORE[service], apiKeyFingerprint(key));

/** LLM（OpenRouter）実費が発生する操作の共通事前チェック。
 *  未設定・接続テスト未実施なら、その場で失敗させる代わりに「次にやるべきこと」を案内して
 *  API設定画面へ誘導する（実行してから失敗する手戻り・無駄な課金を防ぐ）。 */
async function ensureLlmReady() {
    if (isApiKeyVerified('openrouter')) return true;
    const llm = await api('/api/llm-status');
    if (llm.status?.tokenConfigured && llm.health?.reachable) return true; // 実疎通 OK ＝テスト済み相当
    alert('OpenRouter の API キーが未設定か、接続テストが完了していません。\n\n先に「API設定」画面でキーを保存し、「接続テスト」を実行してください。\n（このあと API設定画面を開きます）');
    location.hash = '#/settings';
    return false;
}

/** Meshy（3D 自動生成）実費が発生する操作の共通事前チェック。 */
function ensureMeshyReady() {
    if (isApiKeyVerified('meshy')) return true;
    alert('Meshy の API キーが未設定か、接続テストが完了していません。\n\n先に「API設定」画面でキーを保存し、「接続テスト」を実行してください。\n（このあと API設定画面を開きます）');
    location.hash = '#/settings';
    return false;
}

// ブラウザ完結版では tool-bundle.js が window.__VN_BROWSER_BACKEND__ を注入する。
// これがあれば fetch せず backend.dispatch へ委譲する（Node ローカル版は未定義＝従来 fetch）。
const IS_BROWSER = typeof window !== 'undefined' && !!window.__VN_BROWSER_BACKEND__;

/** 一覧・プレビューの判定は「工程の記録」を正とする。
 *  未生成ユニットの検査（missingGeneratedUnitIds）は詳細画面（⑥）と取り込み時のサーバ検査で行う。
 *  一覧側でも再検査すると、後から追加されたユニットや取り込んだプロジェクトで
 *  「完了済みの工程が未完了に戻る」（次の作業に完了済みの⑥-2が出続ける）ため。 */
const isProjectStepDone = (projectState, stepId) => isProjectStepComplete(projectState?.meta ?? projectState, stepId);

/** プレビューは、最新の設計から生成・取り込み・検証まで完了したデータだけを対象にする
 *  （未生成の必須ユニットがあるまま起動させない）。 */
const isProjectPreviewAvailable = (projectState) => {
    const meta = projectState?.meta ?? projectState;
    return IS_BROWSER
        && !!meta?.gameId
        && ['worldDesign', 'requestText', 'generateJson', 'import']
            .every((stepId) => isProjectStateStepComplete(projectState, stepId));
};

// ── 公開中の作品に対する操作制限（2026-07-26 指示）─────────────────
// 「公開中」＝ ⑦ で公開して取り下げていない状態（限定公開・一般公開・公開後の一時停止を含む）。
// 公開中の削除を許すと「公開→削除→公開」を繰り返して少ない容量で無限に公開できてしまうため、
// 公開を取り下げて完全に非公開へ戻すまでは削除も編集もさせない（作品データの編集は非公開のときだけ）。
// プレビューは公開ページで実際に遊べるため、公開中は意味がないので押させない。
const PUBLISH_LOCK_TIP_DELETE = '公開中の作品は削除できません。「マイ公開ゲーム」で公開を取り下げて完全に非公開へ戻すと削除できます';
const PUBLISH_LOCK_TIP_PREVIEW = '公開中の作品はプレビューできません（公開ページで実際に遊べます）';
const PUBLISH_LOCK_TIP_EDIT = '公開中の作品は編集できません。「マイ公開ゲーム」で公開を取り下げて完全に非公開へ戻すと編集できます';

// ユーザー認証（Supabase Auth）。tool-bundle.js が接続設定済みのときだけ window.__VN_AUTH__ を注入する。
// 未注入（Node 版・接続未設定のローカル）では認証なしで従来どおり動作する。
const AUTH = (typeof window !== 'undefined' && window.__VN_AUTH__) || null;
const AUTH_ENABLED = IS_BROWSER && !!AUTH?.enabled;
const AUTH_RETURN_STORE = 'vn-auth-return';
const AUTH_EXPIRED_STORE = 'vn-auth-expired';
// 最後にログインしたメールアドレス（ログイン画面の初期表示に使う。パスワードは保存しない＝
// パスワードの記憶はブラウザのパスワードマネージャに任せる。平文保存は絶対にしない）
const AUTH_LAST_EMAIL_STORE = 'vn-auth-last-email';
// 明示的なログアウト直後の目印（「セッション切れ」ではなく「ログアウトしました」を表示するため）
const AUTH_SIGNEDOUT_STORE = 'vn-auth-signedout';

function apiKeyHeaders() {
    const h = {};
    if (getApiKey('openrouter')) h['x-openrouter-key'] = getApiKey('openrouter');
    if (getApiKey('meshy')) h['x-meshy-key'] = getApiKey('meshy');
    // `{}` も常に送り、全項目をデフォルトに戻したときにNodeサーバの古いメモリ値を消す。
    h['x-openrouter-model-settings'] = JSON.stringify(getStoredOpenRouterModelSettings());
    return h;
}

async function api(pathName, options = {}) {
    const method = options.method ?? (options.body ? 'POST' : 'GET');
    const backend = typeof window !== 'undefined' ? window.__VN_BROWSER_BACKEND__ : null;
    if (backend) {
        try {
            return await backend.dispatch(method, pathName, options.body ?? {});
        } catch (e) {
            return { ok: false, error: e && e.message ? e.message : String(e) };
        }
    }
    let res;
    try {
        res = await fetch(pathName, {
            method,
            headers: { 'Content-Type': 'application/json', ...apiKeyHeaders() },
            body: options.body ? JSON.stringify(options.body) : undefined,
        });
    } catch (e) {
        // サーバ停止・ネットワーク断は fetch が reject する。握りつぶさず ok:false へ正規化して
        // 呼び出し元のエラー表示・pollJob の再試行に乗せる（network: 接続レベルの失敗の目印。
        // pollJob はこれを見て「サーバ側でジョブが継続中の可能性」を失敗と区別する）。
        // 文言に内部構成（起動コマンド等）を書かない（公開版の絶対厳守事項）
        return { ok: false, network: true, error: `サーバへ接続できません（${e?.message ?? e}）。ネットワーク接続を確認し、しばらくしてからページを再読み込みしてください` };
    }
    const json = await res.json().catch(() => ({ ok: false, error: `HTTP ${res.status}` }));
    if (!res.ok && json.ok === undefined) json.ok = false;
    return json;
}

/** preview-sw（scope /preview/）が active になるまで待つ。
 *  ツール画面（/）では navigator.serviceWorker.ready がこの SW を対象にしない（スコープ不一致で
 *  永遠に解決しない）ため、scope 指定の getRegistration をポーリングする。 */
async function waitForPreviewSw(timeoutMs) {
    const deadline = Date.now() + timeoutMs;
    for (;;) {
        const reg = await navigator.serviceWorker.getRegistration('/preview/');
        if (reg?.active) return;
        if (Date.now() >= deadline) throw new Error('preview-sw timeout');
        await new Promise((resolve) => setTimeout(resolve, 200));
    }
}

/** ブラウザ完結版でプレビュー（/preview/<projectId>/）を新しいタブで開く。 */
async function openPreview(projectId) {
    // ユーザー操作の同期タイミングでタブを開き popup blocker を回避する。
    // プロジェクトごとの名前付きウィンドウにして、確認のたびにタブが増殖しないようにする。
    const previewWindow = window.open('about:blank', `vn-preview-${projectId}`);
    if (!previewWindow) {
        alert('プレビューを開けませんでした。このサイトのポップアップを許可して再度実行してください。');
        return;
    }
    previewWindow.opener = null;
    if (IS_BROWSER && 'serviceWorker' in navigator) {
        try {
            await waitForPreviewSw(8000);
        } catch {
            // フォールバック画面が対処法を表示するため、タイムアウト後も開く。
        }
    }
    previewWindow.location.replace(`/preview/${encodeURIComponent(projectId)}/`);
    previewWindow.focus();
}

// ── 実行中ロック（スピナー表示＋誤操作防止）────────────────────
// 全ジョブは pollJob() を必ず通るため、ここで一元的に「押したボタンのスピナー」「全画面ロック」
// 「画面切替ガード」を掛ける。ジョブ自体はサーバ側で走るので画面遷移しても処理は止まらないが、
// 多重実行（外部 API の二重課金・コスト合計の混在）とログ喪失を防ぐ。
const JOB_KIND_LABEL = {
    scaffold: 'スキャフォールド',
    'story-scenario': 'ゲームシナリオ文書の作成',
    'story-analyze': '① 物語解析',
    'story-scaffold': '② ベースデータ生成',
    'story-image-prompts': '③-1 画像生成プロンプト',
    'story-image-generate': '画像の自動生成',
    'story-model-generate': '3Dキャラの自動生成（Meshy）',
    'story-world-design': '④ ワールド設計',
    'story-start-screen': '⑤ 開始画面設計の生成',
    'story-generate-json': '⑥-2 JSON 生成',
    'story-import': '取り込みと検証',
};
const PAID_API_JOB_KINDS = new Set([
    'story-scenario', 'story-analyze', 'story-image-prompts', 'story-image-generate',
    'story-model-generate', 'story-world-design', 'story-generate-json',
    'story-start-screen',
]);
const JOB_COST_LABEL = {
    'story-analyze': '解析コスト',
    'story-image-prompts': 'プロンプト生成コスト',
    'story-image-generate': '画像生成コスト',
    'story-model-generate': 'GLB生成コスト',
    'story-world-design': '設計コスト',
    'story-start-screen': '開始画面素材コスト',
    'story-generate-json': '生成コスト',
};
const BUSY_COMPLETION_PRIORITY = { success: 1, failure: 2, canceled: 3 };
let busyCount = 0;
let busyHash = null;
let busyCompletionOutcome = null;
let lastClickedButton = null;
const buttonBusyStates = new WeakMap();
const guardedOnclick = Symbol('vnGuardedOnclick');
let completionAudioContext = null;
let completionAudioPrimed = false;
let completionSoundConfig = null;
// 音程・長さ・音量は config/ui.json を正本とし、Node版／ブラウザ完結版の共通APIから取得する。
const completionSoundConfigReady = api('/api/settings/ui')
    .then((r) => {
        completionSoundConfig = r?.ok === false ? null : (r?.processing?.completionSound ?? null);
        return completionSoundConfig;
    })
    .catch(() => null);
// 処理待ち中の ESC 中断用の状態。currentJobId はジョブ実行中のみ非 null（削除等の即時処理中は null）。
let currentJobId = null;
let currentJobKind = null;
let cancelRequested = false;

function getCompletionAudioContext() {
    const AudioContextClass = globalThis.AudioContext ?? globalThis.webkitAudioContext;
    if (typeof AudioContextClass !== 'function') return null;
    if (!completionAudioContext || completionAudioContext.state === 'closed') {
        try {
            completionAudioContext = new AudioContextClass();
            completionAudioPrimed = false;
        } catch {
            return null;
        }
    }
    return completionAudioContext;
}

/** 非同期処理の終了後にも音を出せるよう、ユーザーのクリック中に Web Audio を有効化する。 */
function primeProcessingCompletionAudio() {
    const context = getCompletionAudioContext();
    if (!context) return;
    try {
        if (context.state === 'suspended') Promise.resolve(context.resume()).catch(() => {});
        if (completionAudioPrimed) return;
        // Safari系を含む自動再生制限の解除用。無音・10msのため利用者には聞こえない。
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        gain.gain.setValueAtTime(0, context.currentTime);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(context.currentTime);
        oscillator.stop(context.currentTime + 0.01);
        completionAudioPrimed = true;
    } catch {
        // 音声非対応・端末側制限でも本処理とUIは継続する。
    }
}

/** 成功＝明るい上昇音、失敗＝低い下降音。キャンセル時は呼び出さない。 */
function playProcessingCompletionSound(outcome) {
    if (outcome !== 'success' && outcome !== 'failure') return 0;
    const allSounds = completionSoundConfig;
    const sound = allSounds?.enabled === true ? allSounds[outcome] : null;
    if (!sound || !Array.isArray(sound.notes) || sound.notes.length === 0) return 0;
    const context = getCompletionAudioContext();
    if (!context || context.state === 'closed') return 0;
    try {
        // await を挟むと直後の alert() が音声スケジュール前にJSを停止するため、
        // resume要求と全音符の登録を同期的に済ませてから呼び出し元へ戻す。
        if (context.state !== 'running') Promise.resolve(context.resume()).catch(() => {});
        const allowedWaveforms = new Set(['sine', 'square', 'sawtooth', 'triangle']);
        const waveform = allowedWaveforms.has(sound.waveform) ? sound.waveform : 'sine';
        const volumeValue = Number(sound.volume);
        const volume = Number.isFinite(volumeValue) ? Math.min(Math.max(volumeValue, 0.001), 0.3) : 0.1;
        const gapValue = Number(sound.gapMs);
        const gapSec = Number.isFinite(gapValue) ? Math.min(Math.max(gapValue, 0), 500) / 1000 : 0;
        let cursor = context.currentTime;
        let scheduledNotes = 0;
        for (const note of sound.notes.slice(0, 4)) {
            const frequency = Number(note?.frequencyHz);
            const durationMs = Number(note?.durationMs);
            if (!Number.isFinite(frequency) || !Number.isFinite(durationMs)) continue;
            const safeFrequency = Math.min(Math.max(frequency, 80), 2400);
            const durationSec = Math.min(Math.max(durationMs, 40), 1000) / 1000;
            const endAt = cursor + durationSec;
            const attackEnd = cursor + Math.min(0.012, durationSec / 3);
            const releaseStart = Math.max(attackEnd, endAt - Math.min(0.04, durationSec / 2));
            const oscillator = context.createOscillator();
            const gain = context.createGain();
            oscillator.type = waveform;
            oscillator.frequency.setValueAtTime(safeFrequency, cursor);
            gain.gain.setValueAtTime(0.0001, cursor);
            gain.gain.exponentialRampToValueAtTime(volume, attackEnd);
            gain.gain.setValueAtTime(volume, releaseStart);
            gain.gain.exponentialRampToValueAtTime(0.0001, endAt);
            oscillator.connect(gain);
            gain.connect(context.destination);
            oscillator.start(cursor);
            oscillator.stop(endAt + 0.02);
            cursor = endAt + gapSec;
            scheduledNotes += 1;
        }
        const leadValue = Number(allSounds.dialogLeadMs);
        return scheduledNotes > 0 && Number.isFinite(leadValue)
            ? Math.min(Math.max(leadValue, 0), 1000)
            : 0;
    } catch {
        // 通知音だけが失敗しても、完了結果の表示や後続操作を妨げない。
        return 0;
    }
}

function recordBusyCompletion(outcome) {
    if (!(outcome in BUSY_COMPLETION_PRIORITY)) return;
    if (!busyCompletionOutcome
        || BUSY_COMPLETION_PRIORITY[outcome] > BUSY_COMPLETION_PRIORITY[busyCompletionOutcome]) {
        busyCompletionOutcome = outcome;
    }
}

document.addEventListener('click', (ev) => {
    const b = ev.target.closest('button');
    if (b) {
        // onclick がDOM挿入後に設定された場合も、target phaseへ届く前に必ず共通guardへ接続する。
        guardButtonOnclickHandlers(b);
        lastClickedButton = b;
        primeProcessingCompletionAudio();
    }
}, true);

function retainButtonBusy(button, { exclusive = false } = {}) {
    if (!(button instanceof HTMLButtonElement)) return true;
    const state = buttonBusyStates.get(button);
    if (state) {
        if (exclusive) return false;
        state.count += 1;
        return true;
    }
    buttonBusyStates.set(button, {
        count: 1,
        initialDisabled: button.disabled,
        initialAriaBusy: button.getAttribute('aria-busy'),
    });
    button.classList.add('loading');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    return true;
}

function releaseButtonBusy(button) {
    if (!(button instanceof HTMLButtonElement)) return;
    const state = buttonBusyStates.get(button);
    if (!state) return;
    state.count -= 1;
    if (state.count > 0) return;
    buttonBusyStates.delete(button);
    button.classList.remove('loading');
    button.disabled = state.initialDisabled;
    if (state.initialAriaBusy === null) button.removeAttribute('aria-busy');
    else button.setAttribute('aria-busy', state.initialAriaBusy);
}

/** 非同期onclick全体を押下直後から保護する。同期onclickは同じtick内で解除され、表示を変えない。 */
async function runButtonTask(button, action) {
    if (!retainButtonBusy(button, { exclusive: true })) return { skipped: true, busy: true };
    try {
        return await action();
    } finally {
        releaseButtonBusy(button);
    }
}

/**
 * 動的画面が設定した onclick を共通busy契約で包む。
 * 戻り値がPromiseなら完了まで、同期処理なら同じイベント中だけ再入を拒否する。
 */
function guardButtonOnclickHandlers(rootEl) {
    const buttons = [];
    if (rootEl instanceof HTMLButtonElement) buttons.push(rootEl);
    if (rootEl?.querySelectorAll) buttons.push(...rootEl.querySelectorAll('button'));
    for (const button of buttons) {
        const handler = button.onclick;
        if (typeof handler !== 'function' || handler[guardedOnclick]) continue;
        const wrapped = function guardedButtonClick(event) {
            return runButtonTask(button, () => handler.call(this, event));
        };
        wrapped[guardedOnclick] = true;
        button.onclick = wrapped;
    }
}

function setBusy(on, label, completionOutcome = null) {
    const lock = document.getElementById('busy-lock');
    if (on) {
        if (busyCount === 0) {
            busyHash = location.hash || '#/';
            busyCompletionOutcome = null;
            // オーバーレイはポインタしか遮断しないため、キーボード操作（Tab→Enter）による
            // 多重実行（二重課金）も inert で封じる
            document.querySelector('main')?.setAttribute('inert', '');
            document.querySelector('header')?.setAttribute('inert', '');
        }
        busyCount++;
        if (label) document.getElementById('busy-label').textContent = label;
        lock.style.display = '';
    } else if (busyCount > 0) {
        recordBusyCompletion(completionOutcome);
        if (--busyCount === 0) {
            const completedOutcome = busyCompletionOutcome;
            busyCompletionOutcome = null;
            lock.style.display = 'none';
            busyHash = null;
            document.querySelector('main')?.removeAttribute('inert');
            document.querySelector('header')?.removeAttribute('inert');
            return playProcessingCompletionSound(completedOutcome);
        }
    }
    return 0;
}

/** 音列を先に登録し、鳴り始めるまで結果ダイアログを開かせない共通終了処理。 */
async function finishBusy(completionOutcome) {
    const dialogLeadMs = setBusy(false, null, completionOutcome);
    if (dialogLeadMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, dialogLeadMs));
    }
}
window.addEventListener('beforeunload', (ev) => {
    if (busyCount > 0) { ev.preventDefault(); ev.returnValue = ''; }
});

// 処理待ち中に ESC を押したら「処理を中断しますか？」を確認し、OK でジョブへ中断要求を送る。
// 実処理はサーバ/バックエンド側の runner が「キリの良い区切り」で停止する（致命的な中途状態を作らない）。
document.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape' || busyCount === 0 || cancelRequested) return;
    ev.preventDefault();
    if (!currentJobId) {
        // ジョブを介さない即時処理（削除など・1 秒未満で完了）は安全に中断できる区切りがない。
        alert('この処理は中断できません（まもなく完了します）。');
        return;
    }
    if (currentJobKind === 'npm-ci') {
        // npm ci は途中で止めると node_modules が壊れるため、安全のため中断しない。
        alert('npm ci は依存関係の破損を避けるため中断できません。完了までお待ちください。');
        return;
    }
    if (!confirm('処理を中断しますか？\n\n安全な区切りまで進めてから停止します（それまでの途中経過は保存されない場合があります）。')) return;
    cancelRequested = true;
    const label = document.getElementById('busy-label');
    if (label) label.textContent = '中断しています…（安全な区切りで停止します）';
    api(`/api/jobs/${currentJobId}/cancel`, { body: {} }).then((r) => {
        // 中断要求自体が失敗したら状態を戻し、ESC で再度中断できるようにする
        if (r && r.ok === false && currentJobId) {
            cancelRequested = false;
            if (label) label.textContent = `中断要求が失敗しました（${r.error ?? '不明なエラー'}）。ESC でもう一度中断できます`;
        }
    });
});

/** ジョブを介さない即時 API 実行（削除など）を、pollJob と同じ全画面ロックで包む共通ラッパ。
 *  実行中は #busy-lock（ツール共通の処理待ちメッセージ）が全クリックを遮断し、画面切替・タブ閉じも
 *  抑止する（誤操作・多重実行防止）。btn を渡すとそのボタンにもスピナーを出す。 */
async function runBlocking(label, fn, btn = lastClickedButton) {
    setBusy(true, label);
    retainButtonBusy(btn);
    let completionOutcome = 'failure';
    try {
        await completionSoundConfigReady;
        const result = await fn();
        completionOutcome = result?.ok === false ? 'failure' : 'success';
        return result;
    } finally {
        await finishBusy(completionOutcome);
        releaseButtonBusy(btn);
    }
}

/** ジョブをポーリングし、logEl にログを流して完了時 result を返す。
 *  実行中は全画面ロック＋発火ボタンにスピナーを表示する（誤操作・多重実行防止）。 */
async function pollJob(jobId, logEl, { lockAlreadyHeld = false, button = lastClickedButton } = {}) {
    const btn = button;
    currentJobId = jobId;
    if (!lockAlreadyHeld) {
        setBusy(true, '処理中です。完了までお待ちください…');
        retainButtonBusy(btn);
    }
    let job;
    try {
        await completionSoundConfigReady;
        let labeled = false;
        let pollFailures = 0;
        for (;;) {
            const r = await api(`/api/jobs/${jobId}`);
            if (!r.ok) {
                pollFailures += 1;
                // サーバが応答した上でのエラー（ジョブ消滅・再起動でジョブ喪失など）は再試行しても
                // 回復しないため、数回で確定する。接続レベルの失敗（network）はジョブ自体が
                // サーバ側で継続している可能性が高い（LLM/Meshy は数分〜20分・実費発生済み）ので、
                // 偽の「失敗」を確定させず、約 2 分ごとにユーザーへ継続可否を確認しながら再試行し続ける。
                if (!r.network && pollFailures >= 3) {
                    job = { id: jobId, status: 'failed', error: r.error ?? 'ジョブ取得に失敗', log: [] };
                    break;
                }
                if (r.network && pollFailures % 24 === 0
                    && !confirm('サーバに接続できません。処理はサーバ側で継続している可能性があります。\n\n再接続を試し続けますか？\n\n（キャンセルすると画面のロックを解除します。処理自体は中断されません。サーバの起動を確認してからページを再読み込みしてください）')) {
                    job = {
                        id: jobId, status: 'failed', connectionLost: true, log: [],
                        error: 'サーバとの接続が切れたため、画面での追跡を中止しました。処理はサーバ側で継続または完了している可能性があります。サーバ起動を確認し、ページを再読み込みして状態を確認してください。',
                    };
                    break;
                }
                const retryMs = Math.min(1000 * pollFailures, 5000);
                // 中断要求後は「中断しています…」の表示を維持する
                if (!cancelRequested) document.getElementById('busy-label').textContent = `接続を再試行中…（${pollFailures}回目）`;
                await new Promise((resolve) => setTimeout(resolve, retryMs));
                continue;
            }
            pollFailures = 0;
            currentJobKind = r.job.kind ?? null;
            // 中断要求後はラベルを上書きしない（「中断しています…」を維持する）
            if (!labeled && r.job.kind && !cancelRequested) {
                document.getElementById('busy-label').textContent =
                    `${JOB_KIND_LABEL[r.job.kind] ?? r.job.kind} を実行中…（完了までお待ちください）`;
                labeled = true;
            }
            if (logEl) {
                // 個別実費は集計根拠なので、ログに記録された精度のまま表示する。
                logEl.textContent = r.job.log.join('\n');
                logEl.scrollTop = logEl.scrollHeight;
            }
            if (r.job.status !== 'running') { job = r.job; break; }
            await new Promise((resolve) => setTimeout(resolve, 1200));
        }
    } finally {
        const completionOutcome = job?.status === 'canceled'
            ? 'canceled'
            : (job?.status === 'done' ? 'success' : 'failure');
        await finishBusy(completionOutcome);
        releaseButtonBusy(btn);
        currentJobId = null;
        currentJobKind = null;
        cancelRequested = false;
    }
    // 中断完了メッセージ（オーバーレイを閉じた後に表示＝要望の「処理を中断しました」メッセージウインドウ）
    if (job && job.status === 'canceled') {
        alert('処理を中断しました。\n\n安全な区切りで停止しました（途中経過は保存されていない場合があります）。');
    }
    return job;
}

/** ジョブ作成 API の応答待ちから即時ロックする。高速な連打による二重課金を防ぐ。 */
async function saveCompletedGenerationJob(pathName, requestBody, job, context = {}) {
    if (!job || !PAID_API_JOB_KINDS.has(job.kind)) return { ok: true, skipped: true };
    const projectId = pathName.match(/^\/api\/projects\/([^/]+)\//)?.[1];
    if (!projectId) return { ok: true, skipped: true };
    const castLogs = job.result?.castLogs && typeof job.result.castLogs === 'object' ? job.result.castLogs : {};
    const response = await api(`/api/projects/${projectId}/generation-logs`, {
        body: {
            run: {
                id: `job:${job.id}`,
                kind: job.kind,
                mode: context.batchRunId ? 'batch' : 'individual',
                ...(context.batchRunId ? { batchRunId: context.batchRunId } : {}),
                ...(context.batchStep ? { batchStep: context.batchStep } : {}),
                startedAt: job.startedAt,
                finishedAt: job.finishedAt,
                status: job.status,
                ...(job.error ? { error: job.error } : {}),
                ...(Array.isArray(requestBody?.castIds) ? { castIds: requestBody.castIds } : {}),
                lines: job.log ?? [],
                castLogs,
                ...(job.costSummary ? { costSummary: job.costSummary } : {}),
            },
        },
    });
    if (response.ok === false) {
        job.logSaveError = response.error ?? '生成ログを保存できませんでした';
        if (logEl) logEl.textContent += `\n⚠ ${job.logSaveError}`;
    }
    return response;
}

/** ジョブ作成 API の応答待ちから即時ロックし、有料 API ジョブは完了後にプロジェクトへログを永続保存する。 */
async function startAndPoll(pathName, body, logEl, generationLogContext = {}) {
    const btn = lastClickedButton;
    let handedToPoller = false;
    let response = null;
    setBusy(true, '処理を開始しています…');
    retainButtonBusy(btn);
    try {
        await completionSoundConfigReady;
        response = await api(pathName, { body });
        if (!response.jobId) return { response, job: null };
        handedToPoller = true;
        const job = await pollJob(response.jobId, logEl, { lockAlreadyHeld: true, button: btn });
        await saveCompletedGenerationJob(pathName, body, job, generationLogContext);
        compactCompletedJobLog(logEl, job);
        return { response, job };
    } finally {
        if (!handedToPoller) {
            await finishBusy(response?.ok === false || !response?.jobId ? 'failure' : 'success');
            releaseButtonBusy(btn);
        }
    }
}

function shortResultTime(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** 集計済みトータルコストの画面表示専用。個別実費・API利用額・モデル単価には使わない。 */
function formatAggregateUsdCost(value) {
    if (!Number.isFinite(Number(value))) return null;
    // 保存値は変更せず、集計結果の表示だけ小数3桁固定（小数第4位を四捨五入）にする。
    const rounded = Math.round((Number(value) + Number.EPSILON) * 1000) / 1000;
    return `$${rounded.toFixed(3)}`;
}

function formatCostSummaryValue(summary, { includeMeshyCredits = false } = {}) {
    if (!summary || !Number.isFinite(Number(summary.totalUsd))) return null;
    const unknownCalls = Number(summary.openRouterUnknownCalls ?? 0);
    if (unknownCalls > 0 && Number(summary.totalUsd) === 0) return '不明（APIが金額を返しませんでした）';
    const usd = formatAggregateUsdCost(summary.totalUsd);
    const credits = Number(summary.meshyCredits ?? 0);
    const known = includeMeshyCredits && credits > 0 ? `${usd}（${credits.toLocaleString()}P）` : usd;
    // 一部の呼び出しだけ usage.cost が欠けた場合、既知額を工程合計と誤認させない。
    return unknownCalls > 0 ? `${known}＋金額不明 ${unknownCalls}件` : known;
}

/** 実費集計を最優先し、存在しない場合だけ「推定」と明示した概算を返す。 */
function formatCostSummaryOrEstimate(actualSummary, estimatedSummary, options = {}) {
    const actual = formatCostSummaryValue(actualSummary, options);
    if (actual !== null) return actual;
    const estimated = formatCostSummaryValue(estimatedSummary, options);
    return estimated !== null ? '推定 ' + estimated : null;
}

/** GLB完成一式専用。ポイントを先にし、USD換算は小数第1位へ統一する。 */
function formatGlbCostSummaryOrEstimate(actualSummary, estimatedSummary) {
    const estimatedCredits = Number(estimatedSummary?.meshyCredits);
    if (!Number.isFinite(estimatedCredits) || estimatedCredits < 0) return null;
    const actualCredits = Number(actualSummary?.meshyCredits);
    // Meshyの再実行ログはキャッシュ未使用の工程分だけになる。規定工程のPと一致した
    // 完全なログだけを実績として採用し、3P等の部分ログは完成一式の推定へ戻す。
    const hasCompleteActual = Number.isFinite(actualCredits)
        && Math.abs(actualCredits - estimatedCredits) < 0.000001;
    const summary = hasCompleteActual ? actualSummary : estimatedSummary;
    const credits = Number(summary?.meshyCredits);
    const directUsd = summary?.meshyUsd === null || summary?.meshyUsd === undefined || summary?.meshyUsd === ''
        ? Number.NaN : Number(summary.meshyUsd);
    const rate = Number(summary?.meshyUsdPerCredit);
    const usd = Number.isFinite(directUsd) ? directUsd : credits * rate;
    if (!Number.isFinite(credits) || credits < 0 || !Number.isFinite(usd) || usd < 0) return null;
    const roundedUsd = Math.round((usd + Number.EPSILON) * 10) / 10;
    return `${credits.toLocaleString()}P[${hasCompleteActual ? '' : '推定 '}$${roundedUsd.toFixed(1)}]`;
}

/** 保存された作成時モデルとカタログの1枚単価から、画像1枚分の概算を作る。 */
function estimateImageGenerationCost(entry, generationInfo = {}) {
    if (!entry?.imageFile || entry.imageSource === 'upload') return null;
    const modelId = String(entry.imageModel ?? generationInfo?.defaultModel ?? '');
    if (!modelId) return null;
    const models = Array.isArray(generationInfo?.models) ? generationInfo.models : [];
    const model = models.find((candidate) => candidate?.id === modelId);
    const totalUsd = Number(model?.estimatedUsdPerImage);
    if (!Number.isFinite(totalUsd) || totalUsd < 0) return null;
    return { totalUsd, openRouterUsd: totalUsd, model: modelId };
}

/** 現行Meshy工程（画像→3D、再テクスチャ、必要時リグ＋アニメ）から1体分を概算する。 */
function estimateMeshyGenerationCost(entry, generationInfo = {}) {
    if (!entry?.modelGeneratedAt) return null;
    const guide = generationInfo?.generationCostGuide?.meshy;
    const imageTo3d = Number(guide?.imageTo3d);
    const retexture = Number(guide?.retexture);
    const rigging = Number(guide?.rigging);
    const animationEach = Number(guide?.animationEach);
    const usdPerCredit = Number(guide?.usdPerCredit);
    if (![imageTo3d, retexture, rigging, animationEach, usdPerCredit].every(Number.isFinite)) return null;
    const characterType = entry.role === 'enemy' && entry.humanoid === true ? 'humanoidEnemy' : entry.role;
    const animationCount = Number(guide?.animationCountByCharacterType?.[characterType]);
    if (!Number.isFinite(animationCount) || animationCount < 0) return null;
    const meshyCredits = imageTo3d + retexture + (animationCount > 0 ? rigging : 0) + animationEach * animationCount;
    if (meshyCredits < 0 || usdPerCredit < 0) return null;
    const meshyUsd = meshyCredits * usdPerCredit;
    return { totalUsd: meshyUsd, meshyUsd, meshyCredits, meshyUsdPerCredit: usdPerCredit };
}

function costDetail(label, summary, options = {}) {
    const value = formatCostSummaryValue(summary, options);
    return value ? `${label}: ${value}` : null;
}

function generationLogRuns(d) {
    return Array.isArray(d?.generationLogs?.runs) ? d.generationLogs.runs : [];
}

function latestGenerationRun(d, kind, predicate = () => true) {
    return generationLogRuns(d)
        .filter((run) => run.kind === kind && predicate(run))
        .sort((a, b) => String(b.finishedAt ?? b.startedAt).localeCompare(String(a.finishedAt ?? a.startedAt)))[0] ?? null;
}

function stageCostDetail(d, kind, label = JOB_COST_LABEL[kind]) {
    const run = latestGenerationRun(d, kind);
    return costDetail(label, run?.costSummary, { includeMeshyCredits: kind === 'story-model-generate' });
}

function latestCastCostSummary(d, kind, castId) {
    const runs = generationLogRuns(d)
        .filter((run) => run.kind === kind)
        .sort((a, b) => String(b.finishedAt ?? b.startedAt).localeCompare(String(a.finishedAt ?? a.startedAt)));
    for (const run of runs) {
        const summary = run.costSummary?.subtasks?.[castId]
            ?? ((run.castIds ?? []).length === 1 && run.castIds[0] === castId ? run.costSummary : null);
        // 最新試行に金額が無い場合も、過去の実費が存在するなら推定へ落とさず、その実費を使う。
        if (summary) return summary;
    }
    return null;
}

function previousResult(summary, at = null, details = []) {
    return { summary, at, details: details.filter(Boolean) };
}

function showPreviousResult(logEl, summary, at = new Date().toISOString()) {
    if (!logEl) return;
    logEl.textContent = `前回結果: ${summary}${at ? `\n実行日時: ${shortResultTime(at)}` : ''}`;
}

function jobLogBlock(id, state = { needed: true }) {
    let text;
    if (state?.summary) {
        text = `前回結果: ${state.summary}${state.at ? `\n実行日時: ${shortResultTime(state.at)}` : ''}`
            + (state.details?.length ? `\n${state.details.join('\n')}` : '');
    } else if (state?.needed) {
        text = '（実行待ち）';
    } else {
        text = state?.unavailable ?? '（現在は実行不要です）';
    }
    return `<pre class="log result-summary" id="${id}">${esc(text)}</pre>`;
}

function compactCompletedJobLog(logEl, job) {
    if (!logEl || !job) return;
    const costLine = costDetail(JOB_COST_LABEL[job.kind] ?? '生成コスト', job.costSummary, {
        includeMeshyCredits: job.kind === 'story-model-generate',
    });
    if (job.status === 'canceled') {
        logEl.textContent = `前回結果: 中断しました（再実行待ち）${costLine ? `\n${costLine}` : ''}`;
        return;
    }
    if (job.status === 'failed') {
        const reason = jobFailureMessage(job).split(/\r?\n/).filter(Boolean).slice(0, 2).join(' / ');
        logEl.textContent = `前回結果: 失敗（再実行待ち）${reason ? `\n${reason}` : ''}${costLine ? `\n${costLine}` : ''}`;
        return;
    }
    const lines = (job.log ?? [])
        .map((line) => String(line).trim())
        .filter((line) => line && !/^\[(cost|audit)\]/i.test(line))
        .filter((line) => /^(✔|✖|⚠)|完了|検証[:：]/.test(line));
    logEl.textContent = `前回結果: ${lines.at(-1)?.replace(/^✔\s*/, '') || '完了しました。'}${costLine ? `\n${costLine}` : ''}`;
}

function projectAnalyzeLogState(d) {
    const step = d.meta?.steps?.analyze;
    if (d.analysis && step?.ok !== false) return previousResult(
        `解析完了（キャスト ${d.analysis.cast?.length ?? 0}人）`,
        step?.at,
        [stageCostDetail(d, 'story-analyze')],
    );
    return d.novelAvailable ? { needed: true } : { needed: false, unavailable: '（物語の登録後に実行できます）' };
}

function projectScaffoldLogState(d) {
    const step = d.meta?.steps?.scaffold;
    if (d.meta?.gameId && step?.ok !== false) return previousResult('準備完了', step?.at);
    return d.analysis ? { needed: true } : { needed: false, unavailable: '（① 物語解析の完了後に実行できます）' };
}

function projectCharacterLogState(d) {
    const entries = d.imagePrompts?.prompts ?? [];
    if (entries.length > 0) {
        const images = entries.filter((entry) => entry.imageFile).length;
        const models = entries.filter((entry) => entry.modelGeneratedAt).length;
        const steps = [d.meta?.steps?.imagePrompts, d.meta?.steps?.castImages, d.meta?.steps?.castModels].filter(Boolean);
        const latest = steps.sort((a, b) => String(b.at).localeCompare(String(a.at)))[0];
        return previousResult(
            `キャラ ${entries.length}人 / 画像 ${images}/${entries.length} / 3Dモデル ${models}/${entries.length}`,
            latest?.at,
            [
                stageCostDetail(d, 'story-image-prompts'),
                stageCostDetail(d, 'story-image-generate'),
                stageCostDetail(d, 'story-model-generate'),
            ],
        );
    }
    return d.analysis ? { needed: true } : { needed: false, unavailable: '（① 物語解析の完了後に実行できます）' };
}

function projectWorldLogState(d) {
    const step = d.meta?.steps?.worldDesign;
    if (d.worldDesign && step?.ok !== false) {
        return previousResult(
            `設計完了（地域 ${step?.areas ?? d.worldDesign.areas?.length ?? 0} / 建物 ${step?.buildings ?? d.worldDesign.buildings?.length ?? 0}）`,
            step?.at,
            [stageCostDetail(d, 'story-world-design')],
        );
    }
    return d.analysis ? { needed: true } : { needed: false, unavailable: '（① 物語解析の完了後に実行できます）' };
}

function projectJsonLogState(d, units) {
    const status = d.generated;
    if (status) {
        const success = units.filter((unit) => status.units?.[unit.id]?.ok).length;
        const remaining = units.length - success;
        const suffix = remaining > 0 ? ` / 要生成 ${remaining}` : '';
        return previousResult(
            `JSON生成 ${success}/${units.length}${suffix}`,
            status.generatedAt ?? d.meta?.steps?.generateJson?.at,
            [stageCostDetail(d, 'story-generate-json')],
        );
    }
    const ready = !!d.requestText && !!d.worldDesign && !!d.meta?.gameId;
    return ready ? { needed: true } : { needed: false, unavailable: '（⑥-1 AI依頼文の完了後に実行できます）' };
}

function importSummaryFromStep(step) {
    const validation = step?.validation;
    const status = step?.ok === false ? '要確認（再実行待ち）' : '完了';
    const lines = [`取り込み ${status}${Number.isFinite(step?.written) ? `（${step.written}ファイル）` : ''}`];
    if (validation) {
        const mark = (value) => value ? 'OK' : '要確認';
        lines.push(`ファイル形式 ${mark(validation.schema)} / データ ${mark(validation.integrity)} / 翻訳 ${mark(validation.i18n)} / 素材 ${mark(validation.assets)}`);
    }
    return lines.join('\n');
}

function importSummaryFromResult(out) {
    const written = out?.import?.results?.filter((entry) => ['created', 'overwritten', 'merged'].includes(entry.status)).length;
    return importSummaryFromStep({
        ok: out?.ok,
        written,
        validation: out?.schema ? {
            schema: !!out.schema.ok,
            integrity: !!out.integrity?.ok,
            i18n: !!out.i18n?.ok,
            assets: !!out.assets?.ok,
        } : null,
    });
}

function projectImportLogState(d, units) {
    const step = d.meta?.steps?.import;
    if (step && isProjectStateStepComplete(d, 'import')) return previousResult(importSummaryFromStep(step), step.at);
    const ready = isProjectStateStepComplete(d, 'generateJson')
        && units.every((unit) => d.generated?.units?.[unit.id]?.ok);
    return ready ? { needed: true } : { needed: false, unavailable: '（⑥-2 JSON生成の完了後に実行できます）' };
}

function validationLogState(game) {
    const validation = game?.lastValidation ?? {};
    const entries = [
        ['ファイル形式', validation.schema], ['データ', validation.integrity],
        ['翻訳', validation.i18n], ['素材', validation.assets],
    ];
    if (!entries.some(([, value]) => value)) return { needed: true };
    const mark = (value) => !value ? '未実行' : value.ok ? 'OK' : '要確認';
    const latest = entries.map(([, value]) => value?.at).filter(Boolean).sort().at(-1);
    return previousResult(entries.map(([label, value]) => `${label} ${mark(value)}`).join(' / '), latest);
}

function operationLogState(game, keys) {
    const operations = game?.lastOperations ?? {};
    const entries = keys.map(([key, label]) => [label, operations[key]]).filter(([, value]) => value);
    if (entries.length === 0) return { needed: true };
    const latest = entries.map(([, value]) => value.at).filter(Boolean).sort().at(-1);
    return previousResult(entries.map(([label, value]) => `${label} ${value.ok === false ? '失敗' : '完了'}`).join(' / '), latest);
}

/** 失敗ジョブから「原因」を人間向けに組み立てる（致命的エラーは必ず画面に原因を出すための共通処理）。
 *  優先度: job.error（throw 由来）→ result.failures[]（キャスト別失敗）→ ログ末尾のエラー行 → 原因不明。 */
function jobFailureMessage(job) {
    const lines = [];
    if (job?.error) lines.push(job.error);
    if (!job?.error && typeof job?.result?.error === 'string' && job.result.error) lines.push(job.result.error);
    const failures = job?.result?.failures;
    if (Array.isArray(failures) && failures.length > 0) {
        for (const f of failures.slice(0, 5)) lines.push(`✖ ${f.castId ?? ''}: ${f.message ?? f.code ?? '不明'}`);
        if (failures.length > 5) lines.push(`…ほか ${failures.length - 5}件`);
    }
    if (lines.length === 0) {
        lines.push(...(job?.log ?? []).filter((l) => /✖|エラー|失敗|error/i.test(l)).slice(-3).map((l) => l.trim()));
    }
    if (lines.length === 0) {
        lines.push(IS_BROWSER
            ? '原因不明（画面下のログ欄を確認してください）'
            : '原因不明（画面下のログ欄と logs/ の監査ログを確認してください）');
    }
    return lines.join('\n');
}

/** 失敗ジョブをユーザへ通知する（ダイアログに原因を直接表示 + DevTools コンソールにも出す + ログ欄へスクロール）。 */
function reportJobFailure(title, job, logEl) {
    if (job?.status === 'canceled') return; // 中断はユーザー操作。失敗として通知しない（pollJob が「処理を中断しました」を表示済み）
    const reason = jobFailureMessage(job);
    console.error(`[${title}] ジョブ失敗:`, reason, job);
    alert(`${title}に失敗しました:\n\n${reason}`);
    if (logEl) logEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

/**
 * 実行前確認のダイアログ（ボタン名を操作に合わせて出せる版）。
 * ブラウザ標準の confirm() はボタンが「OK / キャンセル」固定で何が起きるか伝わらないため、
 * 「保存」など操作名そのままのボタンで確認したい場面はこちらを使う。
 * @returns {Promise<boolean>} true = 実行、false = 中止（ESC・キャンセル・背景クリック）
 */
function confirmDialog({ title, message, confirmLabel = '実行', cancelLabel = 'キャンセル', danger = false }) {
    return new Promise((resolve) => {
        const dialog = document.createElement('dialog');
        dialog.className = 'file-change-dialog confirm-dialog';
        dialog.innerHTML = `<div class="head-row"><h3>${esc(title)}</h3></div>
            <p class="confirm-dialog-message">${esc(message)}</p>
            <div class="actions">
                <button${danger ? ' class="danger"' : ''} data-confirm-ok>${esc(confirmLabel)}</button>
                <button class="secondary" data-confirm-cancel>${esc(cancelLabel)}</button>
            </div>`;
        document.body.appendChild(dialog);
        let answer = false;
        dialog.querySelector('[data-confirm-ok]').onclick = () => { answer = true; dialog.close(); };
        dialog.querySelector('[data-confirm-cancel]').onclick = () => dialog.close();
        dialog.addEventListener('close', () => { dialog.remove(); resolve(answer); });
        dialog.showModal();
    });
}

// ─────────────────────────────────────────────
// ダッシュボード
// ─────────────────────────────────────────────
// ダッシュボードの「次の作業」表示: STORY_STEPS（story-project.mjs）と同順で最初の未完了ステップを探す
const DASH_NEXT_STEPS = [
    ['analyze', '① 解析', 'analyze'], ['scaffold', '② ベースデータ生成', 'scaffold'], ['imagePrompts', '③ キャラ生成', 'prompts'],
    ['worldDesign', '④ ワールド設計', 'world'], ['startScreen', '⑤ 開始画面設計', 'startscreen'], ['requestText', '⑥-1 AI 依頼文', 'generate'], ['generateJson', '⑥-2 JSON 生成', 'generate'],
    ['import', '⑥-3 取り込みと検証', 'generate'],
    // 取り込みまで終わったら次にやることは公開。完了済みの工程を出し続けない
    ['publish', '⑦ 限定公開', 'publish'],
];

// ── プロジェクトの保存（エクスポート）・取り込み（インポート）─────────────
// ブラウザ内の作業データ一式（物語・解析・プロンプト・ゲーム本体・生成画像・生成3Dモデル）を
// .zip 1ファイルにまとめる。バックアップ、別環境への持ち込み、似た別ゲームの土台づくりに使う。

/** inspect 済みのファイル（取り込みフォームの状態）。画面を離れたら破棄される。 */
let archivePending = null;

function archivePanelHtml() {
    return `<div class="card">
        <h3>プロジェクトの保存・取り込み</h3>
        <div class="actions">
            <input type="file" id="archive-file" accept=".zip,application/zip" style="display:none">
            <button class="secondary" id="archive-pick" title="保存したファイル（.zip）を選び、内容を確認してから取り込みます。復元（元と同じプロジェクトに戻す）と複製（似た別ゲームを新規作成）を選べます">⬆ ファイルから取り込み</button>
        </div>
        <div id="archive-panel" aria-live="polite"></div>
    </div>`;
}

function setupArchivePanel() {
    const input = document.getElementById('archive-file');
    const pick = document.getElementById('archive-pick');
    if (!input || !pick) return;
    pick.onclick = () => input.click();
    input.onchange = async () => {
        const file = input.files?.[0];
        input.value = ''; // 同じファイルを選び直せるようにする
        if (file) await inspectArchiveFile(file);
    };
}

async function exportProjectToFile(projectId, title, btn) {
    // 保存は数十 MB のファイル作成＋ダウンロードになる。押し間違いのまま走らせない
    //（他の「データが動く操作」と同じく実行前に確認する。ボタン名は「保存」「キャンセル」）
    const proceed = await confirmDialog({
        title: 'ファイルに保存',
        message: `「${title}」の作業データ一式を 1つのファイル（.zip）に保存します。\n`
            + '含まれるもの: 物語・解析・プロンプト・ゲーム本体・生成画像・生成3Dモデル\n'
            + 'ブラウザのダウンロード先へ保存されます。',
        confirmLabel: '保存',
    });
    if (!proceed) return;
    const res = await runBlocking(
        'ファイルを作成しています…（完了までお待ちください）',
        () => api('/api/project-archive/export', { body: { projectId } }),
        btn,
    );
    if (!res.ok) { alert(`保存に失敗しました:\n${res.error ?? '不明なエラー'}`); return; }
    const url = URL.createObjectURL(new Blob(res.parts, { type: 'application/zip' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = res.fileName;
    link.rel = 'noopener';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    alert(`「${title}」をファイルに保存しました。\n\nファイル名: ${res.fileName}\nサイズ: ${mb(res.size)}MB\n\nブラウザのダウンロード先をご確認ください。`);
}

async function inspectArchiveFile(file) {
    const panel = document.getElementById('archive-panel');
    panel.innerHTML = '<p class="dim">ファイルを読み込んでいます……</p>';
    let bytes;
    try {
        bytes = new Uint8Array(await file.arrayBuffer());
    } catch (e) {
        panel.innerHTML = `<span class="err" role="alert">${esc(`ファイルを読み込めませんでした（${e?.message ?? e}）`)}</span>`;
        return;
    }
    const res = await api('/api/project-archive/inspect', { body: { bytes } });
    if (!res.ok) {
        panel.innerHTML = `<span class="err" role="alert">${esc(res.error ?? '読み込みに失敗しました')}</span>`;
        return;
    }
    archivePending = { ...res, bytes, fileName: file.name };
    renderArchiveImportForm();
}

function renderArchiveImportForm() {
    const panel = document.getElementById('archive-panel');
    if (!panel || !archivePending) return;
    const { summary, conflicts, suggestedGameId, fileName } = archivePending;
    const title = localizedText(summary.title, summary.projectId);
    // 同じ gameId を別プロジェクトが使っている場合、復元すると相手のゲーム本体・モデルを奪ってしまう
    const restoreBlocked = !!conflicts.gameIdOwner && conflicts.gameIdOwner !== summary.projectId;
    const defaultMode = restoreBlocked ? 'clone' : 'restore';
    const row = (label, value) => `<tr><th>${esc(label)}</th><td>${value}</td></tr>`;
    panel.innerHTML = `<div class="card" style="margin-top:10px">
        <h3>取り込む内容</h3>
        <table class="info-table"><tbody>
            ${row('タイトル', esc(title))}
            ${row('ゲームID', esc(summary.gameId ?? '（未設定）'))}
            ${row('ファイル', esc(fileName))}
            ${row('容量', `${mb(summary.bytes?.total)}MB`)}
            ${row('保存日時', esc((summary.exportedAt ?? '').replace('T', ' ').slice(0, 16) || '不明'))}
            ${row('物語・解析', `${summary.counts?.project ?? 0}件`)}
            ${row('ゲーム本体', `${summary.counts?.preview ?? 0}件`)}
            ${row('3Dモデル', `${summary.counts?.assets ?? 0}件`)}
        </tbody></table>
        ${summary.hasModels ? '' : '<div class="warn">このファイルには生成済み3Dモデルが含まれていません（取り込み後に3D生成が必要です）。</div>'}
        ${restoreBlocked ? `<div class="warn">ゲームID「${esc(summary.gameId)}」は別のプロジェクトが使用中のため、複製としてのみ取り込めます。</div>` : ''}
        ${conflicts.sameProject && !restoreBlocked ? '<div class="warn">同じプロジェクトがこのブラウザにあります。「復元」を選ぶと現在の内容が置き換わります。</div>' : ''}
        <h3>取り込み方法</h3>
        <label class="choice">
            <input type="radio" name="archive-mode" value="restore" ${defaultMode === 'restore' ? 'checked' : ''} ${restoreBlocked ? 'disabled' : ''}>
            <span>復元して取り込む<small>元と同じプロジェクトとして戻します</small></span>
        </label>
        <label class="choice">
            <input type="radio" name="archive-mode" value="clone" ${defaultMode === 'clone' ? 'checked' : ''}>
            <span>別ゲームとして複製する<small>似た内容の新しいゲームを作ります</small></span>
        </label>
        <table class="info-table" id="archive-clone-fields" ${defaultMode === 'clone' ? '' : 'hidden'}><tbody>
            <tr><th>新しいゲームID</th><td>
                <input type="text" id="archive-new-gameid" maxlength="40" value="${esc(suggestedGameId ?? '')}">
                <small>英小文字はじまり・英数字/_/-</small></td></tr>
            <tr><th>新しいタイトル</th><td>
                <input type="text" id="archive-new-title" maxlength="120" placeholder="${esc(localizedText(summary.title, ''))}">
                <small>任意・空欄なら元のまま</small></td></tr>
        </tbody></table>
        <div class="actions">
            <button id="archive-run" title="確認のうえ、このファイルの内容を取り込みます">取り込む</button>
            <button class="secondary" id="archive-cancel">やめる</button>
        </div>
        <div id="archive-msg" aria-live="polite"></div>
    </div>`;
    for (const radio of panel.querySelectorAll('input[name="archive-mode"]')) {
        radio.onchange = () => {
            document.getElementById('archive-clone-fields').hidden = radio.value !== 'clone' || !radio.checked;
        };
    }
    document.getElementById('archive-cancel').onclick = () => { archivePending = null; panel.innerHTML = ''; };
    document.getElementById('archive-run').onclick = (ev) => runArchiveImport(ev.target);
}

async function runArchiveImport(btn) {
    const panel = document.getElementById('archive-panel');
    const msg = document.getElementById('archive-msg');
    const mode = panel.querySelector('input[name="archive-mode"]:checked')?.value ?? 'restore';
    const newGameId = document.getElementById('archive-new-gameid')?.value.trim() ?? '';
    const newTitle = document.getElementById('archive-new-title')?.value.trim() ?? '';
    const summary = archivePending?.summary;
    if (!summary) return;
    const confirmText = mode === 'restore'
        ? `このファイルを復元して取り込みますか？\n\nゲームID: ${summary.gameId ?? '（未設定）'}\n${archivePending.conflicts.sameProject ? '※ 同じプロジェクトの現在の内容は置き換わります。' : ''}`
        : `別ゲームとして複製しますか？\n\n新しいゲームID: ${newGameId || '(自動)'}\n元のデータはそのまま残ります。`;
    if (!confirm(confirmText)) return;
    const res = await runBlocking(
        '取り込んでいます…（完了までお待ちください）',
        () => api('/api/project-archive/import', {
            body: { token: archivePending.token, bytes: archivePending.bytes, mode, newGameId, newTitle },
        }),
        btn,
    );
    if (!res.ok) {
        msg.innerHTML = `<span class="err" role="alert">${esc(res.error ?? '取り込みに失敗しました')}</span>`;
        return;
    }
    archivePending = null;
    alert(`取り込みが完了しました。\n\nゲームID: ${res.gameId ?? '（未設定）'}\n取り込んだファイル: ${res.counts.project + res.counts.preview + res.counts.assets}件${res.renamedFrom ? `\n（${res.renamedFrom} から複製しました）` : ''}`);
    renderDashboard();
}

// ホームの「ゲーム」欄に出す容量のキャッシュ（画面を開き直すたびに「計算中…」へ戻さない）。
const projectSizeCache = new Map();

// 容量は全画面で「公開したときに使う容量」に統一する（同じゲームで違う数字を出さない）。
// 作業データ（物語の原文・解析結果・元画像など）は配信されないので含めない。
const PROJECT_SIZE_TIP = '公開したときに使う容量（ゲームデータ・キャスト・開始画面素材・生成3Dモデル）です。'
    + '「⑦ 限定公開」「マイ公開ゲーム」に出る容量と同じ基準で、物語の原文や解析結果などの作業データは含みません';

/** 一覧を描いたあとに各プロジェクトの容量を集計して差し込む（描画をブロックしない）。 */
async function fillProjectSizes(projectIds) {
    await Promise.all(projectIds.map(async (projectId) => {
        const r = await api(`/api/projects/${projectId}/size`);
        const cell = app.querySelector(`[data-project-size="${CSS.escape(projectId)}"]`);
        if (!r.ok) { if (cell) cell.innerHTML = '<span class="dim">-</span>'; return; }
        const total = r.bytes?.total ?? 0;
        projectSizeCache.set(projectId, total);
        if (cell) cell.textContent = formatMb(total);
    }));
}

// ホームの描画世代。背景での取り直し中に別画面へ移動したら、古い応答で描き直さないための目印。
let dashboardRenderToken = 0;

/** 記憶済みの公開状態から Map を作る（ネットワークを待たずに描くため）。 */
function cachedPublishedByGameId() {
    const map = new Map();
    if (!AUTH_ENABLED || !AUTH.isAuthenticated()) return map;
    for (const [gameId, game] of PUBLISHED_GAME_STATE) map.set(gameId, game);
    return map;
}

/** 公開状態の署名。背景で取り直した結果が変わったときだけ描き直す（無駄なちらつきを出さない）。 */
function publishedStateSignature(map) {
    return [...map.values()]
        .map((g) => [g?.game_id, g?.visibility, g?.moderation_status, g?.current_rev, g?.bytes_total].join(':'))
        .sort()
        .join('|');
}

/**
 * ホームを描く。
 * 公開状態（mine）はネットワーク往復（実測 0.8〜1.9秒。Supabase のトークン更新が挟まるとさらに +1秒）
 * なので、2 回目以降は記憶済みの状態で先に描き、取り直しは裏で行う。
 * 待ってから描いていた頃は、その間ずっと前の画面のままで「ホームが数秒出てこない」状態だった。
 */
async function renderDashboard() {
    const token = ++dashboardRenderToken;
    const [r, pr] = await Promise.all([api('/api/games'), api('/api/projects')]);
    if (token !== dashboardRenderToken) return;
    if (r.ok === false || pr.ok === false) {
        app.innerHTML = `<div class="card"><p class="err">一覧の取得に失敗しました:</p>
            <pre class="log">${esc(r.ok === false ? (r.error ?? '不明なエラー') : (pr.error ?? '不明なエラー'))}</pre>
            <div class="actions"><button onclick="location.reload()" title="ページを再読み込みして一覧を取得し直します">再読み込み</button></div></div>`;
        return;
    }
    const games = r.games ?? [];
    const projects = pr.projects ?? [];
    if (!AUTH_ENABLED || !AUTH.isAuthenticated()) {
        paintDashboard(games, projects, new Map(), pr.units);
        return;
    }
    if (!publishedGamesLoaded) {
        // 初回だけは待つ。公開状態が分からないまま描くと、公開中の作品の編集・削除ロックを
        // 誤って外してしまう。待っている間は前の画面のままにせず、読み込み中を出す
        app.innerHTML = '<h2><span class="h2-ico" aria-hidden="true">🏠</span>ホーム</h2><p class="dim">読み込み中……</p>';
        await fetchPublishApi('GET', 'mine');
        if (token !== dashboardRenderToken) return;
        paintDashboard(games, projects, cachedPublishedByGameId(), pr.units);
        return;
    }
    // 2 回目以降: 記憶で即描画 → 裏で取り直し → 変わっていたときだけ描き直す。
    // 記憶は公開・取り下げ・公開範囲の変更で必ず捨てられる（publishedGamesLoaded=false）ため、
    // 自分の操作で古いロック状態が残ることはない
    const shown = cachedPublishedByGameId();
    const before = publishedStateSignature(shown);
    paintDashboard(games, projects, shown, pr.units);
    fetchPublishApi('GET', 'mine').then(() => {
        if (token !== dashboardRenderToken) return;
        // 取り込み確認パネルの表示中・処理実行中は描き直さない。
        // 描き直すと innerHTML ごと入れ替わり、選択中のファイルの確認表示が消える
        if (archivePending || busyCount > 0) return;
        const latest = cachedPublishedByGameId();
        if (publishedStateSignature(latest) === before) return;
        paintDashboard(games, projects, latest, pr.units);
    }).catch(() => { /* 取り直しの失敗で表示中の一覧を壊さない */ });
}

/** ホームの本体を描いて操作を配線する（renderDashboard から即時描画・再描画の 2 回呼ばれ得る）。 */
function paintDashboard(games, projects, publishedByGameId, units) {
    const projectCards = projects.map((p) => {
        const projectState = { ...p, units: units ?? [] };
        const stepsDone = Object.keys(p.steps ?? {}).filter((stepId) => isProjectStepDone(p, stepId)).length;
        const stepsTotal = Object.keys(p.steps ?? {}).length || 8;
        const published = p.gameId ? publishedByGameId.get(p.gameId) : null;
        // 未生成の必須ユニット（後から追加されたユニット等）は理由を明示して⑥へ戻す。
        // 「完了済みの工程が理由もなく次の作業に出る」状態にはしない
        const missingUnits = missingGeneratedUnitIds(projectState);
        const recordedNext = DASH_NEXT_STEPS.find(([id]) => (id === 'publish' ? !published : !isProjectStepDone(p, id)));
        const next = (recordedNext?.[0] === 'publish' && missingUnits.length > 0)
            ? ['generateJson', `⑥-2 JSON 生成（未生成 ${missingUnits.length}件）`, 'generate']
            : recordedNext;
        const nextHtml = next
            ? `<a href="#/project/${esc(p.projectId)}/${next[2]}">${esc(next[1])}</a>`
            : `<span class="ok">✔ ${esc(VISIBILITY_LABELS[published.visibility] ?? published.visibility)}で公開中</span>`;
        // 1 行目 = よく使う画面移動（プレビュー・公開ページ・次工程へ）、
        // 2 行目 = 必要なときしか押さない操作（ファイルに保存・削除）。全画面共通の配置ルール
        const primaryActions = [
            // 公開中は「公開ページ」で実際に遊べるため、プレビューは押せなくする
            isProjectPreviewAvailable(projectState)
                ? (published
                    ? `<button disabled title="${esc(PUBLISH_LOCK_TIP_PREVIEW)}">▶ プレビュー</button>`
                    : `<button data-preview-project="${esc(p.projectId)}" title="ブラウザ内の作業データでゲームを起動します（新しいタブ）">▶ プレビュー</button>`)
                : '',
            AUTH_ENABLED && published
                ? `<a href="${esc(PUBLISH_GAMES_ORIGIN)}/${esc(p.gameId)}/" target="_blank" rel="noopener noreferrer"><button class="secondary" title="公開中のゲームを新しいタブで開いて実際に遊んで確認します">▶ 公開ページ</button></a>`
                : (AUTH_ENABLED && isProjectPreviewAvailable(projectState)
                    ? `<button class="secondary" onclick="location.hash='#/project/${esc(p.projectId)}/publish'" title="このゲームを限定公開（URLを知っている人だけが遊べる状態）にする画面へ移動します">⑦ 限定公開へ</button>`
                    : (missingUnits.length > 0
                        ? `<button class="secondary" onclick="location.hash='#/project/${esc(p.projectId)}/generate'" title="未生成のJSONユニットを生成する画面へ移動します（プレビュー・公開の前に必要です）">⑥-2 JSON 生成へ</button>`
                        : '')),
        ];
        const subActions = [
            IS_BROWSER
                ? `<button class="secondary" data-export-project="${esc(p.projectId)}" data-export-title="${esc(localizedText(p.title, p.projectId))}" title="作業データ一式（物語・解析・プロンプト・ゲーム本体・生成画像・生成3Dモデル）を 1つのファイルに保存します。バックアップ・別のパソコンや別サイトへの持ち込み・似た別ゲームの土台に使えます">⬇ ファイルに保存</button>`
                : '',
            // 公開中は削除させない（取り下げて完全に非公開へ戻すまでは消せない仕様に統一）
            published
                ? `<button class="secondary" disabled title="${esc(PUBLISH_LOCK_TIP_DELETE)}">削除</button>`
                : `<button class="secondary" data-del-project="${esc(p.projectId)}" data-del-title="${esc(localizedText(p.title, p.projectId))}" title="この物語プロジェクト一式（解析・画像・プロンプト・原文コピー）を削除します。実行前に確認ダイアログが表示されます">削除</button>`,
        ];
        // 「ゲーム」欄には容量も出す。公開中はサーバが記録している公開済みの容量をそのまま使い、
        // 「マイ公開ゲーム」「⑦ 限定公開」と必ず同じ数字にする。未公開は同じ基準で見積もる
        //（集計はファイル実体を読むため一覧描画では待たず、描画後に差し込む）
        const knownSize = published ? Number(published.bytes_total || 0) : projectSizeCache.get(p.projectId);
        const gameCell = p.gameId
            ? `<span>${IS_BROWSER ? '準備済み' : `<a href="#/game/${esc(p.gameId)}">${esc(p.gameId)}</a>`}</span>`
                + `<span class="dim"> / </span>`
                + `<span data-project-size="${esc(p.projectId)}" title="${esc(PROJECT_SIZE_TIP)}">${knownSize === undefined ? '<span class="dim">計算中…</span>' : esc(formatMb(knownSize))}</span>`
            : '<span>未生成</span>';
        return `<div class="card">
            <h3><a href="#/project/${esc(p.projectId)}">${esc(localizedText(p.title, '（未解析）'))}</a></h3>
            <table class="info-table"><tbody>
                ${IS_BROWSER ? '' : infoRow('プロジェクトID', `<code>${esc(p.projectId)}</code>`)}
                ${infoRow('物語', `${esc(p.novel?.fileName ?? '-')}<span class="dim">（${(p.novel?.chars ?? 0).toLocaleString()}字）</span>`)}
                ${infoRow('ゲーム', gameCell)}
                ${infoRow('進捗', `${stepsDone}/${stepsTotal}`)}
                ${infoRow('AI実行', `${p.usage?.llmCalls ?? 0}回`)}
                ${infoRow('次の作業', nextHtml)}
            </tbody></table>
            ${actionRowsHtml(primaryActions, subActions)}
        </div>`;
    }).join('');
    const cards = games.map((g) => {
        const v = g.lastValidation ?? {};
        const badge = (k, label) => {
            const s = v[k];
            if (!s) return `<span class="badge">${label}: 未実行</span>`;
            return `<span class="badge ${s.ok ? 'ok' : 'err'}">${label}: ${s.ok ? 'OK' : 'NG'}</span>`;
        };
        return `<div class="card">
            <h3><a href="#/game/${esc(g.gameId)}">${esc(localizedText(g.title, g.gameId))}</a>${g.published ? '<span class="badge warn">公開済み</span>' : ''}</h3>
            <div>ゲームID: <code>${esc(g.gameId)}</code> <span class="dim">${esc(g.title?.en ?? '')}</span></div>
            <div class="dim">ワールド: ${esc(g.maps.join(', ') || '-')} / 生成: ${esc((g.createdAt ?? '').slice(0, 10) || '-')}</div>
            <div style="margin-top:6px">${badge('schema', 'スキーマ')}${badge('integrity', '整合')}${badge('i18n', '翻訳')}${badge('assets', 'アセット')}</div>
            <div class="actions actions-sub"><button class="secondary" data-del-game="${esc(g.gameId)}" data-del-title="${esc(localizedText(g.title, g.gameId))}">削除</button></div>
        </div>`;
    }).join('');
    const gamesSection = IS_BROWSER ? '' : `<h2><span class="h2-ico" aria-hidden="true">🎮</span>ゲーム一覧</h2>
        ${games.length === 0 ? '<p class="dim">newgames/ 配下にゲームはまだありません。上の「物語から新規作成」または「新規ゲーム作成（手動）」から始めてください。</p>' : `<div class="grid">${cards}</div>`}`;
    // 初回セットアップ誘導: キーが未設定・未テストのうちは、最初にやるべきことを先回りで示す
    // （生成ボタンを押してから失敗するのではなく、入口で分かるようにする）
    const setupBanner = isApiKeyVerified('openrouter') ? '' : `<div class="card warn">
        <b>はじめに: API キーの設定が必要です</b>
        <p class="dim" style="margin:6px 0">物語の解析・ゲーム生成には OpenRouter の API キー（ご自身で発行）が必要です。
        「API設定」でキーを保存し、「接続テスト」まで実行すると、すべての生成機能が使えるようになります。</p>
        <div class="actions"><button onclick="location.hash='#/settings'">API設定を開く</button></div>
    </div>`;
    app.innerHTML = `<h2><span class="h2-ico" aria-hidden="true">🏠</span>ホーム</h2>${setupBanner}<div class="actions" style="margin-bottom:14px">
            <button onclick="location.hash='#/story-new'">📖 物語から新規作成</button>
            ${IS_BROWSER ? '' : '<button class="secondary" onclick="location.hash=\'#/new\'">＋ 新規ゲーム作成（手動）</button>'}
        </div>
        ${gamesSection}
        <h2 style="margin-top:18px">${IS_BROWSER ? '作成中のゲーム' : '物語プロジェクト（F13〜F16）'}</h2>
        ${IS_BROWSER ? archivePanelHtml() : ''}
        ${projects.length === 0 ? '<p class="dim">物語プロジェクトはまだありません。「物語から新規作成」で小説テキストを登録すると、解析→ゲーム生成→キャラ生成→ワールド設計→JSON 生成まで進められます。</p>' : `<div class="grid">${projectCards}</div>`}`;
    if (IS_BROWSER) setupArchivePanel();
    // 容量は描画後に差し込む（集計の失敗で一覧そのものを壊さない）。
    // 公開中はサーバの記録値を描画済みなので、集計するのは未公開のプロジェクトだけ
    fillProjectSizes(projects
        .filter((p) => p.gameId && !publishedByGameId.has(p.gameId))
        .map((p) => p.projectId)).catch(() => {});
    for (const btn of app.querySelectorAll('button[data-export-project]')) {
        btn.onclick = () => exportProjectToFile(btn.dataset.exportProject, btn.dataset.exportTitle, btn);
    }
    for (const btn of app.querySelectorAll('button[data-preview-project]')) {
        btn.onclick = () => openPreview(btn.dataset.previewProject);
    }
    for (const btn of app.querySelectorAll('button[data-del-project]')) {
        btn.onclick = async () => {
            const projectId = btn.dataset.delProject;
            const name = btn.dataset.delTitle;
            // 公開中のプロジェクトには削除ボタン自体を出さないため、ここへは非公開のものだけが来る
            const details = IS_BROWSER
                ? '削除されるもの: プロジェクト一式とブラウザ内のベースデータ・プレビュー用作業データ'
                : '削除されるもの: プロジェクト一式（解析結果・キャスト画像・画像プロンプト・ワールド設計・原文コピー）\n残るもの: 生成済みのゲーム本体（newgames/）と 3D モデル（assets/newgames/）';
            const ok = confirm(`物語プロジェクト「${name}（${projectId}）」を削除しますか？\n\n${details}\n\n※この操作は取り消せません。`);
            if (!ok) return;
            const r = await runBlocking('削除中…（完了までお待ちください）', () => api(`/api/projects/${projectId}/delete`, { body: {} }), btn);
            if (!r.ok) { alert(`削除に失敗しました:\n${r.error ?? '不明なエラー'}`); return; }
            alert(`「${name}」を削除しました。${r.unlinkedGameId ? `\n（ゲーム ${r.unlinkedGameId} とのリンクを解除しました。ゲーム本体は残っています）` : ''}`);
            renderDashboard();
        };
    }
    for (const btn of app.querySelectorAll('button[data-del-game]')) {
        btn.onclick = async () => {
            const gameId = btn.dataset.delGame;
            const name = btn.dataset.delTitle;
            const ok = confirm(`「${name}（${gameId}）」を削除しますか？\n\n削除されるもの: ゲーム環境一式（newgames/${gameId}/）\n残るもの: 生成済みの 3D モデル等（assets/newgames/${gameId}/。不要なら手動で削除してください）\n\n※この操作は取り消せません。`);
            if (!ok) return;
            const r = await runBlocking('削除中…（完了までお待ちください）', () => api(`/api/games/${gameId}/delete`, { body: {} }), btn);
            if (!r.ok) { alert(`削除に失敗しました:\n${r.error ?? '不明なエラー'}`); return; }
            alert(`「${name}」を削除しました。${r.unlinkedProjects?.length ? `\n（物語プロジェクト ${r.unlinkedProjects.join(', ')} のゲーム紐付けを解除しました）` : ''}`);
            renderDashboard();
        };
    }
}

// ─────────────────────────────────────────────
// 物語から作成（F13: プロジェクト作成）
// ─────────────────────────────────────────────
/** 予測 USD の表示。単価が取れず null のときは「金額なし」として null を返す
 *（Number(null) は 0 になるため、ここで弾かないと単価不明が「$0」に化ける）。
 *  小額は 4 桁まで出す（合計と内訳が足し算として合って見えるように、境界は $0.1）。 */
function formatEstimateUsd(usd) {
    if (usd === null || usd === undefined || usd === '') return null;
    const n = Number(usd);
    if (!Number.isFinite(n) || n < 0) return null;
    return `$${n < 0.1 ? n.toFixed(4) : n.toFixed(2)}`;
}

/** 「ゲームシナリオ文書作成」の確認ダイアログに出す、解析コスト予測の各行。 */
function scenarioEstimateLines(preview) {
    const e = preview.estimate ?? {};
    const languageLabel = e.language === 'ja' ? '日本語' : '英語';
    const total = formatEstimateUsd(e.totalUsd);
    const lines = [
        `小説: ${Number(e.usedCharacters ?? 0).toLocaleString()}字（${languageLabel}）`,
        `使用モデル: ${e.model ?? '(未設定)'}`,
    ];
    if (e.truncated) {
        lines.push(`入力上限を超えるため、後ろ側 ${Number(e.droppedCharacters ?? 0).toLocaleString()}字は削除して処理します`);
    }
    if (total) {
        lines.push(`解析コスト（予測）: 約 ${total}`);
        lines.push(`内訳: 入力 ${Number(e.inputTokens ?? 0).toLocaleString()}トークン ≒${formatEstimateUsd(e.inputUsd)} / 出力 最大 ${Number(e.outputTokens ?? 0).toLocaleString()}トークン ≒${formatEstimateUsd(e.outputUsd)}`);
        lines.push('予測値です。実際の請求額は OpenRouter の実費が正です。');
    } else {
        // 単価が取れなくても実行はできる。金額が出せない理由を隠さない
        lines.push(`解析コスト（予測）: 算出できませんでした（${preview.pricingError ?? 'モデル単価を取得できません'}）`);
        lines.push(`内訳: 入力 ${Number(e.inputTokens ?? 0).toLocaleString()}トークン / 出力 最大 ${Number(e.outputTokens ?? 0).toLocaleString()}トークン`);
    }
    lines.push('文書が長いほど時間がかかります。');
    return lines;
}

async function renderStoryNew() {
    const llm = await api('/api/llm-status');
    const tokenOk = llm.status?.tokenConfigured;
    const reachable = llm.health?.reachable;
    app.innerHTML = `<h2><span class="h2-ico" aria-hidden="true">📖</span>物語から新規ゲーム作成</h2>
    <div class="card ${tokenOk && reachable ? '' : 'warn'}">
        <h3>AI接続状態</h3>
        <div>接続: ${reachable ? '<span class="ok">✔ 利用可能</span>' : '<span class="err">✖ 接続不可</span>'}${Number.isFinite(llm.health?.remainingUsd) ? `（残高 $${Number(llm.health.remainingUsd).toFixed(2)}）` : ''}</div>
        <div>API キー: ${tokenOk ? `<span class="ok">設定済み（${esc(llm.status.tokenSource)}）</span>` : '<span class="err">未設定</span>'}${tokenOk && !reachable && !isApiKeyVerified('openrouter') ? ' <span class="warn">（接続テスト未実施）</span>' : ''}</div>
        ${llm.health?.error ? `<pre class="log">${esc(llm.health.error)}</pre>` : ''}
        ${tokenOk && reachable ? '' : '<div class="actions"><button class="secondary" onclick="location.hash=\'#/settings\'" title="API設定画面でキーの保存と接続テストを行います">API設定を開く</button></div>'}
    </div>
    <div class="card">
        <h3>物語の文書</h3>
        <p class="dim">小説ファイルを読み込み、「ゲームシナリオ文書作成」でゲーム生成用の文書にまとめます。すでにゲームシナリオ文書がある場合は、下の欄へ直接貼り付けても進められます。</p>
        <label class="field"><span>小説ファイルを選択（.txt / .md）</span><input type="file" id="sn-file" accept=".txt,.md,text/plain"></label>
        <div class="dim" id="sn-file-info">ファイルは選択されていません</div>
        <div class="dim" id="sn-file-limit"></div>
        <div class="actions"><button id="sn-scenario" disabled>ゲームシナリオ文書作成</button></div>
        <pre class="log" id="sn-scenario-log" hidden></pre>
        <label class="field"><span>または、ゲームシナリオ文書（１万文字以内）を張り付け</span><textarea id="sn-text" class="tall" placeholder="ここにゲームシナリオ文書……（200文字以上）"></textarea></label>
        <div class="dim" id="sn-count">0字</div>
        <label class="field"><span>表示用タイトル（ファイル名。省略可）</span><input type="text" id="sn-name" placeholder="例: 時計仕掛けの都.txt"></label>
        <label class="field"><input type="checkbox" id="sn-batch" checked> 作成後、残りの工程を一括実行する（① 解析〜⑥ 取り込み。開始前に工程一覧と実費の目安を確認できます）</label>
        <div class="actions"><button id="sn-create">プロジェクトを作成して解析へ</button></div>
        <div id="sn-result"></div>
    </div>`;
    // 読み込んだ小説の全文は画面へ出さずここで保持する（40万〜100万文字になり得るため。
    // 表示するのはファイル名と文字数だけ＝ブラウザが固まらない）
    let novelSource = null;
    const scenarioBtn = document.getElementById('sn-scenario');
    const updateCount = () => {
        const n = document.getElementById('sn-text').value.length;
        const el = document.getElementById('sn-count');
        el.textContent = `${n.toLocaleString()}字${n > 0 && n < 200 ? '（200字以上必要です）' : ''}`;
    };
    document.getElementById('sn-text').addEventListener('input', updateCount);
    // 入力上限・編集欄の上限は config（story-pipeline.json の scenario）を正とし、画面へ書かない
    const scenarioLimits = await api('/api/story-scenario');
    if (scenarioLimits.ok) {
        document.getElementById('sn-text').maxLength = scenarioLimits.limits.editorMaxCharacters;
        const input = scenarioLimits.limits.inputMaxCharacters ?? {};
        document.getElementById('sn-file-limit').textContent =
            `読み込める文書の上限: 日本語 ${Number(input.ja ?? 0).toLocaleString()}字 / 英語ほか ${Number(input.default ?? 0).toLocaleString()}字（超えた分は後ろ側が削除されます）`;
    }
    document.getElementById('sn-file').onchange = async (ev) => {
        const file = ev.target.files?.[0];
        const info = document.getElementById('sn-file-info');
        if (!file) return;
        info.textContent = '読み込み中……';
        scenarioBtn.disabled = true;
        try {
            const text = await file.text();
            novelSource = { name: file.name, text };
            // ファイル名はブラウザ標準の省略表示（「ベンチ…城塞.txt」）ではなくフルで出す
            info.innerHTML = `読み込んだファイル: <b>${esc(file.name)}</b><span class="dim"> / ${text.length.toLocaleString()}字</span>`;
            document.getElementById('sn-name').value = file.name;
            scenarioBtn.disabled = false;
        } catch (e) {
            novelSource = null;
            info.innerHTML = `<span class="err">ファイルを読み込めませんでした（${esc(e?.message ?? e)}）</span>`;
        }
    };
    scenarioBtn.onclick = async () => {
        if (!novelSource) { alert('先に小説ファイルを選択してください。'); return; }
        // LLM 実費が発生するため、キーの設定と接続テストをここで必須にする
        if (!(await ensureLlmReady())) return;
        const textEl = document.getElementById('sn-text');
        if (textEl.value.trim() && !confirm('下の「ゲームシナリオ文書」欄の内容を、生成した文書で置き換えますか？')) return;
        // 実行前に解析コストの予測を出す（小説全文を投げるため、押す前に金額が分かるようにする）
        const preview = await runBlocking('解析コストを見積もっています…',
            () => api('/api/story-scenario/estimate', { body: { text: novelSource.text } }), scenarioBtn);
        if (!preview.ok) { alert(`解析コストを見積もれませんでした:\n${preview.error ?? '不明なエラー'}`); return; }
        if (!confirm(`「${novelSource.name}」からゲームシナリオ文書を作成しますか？\n\n${scenarioEstimateLines(preview).join('\n')}`)) return;
        const logEl = document.getElementById('sn-scenario-log');
        logEl.hidden = false;
        logEl.textContent = '';
        const r = await api('/api/story-scenario', { body: { text: novelSource.text, fileName: novelSource.name } });
        if (!r.ok) { logEl.textContent = r.error ?? 'ゲームシナリオ文書の作成に失敗しました'; return; }
        const job = await pollJob(r.jobId, logEl);
        if (job.status !== 'done') {
            if (job.status !== 'canceled') alert(`ゲームシナリオ文書の作成に失敗しました:\n${job.error ?? '不明なエラー'}`);
            return;
        }
        textEl.value = job.result?.text ?? '';
        updateCount();
        textEl.scrollTop = 0;
    };
    document.getElementById('sn-create').onclick = async () => {
        // LLM 実費が発生する工程（① 解析〜）へ進む前に、キーの設定と接続テストをここで必須にする
        if (!(await ensureLlmReady())) return;
        const text = document.getElementById('sn-text').value;
        const fileName = document.getElementById('sn-name').value.trim() || undefined;
        // ファイルだけ読み込んで「ゲームシナリオ文書作成」を押し忘れた状態で進ませない
        if (!text.trim() && novelSource) {
            alert('先に「ゲームシナリオ文書作成」を実行してください。\n\n読み込んだ小説はそのままでは解析に使いません（長すぎるため、ゲームシナリオ文書にまとめてから解析します）。');
            return;
        }
        if (!confirm(`このゲームシナリオ文書で新規プロジェクトを作成しますか？${document.getElementById('sn-batch').checked
            ? '\n\n（作成後の一括実行は、工程一覧と実費の確認ダイアログを経てから開始されます）' : ''}`)) return;
        // 連打による同一物語の多重プロジェクト作成を防ぐ（長文貼り付け時は応答が伸びるため必須）
        const r = await runBlocking('プロジェクトを作成しています…', () => api('/api/projects', { body: { text, fileName } }));
        if (!r.ok) {
            document.getElementById('sn-result').innerHTML = `<p class="err">${esc(r.error ?? '作成に失敗しました')}</p>`;
            return;
        }
        if (document.getElementById('sn-batch').checked) {
            // プロジェクト画面へ遷移後、renderProject が一括実行（確認ダイアログつき）を起動する
            sessionStorage.setItem('vn-batch-autorun', r.project.projectId);
        }
        location.hash = `#/project/${r.project.projectId}/analyze`;
    };
}

// ─────────────────────────────────────────────
// 一括実行（おまかせ）: 未完了の工程を 1 クリック＋確認 1 回で連続実行する
// ─────────────────────────────────────────────
// 開始ワールドの土台（マイワールド 6 種）の表示ラベル。実体は config/story-pipeline.json の
// startWorldBases（選定ロジック側）と揃える
const START_WORLD_BASE_LABEL = {
    bottom_world: '基本（平原）', sea_world: '海', sky_world: '空',
    soil_world: '砂', space_world: '宇宙', water_world: '水中',
};

const BATCH_STEP_LABEL = {
    analyze: '① 解析', scaffold: '② ベースデータ生成', prompts: '③-1 画像生成プロンプト', images: '③-2 画像の自動生成',
    models: '③-3 3Dモデルの自動生成', world: '④ ワールド設計', startScreen: '⑤ 開始画面設計',
    request: '⑥-1 AI 依頼文', generate: '⑥-2 JSON 生成', import: '⑥-3 取り込みと検証',
};

/** 工程記録は成功時に ok を省略できるため、「記録あり・明示的な失敗ではない」を完了とする。 */
function isProjectStepComplete(projectMeta, stepId) {
    const step = projectMeta?.steps?.[stepId];
    return !!step && step.ok !== false;
}

/** 現行カタログに必要なLLMユニットのうち、未生成または失敗中のID。
 *  generated が応答に含まれない（＝実生成状態を取得できていない）場合は「不明」として空を返す。
 *  欠損を「全ユニット未生成」と誤判定すると、完了済みの案件に「⑥-2 JSON 生成（未生成 N 件）」が
 *  出続けてプレビューまで消える（生成済みかどうかは応答の generated だけが根拠であり、
 *  null＝status.json が実際に無い、は従来どおり未生成として扱う）。 */
function missingGeneratedUnitIds(projectState) {
    if (!Array.isArray(projectState?.units)) return [];
    if (projectState?.generated === undefined) return [];
    const status = projectState?.generated?.units ?? {};
    return projectState.units
        .filter((unit) => unit.kind === 'llm' && !status[unit.id]?.ok)
        .map((unit) => unit.id);
}

/** 工程フラグと現行ユニットカタログの実生成状態を照合する。 */
function isProjectStateStepComplete(projectState, stepId) {
    const meta = projectState?.meta ?? projectState;
    if (!isProjectStepComplete(meta, stepId)) return false;
    if (stepId === 'generateJson' || stepId === 'import') {
        return missingGeneratedUnitIds(projectState).length === 0;
    }
    return true;
}

/** GLB生成、またはプレイアブル3点セットのゲーム設定反映が必要か。 */
function needsCastModelStep(entry) {
    if (!entry?.modelGeneratedAt) return true;
    const playable = entry.role === 'protagonist' || entry.role === 'partner';
    return playable && !entry.modelAppliedAt;
}

/** プロジェクト詳細（getProjectDetail 応答）から未完了の工程を実行順で返す。 */
function remainingPipelineSteps(d) {
    const meta = d.meta ?? {};
    const steps = [];
    if (!d.analysis) steps.push('analyze');
    if (!meta.gameId) steps.push('scaffold');
    if (!d.imagePrompts) steps.push('prompts');
    // 画像が 1 体でも未生成なら自動生成を挟む（プロンプト未生成時は全キャスト分を生成）
    if (!d.imagePrompts || (d.imagePrompts.prompts ?? []).some((e) => !e.imageFile)) steps.push('images');
    // GLB が 1 体でも未生成なら ④ へ進めず ③ に留める（2026-07-11 指示。
    // 旧データで生成済みGLBが characters.json へ未反映の場合も、キャッシュ再利用（追加課金なし）で同期する）
    if (!d.imagePrompts || (d.imagePrompts.prompts ?? []).some(needsCastModelStep)) steps.push('models');
    // v1 設計だけが LLM 再設計の対象（v2.0→v2.1 は⑥実行時に無償で自動移行される）
    const needsWorldV2 = !d.worldDesign || !String(d.worldDesign.formatVersion ?? '').startsWith('2.');
    if (needsWorldV2) steps.push('world');
    // ⑤ 開始画面設計: 背景＋プレイアブル立ち絵がそろっていなければ生成する（手動アップロード分は保全）
    if (needsWorldV2 || !d.startScreen?.artMode) steps.push('startScreen');
    // ⑥は画面の工程番号どおり、依頼文 → JSON → 取り込みを個別に判定する。
    // 前工程が未完了なら、メタデータが不整合でも必ず後工程までやり直す。
    const needsRequest = needsWorldV2 || !d.requestText || !isProjectStepComplete(meta, 'requestText');
    const needsGenerate = needsRequest || !isProjectStateStepComplete(d, 'generateJson');
    const needsImport = needsGenerate || !isProjectStateStepComplete(d, 'import');
    if (needsRequest) steps.push('request');
    if (needsGenerate) steps.push('generate');
    if (needsImport) steps.push('import');
    return steps;
}

/** 一括実行の 1 工程。{ ok } / { ok:false, error } / { canceled:true } を返す。 */
async function runBatchStep(projectId, step, logEl, batchRunId = null) {
    const poll = async (path, bodyObj = {}) => {
        const { response, job } = await startAndPoll(path, bodyObj, logEl, { batchRunId, batchStep: step });
        if (!job) return { ok: false, error: response.error ?? '開始に失敗しました' };
        if (job.status === 'canceled') return { canceled: true, job };
        if (job.status !== 'done') return { ok: false, error: jobFailureMessage(job), job };
        return { ok: true, job };
    };
    switch (step) {
        case 'analyze': return poll(`/api/projects/${projectId}/analyze`);
        case 'scaffold': {
            // Node版は内部の安全検証を経て実ファイルを生成し、ブラウザ版はIndexedDBへベースデータを生成する。
            const r = await api(`/api/projects/${projectId}/scaffold`, { body: { npmCi: true } });
            if (r.ok === false || !r.jobId) {
                return { ok: false, error: r.report?.errors?.join('\n') || r.error || '開始に失敗しました' };
            }
            const job = await pollJob(r.jobId, logEl);
            if (job.status === 'canceled') return { canceled: true, job };
            if (job.status !== 'done' || job.result?.ok === false) return { ok: false, error: jobFailureMessage(job), job };
            return { ok: true, job };
        }
        case 'prompts': return poll(`/api/projects/${projectId}/image-prompts`);
        case 'images': {
            // 直前の③-1で生成されたプロンプトを取得し、画像が無いキャストだけを対象にする
            //（一括の再実行で生成済み画像を作り直して二重課金しないため）
            const detail = await api(`/api/projects/${projectId}`);
            const missing = (detail.imagePrompts?.prompts ?? []).filter((e) => !e.imageFile).map((e) => e.castId);
            if (detail.ok !== false && missing.length === 0) return { ok: true, logLines: ['対象キャストはすべて画像生成済みのためスキップしました。'] };
            return poll(`/api/projects/${projectId}/image-generate`, { ...(missing.length ? { castIds: missing } : {}), batch: true });
        }
        case 'models': {
            // GLB 未生成、またはプレイアブルGLBがゲーム設定へ未反映のキャストだけを対象にする。
            // 旧データの反映処理では Meshy タスクキャッシュを再利用し、二重課金しない。
            const detail = await api(`/api/projects/${projectId}`);
            const missing = (detail.imagePrompts?.prompts ?? []).filter(needsCastModelStep).map((e) => e.castId);
            if (detail.ok !== false && missing.length === 0) return { ok: true, logLines: ['対象キャストはすべてGLB生成・反映済みのためスキップしました。'] };
            return poll(`/api/projects/${projectId}/model-generate`, { ...(missing.length ? { castIds: missing } : {}), batch: true });
        }
        case 'world': return poll(`/api/projects/${projectId}/world-design`);
        case 'startScreen': {
            // 背景・立ち絵の未生成分だけを自動生成する（アップロード・生成済みの素材は再課金しない）
            const detail = await api(`/api/projects/${projectId}`);
            if (detail.ok !== false && detail.startScreen?.artMode) {
                return { ok: true, logLines: ['開始画面素材（背景・立ち絵）は作成済みのためスキップしました。'] };
            }
            return poll(`/api/projects/${projectId}/start-screen/generate`, { batch: true });
        }
        case 'request': {
            const rq = await api(`/api/projects/${projectId}/request-text`, { body: {} });
            if (rq.ok === false) return { ok: false, error: rq.error ?? '依頼文の生成に失敗しました' };
            return { ok: true, logLines: ['⑥-1 AI 依頼文を生成しました。'] };
        }
        case 'generate': {
            // 後日カタログに追加されたユニットだけが未生成な既存案件では、
            // 生成済みユニットへの再課金を避け、未生成分だけを生成する。
            const detail = await api(`/api/projects/${projectId}`);
            if (detail.ok === false) {
                return { ok: false, error: detail.error ?? 'プロジェクト状態の取得に失敗しました' };
            }
            const missing = missingGeneratedUnitIds(detail);
            return poll(`/api/projects/${projectId}/generate-json`, {
                ...(missing.length > 0 ? { units: missing } : {}),
                requireComplete: true,
            });
        }
        case 'import': {
            const r = await poll(`/api/projects/${projectId}/import`);
            if (r.ok && r.job?.result && r.job.result.ok === false) {
                return { ok: false, error: '取り込み後の検証で問題が見つかりました（ログ参照）。「⑥ JSON設計」タブでユニットの再生成や修正ができます。' };
            }
            return r;
        }
        default: return { ok: false, error: `不明な工程: ${step}` };
    }
}

// ── 一括実行の進捗バー ─────────────────────────
// 実処理の進捗率は外部 API（LLM 等）の待ち時間が支配的で事前計測できないため、
// 工程ごとに「見なし配分（%）」を割り当て、実行中は配分上限の 1% 手前まで少しずつ進めて見せる
// （工程完了で配分ぶんへスナップ→全工程完了で 100%）。配分は体感の処理時間に比例させ、
// 明らかに時間のかかる工程（LLM をユニット数ぶん呼ぶ ⑥-2、チャンク解析の ①）を多めにする。
const BATCH_STEP_WEIGHT = { analyze: 20, scaffold: 5, prompts: 13, images: 18, models: 30, world: 12, startScreen: 14, request: 1, generate: 22, import: 10 };
let batchProgress = null; // { value, base, span, scale, timer, delay }

function batchProgressRender() {
    const box = document.getElementById('busy-progress');
    if (!box) return;
    if (!batchProgress) { box.style.display = 'none'; return; }
    box.style.display = '';
    const v = Math.max(0, Math.min(100, Math.round(batchProgress.value)));
    document.getElementById('busy-progress-fill').style.width = `${v}%`;
    document.getElementById('busy-progress-pct').textContent = `${v}%`;
}

function batchProgressStopTimer() {
    if (batchProgress?.timer) { clearTimeout(batchProgress.timer); batchProgress.timer = null; }
}

function batchProgressStart(steps) {
    batchProgressStopTimer();
    const total = steps.reduce((sum, s) => sum + (BATCH_STEP_WEIGHT[s] ?? 10), 0);
    batchProgress = { value: 0, base: 0, span: 0, scale: total > 0 ? 100 / total : 1, timer: null, delay: 0 };
    batchProgressRender();
}

function batchProgressStepBegin(step) {
    if (!batchProgress) return;
    batchProgressStopTimer();
    batchProgress.base = batchProgress.value;
    batchProgress.span = (BATCH_STEP_WEIGHT[step] ?? 10) * batchProgress.scale;
    // 1% ずつ進め、配分上限に近づくほど間隔を広げる（長い外部待ちでも止まって見えにくい。
    // 完了検知は本物の工程完了＝スナップで示すため、ここでは上限の 1% 手前で止める）
    batchProgress.delay = 1000;
    const tick = () => {
        if (!batchProgress) return;
        const cap = batchProgress.base + batchProgress.span - 1;
        if (batchProgress.value < cap) {
            batchProgress.value += 1;
            batchProgressRender();
        }
        batchProgress.delay = Math.min(batchProgress.delay * 1.12, 15000);
        batchProgress.timer = setTimeout(tick, batchProgress.delay);
    };
    batchProgress.timer = setTimeout(tick, batchProgress.delay);
    batchProgressRender();
}

function batchProgressStepDone() {
    if (!batchProgress) return;
    batchProgressStopTimer();
    batchProgress.value = batchProgress.base + batchProgress.span;
    batchProgressRender();
}

/** 成功時は 100% を表示してから閉じる。中断・失敗時はそのまま非表示にする。 */
function batchProgressEnd({ completed = false } = {}) {
    batchProgressStopTimer();
    if (batchProgress && completed) {
        batchProgress.value = 100;
        batchProgressRender();
    }
    batchProgress = null;
    if (!completed) batchProgressRender();
}

// 工程 → 開くタブ（「次の作業」ボタンと一括実行のリアルタイム更新で共用）
const PIPELINE_STEP_TAB = {
    analyze: 'analyze', scaffold: 'scaffold', prompts: 'prompts', images: 'prompts', models: 'prompts',
    world: 'world', startScreen: 'startscreen', request: 'generate', generate: 'generate', import: 'generate',
};
const PIPELINE_STEP_SECTION = { request: 'requestText', generate: 'generateJson', import: 'import' };

// ①〜⑥が全部終わったときの「次の作業」（renderProject が現在のプロジェクトに合わせて更新する）。
let projPublishHint = { available: false, published: false };

/** ①〜⑥完了後の行き先。⑦ 公開へ誘導できないときだけ null（＝ボタンを無効化）。 */
function projFallbackNext() {
    if (!projPublishHint.available) return null;
    return projPublishHint.published
        ? { tab: 'publish', label: '⑦ 公開設定', title: '公開中のゲームの公開状態を確認します' }
        : { tab: 'publish', label: '⑦ 限定公開', title: '①〜⑥が完了しました。URLを知っている人だけが遊べる形で公開できます' };
}

/** ヘッダの「次の作業」ボタンを即時更新する（一括実行の工程完了ごとに呼ぶ）。
 *  nextStep が無ければ全工程完了＝一括実行は無効化し、「次の作業」は ⑦ 公開へ振り替える。 */
function updateProjNextButton(nextStep) {
    const btn = document.getElementById('proj-next');
    const batchBtn = document.getElementById('proj-batch');
    if (!btn) return;
    if (!nextStep) {
        if (batchBtn) {
            batchBtn.disabled = true;
            batchBtn.title = '未作成の工程はありません。前工程を変更した場合に再び実行できます。';
        }
        const fallback = projFallbackNext();
        delete btn.dataset.nextStep;
        if (!fallback) {
            btn.disabled = true;
            btn.textContent = '次の作業: すべて完了';
            btn.title = '全工程が完了しています。';
            delete btn.dataset.next;
            return;
        }
        btn.disabled = false;
        btn.dataset.next = fallback.tab;
        btn.title = fallback.title;
        btn.textContent = `次の作業: ${fallback.label}`;
        delete btn.dataset.ico; // ラベルが変わったのでアイコンを付け直す
        return;
    }
    btn.disabled = false;
    btn.dataset.next = PIPELINE_STEP_TAB[nextStep] ?? 'analyze';
    btn.dataset.nextStep = nextStep;
    btn.title = '現在の進捗で次に必要な画面を開きます';
    btn.textContent = `次の作業: ${BATCH_STEP_LABEL[nextStep] ?? nextStep}`;
    if (batchBtn && !batchBtn.classList.contains('loading')) {
        batchBtn.disabled = false;
        batchBtn.title = '未完了の工程を順番に実行します';
    }
    delete btn.dataset.ico; // ラベルが変わったのでアイコンを付け直す（MutationObserver が再適用）
}

/** 一括実行の完了直後、再描画を待たずにヘッダへプレビューボタンを追加する。 */
function ensureProjectPreviewButton(projectId) {
    if (!IS_BROWSER || document.getElementById('proj-preview')) return;
    const nextBtn = document.getElementById('proj-next');
    if (!nextBtn) return;
    const btn = document.createElement('button');
    btn.id = 'proj-preview';
    btn.title = 'ゲームを新しいタブで開きます';
    btn.textContent = '▶ プレビュー';
    btn.onclick = () => openPreview(projectId);
    nextBtn.insertAdjacentElement('afterend', btn);
}

// 一括実行の工程 → 画面上部の工程チップ（STORY_STEPS の id）対応。
const BATCH_STEP_CHIPS = {
    analyze: ['analyze'], scaffold: ['scaffold'], prompts: ['imagePrompts'], images: [], models: [],
    world: ['worldDesign'], startScreen: ['startScreen'], request: ['requestText'], generate: ['generateJson'], import: ['import'],
};

/** 工程完了時にヘッダの工程チップを即時 ✔（ok 色）へ更新する（一括実行中は画面がロックされ
 *  再描画されないため、ここでリアルタイムに反映する）。 */
function markStepChipsDone(step) {
    for (const chipId of BATCH_STEP_CHIPS[step] ?? []) {
        const chip = document.getElementById(`step-chip-${chipId}`);
        if (chip) {
            chip.className = 'badge ok';
            chip.textContent = `✔ ${chip.dataset.chip ?? ''}`.trim();
        }
    }
}

async function savePipelineBatchRun(projectId, run) {
    return api(`/api/projects/${projectId}/generation-logs`, { body: { run } });
}

function formatStoredRun(run, lines = run?.lines ?? []) {
    const at = String(run?.startedAt ?? '').replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC');
    const status = { running: '実行中', done: '完了', failed: '失敗', canceled: '中断' }[run?.status] ?? run?.status ?? '-';
    const kind = run?.kind === 'pipeline-batch' ? '一括実行全体' : (JOB_KIND_LABEL[run?.kind] ?? run?.kind ?? '生成処理');
    return [`===== ${at} / ${kind} / ${status} =====`, ...lines].join('\n');
}

async function runPipelineBatch(projectId, batchBtn = null) {
    const d = await api(`/api/projects/${projectId}`);
    if (d.ok === false) { alert(`プロジェクトを取得できません:\n${d.error ?? '不明なエラー'}`); return; }
    let steps = remainingPipelineSteps(d);
    if (steps.length === 0) { alert('未完了の工程はありません（取り込みまで完了済みです）。'); return; }
    // LLM を使う工程が含まれる場合はキー設定と疎通を先に確認する（途中で失敗してからの手戻りを防ぐ）。
    // 基準は「プロジェクトを作成して解析へ」と同じ: 実疎通 OK か、API設定画面の接続テスト済み
    const usesLlm = steps.some((s) => ['analyze', 'prompts', 'images', 'world', 'startScreen', 'generate'].includes(s));
    if (usesLlm && !(await ensureLlmReady())) return;
    // ③-3 3Dモデル生成（Meshy）を含む場合はキーを確認。未設定ならスキップして続行できる
    //（従来どおり LLM 工程のみの一括実行。3D は後から③の画面で個別生成できる）
    if (steps.includes('models') && !isApiKeyVerified('meshy')) {
        if (!confirm('3Dモデル未生成のキャストがありますが、Meshy API キーが未設定（または接続テスト未実施）です。\n\n'
            + 'OK ＝ 3Dモデル生成をスキップして残りの工程を実行\n'
            + 'キャンセル ＝ 中止（「API設定」でキーを設定してから再実行）')) {
            location.hash = '#/settings';
            return;
        }
        steps = steps.filter((s) => s !== 'models');
        if (steps.length === 0) { alert('未完了の工程はありません（残りは 3Dモデル生成のみです。③ キャラ生成の画面で個別生成してください）。'); return; }
    }
    const usesMeshy = steps.includes('models');
    if (!confirm(`残りの工程を一括実行します:\n\n${steps.map((s) => `・${BATCH_STEP_LABEL[s]}`).join('\n')}\n\n`
        + (usesLlm ? `LLM 実費の目安: 解析〜JSON ≒$0.02〜0.1${steps.includes('images') ? '＋キャスト画像 ≒$0.01〜0.05/枚 × キャスト数' : ''}${steps.includes('startScreen') ? '＋開始画面素材（背景1枚＋立ち絵×プレイアブル数）≒$0.01〜0.05/枚' : ''}${usesMeshy ? '' : '（3D の自動生成は含まれません）'}\n` : '')
        + (usesMeshy ? '3Dモデル実費の目安: 1体 ≒30〜39P × 未生成体数（Meshy クレジット / 1体あたり数分〜30分）\n' : '')
        + '途中で止めたいときは ESC キーで中断できます。実行しますか？')) return;
    const batchRunId = globalThis.crypto?.randomUUID?.() ?? `batch-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const batchStartedAt = new Date().toISOString();
    const batchLines = [
        `一括実行を開始: ${batchStartedAt}`,
        ...steps.map((step) => `・${BATCH_STEP_LABEL[step]}`),
    ];
    const initialLogSave = await savePipelineBatchRun(projectId, {
        id: `pipeline:${batchRunId}`, kind: 'pipeline-batch', mode: 'batch', batchRunId,
        startedAt: batchStartedAt, finishedAt: batchStartedAt, status: 'running', lines: batchLines,
    });
    if (initialLogSave.ok === false) {
        alert(`一括実行ログの保存を開始できないため、処理を中止しました。\n\n${initialLogSave.error ?? '保存エラー'}`);
        return;
    }
    // 実行中は画面切替がロックされるため、進捗と共通ログをこの場（タブ本体）に描画する
    const body = document.getElementById('tab-body') ?? app;
    body.innerHTML = `<div class="card"><h3>🚀 一括実行（${steps.length}工程）</h3>
        <ol>${steps.map((s) => `<li>${esc(BATCH_STEP_LABEL[s])} <span class="badge" id="batch-badge-${esc(s)}">待機</span></li>`).join('')}</ol>
        ${jobLogBlock('batch-log', { needed: false, unavailable: '処理を開始しています…' })}
        <div id="batch-result"></div>
    </div>`;
    const logEl = document.getElementById('batch-log');
    const setBadge = (s, cls, text) => {
        const b = document.getElementById(`batch-badge-${s}`);
        if (!b) return;
        b.className = `badge ${cls}`;
        b.textContent = text;
        // 実行中の行は行全体を黄色フォントで点滅させる（完了・失敗・中断で解除）
        b.closest('li')?.classList.toggle('batch-running', text === '実行中…');
    };
    let failedStep = null;
    let canceled = false;
    let lastError = '';
    let activeStep = steps[0] ?? null;
    setBusy(true, '一括実行を開始しています…');
    if (batchBtn) { batchBtn.classList.add('loading'); batchBtn.disabled = true; }
    batchProgressStart(steps); // 進捗バー: 工程ごとの見なし配分で 0→100%
    try {
        await completionSoundConfigReady;
        for (const [stepIndex, step] of steps.entries()) {
            activeStep = step;
            setBadge(step, '', '実行中…');
            batchProgressStepBegin(step);
            const r = await runBatchStep(projectId, step, logEl, batchRunId);
            batchLines.push('', `── ${BATCH_STEP_LABEL[step]} ──`, ...(r.job?.log ?? r.logLines ?? []));
            if (r.error) batchLines.push(`✖ ${r.error}`);
            await savePipelineBatchRun(projectId, {
                id: `pipeline:${batchRunId}`, kind: 'pipeline-batch', mode: 'batch', batchRunId,
                batchStep: step, startedAt: batchStartedAt, finishedAt: new Date().toISOString(),
                status: r.canceled ? 'canceled' : (r.ok ? 'running' : 'failed'), lines: batchLines,
            });
            if (r.canceled) { setBadge(step, 'warn', '中断'); canceled = true; updateProjNextButton(step); break; }
            if (!r.ok) { setBadge(step, 'err', '失敗'); failedStep = step; lastError = r.error ?? '不明なエラー'; updateProjNextButton(step); break; }
            setBadge(step, 'ok', '完了');
            markStepChipsDone(step);
            updateProjNextButton(steps[stepIndex + 1] ?? null); // ヘッダの「次の作業」も即時更新（全完了で無効化）
            batchProgressStepDone();
        }
        // 全工程成功時は 100% を一拍（0.7 秒）見せてから閉じる
        if (!failedStep && !canceled) {
            batchProgressEnd({ completed: true });
            await new Promise((resolve) => setTimeout(resolve, 700));
        }
    } catch (error) {
        failedStep = activeStep ?? steps[0] ?? 'analyze';
        lastError = error?.message ?? String(error);
        batchLines.push(`✖ 予期しないエラー: ${lastError}`);
        setBadge(failedStep, 'err', '失敗');
        updateProjNextButton(failedStep);
    } finally {
        const finalStatus = canceled ? 'canceled' : (failedStep ? 'failed' : 'done');
        batchLines.push('', finalStatus === 'done' ? '✔ 一括実行が完了しました。' : finalStatus === 'canceled' ? '■ 一括実行を中断しました。' : `✖ 一括実行が失敗しました: ${lastError}`);
        const finalLogSave = await savePipelineBatchRun(projectId, {
            id: `pipeline:${batchRunId}`, kind: 'pipeline-batch', mode: 'batch', batchRunId,
            startedAt: batchStartedAt, finishedAt: new Date().toISOString(), status: finalStatus,
            ...(lastError ? { error: lastError } : {}), lines: batchLines,
        });
        if (finalLogSave.ok === false) lastError = `${lastError ? `${lastError}\n` : ''}一括実行ログの最終保存に失敗: ${finalLogSave.error ?? '保存エラー'}`;
        batchProgressEnd({ completed: false }); // 失敗・中断・例外時の後始末（成功時は表示済み＝非表示化のみ）
        await finishBusy(finalStatus === 'canceled' ? 'canceled' : (finalStatus === 'done' ? 'success' : 'failure'));
        if (batchBtn) {
            batchBtn.classList.remove('loading');
            batchBtn.disabled = !failedStep && !canceled;
        }
    }
    const resultEl = document.getElementById('batch-result');
    if (!resultEl) return; // 実行中に画面が破棄された場合（通常は起きない）
    if (canceled) {
        resultEl.innerHTML = '<p class="warn">中断しました。「🚀 未作成を一括実行」でいつでも続きから再開できます。</p>';
    } else if (failedStep) {
        resultEl.innerHTML = `<p class="err">「${esc(BATCH_STEP_LABEL[failedStep])}」で失敗しました:</p><pre class="log">${esc(lastError)}</pre>
            <p class="dim">該当タブで個別に再実行するか、原因を解消してから「🚀 未作成を一括実行」で失敗した工程から再開できます。</p>`;
        alert(`一括実行が「${BATCH_STEP_LABEL[failedStep]}」で失敗しました:\n\n${lastError}`);
    } else {
        // 最終工程の成功を確認できた時点で、ヘッダにもプレビューを即時表示する。
        ensureProjectPreviewButton(projectId);
        resultEl.innerHTML = `<p class="ok">✔ 全工程が完了しました。プレビューでゲームの動作を確認してください。</p>
            <div class="actions">
                ${IS_BROWSER ? '<button id="batch-preview" title="ブラウザ内の作業データでゲームを起動します（新しいタブ）">▶ プレビューを開く</button>' : ''}
                <button id="batch-reload" class="secondary" title="ステップ表示を最新の状態に更新します">画面を更新</button>
            </div>`;
        const pv = document.getElementById('batch-preview');
        if (pv) pv.onclick = () => openPreview(projectId);
        document.getElementById('batch-reload').onclick = () => reloadProject(projectId, 'generate');
        alert('🎉 一括実行が完了しました。プレビューで動作を確認してください。');
    }
}

// ─────────────────────────────────────────────
// 物語プロジェクト詳細（F13〜F17）
// ─────────────────────────────────────────────
const PROJECT_TABS = [
    ['analyze', '① 解析'], ['scaffold', '② ベースデータ生成'], ['prompts', '③ キャラ生成'],
    ['world', '④ ワールド設計'], ['startscreen', '⑤ 開始画面設計'], ['generate', '⑥ JSON設計'], ['models', '3D モデル'],
    ['publish', '⑦ 限定公開'],
];

// 進捗チップ（✓①〜✓⑥-3）クリックで開く工程タブ（⑥系は同一の generate タブ）
const STEP_CHIP_TAB = {
    analyze: 'analyze', scaffold: 'scaffold', imagePrompts: 'prompts', worldDesign: 'world',
    startScreen: 'startscreen', requestText: 'generate', generateJson: 'generate', import: 'generate',
};

// ── 公開中プロジェクトの編集ロック（①〜⑥）────────────────────────
// 公開中は作品データを一切変更させない（内容差し替えによる審査回避と、
// 「公開→削除→公開」の繰り返しによる容量回避を作らないため）。⑦ 限定公開タブだけは
// 公開状態の確認・取り下げに使うのでロックしない。
const PUBLISH_LOCKED_TABS = new Set(['analyze', 'scaffold', 'prompts', 'world', 'startscreen', 'generate', 'models']);

// ロック中でも使える「見るだけ」の操作（作品データを変えない・課金しない）。
const PUBLISH_LOCK_ALLOW_SELECTOR = [
    'form[method="dialog"] button',   // ダイアログの「閉じる」
    'button[data-act="view"]',        // ③ プロンプト表示
    'button[data-act="genlog"]',      // ③ 生成ログ表示
    '#ip-view-copy',                  // ③ 英語プロンプトをコピー
    'button[data-ss-preview-char]',   // ⑤ 配置プレビューの表示キャラ切替
    'textarea[readonly]',             // ログ表示欄（元から読み取り専用）
    '[data-lock-allow]',
].join(', ');

// 値をキーボードで打ち替える入力＝ReadOnly で見せる（内容は読める・コピーできる）。
// range / checkbox / radio / file は readOnly が効かないため disabled にする。
const PUBLISH_LOCK_READONLY_INPUT_TYPES = new Set([
    'text', 'search', 'url', 'tel', 'email', 'password', 'number', 'date', 'time', 'datetime-local', 'month', 'week',
]);

const PUBLISH_LOCK_TARGETS = 'button, input, select, textarea';

/** 部品 1 つを閲覧専用にする（ReadOnly か Disable かは種類で決める）。 */
function applyPublishLockTo(el) {
    if (el.matches(PUBLISH_LOCK_ALLOW_SELECTOR) || el.closest('[data-lock-allow]')) return;
    const readonlyInput = el.tagName === 'INPUT' && PUBLISH_LOCK_READONLY_INPUT_TYPES.has(el.type);
    if (el.tagName === 'TEXTAREA' || readonlyInput) { if (!el.readOnly) el.readOnly = true; }
    else if (!el.disabled) el.disabled = true;
    el.classList.add('publish-locked');
    if (el.title !== PUBLISH_LOCK_TIP_EDIT) el.title = PUBLISH_LOCK_TIP_EDIT;
    // <label class="file-btn"><input type="file" hidden>…</label> はボタンに見えるため、
    // 中の input を無効化するだけでなくラベル側も無効表示にする
    el.closest('label.file-btn')?.classList.add('publish-locked');
}

// 画面が生きている間ロックを維持する監視（タブを描き直すたびに張り替える）。
let publishLockObserver = null;

/**
 * 公開中プロジェクトの工程画面を閲覧専用にする（編集系コンポーネントを全て無効化）。
 * 1 回無効化して終わりにはしない: ⑤ 開始画面設計のように**描画後の非同期処理**が
 * `disabled` を付け直す画面（背景の縦横比判定・立ち絵の調整バー同期）があり、
 * その書き戻しでロックが外れるため、以後の変更を監視して即座に戻す。
 */
function lockProjectEditing(rootEl) {
    publishLockObserver?.disconnect();
    publishLockObserver = null;
    if (!rootEl) return;
    for (const el of rootEl.querySelectorAll(PUBLISH_LOCK_TARGETS)) applyPublishLockTo(el);
    publishLockObserver = new MutationObserver((records) => {
        for (const record of records) {
            if (record.type === 'attributes') {
                // ロック済みの部品が有効化されたら戻す（同じ値の再設定では何もしない＝ループしない）
                if (record.target.nodeType === 1 && record.target.classList.contains('publish-locked')) {
                    applyPublishLockTo(record.target);
                }
                continue;
            }
            for (const node of record.addedNodes) { // 後から差し込まれた部品もロックする
                if (node.nodeType !== 1) continue;
                if (node.matches(PUBLISH_LOCK_TARGETS)) applyPublishLockTo(node);
                for (const el of node.querySelectorAll(PUBLISH_LOCK_TARGETS)) applyPublishLockTo(el);
            }
        }
    });
    publishLockObserver.observe(rootEl, {
        subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'readonly'],
    });
}

/** ロック解除（非公開のプロジェクトを開いたとき。前の画面の DOM を監視し続けない）。 */
function releaseProjectEditingLock() {
    publishLockObserver?.disconnect();
    publishLockObserver = null;
}

/** ロックの理由と解除方法（公開の取り下げ）を画面上部に出す。説明だけで行き止まりにしない。 */
function publishLockNoticeHtml(projectId, published) {
    const visibility = VISIBILITY_LABELS[published?.visibility] ?? published?.visibility ?? '';
    return `<div class="card warn" id="proj-lock-notice" data-lock-allow>
        <div class="head-row"><b>公開中のため編集できません</b>${visibility ? `<span class="badge warn">${esc(visibility)}</span>` : ''}</div>
        <p class="dim">公開中の作品は ① 解析 〜 ⑥ JSON設計 の内容を変更できません。編集するには公開を取り下げて、完全に非公開の状態へ戻してください。</p>
        <div class="actions">
            <button onclick="location.hash='#/mygames'" title="公開状態の変更・公開取り下げを行う「マイ公開ゲーム」画面へ移動します">マイ公開ゲームで取り下げる</button>
            <button class="secondary" onclick="location.hash='#/project/${esc(projectId)}/publish'" title="このプロジェクトの公開状態を確認します">⑦ 公開状態を見る</button>
        </div>
    </div>`;
}

async function renderProject(projectId, tab = 'analyze') {
    if (IS_BROWSER && tab === 'models') tab = 'prompts';
    const [d, generationInfo] = await Promise.all([
        api(`/api/projects/${projectId}`),
        tab === 'prompts' ? api('/api/image-models') : Promise.resolve(null),
        ensurePublishedGamesLoaded(), // 公開中なら①〜⑥を閲覧専用にするため、描画前に公開状態を揃える
    ]);
    if (!d.ok) { app.innerHTML = `<p class="err">${esc(d.error ?? 'プロジェクトが見つかりません')}</p>`; return; }
    const meta = d.meta ?? {};
    const published = publishedGameOf(meta.gameId);
    const editLocked = !!published;
    const stepChips = (d.steps ?? []).map((s) => {
        const st = meta.steps?.[s.id];
        // id / data-chip: 一括実行が工程完了のたびにこのチップを ✔（ok 色）へ即時更新するために使う。
        // クリックで該当工程のタブへ移動できる（onclick は描画後に付与）
        const complete = isProjectStateStepComplete(d, s.id);
        return `<span class="badge step-chip ${st ? (complete ? 'ok' : 'err') : ''}" id="step-chip-${esc(s.id)}" data-chip="${esc(s.label.split(' ')[0])}" title="${esc(s.label)}">${st ? (complete ? '✔' : '✖') : '・'} ${esc(s.label.split(' ')[0])}</span>`;
    }).join('');
    const u = meta.usage ?? {};
    // 「次の作業」は一括実行と同じ remainingPipelineSteps で、⑥-1/⑥-2/⑥-3も個別判定する。
    const remaining = remainingPipelineSteps(d);
    const nextStep = remaining[0] ?? null;
    const next = nextStep ? {
        step: nextStep, tab: PIPELINE_STEP_TAB[nextStep] ?? 'analyze', label: BATCH_STEP_LABEL[nextStep],
    } : null;
    // ①〜⑥完了後の行き先は ⑦ 公開（「すべて完了」で行き止まりにしない）。
    // 公開済みかどうかは mine 応答の記憶を使う（このためだけの通信は足さない）
    projPublishHint = { available: AUTH_ENABLED && !!meta.gameId, published: editLocked };
    // 公開中は①〜⑥へ戻す導線を出さない（編集できないため）。行き先は ⑦ 公開設定に固定する
    const headerNext = editLocked ? projFallbackNext() : (next ?? projFallbackNext());
    const gameLabel = meta.gameId
        ? (IS_BROWSER ? '準備済み' : `<a href="#/game/${esc(meta.gameId)}">${esc(meta.gameId)}</a>`)
        : '未生成';
    app.innerHTML = `<h2>📖 <span>${esc(localizedText(d.analysis?.game?.title, '（未解析の物語）'))}</span>${IS_BROWSER ? '' : ` <span class="dim">${esc(projectId)}</span>`}</h2>
    <div class="dim">物語: ${esc(meta.novel?.fileName ?? '-')}（${(meta.novel?.chars ?? 0).toLocaleString()}字）${d.novelAvailable ? '' : '<span class="warn">（原文なし: Git 管理外のため再登録が必要）</span>'}
        / ゲーム: ${gameLabel}
        / AI実行: ${u.llmCalls ?? 0}回${IS_BROWSER ? '' : `（入 ${(u.inputTokens ?? 0).toLocaleString()} / 出 ${(u.outputTokens ?? 0).toLocaleString()}tok）`}</div>
    <div class="project-status">
        <div class="project-status-steps">${stepChips}</div>
        <div class="project-status-actions">
            <button id="proj-batch" ${editLocked
        ? `disabled title="${esc(PUBLISH_LOCK_TIP_EDIT)}"`
        : (next ? 'title="未完了の工程を順番に実行します"' : 'disabled title="未作成の工程はありません。前工程を変更した場合に再び実行できます。"')}>🚀 未作成を一括実行</button>
            <button id="proj-next" class="secondary" ${headerNext
        ? `data-next="${headerNext.tab}"${headerNext.step ? ` data-next-step="${headerNext.step}"` : ''} title="${esc(headerNext.title ?? '現在の進捗で次に必要な画面を開きます')}"`
        : 'disabled title="全工程が完了しています。"'}>${headerNext ? `次の作業: ${esc(headerNext.label)}` : '次の作業: すべて完了'}</button>
            ${isProjectPreviewAvailable(d)
        ? (editLocked
            ? `<button id="proj-preview" disabled title="${esc(PUBLISH_LOCK_TIP_PREVIEW)}">▶ プレビュー</button>`
            : '<button id="proj-preview" title="ゲームを新しいタブで開きます">▶ プレビュー</button>')
        : ''}
        </div>
        <div class="project-status-actions actions-sub">
            ${d.novelAvailable ? '' : '<button id="proj-novel-fix" class="secondary" title="① 解析画面を開いて、物語の原文ファイルを登録し直します">物語の原文を再登録</button>'}
            <button id="proj-generation-logs" class="secondary" title="一括実行全体と個別実行した有料APIの保存済みログ履歴を表示します">生成ログ表示</button>
        </div>
    </div>
    <div class="tabs" style="margin-top:10px">${PROJECT_TABS.filter(([k]) => !(IS_BROWSER && k === 'models') && !(k === 'publish' && !AUTH_ENABLED)).map(([k, label]) => `<button class="${k === tab ? 'active' : ''}" onclick="location.hash='#/project/${esc(projectId)}/${k}'">${label}</button>`).join('')}</div>
    <div id="tab-body"></div>
    <dialog id="project-generation-log-dialog" class="file-change-dialog generation-log-dialog">
        <div class="head-row"><h3>生成ログ履歴</h3><form method="dialog"><button class="secondary">閉じる</button></form></div>
        <p class="dim">これまでに実行した生成処理のログを表示します。</p>
        <textarea id="project-generation-log-body" class="log-box" readonly></textarea>
    </dialog>`;
    // 進捗チップのクリックで該当工程のタブへ移動する（⑥系は該当セクションまで自動スクロール）
    for (const s of d.steps ?? []) {
        const chip = document.getElementById(`step-chip-${s.id}`);
        if (!chip) continue;
        chip.onclick = () => {
            const targetHash = `#/project/${projectId}/${STEP_CHIP_TAB[s.id] ?? 'analyze'}`;
            if (location.hash === targetHash) {
                scrollToStepSection(s.id); // 既に同じタブを表示中なら即スクロール
            } else {
                sessionStorage.setItem('vn-step-scroll', s.id); // タブ描画後に renderProject が消費する
                location.hash = targetHash;
            }
        };
    }
    const previewBtn = document.getElementById('proj-preview');
    if (previewBtn) previewBtn.onclick = () => openPreview(projectId);
    // 原文なしの警告は説明で終わらせず、その場から登録画面（①）へ入れるようにする
    const novelFixBtn = document.getElementById('proj-novel-fix');
    if (novelFixBtn) novelFixBtn.onclick = () => {
        const loadBtn = document.getElementById('an-file-load');
        if (tab === 'analyze' && loadBtn) loadBtn.click(); // ①表示中ならファイル選択を直接開く
        else location.hash = `#/project/${projectId}/analyze`;
    };
    const nextBtn = document.getElementById('proj-next');
    if (nextBtn) nextBtn.onclick = () => {
        const targetHash = `#/project/${projectId}/${nextBtn.dataset.next}`;
        const section = PIPELINE_STEP_SECTION[nextBtn.dataset.nextStep];
        if (location.hash === targetHash && section) scrollToStepSection(section);
        else {
            if (section) sessionStorage.setItem('vn-step-scroll', section);
            location.hash = targetHash;
        }
    };
    const batchBtn = document.getElementById('proj-batch');
    if (batchBtn) batchBtn.onclick = () => runPipelineBatch(projectId, batchBtn);
    document.getElementById('proj-generation-logs').onclick = async () => {
        const r = await api(`/api/projects/${projectId}/generation-logs`);
        const allRuns = (r.runs ?? []).sort((a, b) => String(a.startedAt).localeCompare(String(b.startedAt)));
        const batchRuns = allRuns.filter((run) => run.kind === 'pipeline-batch');
        const individualRuns = allRuns.filter((run) => run.mode === 'individual' && PAID_API_JOB_KINDS.has(run.kind));
        const none = globalThis.VN_I18N?.t?.('（まだログがありません）') ?? '（まだログがありません）';
        const box = document.getElementById('project-generation-log-body');
        const sections = [];
        if (batchRuns.length) sections.push(`【一括実行の全体ログ】\n\n${batchRuns.map((run) => formatStoredRun(run)).join('\n\n')}`);
        if (individualRuns.length) sections.push(`【個別実行した有料APIログ】\n\n${individualRuns.map((run) => formatStoredRun(run)).join('\n\n')}`);
        box.value = sections.join('\n\n\n') || none;
        document.getElementById('project-generation-log-dialog').showModal();
        box.scrollTop = box.scrollHeight;
    };
    // 「物語から作成」画面の「作成後に一括実行」チェック経由の自動起動（確認ダイアログは runPipelineBatch 側で出す）
    if (sessionStorage.getItem('vn-batch-autorun') === projectId && !editLocked) {
        sessionStorage.removeItem('vn-batch-autorun');
        runPipelineBatch(projectId, batchBtn);
    }
    const body = document.getElementById('tab-body');
    const render = {
        analyze: renderProjectAnalyze, scaffold: renderProjectScaffold, prompts: renderProjectPrompts,
        world: renderProjectWorld, startscreen: renderProjectStartScreen, generate: renderProjectGenerate,
        models: renderProjectModels, publish: renderProjectPublish,
    }[tab] ?? renderProjectAnalyze;
    await render(body, projectId, d, generationInfo);
    // 公開中は①〜⑥を閲覧専用にする（⑦ 限定公開タブは公開状態の確認・取り下げに使うので対象外）。
    // ロック後に理由と解除導線（公開の取り下げ）を先頭へ差し込む
    if (editLocked && PUBLISH_LOCKED_TABS.has(tab)) {
        lockProjectEditing(body);
        body.insertAdjacentHTML('afterbegin', publishLockNoticeHtml(projectId, published));
    } else {
        releaseProjectEditingLock();
    }
    // 進捗チップ（⑥-1/⑥-2/⑥-3）クリック経由の遷移なら、⑥ページ内の該当セクションへ自動スクロールする
    const pendingScroll = sessionStorage.getItem('vn-step-scroll');
    if (pendingScroll) {
        sessionStorage.removeItem('vn-step-scroll');
        scrollToStepSection(pendingScroll);
    }
}

/** 工程 id（requestText/generateJson/import）に対応する⑥ページ内セクションへスクロールする。 */
function scrollToStepSection(stepId) {
    document.getElementById(`sec-${stepId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function reloadProject(projectId, tab) {
    renderProject(projectId, tab);
}

const PIPELINE_RESET_WARNING = {
    analyze: '⚠ この操作を実行すると、整合性を保つため②以降の工程データはすべて初期化されます。',
    scaffold: '⚠ この操作を実行すると、整合性を保つため③以降の工程データはすべて初期化されます。',
    imagePrompts: '⚠ この操作を実行すると、整合性を保つため④以降の工程データ（⑤ 開始画面設計を含む）はすべて初期化されます。',
    worldDesign: '⚠ この操作を実行すると、整合性を保つため⑤ 開始画面設計と⑥-1以降の工程データはすべて初期化されます。',
    requestText: '⚠ この操作を実行すると、整合性を保つため⑥-2以降の工程データはすべて初期化されます。',
    generateJson: '⚠ この操作を実行すると、整合性を保つため⑥-3の取り込み結果は初期化されます。',
};

function appendResetWarning(message, stageId, enabled = true) {
    return enabled ? `${message}\n\n${PIPELINE_RESET_WARNING[stageId]}` : message;
}

// 前工程が未完で実行できない画面は、説明だけで終わらせずその工程へ入るボタンを併記する。
const PROJECT_STEP_JUMP = {
    analyze: ['analyze', '① 解析'],
    scaffold: ['scaffold', '② ベースデータ生成'],
    prompts: ['prompts', '③ キャラ生成'],
    world: ['world', '④ ワールド設計'],
    startscreen: ['startscreen', '⑤ 開始画面設計'],
    generate: ['generate', '⑥ JSON設計'],
};

/** 「先に○○が必要」の直後に置く、その工程へのジャンプボタン列。 */
function stepJumpActionsHtml(projectId, stepKeys) {
    const buttons = stepKeys.map((key) => {
        const [tab, label] = PROJECT_STEP_JUMP[key] ?? [];
        if (!tab) return '';
        return `<button class="secondary" onclick="location.hash='#/project/${esc(projectId)}/${tab}'"`
            + ` title="${esc(label)}の画面を開きます">${esc(label)}へ</button>`;
    }).join('');
    return buttons ? `<div class="actions">${buttons}</div>` : '';
}

function renderProjectAnalyze(el, projectId, d) {
    const a = d.analysis;
    el.innerHTML = `<div class="card">
        <h3>① 物語解析</h3>
        <p class="dim">物語から作品設定・キャラクター・舞台案を作成します。結果はあとから編集できます。</p>
        <div class="actions">
            <input type="file" id="an-file" accept=".txt,.md,text/plain,text/markdown" style="display:none">
            <button id="an-file-load" class="secondary">ファイルの読み込み</button>
            <button id="an-run">${a ? '再解析を実行' : '解析を実行'}</button>
        </div>
        ${jobLogBlock('an-log', projectAnalyzeLogState(d))}
    </div>
    <div id="an-result">${a ? renderAnalysisSummary(a) : '<p class="dim">未解析です。</p>'}</div>
    ${a ? `<div class="card"><details><summary>解析結果を編集</summary>
        <textarea id="an-json" class="tall mono">${esc(JSON.stringify(a, null, 2))}</textarea>
        <div class="actions"><button id="an-save">解析結果を保存</button></div>
        <div id="an-save-result"></div></details></div>` : ''}`;
    document.getElementById('an-run').onclick = async () => {
        if (!(await ensureLlmReady())) return;
        // 実費が発生する実行は初回も必ず確認をとる（統一ルール: データ変更・送信・課金は実行前確認）
        if (!confirm(a
            ? appendResetWarning('再解析を実行しますか？\n\n現在の解析結果（画面で編集した内容を含む）は上書きされます。LLM 実費が発生します。', 'analyze')
            : '物語解析を実行しますか？（LLM 実費が発生します）')) return;
        const { response: r, job } = await startAndPoll(`/api/projects/${projectId}/analyze`, {}, document.getElementById('an-log'));
        if (!job) { document.getElementById('an-result').innerHTML = `<p class="err">${esc(r.error ?? '失敗')}</p>`; return; }
        if (job.status === 'done') reloadProject(projectId, 'analyze');
        else reportJobFailure('① 物語解析', job, document.getElementById('an-log'));
    };
    const novelFile = document.getElementById('an-file');
    document.getElementById('an-file-load').onclick = () => {
        novelFile.value = '';
        novelFile.click();
    };
    novelFile.onchange = async () => {
        const file = novelFile.files?.[0];
        if (!file) return;
        const text = await file.text();
        if (text.trim().length < 200) {
            alert('物語の本文が短すぎます（200文字以上のテキストファイルが必要です）。');
            return;
        }
        const message = `物語文書を「${file.name}」に差し替えますか？\n\n現在の解析結果は削除され、再解析が必要になります。`;
        if (!confirm(appendResetWarning(message, 'analyze'))) return;
        const r = await runBlocking('物語文書を読み込んでいます…', () => api(`/api/projects/${projectId}/novel`, {
            method: 'PUT', body: { text, fileName: file.name },
        }));
        if (!r.ok) { alert(r.error ?? '物語文書の読み込みに失敗しました'); return; }
        reloadProject(projectId, 'analyze');
    };
    const saveBtn = document.getElementById('an-save');
    if (saveBtn) saveBtn.onclick = async () => {
        if (!confirm(appendResetWarning('解析結果を上書き保存しますか？', 'analyze', !!d.meta.gameId))) return;
        let json;
        try { json = JSON.parse(document.getElementById('an-json').value); }
        catch (e) { document.getElementById('an-save-result').innerHTML = `<p class="err">JSON 構文エラー: ${esc(e.message)}</p>`; return; }
        const r = await api(`/api/projects/${projectId}/analysis`, { method: 'PUT', body: { analysis: json } });
        if (r.ok) reloadProject(projectId, 'analyze');
        else document.getElementById('an-save-result').innerHTML = `<p class="err">${esc(r.error ?? '保存に失敗')}</p>`;
    };
    const gameSettingsDialog = document.getElementById('game-settings-dialog');
    const subtitleInput = document.getElementById('game-setting-subtitle');
    const subtitleCount = document.getElementById('game-setting-subtitle-count');
    const updateSubtitleCount = () => {
        if (subtitleInput && subtitleCount) subtitleCount.textContent = `${Array.from(subtitleInput.value).length} / 40`;
    };
    document.getElementById('game-settings-edit')?.addEventListener('click', () => {
        updateSubtitleCount();
        gameSettingsDialog?.showModal();
    });
    subtitleInput?.addEventListener('input', updateSubtitleCount);
    const gameSettingsSaveButton = document.getElementById('game-settings-save');
    gameSettingsSaveButton?.addEventListener('click', async () => {
        const subtitle = subtitleInput.value.trim();
        if (Array.from(subtitle).length > 40) {
            document.getElementById('game-settings-save-result').innerHTML = '<p class="err">サブタイトルは40文字以内にしてください。</p>';
            return;
        }
        if (!confirm('ゲーム設定案を上書き保存しますか？\n\n編集した内容は作成済みの工程データ（ゲーム開始画面のタイトル・原作リンク等）へ直接反映されます。他の工程の作り直しは不要です。')) return;
        const next = JSON.parse(JSON.stringify(a));
        next.game.title.ja = document.getElementById('game-setting-title-ja').value.trim();
        next.game.title.en = document.getElementById('game-setting-title-en').value.trim();
        next.game.subtitle = subtitle;
        next.game.originalUrl = document.getElementById('game-setting-original-url').value.trim() || null;
        next.game.genre = document.getElementById('game-setting-genre').value.trim();
        next.game.worldSetting = document.getElementById('game-setting-world').value.trim();
        next.game.description.ja = document.getElementById('game-setting-description-ja').value.trim();
        next.game.description.en = document.getElementById('game-setting-description-en').value.trim();
        const requestedOriginalUrl = next.game.originalUrl;
        const result = document.getElementById('game-settings-save-result');
        result.innerHTML = '<p class="dim">保存しています…</p>';
        const r = await runBlocking(
            'ゲーム設定を保存しています…',
            () => api(`/api/projects/${projectId}/analysis`, {
                method: 'PUT', body: { analysis: next, settingsOnly: true },
            }),
            gameSettingsSaveButton,
        );
        if (!r.ok) {
            result.innerHTML = `<p class="err">${esc(r.error ?? '保存に失敗')}</p>`;
            return;
        }
        if (requestedOriginalUrl && !r.analysis?.game?.originalUrl) {
            alert('原作URLは、読み込んだ小説文書内に記載された対応小説公開サイトのURLだけを設定できます。URLは保存されませんでした。');
        }
        gameSettingsDialog?.close();
        reloadProject(projectId, 'analyze');
    });
}

function renderAnalysisSummary(a) {
    const g = a.game;
    const sheetRows = [
        ['作品タイトル', a.summary.title], ['主人公', a.summary.protagonist], ['プレイアブルキャラ', a.summary.playable],
        ['都市・マップ一覧', a.summary.maps], ['主要建築物', a.summary.buildings], ['NPC 一覧', a.summary.npcs],
        ['敵一覧', a.summary.enemies], ['アイテム一覧', a.summary.items], ['スキル一覧', a.summary.skills],
        ['章イベント', a.summary.events], ['BGM / SE 方針', a.summary.audio], ['翻訳方針', a.summary.translation],
    ];
    const originalUrlCell = g.originalUrl
        ? `<a href="${esc(g.originalUrl)}" target="_blank" rel="noopener noreferrer">${esc(g.originalUrl)}</a>`
        : '（なし）';
    return `<div class="card">
        <div class="head-row"><h3>ゲーム設定案</h3><button id="game-settings-edit" class="secondary game-settings-edit">編集</button></div>
        <table>
            <tr><th>タイトル</th><td>${esc(g.title.ja)} / ${esc(g.title.en)}</td><th>ジャンル</th><td>${esc(g.genre)}</td></tr>
            <tr><th>サブタイトル</th><td colspan="3">${esc(g.subtitle || '（未記入）')} <span class="dim">（${Array.from(g.subtitle || '').length} / 40）</span></td></tr>
            <tr><th>原作URL</th><td colspan="3">${originalUrlCell}</td></tr>
            <tr><th>最初の舞台</th><td>${esc(localizedText(a.world.startMap.name))}</td><th>舞台タイプ</th><td>${esc(START_WORLD_BASE_LABEL[g.startWorldBase] ?? '基本（平原）')}</td></tr>
            <tr><th>主人公</th><td>${g.protagonist === 'girl' ? '少女側' : '少年側'} = ${esc(localizedText(g.protagonist === 'girl' ? g.characters.girl.name : g.characters.boy.name))}</td>
                <th>プレイアブル</th><td>${esc(localizedText(g.characters.boy.name))} / ${esc(localizedText(g.characters.girl.name))}</td></tr>
        </table>
    </div>
    <div class="card">
        <h3>作品要約シート（12項目・自動推測）</h3>
        <table>${sheetRows.map(([k, v]) => `<tr><th style="white-space:nowrap">${k}</th><td>${esc(v || '（未記入）')}</td></tr>`).join('')}</table>
    </div>
    <div class="card">
        <h3>キャスト（${a.cast.length}体）</h3>
        <table><tr><th>区分</th><th>名前</th><th>外見</th></tr>
        ${a.cast.map((c) => {
            return `<tr><td>${esc(c.role)}</td><td>${esc(c.name.ja)} / ${esc(c.name.en)}</td><td class="dim">${esc((c.appearance || '').slice(0, 120))}</td></tr>`;
        }).join('')}</table>
    </div>
    <dialog id="game-settings-dialog" class="game-settings-dialog">
        <div class="head-row"><h3>ゲーム設定案を編集</h3><form method="dialog"><button class="secondary">閉じる</button></form></div>
        <div class="row">
            <label class="field"><span>タイトル（日本語）</span><input type="text" id="game-setting-title-ja" value="${esc(g.title.ja)}"></label>
            <label class="field"><span>タイトル（英語）</span><input type="text" id="game-setting-title-en" value="${esc(g.title.en)}"></label>
        </div>
        <label class="field"><span>サブタイトル（40文字以内）</span><input type="text" id="game-setting-subtitle" maxlength="40" value="${esc(g.subtitle || '')}"><small id="game-setting-subtitle-count" class="dim"></small></label>
        <label class="field"><span>原作URL</span><input type="text" inputmode="url" id="game-setting-original-url" value="${esc(g.originalUrl || '')}"></label>
        <p class="dim">原作URLは、読み込んだ小説文書内に記載された「なろう・カクヨム・Royal Road」等の対応小説公開サイトURLだけを使用します。文書にURLがなければフッターの原作リンクは表示されません。</p>
        <div class="row">
            <label class="field"><span>ジャンル</span><input type="text" id="game-setting-genre" value="${esc(g.genre || '')}"></label>
            <label class="field"><span>世界設定</span><input type="text" id="game-setting-world" value="${esc(g.worldSetting || '')}"></label>
        </div>
        <label class="field"><span>紹介文（日本語）</span><textarea id="game-setting-description-ja">${esc(g.description?.ja || '')}</textarea></label>
        <label class="field"><span>紹介文（英語）</span><textarea id="game-setting-description-en">${esc(g.description?.en || '')}</textarea></label>
        <div class="actions"><button id="game-settings-save">変更を保存</button><form method="dialog"><button class="secondary">キャンセル</button></form></div>
        <div id="game-settings-save-result"></div>
    </dialog>`;
}

function renderProjectScaffold(el, projectId, d) {
    const done = !!d.meta.gameId;
    el.innerHTML = `<div class="card">
        <h3>② ${IS_BROWSER ? 'ベースデータを生成' : 'ゲーム生成（スキャフォールド F1）'}</h3>
        ${done ? `<p class="ok">${IS_BROWSER ? '✔ 準備済み' : `✔ 生成済み: <a href="#/game/${esc(d.meta.gameId)}">${esc(d.meta.gameId)}</a>（このプロジェクトから再生成できます）`}</p>` : ''}
        ${!d.analysis ? `<p class="err">先に「① 解析」を実行してください。</p>${stepJumpActionsHtml(projectId, ['analyze'])}` : `
        <p class="dim">${IS_BROWSER
            ? 'ゲーム作成に必要な準備データを作成します。'
            : 'gameId・作品名・2 キャラ構成・開始ワールド ID は解析結果（①のタブで編集可）が使われます。'}</p>
        ${IS_BROWSER ? '' : '<label class="field"><input type="checkbox" id="sc-npmci" checked> 複製後に npm ci を実行する（JSON 生成のスキーマ検証に必要）</label>'}
        <div class="actions">${IS_BROWSER
            ? '<button id="sc-exec">② ベースデータを生成</button>'
            : '<button id="sc-dry">① ドライラン</button><button id="sc-exec" disabled>② 実行</button>'}
        </div>`}
        <div id="sc-report"></div>
        ${jobLogBlock('sc-log', projectScaffoldLogState(d))}
    </div>`;
    if (!d.analysis) return;
    const buildBody = () => ({ npmCi: document.getElementById('sc-npmci')?.checked ?? false });
    document.getElementById('sc-npmci')?.addEventListener('change', () => {
        document.getElementById('sc-exec').disabled = true;
    });
    const scaffoldDryRunButton = document.getElementById('sc-dry');
    if (scaffoldDryRunButton) scaffoldDryRunButton.onclick = async () => {
        const r = await api(`/api/projects/${projectId}/scaffold`, { body: { ...buildBody(), dryRun: true } });
        const rep = r.report;
        const el2 = document.getElementById('sc-report');
        if (!rep) { el2.innerHTML = `<p class="err">${esc(r.error ?? '失敗')}</p>`; return; }
        el2.innerHTML = `<div class="card">
            <h3>ドライラン結果（gameId: ${esc(rep.opts?.gameId ?? '')}）</h3>
            ${rep.errors.length ? `<ul class="plain">${rep.errors.map((e) => `<li class="err">${esc(e)}</li>`).join('')}</ul>` : ''}
            <ol>${rep.steps.map((s) => `<li>${esc(s)}</li>`).join('')}</ol>
            ${rep.warnings.length ? `<ul class="plain">${rep.warnings.map((w) => `<li class="warn">⚠ ${esc(w)}</li>`).join('')}</ul>` : ''}
        </div>`;
        document.getElementById('sc-exec').disabled = !rep.ok;
    };
    document.getElementById('sc-exec').onclick = async () => {
        const confirmation = IS_BROWSER
            ? '② ベースデータを生成しますか？\n\n後続工程の土台となるキャラ設定・共有データ・開始ワールドを作成します。'
            : '② ゲーム生成を実行しますか？\n\n（ドライラン内容のとおり、ゲーム環境一式を新規作成します）';
        if (!confirm(appendResetWarning(confirmation, 'scaffold', done))) return;
        document.getElementById('sc-exec').disabled = true;
        const { response: r, job } = await startAndPoll(`/api/projects/${projectId}/scaffold`, buildBody(), document.getElementById('sc-log'));
        if (!job) { document.getElementById('sc-report').innerHTML += `<p class="err">${esc(r.error ?? '失敗')}</p>`; return; }
        if (job.status === 'done') reloadProject(projectId, 'scaffold');
        else reportJobFailure(IS_BROWSER ? '② ベースデータ生成' : '② ゲーム生成', job, document.getElementById('sc-log'));
    };
}

function renderProjectPrompts(el, projectId, d, generationInfo = null) {
    const idx = d.imagePrompts;
    const hasDownstreamData = !!d.worldDesign || !!d.requestText
        || Object.keys(d.generated?.units ?? {}).length > 0 || !!d.meta.steps?.import;
    el.innerHTML = `<div class="card">
        <h3>③ キャラ生成</h3>
        <p class="dim">物語に登場するキャラクターの画像と3Dモデルを作成します。</p>
        ${idx
        ? `<table class="cast-info"><tbody><tr>
            <td>画像生成モデル<br><select id="ip-model"><option value="">（読み込み中…）</option></select></td>
            <td class="cast-info-action"><button id="ip-genall" class="btn-xs">全画像の再生成</button></td>
            <td class="cast-info-action"><button id="ip-genglb" class="btn-xs">全GLBの再生成</button></td>
        </tr></tbody></table>`
        : '<div class="actions"><button id="ip-run">プロンプトを生成</button></div>'}
        ${idx && !IS_BROWSER ? '<p class="muted-note">画像自動生成は OpenRouter の 3モデル（コスト安い順に表示・標準は gpt-image-2 ≒$0.0065/枚・実費はログ表示）: gpt-image-2（標準・quality:low・最安 ≒$0.0065/枚・リアル調）/ flux.2-klein-4b（≒$0.014/枚・アニメ調・最速）/ grok-imagine-image-quality（$0.05/枚・アダルト表現の規制が最も緩い）。生成画像は全モデル <b>PNG 透明背景</b>へ統一して保存されます（白背景を自動で透過化＝Meshy 3D モデリングの精度向上）。プロンプトにアダルト系表現を検知すると、生成前に「grok へ切替/プロンプト自動修正/そのまま」を選択できます。モデル選択は保存されます。</p>' : ''}
        ${jobLogBlock('ip-log', projectCharacterLogState(d))}
    </div>
    <div id="ip-list">${idx ? renderPromptCards(idx, projectId, d, generationInfo) : `<p class="dim">未生成です（先に ① 解析が必要）。</p>${stepJumpActionsHtml(projectId, ['analyze'])}`}</div>
    <div class="card" id="ip-view" style="display:none"><div class="head-row"><h3 id="ip-view-title"></h3><button id="ip-view-copy" class="secondary">英語プロンプトをコピー</button></div><pre class="log" id="ip-view-body"></pre></div>
    <dialog id="glb-change-dialog" class="file-change-dialog">
        <div class="head-row"><h3 id="glb-change-title">GLBファイル変更</h3><form method="dialog"><button class="secondary">閉じる</button></form></div>
        <p class="dim">キャラが使用するGLBを種類ごとに個別変更できます。指定した種類のファイルだけが上書きされます。</p>
        <div id="glb-change-list"></div>
    </dialog>
    <dialog id="cast-log-dialog" class="file-change-dialog">
        <div class="head-row"><h3 id="cast-log-title">生成ログ</h3><form method="dialog"><button class="secondary">閉じる</button></form></div>
        <div class="dim" id="cast-log-prompt-label">画像プロンプト生成ログ</div>
        <textarea id="cast-log-prompt" class="log-box" readonly></textarea>
        <div class="dim" id="cast-log-image-label">画像生成ログ</div>
        <textarea id="cast-log-image" class="log-box" readonly></textarea>
        <div class="dim" id="cast-log-glb-label">GLB生成ログ</div>
        <textarea id="cast-log-glb" class="log-box" readonly></textarea>
    </dialog>
    <input type="file" id="ip-upload" accept="image/png,image/jpeg,image/webp" style="display:none">`;
    hydrateCastImages(projectId, el); // ブラウザ版: IndexedDB の画像を blob URL で表示
    hydrateGlbStatus(el); // 推奨GLBの実在確認（未生成は「未作成」表示へ）
    const promptRunBtn = document.getElementById('ip-run');
    if (promptRunBtn) promptRunBtn.onclick = async () => {
        if (!(await ensureLlmReady())) return;
        if (!confirm('全キャストの画像プロンプトを生成しますか？（LLM 実費が発生します）')) return;
        const { response: r, job } = await startAndPoll(`/api/projects/${projectId}/image-prompts`, {}, document.getElementById('ip-log'));
        if (!job) { document.getElementById('ip-list').innerHTML = `<p class="err">${esc(r.error ?? '失敗')}</p>`; return; }
        if (job.status === 'done') reloadProject(projectId, 'prompts');
        else reportJobFailure('③-1 画像生成プロンプト', job, document.getElementById('ip-log'));
    };
    // 画像生成モデルの選択肢（config/llm.json の image.catalog をサーバ配信。OpenRouter の 3 モデル・コスト安い順。
    // 選択は localStorage に保存して次回も維持＝ツール画面の設定でいつでも切替できる）
    let imageModelsInfo = generationInfo?.ok === false ? null : generationInfo;
    (async () => {
        const sel = document.getElementById('ip-model');
        if (!sel) return;
        const r = imageModelsInfo ?? await api('/api/image-models');
        imageModelsInfo = r;
        const groups = r.groups ?? [];
        if (groups.length === 0) { sel.innerHTML = '<option value="">（モデル情報なし）</option>'; return; }
        const saved = localStorage.getItem('vn-image-model');
        const defaultValue = `${r.defaultProvider ?? 'openrouter'}|${r.defaultModel ?? ''}`;
        sel.innerHTML = groups.map((g) => `<optgroup label="${esc(g.label)}">${(g.models ?? []).map((m) => {
            const value = `${g.provider}|${m.id}`;
            const price = m.priceLabel ? `（${m.priceLabel}）` : '';
            const role = m.roleLabel ? `${m.roleLabel}: ` : '';
            return `<option value="${esc(value)}" title="${esc(m.note ?? '')}">${esc(role)}${esc(String(m.id).replace('@cf/', '').replace(/^[^/]+\//, ''))}${esc(price)}</option>`;
        }).join('')}</optgroup>`).join('');
        const values = [...sel.options].map((o) => o.value);
        sel.value = saved && values.includes(saved) ? saved : (values.includes(defaultValue) ? defaultValue : values[0]);
        sel.onchange = () => {
            localStorage.setItem('vn-image-model', sel.value);
            const selectedModel = sel.value.includes('|') ? sel.value.split('|')[1] : '';
            const selectedRole = (r.models ?? []).find((entry) => entry.id === selectedModel)?.role;
            if (OPENROUTER_IMAGE_ROLES.includes(selectedRole)) {
                const settings = getStoredOpenRouterModelSettings();
                settings.imageActiveRole = selectedRole;
                saveStoredOpenRouterModelSettings(settings);
            }
        };
    })();
    // セレクト値 "provider|model" を分解（未選択時は既定 = config 既定モデル）
    const selectedImageModel = () => {
        const v = document.getElementById('ip-model')?.value || '';
        const [provider, model] = v.includes('|') ? v.split('|') : ['', ''];
        return provider && model ? { provider, model } : {};
    };
    const billingNote = () => 'OpenRouter（$ 直接課金・無料枠消費ゼロ。実費はログに表示）';
    // ── アダルト系表現の検知と生成前の選択（計画書§16.10。判定・修正の実体はサーバ側 story-image-gen.mjs）──
    const nsfwMatches = (text) => {
        const kws = imageModelsInfo?.nsfw?.keywords ?? [];
        const escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return kws.filter((k) => new RegExp(`\\b${escRe(k)}\\b`, 'i').test(text ?? ''));
    };
    /** 該当キャストがあれば「grok 切替 / 自動修正 / そのまま」を選ばせる。null = 選択不要（該当なし or 既に grok 選択中） */
    const chooseNsfwPolicy = (riskyEntries, sel) => {
        const rec = imageModelsInfo?.nsfw?.recommendedModel;
        if (!rec || riskyEntries.length === 0) return null;
        const selModel = sel.model ?? imageModelsInfo?.defaultModel ?? '（既定）';
        if (selModel === rec) return null; // 規制が最も緩い推奨モデルを選択済みなら何もしない
        const names = riskyEntries.map((e) => e.nameJa ?? e.castId).join('・');
        const words = [...new Set(riskyEntries.flatMap((e) => nsfwMatches(e.promptEn)))].slice(0, 6).join(', ');
        if (confirm(`⚠ アダルト系表現の可能性があるキャスト: ${names}\n（該当語: ${words}）\n選択中のモデル（${selModel}）では規制により生成が拒否されることがあります。\n\nOK ＝ 該当キャストのみ規制が最も緩い ${rec}（$0.05/枚）へ切り替えて生成（推奨）\nキャンセル ＝ 次の選択肢へ`)) return 'grok';
        if (confirm(`プロンプトを自動修正（性的・扇情的な表現だけを穏当化。キャラの特徴・構図は維持）して ${selModel} のまま生成しますか？\n\nOK ＝ 自動修正して生成\nキャンセル ＝ 修正せずそのまま生成（規制で拒否される可能性あり）`)) return 'sanitize';
        return 'asis';
    };
    const nsfwPolicyLabel = { grok: '該当キャストは grok へ切替', sanitize: 'プロンプト自動修正', asis: 'そのまま生成（拒否リスクあり）' };
    const genallBtn = document.getElementById('ip-genall');
    if (genallBtn) genallBtn.onclick = async () => {
        if (!(await ensureLlmReady())) return;
        const entries = (d.imagePrompts?.prompts ?? []).filter((e) => e.promptEn);
        const count = entries.length;
        const sel = selectedImageModel();
        const nsfwPolicy = chooseNsfwPolicy(entries.filter((e) => nsfwMatches(e.promptEn).length > 0), sel);
        if (!confirm(appendResetWarning(`全 ${count}キャストの画像を自動生成します。\nモデル: ${sel.model ?? '既定'}${nsfwPolicy ? `\nアダルト表現の方針: ${nsfwPolicyLabel[nsfwPolicy]}` : ''}\n課金: ${billingNote()}\n目安: 1枚 ≒ $0.01〜0.05 × ${count}枚。\n実行しますか？`, 'imagePrompts', hasDownstreamData))) return;
        genallBtn.disabled = true;
        try {
            const { response: r, job } = await startAndPoll(
                `/api/projects/${projectId}/image-generate`,
                { ...sel, ...(nsfwPolicy ? { nsfwPolicy } : {}) },
                document.getElementById('ip-log'),
            );
            if (!job) { alert(r.error ?? '画像生成の開始に失敗しました'); return; }
            if (job.status !== 'done') reportJobFailure('一括画像生成（一部）', job, document.getElementById('ip-log'));
            reloadProject(projectId, 'prompts');
        } finally { genallBtn.disabled = false; }
    };
    /** 全GLBの再生成: 画像があるキャスト全員の GLB 一式を Meshy で再生成する（1 ジョブ・キャスト順に逐次）。
     *  画像を変更していないキャストは Meshy タスクキャッシュが再利用されるため追加消費 0P で再配置される。 */
    const genGlbAllBtn = document.getElementById('ip-genglb');
    if (genGlbAllBtn) genGlbAllBtn.onclick = async () => {
        if (!ensureMeshyReady()) return;
        const entries = (d.imagePrompts?.prompts ?? []).filter((e) => e.promptEn);
        const withImage = entries.filter((e) => e.imageFile);
        if (withImage.length === 0) { alert('GLB を生成できるキャストがありません（先に画像を生成してください）。'); return; }
        const skipped = entries.length - withImage.length;
        if (!confirm(appendResetWarning(`全 ${withImage.length}キャストの GLB 一式を Meshy で再生成します。\n`
            + (skipped > 0 ? `（画像未生成の ${skipped}体はスキップします）\n` : '')
            + `GLB: プレイアブル ≒39P〜・人型敵 ≒36P〜・非人型敵/NPC ≒30P × 体数（Meshy クレジット / 1体あたり数分〜30分）\n`
            + '（画像を変更していないキャストは Meshy タスクキャッシュを再利用＝追加消費 0P で再配置されます）\n'
            + 'ESC でいつでも中断できます。実行しますか？', 'imagePrompts', hasDownstreamData))) return;
        genGlbAllBtn.disabled = true;
        try {
            const { response: r, job } = await startAndPoll(
                `/api/projects/${projectId}/model-generate`,
                { castIds: withImage.map((x) => x.castId) },
                document.getElementById('ip-log'),
            );
            if (!job) { alert(r.error ?? '3D生成の開始に失敗しました'); return; }
            if (job.status === 'canceled') return;
            if (job.status !== 'done') reportJobFailure('全GLBの再生成（一部）', job, document.getElementById('ip-log'));
            reloadProject(projectId, 'prompts');
        } finally { genGlbAllBtn.disabled = false; }
    };
    const upload = document.getElementById('ip-upload');
    const fileToBase64 = async (file) => {
        const buf = new Uint8Array(await file.arrayBuffer());
        let bin = '';
        for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
        return btoa(bin);
    };
    if (upload) upload.onchange = async (ev) => {
        const file = ev.target.files?.[0];
        const cast = ev.target.dataset.cast;
        if (!file || !cast) return;
        if (file.size > 25 * 1024 * 1024) { alert('画像が大きすぎます（上限 25MB）'); return; }
        if (!confirm(appendResetWarning(`「${cast}」の画像を「${file.name}」に差し替えますか？\n\n（現在の画像は上書きされます）`, 'imagePrompts', hasDownstreamData))) { ev.target.value = ''; return; }
        const r = await runBlocking('画像をアップロードしています…', async () => {
            return api(`/api/projects/${projectId}/cast-image/${encodeURIComponent(cast)}`, {
                body: { dataBase64: await fileToBase64(file), fileName: file.name },
            });
        });
        if (!r.ok) { alert(r.error ?? '画像の差し替えに失敗しました'); return; }
        reloadProject(projectId, 'prompts');
    };
    const glbDialog = document.getElementById('glb-change-dialog');
    if (glbDialog) glbDialog.addEventListener('click', async (ev) => {
        const saveButton = ev.target.closest('button[data-glb-save]');
        if (!saveButton) return;
        const cast = glbDialog.dataset.cast;
        const slot = saveButton.dataset.glbSave;
        const input = glbDialog.querySelector(`input[data-glb-input="${slot}"]`);
        const status = glbDialog.querySelector(`[data-glb-status="${slot}"]`);
        const file = input?.files?.[0];
        if (!file) { alert('GLBファイルを選択してください'); return; }
        if (!/\.glb$/i.test(file.name)) { alert('GLB形式のファイルを指定してください'); return; }
        if (file.size > 25 * 1024 * 1024) { alert('GLBが大きすぎます（上限 25MB）'); return; }
        if (!confirm(appendResetWarning(`「${cast}」の ${saveButton.dataset.glbLabel} を「${file.name}」に変更しますか？`, 'imagePrompts', hasDownstreamData))) return;
        if (status) status.textContent = 'GLBをアップロードしています…';
        await runButtonTask(saveButton, async () => {
            const r = await api(`/api/projects/${projectId}/cast-glb/${encodeURIComponent(cast)}/${encodeURIComponent(slot)}`, {
                body: { dataBase64: await fileToBase64(file), fileName: file.name },
            });
            if (!r.ok) { if (status) status.textContent = r.error ?? 'GLBの変更に失敗しました'; return; }
            if (status) status.textContent = `✔ 変更完了: ${file.name}（${Math.round(file.size / 1024)}KB）`;
            input.value = '';
        });
    });
    // 共通「英語プロンプトをコピー」（プロンプト表示カードのタイトル右。対象は最後に表示したキャスト）
    const viewCopy = document.getElementById('ip-view-copy');
    if (viewCopy) viewCopy.onclick = async () => {
        const cast = document.getElementById('ip-view').dataset.cast;
        const entry = (d.imagePrompts?.prompts ?? []).find((x) => x.castId === cast);
        if (!entry?.promptEn) return;
        await navigator.clipboard.writeText(entry.promptEn);
        viewCopy.textContent = 'コピーしました';
        setTimeout(() => {
            viewCopy.textContent = '英語プロンプトをコピー';
            delete viewCopy.dataset.ico; // アイコンを付け直す（適用済みマークを外す）
            applyButtonIcons(viewCopy.parentElement);
        }, 1200);
    };
    el.addEventListener('click', async (ev) => {
        const glbLink = ev.target.closest('a[data-glb-path]');
        if (glbLink) {
            ev.preventDefault();
            openGlbViewer(glbLink.dataset.glbPath, glbLink.dataset.glbLabel);
            return;
        }
        const btn = ev.target.closest('button[data-act]');
        if (!btn) return;
        await runButtonTask(btn, async () => {
            const { act, file, cast } = btn.dataset;
            if (act === 'view') {
                const r = await api(`/api/projects/${projectId}/image-prompts/${encodeURIComponent(file)}`);
                const view = document.getElementById('ip-view');
                // 共通「英語プロンプトをコピー」（タイトル右）の対象キャストを記録する
                view.dataset.cast = (d.imagePrompts?.prompts ?? []).find((x) => x.file === file)?.castId ?? '';
                view.style.display = '';
                document.getElementById('ip-view-title').textContent = file;
                document.getElementById('ip-view-body').textContent = r.content ?? r.error ?? '';
                view.scrollIntoView({ behavior: 'smooth' });
            } else if (act === 'regen') {
                if (!(await ensureLlmReady())) return;
                if (!confirm(appendResetWarning(`「${cast}」の画像プロンプトを再生成しますか？（既存は上書き・LLM 実費が発生します）`, 'imagePrompts', hasDownstreamData))) return;
                const { response: r, job } = await startAndPoll(
                    `/api/projects/${projectId}/image-prompts`, { castIds: [cast] }, document.getElementById('ip-log'),
                );
                if (job) {
                    if (job.status === 'done') reloadProject(projectId, 'prompts');
                    else reportJobFailure('プロンプト再生成', job, document.getElementById('ip-log'));
                } else alert(r.error ?? 'プロンプト再生成の開始に失敗しました');
            } else if (act === 'genimg') {
                if (!(await ensureLlmReady())) return;
                const sel = selectedImageModel();
                const entry = (d.imagePrompts?.prompts ?? []).find((x) => x.castId === cast);
                const nsfwPolicy = chooseNsfwPolicy(entry && nsfwMatches(entry.promptEn).length > 0 ? [entry] : [], sel);
                if (!confirm(appendResetWarning(`「${cast}」の画像を自動生成します（1枚 ≒ $0.01〜0.05）。\nモデル: ${sel.model ?? '既定'}${nsfwPolicy ? `\nアダルト表現の方針: ${nsfwPolicyLabel[nsfwPolicy]}` : ''}\n課金: ${billingNote()}\n実行しますか？`, 'imagePrompts', hasDownstreamData))) return;
                const { response: r, job } = await startAndPoll(
                    `/api/projects/${projectId}/image-generate`,
                    { castIds: [cast], ...sel, ...(nsfwPolicy ? { nsfwPolicy } : {}) },
                    document.getElementById('ip-log'),
                );
                if (!job) { alert(r.error ?? '画像生成の開始に失敗しました'); return; }
                if (job.status === 'done') reloadProject(projectId, 'prompts');
                else reportJobFailure('画像生成', job, document.getElementById('ip-log'));
            } else if (act === 'genglb') {
                if (!ensureMeshyReady()) return;
                const entry = (d.imagePrompts?.prompts ?? []).find((x) => x.castId === cast);
                if (!entry?.imageFile) { alert(`先に「画像再生成」でキャスト画像を作成してください: ${cast}`); return; }
                const playable = entry.role === 'protagonist' || entry.role === 'partner';
                const humanoidEnemy = entry.role === 'enemy' && entry.humanoid === true;
                const setNote = playable ? '3点（_wait / _move / _btl）' : entry.role === 'enemy' ? '統合GLB 1点（idle/walk 2クリップ）' : '1点';
                const creditNote = playable ? 'プレイアブル ≒39P' : humanoidEnemy ? '人型敵 ≒36P' : '静的 ≒30P';
                if (!confirm(appendResetWarning(`「${cast}」の GLB 一式（${setNote}）を Meshy で再生成しますか？\n\n（Meshy クレジット消費: ${creditNote}〜 / 所要: 数分〜30分）`, 'imagePrompts', hasDownstreamData))) return;
                const { response: r, job } = await startAndPoll(
                    `/api/projects/${projectId}/model-generate`, { castIds: [cast] }, document.getElementById('ip-log'),
                );
                if (!job) { alert(r.error ?? '3D生成の開始に失敗しました'); return; }
                if (job.status === 'canceled') return;
                if (job.status === 'done') { reloadProject(projectId, 'prompts'); }
                else reportJobFailure(`GLB再生成（${cast}）`, job, document.getElementById('ip-log'));
            } else if (act === 'genlog') {
            // キャスト別の生成ログ履歴を専用ダイアログで表示（上段=画像・下段=GLB）。
            // 一括・個別ともバックエンドがプロジェクトへ保存した同じ履歴を参照する。
            const entry = (d.imagePrompts?.prompts ?? []).find((x) => x.castId === cast);
            document.getElementById('cast-log-title').textContent = `生成ログ: ${entry?.nameJa ?? cast}`;
            const history = await api(`/api/projects/${projectId}/generation-logs`);
            const none = globalThis.VN_I18N?.t?.('（まだログがありません）') ?? '（まだログがありません）';
            for (const [jobKind, boxId, labelId, labelJa] of [
                ['story-image-prompts', 'cast-log-prompt', 'cast-log-prompt-label', '画像プロンプト生成ログ'],
                ['story-image-generate', 'cast-log-image', 'cast-log-image-label', '画像生成ログ'],
                ['story-model-generate', 'cast-log-glb', 'cast-log-glb-label', 'GLB生成ログ'],
            ]) {
                const runs = (history.runs ?? []).filter((run) => {
                    if (run.kind !== jobKind) return false;
                    return Array.isArray(run.castLogs?.[cast]) || (run.castIds ?? []).includes(cast);
                }).sort((a, b) => String(a.startedAt).localeCompare(String(b.startedAt)));
                document.getElementById(labelId).textContent = runs.length ? `${labelJa}（${runs.length}件の履歴）` : labelJa;
                document.getElementById(boxId).value = runs.length
                    ? runs.map((run) => formatStoredRun(run, run.castLogs?.[cast] ?? run.lines ?? [])).join('\n\n')
                    : none;
            }
            document.getElementById('cast-log-dialog').showModal();
            for (const id of ['cast-log-prompt', 'cast-log-image', 'cast-log-glb']) {
                const box = document.getElementById(id);
                box.scrollTop = box.scrollHeight; // 末尾（最新行）を初期表示
            }
            } else if (act === 'replace') {
                const input = document.getElementById('ip-upload');
                input.dataset.cast = cast;
                input.value = '';
                input.click();
            } else if (act === 'replaceglb') {
                const entry = (d.imagePrompts?.prompts ?? []).find((candidate) => candidate.castId === cast);
                if (!entry) return;
                const slots = glbSlotsForPromptEntry(entry);
                glbDialog.dataset.cast = cast;
                document.getElementById('glb-change-title').textContent = `GLBファイル変更: ${entry.nameJa ?? cast}`;
                document.getElementById('glb-change-list').innerHTML = slots.map((slot) => {
                    const previous = entry.manualGlbUploads?.[slot.slot];
                    return `<div class="glb-change-row">
                        <div><strong>${esc(slot.label)}</strong><div class="dim">${esc(slot.name)}</div>${previous ? `<div class="dim">前回の手動変更: ${esc(previous.originalName ?? previous.path)} / ${esc(String(previous.uploadedAt ?? '').replace('T', ' ').slice(0, 16))}</div>` : ''}</div>
                        <input type="file" accept=".glb,model/gltf-binary" data-glb-input="${esc(slot.slot)}">
                        <button type="button" data-glb-save="${esc(slot.slot)}" data-glb-label="${esc(slot.label)}">このGLBを変更</button>
                        <div class="dim" data-glb-status="${esc(slot.slot)}" aria-live="polite"></div>
                    </div>`;
                }).join('');
                glbDialog.showModal();
            }
        });
    });
}

/** GLB を 3D ビューア（viewer.html）で開く。
 *  ブラウザ版: ツール内で生成した GLB（IndexedDB）→ blob URL、無ければ公開アセット（/assets/ 直配信）。
 *  blob URL は viewer タブが読み込むまで生かす必要があるため revoke は同一パスの次回生成時のみ。 */
const glbViewerUrlCache = new Map(); // rel -> blob URL（再生成で古い URL を revoke して差し替え）
async function openGlbViewer(glbPath, label) {
    const rel = String(glbPath ?? '').replace(/^(\.\.\/)+/, '');
    const fileName = rel.split('/').pop() || 'model.glb';
    let src = `/api/asset?path=${encodeURIComponent(rel)}`;
    if (IS_BROWSER) {
        const r = await api('/api/asset-fetch', { body: { path: rel } });
        if (r.ok && r.data) {
            const old = glbViewerUrlCache.get(rel);
            if (old) URL.revokeObjectURL(old);
            src = URL.createObjectURL(new Blob([r.data], { type: r.mime ?? 'model/gltf-binary' }));
            glbViewerUrlCache.set(rel, src);
        } else {
            // 生成前でも公開済みアセット（R2 直配信）にはある場合がある → HEAD で存在確認
            try {
                const head = await fetch(`/${rel}`, { method: 'HEAD' });
                if (!head.ok) { alert(`この GLB はまだ生成・配置されていません: ${label}`); return; }
            } catch {
                alert(`この GLB はまだ生成・配置されていません: ${label}`);
                return;
            }
            src = `/${rel}`;
        }
    }
    window.open(`./viewer.html?src=${encodeURIComponent(src)}&label=${encodeURIComponent(label)}&filename=${encodeURIComponent(fileName)}`, '_blank', 'noopener');
}

// ブラウザ完結版のキャスト画像表示: IndexedDB の画像を API 経由で受け取り blob URL に変換して
// <img data-cast-image> へ流し込む（クリックで原寸表示のリンク先も同じ blob URL）。
// 同一画像の再取得を避けるため「ファイル名＋生成時刻」をキーに URL をキャッシュする
const castImageUrlCache = new Map();
async function hydrateCastImages(projectId, rootEl) {
    if (!IS_BROWSER || !rootEl) return;
    for (const img of rootEl.querySelectorAll('img[data-cast-image]')) {
        const file = img.dataset.castImage;
        const cacheKey = `${projectId}|${file}|${img.dataset.castImageV ?? ''}`;
        delete img.dataset.castImage;
        let url = castImageUrlCache.get(cacheKey);
        if (!url) {
            const r = await api(`/api/projects/${encodeURIComponent(projectId)}/cast-image/${encodeURIComponent(file)}`);
            if (r.ok === false || !r.dataBase64) continue;
            const bin = atob(r.dataBase64);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            url = URL.createObjectURL(new Blob([bytes], { type: r.mime ?? 'image/png' }));
            castImageUrlCache.set(cacheKey, url);
        }
        img.src = url;
        const link = img.closest('a[data-cast-image-link]');
        if (link) link.href = url;
    }
}

function glbSlotsForPromptEntry(entry) {
    const first = String(entry?.glbPath ?? '');
    const glbDir = first.slice(0, first.lastIndexOf('/') + 1);
    // short = カードの「GLB(待機): 」等の行ラベル。label は GLB変更ダイアログ用の従来表記
    if (entry?.role === 'protagonist' || entry?.role === 'partner') {
        return [
            { slot: 'wait', label: '待機（_wait）', short: '待機', name: `${entry.castId}_wait.glb`, path: `${glbDir}${entry.castId}_wait.glb` },
            { slot: 'move', label: '移動（_move）', short: '移動', name: `${entry.castId}_move.glb`, path: `${glbDir}${entry.castId}_move.glb` },
            { slot: 'btl', label: 'バトル（_btl）', short: '戦闘', name: `${entry.castId}_btl.glb`, path: `${glbDir}${entry.castId}_btl.glb` },
        ];
    }
    if (entry?.role === 'enemy') {
        // 敵は idle/walk 2クリップの統合GLB 1ファイル（2026-08-08 本家方式へ統一）
        return [
            { slot: 'model', label: '統合GLB（idle/walk）', short: '統合', name: `${entry.castId}.glb`, path: `${glbDir}${entry.castId}.glb` },
        ];
    }
    return first ? [{ slot: 'model', label: '静的モデル', short: 'モデル', name: first.split('/').pop(), path: first }] : [];
}

/** 推奨GLBリンクの実在を確認し、未生成ファイルのリンクを「未作成」表示へ置き換える。 */
async function hydrateGlbStatus(rootEl) {
    if (!rootEl) return;
    const links = [...rootEl.querySelectorAll('a[data-glb-check]')];
    if (links.length === 0) return;
    const paths = [...new Set(links.map((a) => a.dataset.glbPath))];
    const r = await api('/api/asset-exists', { body: { paths } });
    if (r.ok === false || !r.exists) return; // 確認できない場合はリンクのまま（従来動作）
    for (const a of links) {
        if (r.exists[a.dataset.glbPath]) continue;
        const mark = document.createElement('span');
        mark.textContent = '未作成';
        a.replaceWith(mark);
    }
}

function renderPromptCards(idx, projectId, projectDetail = {}, generationInfo = {}) {
    const roleLabel = { protagonist: '主人公', partner: 'プレイアブル', npc: 'NPC', enemy: '敵' };
    return `<div class="grid">${(idx.prompts ?? []).map((e) => {
        const imgUrl = e.imageFile && !IS_BROWSER
            ? `/api/projects/${encodeURIComponent(projectId)}/cast-image/${encodeURIComponent(e.imageFile)}?v=${encodeURIComponent(e.imageGeneratedAt ?? '')}`
            : null;
        // ブラウザ完結版: 画像は IndexedDB にあり HTTP では配信されないため、
        // data-cast-image を付けて描画後に hydrateCastImages() が blob URL へ差し替える
        const imgBrowser = e.imageFile && IS_BROWSER
            ? `<a data-cast-image-link target="_blank" rel="noopener noreferrer" title="クリックで原寸表示"><img class="prompt-thumb" data-cast-image="${esc(e.imageFile)}" data-cast-image-v="${esc(e.imageGeneratedAt ?? '')}" alt="${esc(e.nameJa)}"></a>`
            : '';
        const nsfwInfo = { grok: '・規制回避: grok へ切替', sanitize: '・規制回避: プロンプト自動修正' }[e.imageNsfwPolicy] ?? '';
        // 画像情報は「[日時] モデル名」の簡潔表示（ベンダー接頭辞は省略・フル情報はツールチップ）
        const imgAt = String(e.imageGeneratedAt ?? '').replace('T', ' ').slice(0, 16);
        const imgInfoFull = e.imageSource === 'upload'
            ? `手動差し替え${e.imageOriginalName ? `（${e.imageOriginalName}）` : ''}`
            : `${e.imageModel ?? '自動生成'}${e.imageProvider === 'openrouter' ? '［OpenRouter］' : ''}`;
        const imgModelValue = e.imageSource === 'upload'
            ? `手動差し替え${e.imageOriginalName ? `（${esc(e.imageOriginalName)}）` : ''}`
            : `${esc(String(e.imageModel ?? '自動生成').split('/').pop())}${nsfwInfo}`;
        const poseLabel = e.pose
            ? ({ aPose: 'A字型', scene: '自然体（シーン）', natural: '自然体' }[e.pose] ?? e.pose)
            : (e.humanoid ? 'A字型' : '自然体'); // 旧エントリ（pose 無し）は従来表記
        const imageCost = formatCostSummaryOrEstimate(
            latestCastCostSummary(projectDetail, 'story-image-generate', e.castId),
            estimateImageGenerationCost(e, generationInfo),
        ) ?? '—';
        const glbCost = formatGlbCostSummaryOrEstimate(
            latestCastCostSummary(projectDetail, 'story-model-generate', e.castId),
            estimateMeshyGenerationCost(e, generationInfo),
        ) ?? '—';
        const glbCostHtml = esc(glbCost).replace('推定 ', '<span class="glb-cost-estimate">推定 </span>');
        const castCostSummary = `<div class="cast-cost-summary">
            <div><span class="cast-cost-title"><span>画像生成コスト</span>: </span><strong>${esc(imageCost)}</strong></div>
            <div class="glb-cost-row"><span class="cast-cost-title"><span>GLB生成コスト</span>: </span><strong class="glb-cost-value">${glbCostHtml}</strong></div>
        </div>`;
        // 画像・GLB 情報は罫線つきテーブルで表示（項目｜内容｜操作｜操作 の 4 列。2026-07-12 指示）。
        // 画像情報行は [日時]｜モデル名 を別セルに分割。GLB は 1 ファイル 1 行で、実在しない
        // ファイルは hydrateGlbStatus() が「未作成」表示へ置き換える。変更・再生成ボタンは
        // 各先頭行の操作列（再生成系は実費発生のため配色は cost クラスで黒背景＋黄色文字）
        const infoRow = (label, cells) => `<tr><th scope="row">${label}</th>${cells}</tr>`;
        const actionCell = (buttonHtml = '', attr = '') => `<td class="cast-info-action"${attr}>${buttonHtml}</td>`;
        const changeImgBtn = `<button class="secondary btn-xs" data-act="replace" data-cast="${esc(e.castId)}">画像変更</button>`;
        const regenImgBtn = `<button class="btn-xs" data-act="genimg" data-cast="${esc(e.castId)}">画像再生成</button>`;
        const changeGlbBtn = `<button class="secondary btn-xs" data-act="replaceglb" data-cast="${esc(e.castId)}">GLB変更</button>`;
        const regenGlbBtn = `<button class="btn-xs" data-act="genglb" data-cast="${esc(e.castId)}">GLB再生成</button>`;
        const imgInfoCells = e.imageFile
            ? `<td>[${esc(imgAt)}]</td><td colspan="2" title="${esc(imgInfoFull)}">${imgModelValue}</td>`
            : '<td colspan="3">未生成（「画像を自動生成」または「画像変更」で外部画像を登録）</td>';
        const glbRows = (() => {
            const files = glbSlotsForPromptEntry(e);
            if (files.length === 0) return infoRow('GLB', `<td>-</td>${actionCell(changeGlbBtn)}${actionCell(regenGlbBtn)}`);
            // 操作セルは rowspan で全 GLB 行にまたがる 1 セルへ縦結合（GLB変更・GLB再生成は
            // 待機行専用でなく全 GLB ファイル共通の操作であることを罫線で示す。2026-07-12 指示）
            const span = ` rowspan="${files.length}"`;
            return files.map((g, i) => infoRow(
                `GLB(${esc(g.short)})`,
                `<td><a href="#" data-glb-check data-glb-path="${esc(g.path)}" data-glb-label="${esc(e.nameJa)}: ${esc(g.name)}" title="クリックで 3D ビューア表示（生成済みの場合）">${esc(g.name)}</a></td>`
                + (i === 0 ? actionCell(changeGlbBtn, span) + actionCell(regenGlbBtn, span) : ''),
            )).join('');
        })();
        // キャラ画像（左セル固定）とプロンプト・ログ操作（右セル固定）をテーブルで区切る（2026-07-13 指示）
        const castImage = imgUrl
            ? `<a href="${imgUrl}" target="_blank" rel="noopener noreferrer" title="クリックで原寸表示"><img class="prompt-thumb" src="${imgUrl}" alt="${esc(e.nameJa)}" loading="lazy"></a>`
            : imgBrowser;
        return `<div class="card">
        <h3>${esc(e.nameJa)} <span class="badge">${esc(roleLabel[e.role] ?? e.role)}</span> <span class="badge ${poseLabel === 'A字型' ? 'ok' : 'warn'}">${esc(poseLabel)}</span></h3>
        <table class="cast-visual"><tbody><tr>
            <td class="cast-visual-image">${castImage}</td>
            <td class="cast-visual-actions">
                <button data-act="view" data-file="${esc(e.file)}">プロンプト表示</button>
                <button class="secondary" data-act="regen" data-cast="${esc(e.castId)}">プロンプト再生成</button>
                <button class="secondary" data-act="genlog" data-cast="${esc(e.castId)}">生成ログ表示</button>
                ${castCostSummary}
            </td>
        </tr></tbody></table>
        <table class="cast-info"><tbody>
            ${infoRow('画像情報', imgInfoCells)}
            ${infoRow('画像ファイル', `<td>${e.imageFile ? esc(e.imageFile) : '未生成'}</td>${actionCell(changeImgBtn)}${actionCell(regenImgBtn)}`)}
            ${glbRows}
        </tbody></table>
    </div>`;
    }).join('')}</div>`;
}

function renderProjectWorld(el, projectId, d) {
    // 再設計バナーは v1 設計のみ（v2.0→v2.1 は構造追加だけのため⑥実行時に自動移行）
    const needsWorldV2 = !!d.worldDesign && !String(d.worldDesign.formatVersion ?? '').startsWith('2.');
    el.innerHTML = `<div class="card">
        <h3>④ ワールド設計</h3>
        ${needsWorldV2 ? '<p class="warn">古い形式の設計です。再設計してください。</p>' : ''}
        <div class="actions"><button id="wd-run">${needsWorldV2 ? '新仕様で再設計' : d.worldDesign ? '再設計を実行（上書き）' : 'ワールド設計を実行'}</button></div>
        ${jobLogBlock('wd-log', projectWorldLogState(d))}
    </div>
    ${d.worldDesignMd
        ? `<div class="card"><h3>設計結果</h3><pre class="log">${esc(d.worldDesignMd)}</pre></div>`
        : (d.analysis
            ? '<p class="dim">未設計です。「ワールド設計を実行」を押してください。</p>'
            : `<p class="dim">未設計です（先に ① 解析が必要）。</p>${stepJumpActionsHtml(projectId, ['analyze'])}`)}
    ${d.worldDesign ? `<div class="card"><details><summary>設計内容を編集</summary>
        <textarea id="wd-json" class="tall mono">${esc(JSON.stringify(d.worldDesign, null, 2))}</textarea>
        <div class="actions"><button id="wd-save">設計を保存</button></div>
        <div id="wd-save-result"></div></details></div>` : ''}`;
    document.getElementById('wd-run').onclick = async () => {
        if (!(await ensureLlmReady())) return;
        if (!confirm(d.worldDesign
            ? appendResetWarning('ワールド設計を再実行しますか？\n\n現在の設計（編集内容を含む）は上書きされます。LLM 実費が発生します。', 'worldDesign')
            : 'ワールド設計を実行しますか？（LLM 実費が発生します）')) return;
        const { response: r, job } = await startAndPoll(`/api/projects/${projectId}/world-design`, {}, document.getElementById('wd-log'));
        if (!job) { document.getElementById('wd-log').textContent = r.error ?? '失敗'; return; }
        // ジョブ開始時点で⑥-3までの取り込み結果は無効化済み。失敗時も古いプレビューを開かせない。
        document.getElementById('proj-preview')?.remove();
        if (job.status === 'done') reloadProject(projectId, 'world');
        else reportJobFailure('ワールド設計', job, document.getElementById('wd-log'));
    };
    const saveBtn = document.getElementById('wd-save');
    if (saveBtn) saveBtn.onclick = async () => {
        if (!confirm(appendResetWarning('ワールド設計を上書き保存しますか？', 'worldDesign', !!d.requestText))) return;
        let json;
        try { json = JSON.parse(document.getElementById('wd-json').value); }
        catch (e) { document.getElementById('wd-save-result').innerHTML = `<p class="err">JSON 構文エラー: ${esc(e.message)}</p>`; return; }
        const r = await api(`/api/projects/${projectId}/world-design`, { method: 'PUT', body: { design: json } });
        if (r.ok) reloadProject(projectId, 'world');
        else document.getElementById('wd-save-result').innerHTML = `<p class="err">${esc(r.error ?? '保存に失敗')}</p>`;
    };
}

// ─────────────────────────────────────────────
// ⑤ 開始画面設計（背景・キャラ選択立ち絵）
// ─────────────────────────────────────────────

function projectStartScreenLogState(d) {
    const ss = d.startScreen;
    if (ss && (ss.backgroundReady || ss.characters?.some((c) => c.ready))) {
        const done = (ss.characters ?? []).filter((c) => c.ready).length;
        const total = (ss.characters ?? []).length;
        return previousResult(
            `背景 ${ss.backgroundReady ? '作成済み' : '未生成'} / 立ち絵 ${done}/${total}${ss.artMode ? '（ゲームへ反映済み）' : '（素材がそろうまで従来画面）'}`,
            d.meta?.steps?.startScreen?.at,
            [stageCostDetail(d, 'story-start-screen')],
        );
    }
    if (!d.analysis) return { needed: false, unavailable: '（① 物語解析の完了後に実行できます）' };
    if (!d.worldDesign) return { needed: false, unavailable: '（④ ワールド設計の完了後に実行できます）' };
    return { needed: true };
}

// ブラウザ完結版の開始画面素材プレビュー: IndexedDB の画像を blob URL 化して流し込む
const startScreenImageUrlCache = new Map();
async function resolveStartScreenImageUrl(projectId, file, version = '') {
    if (!IS_BROWSER) return `/api/projects/${encodeURIComponent(projectId)}/start-screen-image/${encodeURIComponent(file)}?v=${encodeURIComponent(version)}`;
    const cacheKey = `${projectId}|${file}|${version}`;
    let url = startScreenImageUrlCache.get(cacheKey);
    if (!url) {
        const r = await api(`/api/projects/${encodeURIComponent(projectId)}/start-screen-image/${encodeURIComponent(file)}`);
        if (r.ok === false || !r.dataBase64) return null;
        const bin = atob(r.dataBase64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        url = URL.createObjectURL(new Blob([bytes], { type: r.mime ?? 'image/png' }));
        startScreenImageUrlCache.set(cacheKey, url);
    }
    return url;
}

async function hydrateStartScreenImages(projectId, rootEl) {
    if (!rootEl) return;
    for (const img of rootEl.querySelectorAll('img[data-ss-image]')) {
        const file = img.dataset.ssImage;
        delete img.dataset.ssImage;
        const url = await resolveStartScreenImageUrl(projectId, file, img.dataset.ssImageV ?? '');
        if (!url) continue;
        img.src = url;
        // クリックで原寸表示（③ キャラ生成と同じ挙動。blob URL をそのままリンク先にする）
        const link = img.closest('a[data-ss-image-link]');
        if (link) link.href = url;
    }
    for (const div of rootEl.querySelectorAll('[data-ss-bg]')) {
        const file = div.dataset.ssBg;
        delete div.dataset.ssBg;
        const url = await resolveStartScreenImageUrl(projectId, file, div.dataset.ssBgV ?? '');
        if (url) div.style.backgroundImage = `url('${url}')`;
    }
}

function renderProjectStartScreen(el, projectId, d) {
    const ss = d.startScreen;
    if (!d.analysis || !ss) {
        el.innerHTML = `<div class="card"><h3>⑤ 開始画面設計（背景・キャラ選択立ち絵）</h3><p class="dim">未作成です（先に ① 解析が必要）。</p>${stepJumpActionsHtml(projectId, ['analyze'])}</div>`;
        return;
    }
    const casts = ss.characters ?? [];
    const doneCount = casts.filter((c) => c.ready).length;
    const promptsReady = (d.imagePrompts?.prompts ?? []).length > 0;
    const worldReady = !!d.worldDesign;
    const sourceLabel = { auto: '自動生成', upload: 'アップロード' };
    const missing = [
        !worldReady ? '④ ワールド設計（背景の生成に必要）' : null,
        !promptsReady ? '③-1 画像生成プロンプト（立ち絵の生成に必要）' : null,
        !d.meta?.gameId ? '② ベースデータ生成（ゲームへの反映に必要）' : null,
    ].filter(Boolean);
    // 不足工程はその場から開けるようにする（何が足りないかの説明で終わらせない）
    const missingStepKeys = [
        !worldReady ? 'world' : null,
        !promptsReady ? 'prompts' : null,
        !d.meta?.gameId ? 'scaffold' : null,
    ].filter(Boolean);
    // ③ キャラ生成と同じく、サムネイルはクリックで原寸を別タブ表示できるリンクにする
    const imgTag = (file, at, cls) => {
        if (!file) return '<div class="ss-thumb-empty">未生成</div>';
        if (IS_BROWSER) {
            return `<a data-ss-image-link target="_blank" rel="noopener noreferrer" title="クリックで原寸表示"><img class="${cls}" alt="" loading="lazy" data-ss-image="${esc(file)}" data-ss-image-v="${esc(at ?? '')}"></a>`;
        }
        const url = `/api/projects/${esc(projectId)}/start-screen-image/${encodeURIComponent(file)}?v=${encodeURIComponent(at ?? '')}`;
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" title="クリックで原寸表示"><img class="${cls}" alt="" loading="lazy" src="${url}"></a>`;
    };
    el.innerHTML = `<div class="card">
        <h3>⑤ 開始画面設計（背景・キャラ選択立ち絵）</h3>
        <p class="dim">ゲーム開始画面は「環境背景」「選択キャラの全身立ち絵」「タイトル・操作UI（HTML/i18n）」を別レイヤーで重ねます。背景は④ ワールド設計に合わせて文字・人物なしで生成し、立ち絵は③キャラ生成の外見に合わせて生成します。自作画像への差し替えもできます。素材がそろうまでゲームは従来のシンプルな開始画面のままです。</p>
        ${missing.length > 0 ? `<p class="warn">先に完了が必要な工程: ${missing.map(esc).join(' / ')}</p>${stepJumpActionsHtml(projectId, missingStepKeys)}` : ''}
        <div id="ss-runtime-check"></div>
        ${actionRowsHtml(
        // よく使う: 未生成分の自動生成
        `<button id="ss-gen-missing" ${(!worldReady && doneCount === casts.length) ? 'disabled' : ''} title="背景・立ち絵の未生成分だけを自動生成します（生成済み・アップロード済みは課金し直しません）">未生成分を自動生成</button>`,
        // たまにしか使わない: 既存素材を上書きする全再生成
        '<button id="ss-gen-all" class="secondary" title="背景とすべての立ち絵を作り直します（既存素材は上書き）">すべて再生成</button>',
    )}
        ${jobLogBlock('ss-log', projectStartScreenLogState(d))}
    </div>
    <div class="card">
        <h3>背景画像（物語のカバーアート的な環境背景）</h3>
        <div class="ss-slot-row">
            <div class="ss-thumb-box">${imgTag(ss.background.file, ss.background.generatedAt, 'ss-thumb ss-thumb-bg')}</div>
            <div class="ss-slot-info">
                <div>${ss.backgroundReady ? `作成済み（${sourceLabel[ss.background.source] ?? ss.background.source ?? '-'}${ss.background.model ? ` / ${esc(ss.background.model)}` : ''}）` : '未生成'}</div>
                ${actionRowsHtml(
        // よく使う: 背景の生成
        `<button data-ss-act="gen-bg" ${worldReady ? '' : 'disabled'} title="④ ワールド設計から背景プロンプトを合成して生成します">${ss.backgroundReady ? '背景を再生成' : '背景を生成'}</button>`,
        // たまにしか使わない: 自作画像への差し替え
        '<label class="secondary file-btn"><input type="file" id="ss-upload-bg" accept="image/png,image/jpeg,image/webp" hidden>自作画像に差し替え</label>',
    )}
                <p class="dim">背景スケール・クロップ位置・キャラ基準位置の調整は下の「配置プレビュー（近似）」で行えます。</p>
            </div>
        </div>
        <details><summary>背景プロンプト（英語・編集すると次回の背景生成で使用）</summary>
            <textarea id="ss-bg-prompt" class="tall mono" placeholder="（未生成。生成時に④ ワールド設計から自動合成されます）">${esc(ss.background.promptEn ?? '')}</textarea>
            <p class="dim">背景には作品タイトル・文字・人物・UI を入れません（開始画面のタイトルと操作は HTML で重ねるため、日本語版・英語版で同じ画像を共用できます）。</p>
        </details>
    </div>
    <div class="card">
        <h3>キャラ選択立ち絵（プレイアブル ${doneCount}/${casts.length}）</h3>
        <p class="dim">③ キャラ生成の外見記述（英語プロンプト）と同じ内容から、開始画面用の自然な立ち姿・透明背景で生成します。GLB 用の A 字ポーズ画像はそのまま使いません。</p>
        ${casts.map((c) => `
        <div class="ss-slot-row" data-cast="${esc(c.castId)}">
            <div class="ss-thumb-box">${imgTag(c.file, c.generatedAt, 'ss-thumb ss-thumb-char')}</div>
            <div class="ss-slot-info">
                <div><b>${esc(c.nameJa)}</b>（${esc(c.castId)} / ${c.role === 'protagonist' ? '主人公' : '相方'}） — ${c.ready ? `作成済み（${sourceLabel[c.source] ?? c.source ?? '-'}${c.model ? ` / ${esc(c.model)}` : ''}）` : '未生成'}</div>
                ${actionRowsHtml(
        // よく使う: 立ち絵の生成
        `<button data-ss-act="gen-char" data-cast="${esc(c.castId)}" ${promptsReady ? '' : 'disabled'}>${c.ready ? '立ち絵を再生成' : '立ち絵を生成'}</button>`,
        // たまにしか使わない: 自作画像への差し替え・足元の透過補正
        [`<label class="secondary file-btn"><input type="file" data-ss-upload-cast="${esc(c.castId)}" accept="image/png,image/jpeg,image/webp" hidden>自作画像に差し替え</label>`,
            c.ready ? `<button class="secondary" data-ss-act="refine" data-cast="${esc(c.castId)}" title="脚の間に残った白い空間と足元の床影を追加で透過します（画像を作り直さないため API 課金なし）">足元の透過を補正</button>` : ''],
    )}
            </div>
        </div>`).join('')}
    </div>
    <div class="card">
        <h3>配置プレビュー（近似）</h3>
        <p class="dim">背景クロップと立ち絵の位置・大きさの目安です（16:9 画面基準。実ゲームは画面比率により背景の見える範囲が変わりますが、立ち絵と背景の相対的な大きさ・横位置は一致します）。数値を変えたら「調整を保存」でゲームへ反映されます。</p>
        <div class="actions">${casts.map((c, i) => `<button class="secondary ss-preview-char${i === 0 ? ' active' : ''}" data-ss-preview-char="${esc(c.castId)}">${esc(c.nameJa)}</button>`).join('')}</div>
        <div id="ss-preview" class="ss-preview-box">
            <div class="ss-preview-bg" ${ss.background.file ? (IS_BROWSER ? `data-ss-bg="${esc(ss.background.file)}" data-ss-bg-v="${esc(ss.background.generatedAt ?? '')}"` : `style="background-image:url('/api/projects/${esc(projectId)}/start-screen-image/${encodeURIComponent(ss.background.file)}?v=${encodeURIComponent(ss.background.generatedAt ?? '')}')"`) : ''}></div>
            <div class="ss-preview-panel"></div>
            <div class="ss-preview-stage"></div>
            <img id="ss-preview-char" alt="">
            <div class="ss-preview-title">タイトル表示位置（HTML）</div>
        </div>
        <div class="ss-preview-adjust">
            <div>
                <h4>背景画像の調整 <button class="btn-xs secondary" id="ss-bg-reset" title="背景スケール・縦クロップ位置・キャラ基準位置を既定値に戻します（保存するまでゲームには反映されません）">デフォルトに戻す</button></h4>
                <table class="ss-adjust-table">
                    <tr title="背景画像全体の縮尺です（拡大すると外周がクロップされます。横方向のズレはキャラ基準位置で調整）"><td><span>背景スケール（×${esc(String(ss.background.scale))}）</span></td><td><input type="range" id="ss-bg-scale" min="1" max="1.6" step="0.05" value="${esc(String(ss.background.scale))}"></td></tr>
                    <tr><td><span>縦クロップ位置（${esc(String(ss.background.positionY))}%）</span></td><td><input type="range" id="ss-bg-y" min="0" max="100" step="1" value="${esc(String(ss.background.positionY))}"></td></tr>
                    <tr title="背景に描かれた台座（キャラの足場）の中心へ、立ち絵の基準位置を合わせます（全キャラ共通・ゲームの立ち絵位置へ反映）"><td><span>キャラ基準位置（X: ${esc(String(ss.background.stageCenterX))}%）</span></td><td><input type="range" id="ss-stage-x" min="10" max="45" step="1" value="${esc(String(ss.background.stageCenterX))}"></td></tr>
                </table>
                <p class="dim" id="ss-crop-note"></p>
            </div>
            <div>
                <h4 title="上のキャラ切り替えボタンで選択中のキャラに適用されます">立ち絵の調整 <button class="btn-xs secondary" id="ss-char-reset" title="選択中キャラの位置・大きさ・マスクを既定値に戻します（保存するまでゲームには反映されません）">デフォルトに戻す</button></h4>
                <table class="ss-adjust-table">
                    <tr><td><span data-ss-shared-label="offsetX">横位置（0px）</span></td><td><input type="range" data-ss-shared="offsetX" min="-400" max="400" step="2" value="0"></td></tr>
                    <tr><td><span data-ss-shared-label="offsetY">縦位置（0px）</span></td><td><input type="range" data-ss-shared="offsetY" min="-400" max="400" step="2" value="0"></td></tr>
                    <tr><td><span data-ss-shared-label="scale">大きさ（×1）</span></td><td><input type="range" data-ss-shared="scale" min="0.5" max="1.6" step="0.05" value="1"></td></tr>
                </table>
                <label class="field"><input type="checkbox" data-ss-shared="maskEnabled"> 楕円マスクで背景へ馴染ませる（透過でない画像向け）</label>
            </div>
        </div>
        <div class="actions"><button id="ss-save">調整を保存（ゲームへ反映）</button><span class="dim" id="ss-save-result"></span></div>
    </div>`;

    // ── 調整値のローカル状態（保存時に PUT する） ─────────────
    const adjust = {
        background: {
            scale: ss.background.scale,
            positionY: ss.background.positionY,
            stageCenterX: ss.background.stageCenterX,
        },
        characters: Object.fromEntries(casts.map((c) => [c.castId, {
            offsetX: c.offsetX, offsetY: c.offsetY, scale: c.scale, maskEnabled: c.maskEnabled,
        }])),
    };
    let previewCast = casts[0]?.castId ?? null;
    const previewBox = document.getElementById('ss-preview');
    const previewChar = document.getElementById('ss-preview-char');
    const previewStage = previewBox?.querySelector('.ss-preview-stage');
    const previewBg = previewBox?.querySelector('.ss-preview-bg');
    const charUrls = new Map();
    // 背景画像の縦横比から、cover 表示で効かないクロップ軸を無効化して理由を表示する
    //（例: 正方形背景は 16:9 画面で横方向の余白が出ないため「横クロップ位置」が動かない）
    let bgAspect = null;
    const updateCropSliderState = () => {
        const yInput = document.getElementById('ss-bg-y');
        const note = document.getElementById('ss-crop-note');
        if (!yInput || !note) return;
        // 背景スケールで拡大すると縦にも余白が生まれるため、横長画像でもスケール>1なら縦クロップは有効
        const yInert = bgAspect !== null && bgAspect >= (16 / 9) * 1.005 && adjust.background.scale <= 1.001;
        yInput.disabled = yInert;
        note.textContent = yInert
            ? 'ℹ 現在の背景は縦に余白が出ない横長のため、縦クロップ位置は「背景スケール」を上げたときに有効になります。'
            : '';
    };
    const detectBgAspect = async () => {
        if (!ss.background.file) return;
        const url = await resolveStartScreenImageUrl(projectId, ss.background.file, ss.background.generatedAt ?? '');
        if (!url) return;
        const img = new Image();
        img.onload = () => {
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                bgAspect = img.naturalWidth / img.naturalHeight;
                updateCropSliderState();
            }
        };
        img.src = url;
    };
    detectBgAspect();
    const updatePreview = () => {
        if (!previewBox) return;
        if (previewBg) {
            previewBg.style.backgroundPosition = `50% ${adjust.background.positionY}%`;
            previewBg.style.transform = `scale(${Math.round(1.025 * adjust.background.scale * 1000) / 1000})`;
        }
        // 台座ガイドと立ち絵は同じ基準位置（stageCenterX）を中心に置く＝既定でズレない
        const stageX = adjust.background.stageCenterX;
        if (previewStage) previewStage.style.left = `calc(${stageX}% - 11%)`;
        const c = adjust.characters[previewCast];
        const castInfo = casts.find((x) => x.castId === previewCast);
        if (!previewChar || !c || !castInfo?.file) { if (previewChar) previewChar.style.display = 'none'; return; }
        const k = previewBox.clientWidth / 1440; // 実画面幅 ≒1440px 想定の縮尺
        previewChar.style.display = '';
        // ゲーム側の上書きCSS（幅基準: 高さ min(41.63vw,800px)・足元 52px-6.42vw ≒ 92.5px@1440）と同モデル
        previewChar.style.height = `${Math.round(previewBox.clientHeight * 0.74 * c.scale)}px`;
        previewChar.style.left = `calc(${stageX}% + ${Math.round(c.offsetX * k)}px)`;
        previewChar.style.bottom = `${Math.round(previewBox.clientHeight * 0.1142 + c.offsetY * k)}px`;
        // 楕円マスクの効果もプレビューへ反映（エンジンと同じマスク値）
        const mask = c.maskEnabled
            ? 'radial-gradient(ellipse 54% 66% at 50% 52%, #000 0%, #000 48%, rgba(0,0,0,0.78) 64%, rgba(0,0,0,0.28) 82%, transparent 100%)'
            : '';
        previewChar.style.webkitMaskImage = mask;
        previewChar.style.maskImage = mask;
        const url = charUrls.get(castInfo.castId);
        if (url && previewChar.src !== url) previewChar.src = url;
    };
    // 背景側の調整バーへ現在値を反映（「デフォルトに戻す」からも使う）
    const syncBgControls = () => {
        const b = adjust.background;
        const set = (id, value, labelText) => {
            const input = document.getElementById(id);
            if (!input) return;
            input.value = String(value);
            const span = input.closest('tr')?.querySelector('span');
            if (span) span.textContent = labelText;
        };
        set('ss-bg-scale', b.scale, `背景スケール（×${b.scale}）`);
        set('ss-bg-y', b.positionY, `縦クロップ位置（${b.positionY}%）`);
        set('ss-stage-x', b.stageCenterX, `キャラ基準位置（X: ${b.stageCenterX}%）`);
    };
    // 配置プレビュー内の共通調整バー（選択中キャラの値を表示・編集する）。
    // ラベルは背景側と同じ「タイトル（数値）」の横1行表記に統一する
    const sharedLabelText = {
        offsetX: (v) => `横位置（${v}px）`,
        offsetY: (v) => `縦位置（${v}px）`,
        scale: (v) => `大きさ（×${v}）`,
    };
    const syncSharedControls = () => {
        const c = adjust.characters[previewCast];
        for (const input of el.querySelectorAll('[data-ss-shared]')) {
            const key = input.dataset.ssShared;
            input.disabled = !c;
            if (!c) continue;
            if (key === 'maskEnabled') input.checked = c.maskEnabled === true;
            else input.value = String(c[key]);
            const labelEl = el.querySelector(`[data-ss-shared-label="${key}"]`);
            if (labelEl) labelEl.textContent = sharedLabelText[key](c[key]);
        }
    };
    const loadPreviewChar = async () => {
        syncSharedControls();
        const castInfo = casts.find((x) => x.castId === previewCast);
        if (!castInfo?.file) { updatePreview(); return; }
        if (!charUrls.has(castInfo.castId)) {
            const url = await resolveStartScreenImageUrl(projectId, castInfo.file, castInfo.generatedAt ?? '');
            if (url) charUrls.set(castInfo.castId, url);
        }
        updatePreview();
    };
    hydrateStartScreenImages(projectId, el).then(loadPreviewChar);

    // ブラウザ版プレビューは共有エンジン（/_runtime/）のシェルへ素材を注入して表示する。
    // エンジンが開始画面改良前の旧ビルドだと「調整を保存」してもプレビューに反映されないため、
    // 注入アンカーの有無をここで検査して原因をユーザーに知らせる（黙って旧画面のままにしない）
    if (IS_BROWSER) {
        fetch('/_runtime/index.html', { cache: 'no-cache' })
            .then((r) => (r.ok ? r.text() : ''))
            .then((html) => {
                const box = document.getElementById('ss-runtime-check');
                if (!box || !html || html.includes('selected-character-art-frame')) return;
                box.innerHTML = `<p class="warn">${esc('⚠ 共有エンジン（/_runtime/）が開始画面アート未対応の旧ビルドです。エンジンを更新（ローカル開発は sevenfold で npm run build / 公開環境は共有ランタイムの再デプロイ）するまで、保存してもプレビューは従来の開始画面のままになります。')}</p>`;
            })
            .catch(() => { /* 取得失敗時は判定しない（プレビュー自体が開けない状態は別導線で気づける） */ });
    }

    // ── 生成・アップロード・保存のハンドラ ─────────────
    const runGenerate = async (body, label) => {
        if (!(await ensureLlmReady())) return;
        if (!confirm(`${label}\n\n1枚 ≒ $0.01〜0.05（選択中の画像モデルに依存）。実行しますか？`)) return;
        const { response: r, job } = await startAndPoll(`/api/projects/${projectId}/start-screen/generate`, body, document.getElementById('ss-log'));
        if (!job) { alert(r.error ?? '生成の開始に失敗しました'); return; }
        if (job.status === 'canceled') return;
        if (job.status !== 'done') { reportJobFailure('開始画面素材の生成', job, document.getElementById('ss-log')); return; }
        reloadProject(projectId, 'startscreen');
    };
    document.getElementById('ss-gen-missing').onclick = () => runGenerate({ batch: true }, '背景・立ち絵の未生成分を自動生成します。');
    document.getElementById('ss-gen-all').onclick = () => runGenerate(
        { background: true, castIds: casts.map((c) => c.castId), batch: true },
        '背景とすべての立ち絵を作り直します（既存素材は上書きされます）。',
    );
    el.addEventListener('click', async (ev) => {
        const btn = ev.target.closest('button[data-ss-act]');
        if (btn?.dataset.ssAct === 'gen-bg') {
            await runButtonTask(btn, () => runGenerate(
                { background: true },
                ss.backgroundReady ? '背景を再生成します（既存は上書き）。' : '背景を生成します。',
            ));
            return;
        }
        if (btn?.dataset.ssAct === 'gen-char') {
            await runButtonTask(
                btn,
                () => runGenerate({ castIds: [btn.dataset.cast] }, `「${btn.dataset.cast}」の立ち絵を生成します。`),
            );
            return;
        }
        if (btn?.dataset.ssAct === 'refine') {
            await runButtonTask(btn, async () => {
                const cast = btn.dataset.cast;
                if (!confirm(`「${cast}」の立ち絵の足元（脚の間の白残り・床影）を追加で透過します。\n\n画像は作り直さないため API 課金はありません。実行しますか？`)) return;
                const r = await runBlocking('足元の透過を補正しています…', () => api(`/api/projects/${projectId}/start-screen/refine/${encodeURIComponent(cast)}`, { body: {} }));
                if (!r.ok) { alert(r.error ?? '足元の透過補正に失敗しました'); return; }
                if (!r.changed) { alert('補正対象が見つかりませんでした（すでに透過済みか、白残り・床影を検出できませんでした）。'); return; }
                reloadProject(projectId, 'startscreen');
            });
            return;
        }
        const pv = ev.target.closest('button[data-ss-preview-char]');
        if (pv) {
            previewCast = pv.dataset.ssPreviewChar;
            for (const b of el.querySelectorAll('.ss-preview-char')) b.classList.toggle('active', b === pv);
            loadPreviewChar();
        }
    });
    const fileToBase64 = async (file) => {
        const buf = new Uint8Array(await file.arrayBuffer());
        let bin = '';
        for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
        return btoa(bin);
    };
    const uploadSlot = async (slot, file, label) => {
        if (file.size > 25 * 1024 * 1024) { alert('画像が大きすぎます（上限 25MB）'); return; }
        if (!confirm(`${label}を「${file.name}」に差し替えますか？\n\n（現在の画像は上書きされ、ゲームへ即時反映されます）`)) return;
        const r = await runBlocking('画像をアップロードしています…', async () => api(
            `/api/projects/${projectId}/start-screen-image/${encodeURIComponent(slot)}`,
            { body: { dataBase64: await fileToBase64(file), fileName: file.name } },
        ));
        if (!r.ok) { alert(r.error ?? '画像の差し替えに失敗しました'); return; }
        reloadProject(projectId, 'startscreen');
    };
    document.getElementById('ss-upload-bg').onchange = (ev) => {
        const file = ev.target.files?.[0];
        if (file) uploadSlot('background', file, '開始画面の背景画像');
        ev.target.value = '';
    };
    for (const input of el.querySelectorAll('input[data-ss-upload-cast]')) {
        input.onchange = (ev) => {
            const file = ev.target.files?.[0];
            if (file) uploadSlot(input.dataset.ssUploadCast, file, `「${input.dataset.ssUploadCast}」の立ち絵`);
            ev.target.value = '';
        };
    }
    document.getElementById('ss-bg-scale').oninput = (ev) => {
        adjust.background.scale = Number(ev.target.value);
        ev.target.closest('tr').querySelector('span').textContent = `背景スケール（×${adjust.background.scale}）`;
        updateCropSliderState();
        updatePreview();
    };
    document.getElementById('ss-bg-y').oninput = (ev) => {
        adjust.background.positionY = Number(ev.target.value);
        ev.target.closest('tr').querySelector('span').textContent = `縦クロップ位置（${adjust.background.positionY}%）`;
        updatePreview();
    };
    document.getElementById('ss-stage-x').oninput = (ev) => {
        adjust.background.stageCenterX = Number(ev.target.value);
        ev.target.closest('tr').querySelector('span').textContent = `キャラ基準位置（X: ${adjust.background.stageCenterX}%）`;
        updatePreview();
    };
    for (const input of el.querySelectorAll('[data-ss-shared]')) {
        input.oninput = () => {
            const entry = adjust.characters[previewCast];
            if (!entry) return;
            const key = input.dataset.ssShared;
            entry[key] = key === 'maskEnabled' ? input.checked : Number(input.value);
            const labelEl = el.querySelector(`[data-ss-shared-label="${key}"]`);
            if (labelEl) labelEl.textContent = sharedLabelText[key](entry[key]);
            updatePreview();
        };
    }
    // 「デフォルトに戻す」（config/story-pipeline.json startScreen.defaults が正。保存するまでゲームには反映されない）
    const dflt = ss.defaults ?? {};
    document.getElementById('ss-bg-reset').onclick = () => {
        adjust.background.scale = dflt.backgroundScale ?? 1;
        adjust.background.positionY = dflt.backgroundPositionY ?? 50;
        adjust.background.stageCenterX = dflt.stageCenterX ?? 25;
        syncBgControls();
        updateCropSliderState();
        updatePreview();
    };
    document.getElementById('ss-char-reset').onclick = () => {
        const entry = adjust.characters[previewCast];
        if (!entry) return;
        entry.offsetX = dflt.offsetX ?? 0;
        entry.offsetY = dflt.offsetY ?? 0;
        entry.scale = dflt.scale ?? 1;
        entry.maskEnabled = dflt.maskEnabled === true;
        syncSharedControls();
        updatePreview();
    };
    document.getElementById('ss-save').onclick = async () => {
        const promptEn = document.getElementById('ss-bg-prompt')?.value ?? '';
        const r = await runBlocking('調整を保存しています…', () => api(`/api/projects/${projectId}/start-screen`, {
            method: 'PUT',
            body: {
                background: { ...adjust.background, promptEn },
                characters: adjust.characters,
            },
        }));
        const out = document.getElementById('ss-save-result');
        if (!r.ok) { out.textContent = r.error ?? '保存に失敗しました'; return; }
        out.textContent = r.applied ? '✔ 保存してゲームへ反映しました' : '✔ 保存しました（ゲーム生成後に反映されます）';
    };
}

function renderProjectGenerate(el, projectId, d) {
    const llmUnits = (d.units ?? []).filter((u) => u.kind === 'llm');
    const st = d.generated?.units ?? {};
    const canImport = isProjectStateStepComplete(d, 'generateJson');
    const unitLabel = (unit) => String(unit.label ?? unit.id).replace(/\s*[（(][^）)]*\.json[）)]\s*$/, '');
    el.innerHTML = `<div class="card" id="sec-requestText">
        <h3>⑥-1 AI 依頼文</h3>
        <div class="actions">
            <button id="rq-gen">${d.requestText ? '依頼文を再生成（編集内容を破棄して作り直し）' : '依頼文を生成'}</button>
            <button id="rq-save" class="secondary" ${d.requestText ? '' : 'disabled'}>編集内容を保存</button>
            <span id="rq-status" class="dim"></span>
        </div>
        <textarea id="rq-text" class="tall mono" placeholder="（依頼文はまだ生成されていません。①解析 と ④ ワールド設計 の後に生成してください）">${esc(d.requestText ?? '')}</textarea>
    </div>
    <div class="card" id="sec-generateJson">
        <h3>⑥-2 JSON 生成</h3>
        <div class="generate-unit-table-wrap"><table class="generate-unit-table" id="gu-list">
            <thead><tr>
                <th class="generate-unit-check"><label><input type="checkbox" id="gu-check-all"> すべて</label></th>
                <th>項目</th><th>ファイル</th><th>状態</th><th>操作</th>
            </tr></thead>
            <tbody>${llmUnits.map((u) => `<tr>
                <td class="generate-unit-check"><input type="checkbox" data-unit="${esc(u.id)}" aria-label="${esc(unitLabel(u))}を選択" ${st[u.id]?.ok ? '' : 'checked'}></td>
                <td>${esc(unitLabel(u))}</td>
                <td><code>${esc(`${u.id}.json`)}</code></td>
                <td>${st[u.id] ? (st[u.id].ok ? '<span class="badge ok">生成済み</span>' : `<span class="badge err" title="${esc(st[u.id].error ?? '')}">失敗</span>`) : '<span class="badge">未生成</span>'}</td>
                <td class="generate-unit-action"><button class="btn-xs" data-edit-json="${esc(u.id)}" data-json-file="${esc(`${u.id}.json`)}" ${st[u.id]?.ok ? '' : 'disabled'}>編集</button></td>
            </tr>`).join('')}</tbody>
        </table></div>
        <div class="actions"><button id="gu-run">選択ユニットを生成</button></div>
        ${jobLogBlock('gu-log', projectJsonLogState(d, llmUnits))}
        <div id="gu-result"></div>
        <dialog id="json-edit-dialog" class="file-change-dialog json-edit-dialog">
            <div class="head-row"><h3 id="json-edit-title">JSONを編集</h3></div>
            <textarea id="json-edit-text" class="tall mono" spellcheck="false"></textarea>
            <p id="json-edit-note" class="warn" style="display:none">保存後は、もう一度「取り込み実行」が必要です。</p>
            <div id="json-edit-error" class="err" aria-live="polite"></div>
            <div class="actions"><button id="json-edit-save">保存</button><button id="json-edit-cancel" class="secondary">キャンセル</button></div>
        </dialog>
    </div>
    <div class="card" id="sec-import">
        <h3>⑥-3 取り込みと検証</h3>
        <p class="dim">生成した内容をゲームへ反映し、問題がないか確認します。</p>
        <div class="actions">
            <button id="im-exec" ${canImport ? '' : 'disabled title="未生成のJSONがあるため、先に⑥-2 JSON生成を完了してください"'}>取り込み実行</button>
        </div>
        <div id="im-result"></div>
        ${jobLogBlock('im-log', projectImportLogState(d, llmUnits))}
    </div>`;
    document.getElementById('rq-gen').onclick = async () => {
        if (!confirm(d.requestText
            ? appendResetWarning('依頼文を再生成しますか？\n\n画面・保存済みの編集内容は破棄され、既定の内容で作り直されます。', 'requestText')
            : '依頼文を生成しますか？（解析・設計の内容から既定の依頼文を作成します）')) return;
        const r = await api(`/api/projects/${projectId}/request-text`, { body: { force: true } });
        if (r.ok) reloadProject(projectId, 'generate');
        else document.getElementById('rq-status').textContent = r.error ?? '失敗';
    };
    // 依頼文を未生成の状態から自作・貼り付けで書き始めても保存できるようにする
    document.getElementById('rq-text').addEventListener('input', () => { document.getElementById('rq-save').disabled = false; });
    document.getElementById('rq-save').onclick = async () => {
        if (!confirm(appendResetWarning('依頼文を保存しますか？\n\n（保存した内容が ⑥-2 の LLM 実行に使われます）', 'requestText', Object.keys(st).length > 0))) return;
        const r = await api(`/api/projects/${projectId}/request-text`, { method: 'PUT', body: { text: document.getElementById('rq-text').value } });
        if (r.ok) reloadProject(projectId, 'generate');
        else document.getElementById('rq-status').textContent = r.error ?? '保存に失敗';
    };
    // 「全てを☑する」: 全ユニットの一括チェック On/Off（個別操作時は全選択状態へ追従）
    const checkAll = document.getElementById('gu-check-all');
    const unitBoxes = () => [...document.querySelectorAll('#gu-list input[data-unit]')];
    const syncCheckAll = () => {
        const boxes = unitBoxes();
        checkAll.checked = boxes.length > 0 && boxes.every((cb) => cb.checked);
        checkAll.indeterminate = !checkAll.checked && boxes.some((cb) => cb.checked);
    };
    checkAll.onchange = () => { for (const cb of unitBoxes()) cb.checked = checkAll.checked; };
    for (const cb of unitBoxes()) cb.addEventListener('change', syncCheckAll);
    syncCheckAll();
    const jsonDialog = document.getElementById('json-edit-dialog');
    const jsonText = document.getElementById('json-edit-text');
    const jsonError = document.getElementById('json-edit-error');
    let editingUnitId = null;
    for (const button of document.querySelectorAll('button[data-edit-json]')) {
        button.onclick = async () => {
            button.disabled = true;
            button.classList.add('loading');
            const unitId = button.dataset.editJson;
            const r = await api(`/api/projects/${projectId}/generated-json/${encodeURIComponent(unitId)}`);
            button.classList.remove('loading');
            button.disabled = false;
            if (!r.ok) { alert(r.error ?? 'JSONを読み込めませんでした。'); return; }
            editingUnitId = unitId;
            document.getElementById('json-edit-title').textContent = `${r.fileName ?? button.dataset.jsonFile}を編集`;
            jsonText.value = r.content ?? '';
            jsonError.textContent = '';
            document.getElementById('json-edit-note').style.display = d.meta.steps?.import ? '' : 'none';
            jsonDialog.showModal();
            jsonText.focus();
        };
    }
    document.getElementById('json-edit-cancel').onclick = () => jsonDialog.close();
    document.getElementById('json-edit-save').onclick = async () => {
        jsonError.textContent = '';
        try {
            const parsed = JSON.parse(jsonText.value);
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('JSONの最上位はオブジェクトにしてください。');
        } catch (e) {
            jsonError.textContent = e.message.startsWith('JSONの') ? e.message : `JSON構文エラー: ${e.message}`;
            return;
        }
        const saveButton = document.getElementById('json-edit-save');
        saveButton.disabled = true;
        saveButton.classList.add('loading');
        const r = await api(`/api/projects/${projectId}/generated-json/${encodeURIComponent(editingUnitId)}`, {
            method: 'PUT', body: { text: jsonText.value },
        });
        saveButton.classList.remove('loading');
        saveButton.disabled = false;
        if (!r.ok) { jsonError.textContent = r.error ?? '保存できませんでした。'; return; }
        jsonDialog.close();
        reloadProject(projectId, 'generate');
    };
    document.getElementById('gu-run').onclick = async () => {
        if (!(await ensureLlmReady())) return;
        const units = [...document.querySelectorAll('#gu-list input[data-unit]:checked')].map((cb) => cb.dataset.unit);
        if (units.length === 0) { document.getElementById('gu-result').innerHTML = '<p class="warn">ユニットを選択してください。</p>'; return; }
        if (!confirm(appendResetWarning(`選択した ${units.length}ユニットの JSON を生成しますか？\n\n（LLM 実費が発生します。生成済みユニットは上書きされます）`, 'generateJson', !!d.meta.steps?.import))) return;
        const { response: r, job } = await startAndPoll(
            `/api/projects/${projectId}/generate-json`, { units }, document.getElementById('gu-log'),
        );
        if (!job) { document.getElementById('gu-result').innerHTML = `<p class="err">${esc(r.error ?? '失敗')}</p>`; return; }
        if (job.status === 'done') reloadProject(projectId, 'generate');
        else reportJobFailure('⑥-2 JSON 生成', job, document.getElementById('gu-log'));
    };
    const renderImportPlan = (r) => {
        const actionLabel = { overwrite: '更新', merge: '更新', create: '新規' };
        document.getElementById('im-result').innerHTML = `
            <p>事前確認: ${r.plan.length}ファイル</p>
            <table><tr><th>ファイル</th><th>動作</th><th>確認結果</th></tr>
            ${r.plan.map((item) => `<tr><td>${esc(String(item.relPath).split('/').pop())}</td><td>${actionLabel[item.action] ?? esc(item.action)}</td>
                <td>${item.errors.length ? item.errors.map((e) => `<div class="err">${esc(e)}</div>`).join('') : '<span class="ok">OK</span>'}</td></tr>`).join('')}</table>`;
        const allValid = r.plan.length > 0 && r.plan.every((i) => i.errors.length === 0);
        if (!allValid) {
            document.getElementById('im-result').innerHTML += '<p class="err">エラーがあるため取り込みを停止しました。ゲームデータは変更していません。JSON を再生成または修正してください。</p>';
        }
        return allValid;
    };
    document.getElementById('im-exec').onclick = async () => {
        const executeButton = document.getElementById('im-exec');
        executeButton.disabled = true;
        document.getElementById('im-result').innerHTML = '<p class="dim">内容を確認しています…</p>';
        const plan = await api(`/api/projects/${projectId}/import-plan`);
        if (!plan.ok) {
            document.getElementById('im-result').innerHTML = `<p class="err">${esc(plan.error ?? '事前確認に失敗')}</p>`;
            executeButton.disabled = false;
            return;
        }
        if (!renderImportPlan(plan)) {
            executeButton.disabled = false;
            return;
        }
        const overwriteCount = plan.plan.filter((item) => item.action === 'overwrite' || item.action === 'merge').length;
        // 画面上の操作は1ボタンに統合しつつ、既存ファイルを変更する直前の誤クリック確認は維持する。
        if (!confirm(`事前確認に問題はありません。取り込みと検証を実行しますか？\n\n対象: ${plan.plan.length}ファイル（更新: ${overwriteCount}）`)) {
            executeButton.disabled = false;
            return;
        }
        const { response: r, job } = await startAndPoll(`/api/projects/${projectId}/import`, {}, document.getElementById('im-log'));
        if (!job) {
            document.getElementById('im-result').innerHTML += `<p class="err">${esc(r.error ?? '失敗')}</p>`;
            executeButton.disabled = false;
            return;
        }
        if (job.status === 'canceled') { executeButton.disabled = false; return; } // 中断メッセージは pollJob が表示済み
        if (job.status === 'failed') reportJobFailure('取り込みと検証', job, document.getElementById('im-log'));
        const out = job.result ?? {};
        const resultEl = document.getElementById('im-result');
        resultEl.innerHTML = '';
        if (out.schema || out.import) {
            showPreviousResult(document.getElementById('im-log'), importSummaryFromResult(out));
        }
        if (out.ok) {
            // 成功時はステップチップ・「次の作業」を最新化する（検証サマリはログとステップ記録に残る）
            reloadProject(projectId, 'generate');
            return;
        }
        if (out.ok === undefined && job.error) {
            showPreviousResult(document.getElementById('im-log'), `失敗（再実行待ち）\n${job.error}`);
        }
        executeButton.disabled = false;
    };
}

async function renderProjectModels(el, projectId, d) {
    if (!d.meta.gameId) { el.innerHTML = '<p class="dim">ベースデータ未生成のため表示できません（② ベースデータ生成の後に利用可能）。</p>'; return; }
    await renderModelStatusInto(el, d.meta.gameId, projectId);
}

// ─────────────────────────────────────────────
// 3D モデル（GLB）状態 + プレビュー（F17。ゲーム詳細/プロジェクト共用）
// ─────────────────────────────────────────────
async function renderModelStatusInto(el, gameId, projectIdForPrompts = null) {
    const r = await api(`/api/games/${gameId}/models`);
    if (!r.ok) { el.innerHTML = `<p class="err">${esc(r.error ?? '取得に失敗')}</p>`; return; }
    const groupHtml = (r.groups ?? []).filter((g) => g.items.length > 0).map((g) => {
        const isStory = g.id === 'story';
        return `
        <div class="card"><h3>${esc(g.label)}</h3>
        ${isStory ? '<p class="muted-note">「3Dキャラを自動生成」は Meshy API で画像→3D→テクスチャ→GLB配置まで実行します。プレイアブルはリギング＋アニメ3本（≒39P）、人型敵はリギング＋アニメ2本（≒36P）、非人型敵/NPCは静的GLB（≒30P）です。成功済み工程は再利用します。</p>' : ''}
        <table><tr><th>対象</th>${isStory ? '<th>画像</th>' : ''}<th>種別</th><th>GLB パス</th><th>状態</th><th></th></tr>
        ${g.items.flatMap((item) => item.refs.map((ref, i) => `<tr>
            <td>${i === 0 ? esc(item.label) : ''}</td>
            ${isStory ? `<td>${i === 0 && item.imageFile
                ? `<a href="/api/projects/${esc(g.projectId)}/cast-image/${encodeURIComponent(item.imageFile)}" target="_blank" rel="noopener noreferrer"><img class="model-thumb" src="/api/projects/${esc(g.projectId)}/cast-image/${encodeURIComponent(item.imageFile)}?v=${encodeURIComponent(item.imageGeneratedAt ?? '')}" alt="${esc(item.id)}" loading="lazy"></a>`
                : (i === 0 ? '<span class="dim">未生成</span>' : '')}</td>` : ''}
            <td>${esc(ref.kind)}</td>
            <td class="dim">${esc(ref.path)}</td>
            <td>${ref.exists ? `<span class="ok">✔ あり（${ref.sizeKb}KB）</span>` : '<span class="err">✖ 未配置</span>'}</td>
            <td>${ref.exists ? `<button class="secondary" data-glb="${esc(ref.path)}" data-label="${esc(item.label)}">プレビュー</button>` : ''}
                ${isStory && i === 0 ? `<button data-meshy-cast="${esc(item.id)}" data-role="${esc(item.role ?? '')}" data-project="${esc(g.projectId)}" ${item.imageFile ? '' : 'disabled title="③ キャラ生成でキャスト画像を先に生成してください"'}>3Dキャラを自動生成</button>` : ''}
                ${item.promptFile && projectIdForPrompts ? `<a href="#/project/${esc(projectIdForPrompts)}/prompts">キャラ生成へ</a>` : ''}</td>
        </tr>`)).join('')}</table></div>`;
    }).join('');
    el.innerHTML = `<div class="card">
        <h3>3D モデル（GLB）の実在状態</h3>
        <p>参照 ${r.summary.totalRefs}件中 <span class="${r.summary.missing ? 'err' : 'ok'}">未配置 ${r.summary.missing}件</span>。
        ${r.storyProjectId ? `キャラ生成: <a href="#/project/${esc(r.storyProjectId)}/prompts">プロジェクト ${esc(r.storyProjectId)}</a> / ` : ''}
        「3Dキャラを自動生成」（Meshy）または手動作成の GLB を該当パスへ置くと「あり」になり、プレビューできます。</p>
        ${jobLogBlock('mv-log', previousResult(`モデル確認 ${r.summary.totalRefs}件 / 未配置 ${r.summary.missing}件`))}
    </div>
    ${groupHtml}
    <div class="card" id="mv-card" style="display:none">
        <h3 id="mv-title">プレビュー</h3>
        <iframe id="mv-frame" class="viewer" title="GLB プレビュー"></iframe>
        <p class="muted-note">ドラッグで回転・ホイールで拡大縮小。アニメーション付き GLB は先頭クリップを自動再生します。</p>
    </div>`;
    if (el.dataset.mvWired !== '1') {
        el.dataset.mvWired = '1'; // 自己再描画で同じ el を使い回すため、委譲リスナーは 1回だけ張る
        el.addEventListener('click', async (ev) => {
            const btn = ev.target.closest('button[data-glb], button[data-meshy-cast]');
            if (!btn) return;
            if (btn.dataset.glb) {
                const card = document.getElementById('mv-card');
                card.style.display = '';
                document.getElementById('mv-title').textContent = `プレビュー: ${btn.dataset.label}（${btn.dataset.glb}）`;
                document.getElementById('mv-frame').src = `./viewer.html?src=${encodeURIComponent(`/api/asset?path=${btn.dataset.glb}`)}&label=${encodeURIComponent(btn.dataset.label)}`;
                card.scrollIntoView({ behavior: 'smooth' });
                return;
            }
            // Meshy 3D 自動生成（計画書§16.11）
            const cast = btn.dataset.meshyCast;
            const projectId = btn.dataset.project;
            const playable = btn.dataset.role === 'protagonist' || btn.dataset.role === 'partner';
            const flow = playable
                ? '画像→3D（lowpoly・Aポーズ）→テクスチャ→リギング→アニメ×3 →GLB 3点配置'
                : '画像→3D（lowpoly）→テクスチャ →GLB 1点配置';
            const cost = playable ? '≒33P〜（20+10+リグ+アニメ×3）' : '≒30P（20+10）';
            if (!ensureMeshyReady()) return;
            if (!confirm(`「${cast}」の3Dキャラを Meshy で自動生成します。\n工程: ${flow}\n消費: ${cost}（Meshy クレジット）/所要: 数分〜30分\n実行しますか？`)) return;
            btn.disabled = true;
            try {
                const { response: resp, job } = await startAndPoll(
                    `/api/projects/${projectId}/model-generate`, { castIds: [cast] }, document.getElementById('mv-log'),
                );
                if (!job) { alert(resp.error ?? '3D生成の開始に失敗しました'); return; }
                if (job.status !== 'done') reportJobFailure('3Dキャラ自動生成', job, document.getElementById('mv-log'));
                await renderModelStatusInto(el, gameId, projectIdForPrompts);
            } finally { btn.disabled = false; }
        });
    }
}

function renderModelsTab(el, gameId) {
    renderModelStatusInto(el, gameId, null);
}

// ─────────────────────────────────────────────
// 新規作成ウィザード（F1）
// ─────────────────────────────────────────────
function renderNewWizard() {
    app.innerHTML = `<h2><span class="h2-ico" aria-hidden="true">🎮</span>新規ゲーム作成（スキャフォールド）</h2>
    <div class="card">
        <label class="field"><span>サンプルゲームとして作成（data/ のサンプルクエスト一式を適用。初回チュートリアル向け）</span>
            <input type="checkbox" id="w-sample"> サンプルクエストを適用する</label>
        <div class="row">
            <label class="field"><span>gameId（英小文字・数字・_-。例: clockwork_city）</span><input type="text" id="w-gameid" placeholder="my_game">
                <label style="font-weight:normal"><input type="checkbox" id="w-unique" checked title="公開サーバは全ゲーム共通のため、ID の重複を避けるランダム接尾辞（_xxxx）を自動で付けます"> ユニーク化接尾辞（_xxxx）を自動付与（推奨: 公開サーバ全体で ID が衝突しないようにする）</label></label>
            <label class="field"><span>スタートワールド ID</span><input type="text" id="w-startmap" placeholder="start_world"></label>
        </div>
        <div class="row">
            <label class="field"><span>作品名（日本語）</span><input type="text" id="w-title-ja"></label>
            <label class="field"><span>作品名（英語）</span><input type="text" id="w-title-en"></label>
        </div>
        <div class="row">
            <label class="field"><span>説明文（日本語）</span><input type="text" id="w-desc-ja"></label>
            <label class="field"><span>説明文（英語）</span><input type="text" id="w-desc-en"></label>
        </div>
        <h3>スタートキャラ（既定: 少年・少女の共通モデル 2 キャラ構成）</h3>
        <div class="row">
            <label class="field"><span>主人公</span>
                <select id="w-protagonist"><option value="boy">少年側</option><option value="girl">少女側</option></select></label>
            <label class="field"><span>少年側 ID / 表示名(ja) / 表示名(en)</span>
                <div class="row"><input type="text" id="w-boy-id" placeholder="boy"><input type="text" id="w-boy-ja" placeholder="少年"><input type="text" id="w-boy-en" placeholder="Boy"></div></label>
            <label class="field"><span>少女側 ID / 表示名(ja) / 表示名(en)</span>
                <div class="row"><input type="text" id="w-girl-id" placeholder="girl"><input type="text" id="w-girl-ja" placeholder="少女"><input type="text" id="w-girl-en" placeholder="Girl"></div></label>
        </div>
        <label class="field"><input type="checkbox" id="w-npmci" checked> 複製後に npm ci を実行する（時間がかかります）</label>
        <label class="field"><input type="checkbox" id="w-keeptests"> sevenfold 実データ依存テストを削除せず保留する（既定は削除）</label>
        <div class="actions">
            <button id="w-dryrun">① ドライラン（実行内容の確認）</button>
            <button id="w-exec" disabled>② 実行</button>
        </div>
    </div>
    <div id="w-report"></div>
    ${jobLogBlock('w-log')}`;

    const val = (id) => document.getElementById(id).value.trim() || undefined;
    // gameId のユニーク化: 公開サーバは全ゲーム共通のため、チェック ON なら _xxxx（英数4文字）を自動付与する。
    // ドライラン→実行で同じ ID になるよう、付与した結果は入力欄へ書き戻す（既に _xxxx 形式なら何もしない）
    const uniquifiedGameId = () => {
        const el = document.getElementById('w-gameid');
        let id = el.value.trim();
        if (!id) return undefined;
        if (document.getElementById('w-unique').checked && !/_(?=[a-z0-9]{4}$)[a-z0-9]*[0-9][a-z0-9]*$/.test(id)) {
            // 接尾辞は必ず数字を含む 4 文字（_city 等の英単語と誤判定しないため）
            const chars = Array.from({ length: 4 }, () => 'abcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(Math.random() * 36)]);
            chars[Math.floor(Math.random() * 4)] = String(Math.floor(Math.random() * 10));
            id = `${id}_${chars.join('')}`;
            el.value = id;
        }
        return id;
    };
    const buildOptions = () => ({
        sample: document.getElementById('w-sample').checked,
        gameId: uniquifiedGameId(),
        title: { ja: val('w-title-ja'), en: val('w-title-en') },
        description: { ja: val('w-desc-ja'), en: val('w-desc-en') },
        startMapId: val('w-startmap'),
        protagonist: document.getElementById('w-protagonist').value,
        characters: {
            boy: { id: val('w-boy-id'), name: { ja: val('w-boy-ja'), en: val('w-boy-en') } },
            girl: { id: val('w-girl-id'), name: { ja: val('w-girl-ja'), en: val('w-girl-en') } },
        },
        npmCi: document.getElementById('w-npmci').checked,
        keepStaleTests: document.getElementById('w-keeptests').checked,
    });
    app.querySelectorAll('input, select, textarea').forEach((control) => {
        const invalidate = () => { document.getElementById('w-exec').disabled = true; };
        control.addEventListener('input', invalidate);
        control.addEventListener('change', invalidate);
    });

    document.getElementById('w-dryrun').onclick = async () => {
        const r = await api('/api/games', { body: { ...buildOptions(), dryRun: true } });
        const rep = r.report;
        const el = document.getElementById('w-report');
        if (!rep) { el.innerHTML = `<div class="card err">${esc(r.error ?? '失敗')}</div>`; return; }
        el.innerHTML = `<div class="card">
            <h3>ドライラン結果（gameId: ${esc(rep.opts.gameId ?? '')}）</h3>
            ${rep.errors.length ? `<p class="err">エラー:</p><ul class="plain">${rep.errors.map((e) => `<li class="err">${esc(e)}</li>`).join('')}</ul>` : ''}
            <ol>${rep.steps.map((s) => `<li>${esc(s)}</li>`).join('')}</ol>
            ${rep.staleTests?.length ? `<p class="dim">実データ依存テスト ${rep.staleTests.length}件検出</p>` : ''}
            ${rep.warnings.length ? `<p class="warn">警告:</p><ul class="plain">${rep.warnings.map((w) => `<li class="warn">${esc(w)}</li>`).join('')}</ul>` : ''}
        </div>`;
        document.getElementById('w-exec').disabled = !rep.ok;
    };

    document.getElementById('w-exec').onclick = async () => {
        if (!confirm('この内容で新規ゲーム環境を作成しますか？')) return;
        document.getElementById('w-exec').disabled = true;
        const { response: r, job } = await startAndPoll('/api/games', buildOptions(), document.getElementById('w-log'));
        if (!job) {
            document.getElementById('w-report').innerHTML += `<div class="card err">${esc(r.error ?? JSON.stringify(r.report?.errors ?? ''))}</div>`;
            return;
        }
        if (job.status === 'canceled') return; // 中断時は結果カードを描画しない（pollJob が中断メッセージ表示済み）
        if (job.status === 'failed') reportJobFailure('スキャフォールド', job, document.getElementById('w-log'));
        const rep = job.result;
        const html = rep ? `<div class="card">
            <h3 class="${rep.ok ? 'ok' : 'err'}">${rep.ok ? '✔ スキャフォールド完了' : '✖ 失敗'}</h3>
            ${rep.steps.map((s) => `<h3>${esc(s.name)}</h3><ul class="plain">${s.details.map((d) => `<li>${esc(d)}</li>`).join('')}${s.warnings.map((w) => `<li class="warn">⚠ ${esc(w)}</li>`).join('')}</ul>`).join('')}
            ${rep.errors.map((e) => `<p class="err">${esc(e)}</p>`).join('')}
            ${rep.ok ? `<div class="actions"><button onclick="location.hash='#/game/${esc(rep.opts.gameId)}'">ゲーム詳細へ</button></div>` : ''}
        </div>` : `<div class="card err">${esc(job.error ?? '失敗')}</div>`;
        document.getElementById('w-report').innerHTML += html;
    };
}

// ─────────────────────────────────────────────
// ゲーム詳細（タブ構成）
// ─────────────────────────────────────────────
const TABS = [
    ['import', '取り込み'], ['validate', '検証'], ['i18n', '翻訳'], ['assets', 'アセット'],
    ['models', 'モデル'], ['preview', 'プレビュー'], ['generate', '生成・版番'], ['deploy', '公開準備'],
    ['character', 'キャラ設定'], ['world', 'ワールド追加'],
];

async function renderGame(gameId, tab = 'validate') {
    const r = await api('/api/games');
    if (r.ok === false) {
        app.innerHTML = `<div class="card"><p class="err">ゲーム一覧の取得に失敗しました:</p><pre class="log">${esc(r.error ?? '不明なエラー')}</pre></div>`;
        return;
    }
    const game = (r.games ?? []).find((g) => g.gameId === gameId);
    if (!game) { app.innerHTML = `<p class="err">ゲームが見つかりません: ${esc(gameId)}</p>`; return; }
    app.innerHTML = `<h2><span class="h2-ico" aria-hidden="true">🎮</span>${esc(gameId)} <span class="dim">${esc(localizedText(game.title))}</span>${game.published ? '<span class="badge warn">公開済み</span>' : ''}</h2>
    <div class="dim">ワールド: ${esc(game.maps.join(', '))} / キャラ: ${esc((game.characters?.ids ?? []).join(', '))}</div>
    <div class="tabs" style="margin-top:10px">${TABS.map(([k, label]) => `<button class="${k === tab ? 'active' : ''}" onclick="location.hash='#/game/${esc(gameId)}/${k}'">${label}</button>`).join('')}</div>
    <div id="tab-body"></div>`;
    const body = document.getElementById('tab-body');
    const render = {
        import: renderImportTab, validate: renderValidateTab, i18n: renderI18nTab, assets: renderAssetsTab,
        models: renderModelsTab, preview: renderPreviewTab, generate: renderGenerateTab, deploy: renderDeployTab,
        character: renderCharacterTab, world: renderWorldTab,
    }[tab] ?? renderValidateTab;
    render(body, gameId, game);
}

function renderImportTab(el, gameId) {
    el.innerHTML = `<div class="card">
        <h3>AI 出力 JSON の取り込み（F2）</h3>
        <p class="dim">「ファイルパス + JSON コードブロック」形式の AI 出力を貼り付けてください。配置先は maps/ 配下のみ。運用系ファイルと treasures.json は拒否されます。</p>
        <textarea id="imp-text" placeholder="maps/_shared/game-info.json&#10;\`\`\`json&#10;{ ... }&#10;\`\`\`"></textarea>
        <div class="actions">
            <button id="imp-plan">① 解析（ドライラン）</button>
            <label><input type="checkbox" id="imp-ow"> 既存ファイルの上書きを許可</label>
            <button id="imp-apply" disabled>② 配置を実行</button>
        </div>
        <div id="imp-result"></div>
    </div>`;
    const renderPlan = (plan, results) => {
        const actionLabel = { overwrite: '上書き', merge: 'マージ', create: '新規' };
        const statusLabel = { created: '✔ 作成', overwritten: '✔ 上書き', merged: '✔ マージ', skipped: '― 見送り', rejected: '✖ 拒否' };
        const rows = plan.map((item) => {
            const st = results?.find((x) => x.relPath === item.relPath);
            // 見送り/拒否の理由（上書き確認が必要・他エントリのエラー等）まで表示する。
            // ステータス名だけでは「なぜ配置されなかったか」が分からず、正しい JSON を再編集させる誤誘導になる
            const stHtml = st
                ? `${esc(statusLabel[st.status] ?? st.status)}${(st.reasons ?? []).map((x) => `<div class="${st.status === 'rejected' ? 'err' : 'warn'}">${esc(x)}</div>`).join('')}`
                : '';
            return `<tr>
                <td>${esc(item.relPath)}</td>
                <td>${actionLabel[item.action] ?? esc(item.action)}</td>
                <td>${item.schema ? (item.schema.skipped ? `<span class="dim">スキップ</span>` : item.schema.ok ? '<span class="ok">OK</span>' : '<span class="err">NG</span>') : '-'}</td>
                <td>${item.diff ? esc(`+[${item.diff.addedTopKeys}] -[${item.diff.removedTopKeys}] Δ[${item.diff.changedTopKeys}]`) : '-'}</td>
                <td>${item.errors.map((e) => `<div class="err">${esc(e)}</div>`).join('') || stHtml}</td>
            </tr>`;
        }).join('');
        return `<table><tr><th>配置先</th><th>動作</th><th>スキーマ</th><th>差分（トップキー）</th><th>状態</th></tr>${rows}</table>`;
    };
    for (const id of ['imp-text', 'imp-ow']) {
        const control = document.getElementById(id);
        const invalidate = () => { document.getElementById('imp-apply').disabled = true; };
        control.addEventListener('input', invalidate);
        control.addEventListener('change', invalidate);
    }
    document.getElementById('imp-plan').onclick = async () => {
        const r = await api(`/api/games/${gameId}/import`, { body: { text: document.getElementById('imp-text').value, dryRun: true } });
        const orphanWarn = r.orphanBlocks > 0 ? `<p class="warn">⚠ 対応するパス行が見つからないコードブロック ${r.orphanBlocks}件をスキップしました（パス行の直後にコードブロックを置いてください）。</p>` : '';
        document.getElementById('imp-result').innerHTML = (r.plan?.length ? renderPlan(r.plan) : '<p class="warn">エントリが見つかりません。</p>') + orphanWarn;
        const plan = r.plan ?? [];
        const allValid = plan.length > 0 && plan.every((i) => i.errors.length === 0);
        document.getElementById('imp-apply').disabled = !allValid;
        if (!allValid && plan.length > 0) {
            document.getElementById('imp-result').innerHTML += '<p class="err">エラーを含むため、どのファイルも配置しません。入力を修正して再解析してください。</p>';
        }
        // 上書き/マージ（既存ファイルの変更）を含む場合はチェック忘れを先回りで案内する
        if (allValid && plan.some((i) => i.action === 'overwrite' || i.action === 'merge') && !document.getElementById('imp-ow').checked) {
            document.getElementById('imp-result').innerHTML += '<p class="warn">既存ファイルの上書き/マージが含まれます。「② 配置を実行」の前に「既存ファイルの上書きを許可」へチェックを入れてください。</p>';
        }
    };
    document.getElementById('imp-apply').onclick = async () => {
        if (!confirm('生成された内容をゲームデータへ配置しますか？\n\n（ドライラン結果のとおり作成・上書き・マージされます）')) return;
        const r = await runBlocking('取り込みを実行しています…', () => api(`/api/games/${gameId}/import`, {
            body: { text: document.getElementById('imp-text').value, confirmOverwrite: document.getElementById('imp-ow').checked },
        }));
        document.getElementById('imp-result').innerHTML = renderPlan(r.plan ?? [], r.results ?? [])
            + (r.ok
                ? '<p class="muted-note">取り込み後は「検証」「生成・版番」タブで validate / generate:treasures を実行してください。</p>'
                : `<p class="err">${esc(r.error ?? '取り込みに失敗しました')}</p>`);
    };
}

function findingsTable(findings) {
    if (!findings.length) return '<p class="ok">問題は見つかりませんでした。</p>';
    return `<table><tr><th>種別</th><th>ルール</th><th>ファイル</th><th>JSON パス</th><th>内容</th></tr>
        ${findings.map((f) => `<tr><td class="${f.level === 'error' ? 'err' : 'warn'}">${f.level === 'error' ? 'エラー' : '警告'}</td><td>${f.rule}</td><td>${esc(f.file)}</td><td>${esc(f.jsonPath)}</td><td>${esc(f.message)}${f.hint ? `<div class="dim">ヒント: ${esc(f.hint)}</div>` : ''}</td></tr>`).join('')}</table>`;
}

function renderValidateTab(el, gameId, game) {
    el.innerHTML = `<div class="card">
        <h3>検証（F3 スキーマ / F4 整合）</h3>
        <div class="actions">
            <button id="v-schema">validate:maps 実行</button>
            <button id="v-integrity">整合チェック実行</button>
            <button id="v-all">すべて実行（schema + 整合 + 翻訳 + アセット）</button>
        </div>
        <div id="v-result"></div>
        ${jobLogBlock('v-log', validationLogState(game))}
    </div>`;
    const resultEl = document.getElementById('v-result');
    document.getElementById('v-schema').onclick = async () => {
        const logEl = document.getElementById('v-log');
        const { response: r, job } = await startAndPoll(
            `/api/games/${gameId}/validate`, { kind: 'schema' }, logEl,
        );
        if (!job) { resultEl.innerHTML = `<p class="err">${esc(r.error ?? '検証を開始できません')}</p>`; return; }
        if (job.status === 'canceled') return;
        if (job.status === 'failed') reportJobFailure('検証', job, logEl);
        else showPreviousResult(logEl, `ファイル形式 ${job.result?.schema?.ok ? 'OK' : '要確認（再実行待ち）'}`);
        resultEl.innerHTML = `<p class="${job.result?.schema?.ok ? 'ok' : 'err'}">validate:maps → ${job.result?.schema?.ok ? 'OK' : 'NG'}</p>`;
    };
    document.getElementById('v-integrity').onclick = async () => {
        const r = await api(`/api/games/${gameId}/validate`, { body: { kind: 'integrity' } });
        const out = r.integrity ?? {};
        showPreviousResult(document.getElementById('v-log'), `データ ${out.ok ? 'OK' : '要確認（再実行待ち）'}（エラー ${out.errorCount ?? out.errors ?? 0} / 警告 ${out.warningCount ?? out.warnings ?? 0}）`);
        resultEl.innerHTML = findingsTable(out.findings ?? []);
    };
    document.getElementById('v-all').onclick = async () => {
        const logEl = document.getElementById('v-log');
        const { response: r, job } = await startAndPoll(
            `/api/games/${gameId}/validate`, { kind: 'all' }, logEl,
        );
        if (!job) { resultEl.innerHTML = `<p class="err">${esc(r.error ?? '検証を開始できません')}</p>`; return; }
        if (job.status === 'canceled') return;
        if (job.status === 'failed') reportJobFailure('検証', job, logEl);
        const out = job.result ?? {};
        if (job.status !== 'failed') {
            const mark = (value) => value?.ok ? 'OK' : '要確認';
            showPreviousResult(logEl, `ファイル形式 ${mark(out.schema)} / データ ${mark(out.integrity)} / 翻訳 ${mark(out.i18n)} / 素材 ${mark(out.assets)}`);
        }
        resultEl.innerHTML = `<p>schema: <span class="${out.schema?.ok ? 'ok' : 'err'}">${out.schema?.ok ? 'OK' : 'NG'}</span> / 整合: <span class="${out.integrity?.ok ? 'ok' : 'err'}">${out.integrity?.ok ? 'OK' : 'NG'}</span> / 翻訳: <span class="${out.i18n?.ok ? 'ok' : 'err'}">${out.i18n?.ok ? 'OK' : '要対応'}</span> / アセット: <span class="${out.assets?.ok ? 'ok' : 'err'}">${out.assets?.ok ? 'OK' : '不足あり'}</span></p>`
            + findingsTable(out.integrity?.findings ?? []);
    };
}

function renderI18nTab(el, gameId) {
    el.innerHTML = `<div class="card"><h3>i18n チェック（F5）</h3>
        <div class="actions"><button id="i-run">実行</button></div><div id="i-result"></div></div>`;
    document.getElementById('i-run').onclick = async () => {
        const r = await api(`/api/games/${gameId}/validate`, { body: { kind: 'i18n' } });
        const d = r.i18n ?? {};
        document.getElementById('i-result').innerHTML = `
            <p class="${d.ok ? 'ok' : 'warn'}">${d.ok ? '✔ OK' : '要対応があります'}</p>
            ${(d.messagesMissingInEn ?? []).length ? `<h3>en.json messages に無いキー（${d.messagesMissingInEn.length}）</h3><pre class="log">${esc(d.messagesMissingInEn.join('\n'))}</pre>` : ''}
            ${(d.messagesMissingInJa ?? []).length ? `<h3>ja.json messages に無いキー（${d.messagesMissingInJa.length}）</h3><pre class="log">${esc(d.messagesMissingInJa.join('\n'))}</pre>` : ''}
            ${(d.masterKeyMissing ?? []).length ? `<h3>マスタ由来キーの欠落</h3>${(d.masterKeyMissing).map((m) => `<div class="err">${esc(m.file)} ${esc(m.jsonPath)} → ${esc(m.lang)}.json messages.${esc(m.key)}</div>`).join('')}` : ''}
            ${(d.rawStringsUntranslated ?? []).length ? `<h3>生文字列の未翻訳（en.json exact 未登録）</h3><table><tr><th>ファイル</th><th>種別</th><th>文言</th></tr>${d.rawStringsUntranslated.map((x) => `<tr><td>${esc(x.file)}</td><td>${esc(x.kind)}</td><td>${esc(x.text)}</td></tr>`).join('')}</table>` : ''}
            ${d.inheritedAsymmetry && (d.inheritedAsymmetry.missingInEn || d.inheritedAsymmetry.missingInJa) ? `<p class="muted-note">エンジン由来の既知キー非対称: en欠け ${d.inheritedAsymmetry.missingInEn} / ja欠け ${d.inheritedAsymmetry.missingInJa}（sevenfold 本体と同一のため対応不要）</p>` : ''}`;
    };
}

function renderAssetsTab(el, gameId) {
    el.innerHTML = `<div class="card"><h3>アセット存在チェック（F6）</h3>
        <div class="actions"><button id="a-run">ローカル assets/ を確認</button>
        <button id="a-cdn" class="secondary">本番 CDN も確認（HEAD）</button></div><div id="a-result"></div></div>`;
    const run = async (cdn) => {
        // CDN 確認はアセット数分の HEAD リクエストで数十秒かかり得るため、処理中表示と連打防止を掛ける
        const r = await runBlocking(cdn ? '本番 CDN を確認しています…（アセット数によって時間がかかります）' : 'アセットを確認しています…', () => api(`/api/games/${gameId}/validate`, { body: { kind: 'assets', cdn } }));
        const d = r.assets ?? {};
        document.getElementById('a-result').innerHTML = `
            <p class="${d.ok ? 'ok' : 'err'}">参照 ${d.totalRefs}件中 不足 ${(d.missing ?? []).length}件${d.warning ? `<span class="warn">（${esc(d.warning)}）</span>` : ''}</p>
            ${(d.missing ?? []).length ? `<table><tr><th>不足アセット</th><th>本番CDN</th><th>参照元</th></tr>${d.missing.map((m) => `<tr><td>${esc(m.asset)}</td><td>${m.cdn === null ? '-' : m.cdn ? '<span class="ok">あり</span>' : '<span class="err">なし</span>'}</td><td>${esc(m.usages[0]?.file ?? '')} ほか ${m.usageCount}箇所</td></tr>`).join('')}</table><p class="muted-note">不足分の解消（GLB/音源の制作・選定・R2 アップロード）は手動作業です。</p>` : ''}`;
    };
    document.getElementById('a-run').onclick = () => run(false);
    document.getElementById('a-cdn').onclick = () => run(true);
}

function renderPreviewTab(el, gameId) {
    el.innerHTML = `<div class="card"><h3>プレビュー起動（F7）</h3>
        <div class="actions">
            <select id="p-mode" style="max-width:140px"><option value="dev">dev</option><option value="preview">preview</option></select>
            <button id="p-start">起動</button>
            <button id="p-stop" class="danger">停止</button>
            <span id="p-status" class="dim"></span>
        </div>
        <pre class="log" id="p-log">（未起動）</pre>
    </div>
    <div class="card"><h3>手動確認チェックリスト（手順 7）</h3><div id="p-checklist" class="checklist"></div></div>`;
    let timer = null;
    // 別ゲームのプレビュータブへ移動した後も旧タイマーが新画面の DOM を上書きしないよう、
    // 生存条件は「このゲームのプレビュータブの hash と完全一致」にする
    const PREVIEW_HASH = `#/game/${gameId}/preview`;
    const ensureTimer = () => {
        if (timer) return;
        timer = setInterval(() => {
            if (location.hash === PREVIEW_HASH) refresh();
            else { clearInterval(timer); timer = null; }
        }, 2000);
    };
    const refresh = async () => {
        const s = await api(`/api/games/${gameId}/dev-server`);
        const statusEl = document.getElementById('p-status');
        if (!statusEl) return; // タブ切替後に遅延応答が来た場合
        statusEl.innerHTML = s.running
            ? `稼働中（${esc(s.mode)}）${s.url ? ` → <a href="${esc(s.url)}" target="_blank">${esc(s.url)}</a>` : ''}`
            : '停止中';
        if (s.log?.length) {
            const logEl = document.getElementById('p-log');
            logEl.textContent = s.log.join('\n');
            logEl.scrollTop = logEl.scrollHeight;
        }
    };
    const refreshChecklist = async () => {
        const r = await api(`/api/games/${gameId}/checklist`);
        document.getElementById('p-checklist').innerHTML = (r.items ?? []).map((it) => `
            <label><input type="checkbox" data-id="${esc(it.id)}" ${it.checked ? 'checked' : ''}> ${esc(it.label)}${it.checkedAt ? `<span class="dim">（${esc(it.checkedAt.slice(0, 10))}）</span>` : ''}</label>`).join('');
        document.querySelectorAll('#p-checklist input').forEach((cb) => {
            cb.onchange = async () => {
                const r = await api(`/api/games/${gameId}/checklist`, { body: { itemId: cb.dataset.id, checked: cb.checked } });
                if (r.ok === false) {
                    alert(`チェックの保存に失敗しました:\n${r.error ?? '不明なエラー'}`);
                    cb.checked = !cb.checked; // 保存できなかったので表示を戻す
                }
            };
        });
    };
    document.getElementById('p-start').onclick = async () => {
        const r = await api(`/api/games/${gameId}/dev-server`, { body: { action: 'start', mode: document.getElementById('p-mode').value } });
        if (r.ok === false) { alert(`起動に失敗しました:\n${r.error ?? r.reason ?? '不明なエラー'}`); return; }
        ensureTimer();
    };
    document.getElementById('p-stop').onclick = async () => {
        const r = await api(`/api/games/${gameId}/dev-server`, { body: { action: 'stop' } });
        if (r.ok === false) alert(`停止に失敗しました:\n${r.error ?? '不明なエラー'}`);
        if (timer) { clearInterval(timer); timer = null; }
        refresh();
    };
    refresh();
    refreshChecklist();
    ensureTimer();
}

function renderGenerateTab(el, gameId, game) {
    el.innerHTML = `<div class="card"><h3>生成系（F8）</h3>
        <div class="actions">
            <button id="g-npmci" class="secondary">npm ci</button>
            <button id="g-treasures">generate:treasures 実行</button>
            <button id="g-bump">dataCache.version 更新</button>
            <label><input type="checkbox" id="g-pm"> playerModelCache.version も更新（コアキャラ GLB 差し替え時）</label>
        </div>
        <div id="g-result"></div>
        ${jobLogBlock('g-log', operationLogState(game, [['npmCi', '依存準備'], ['treasures', '宝箱生成'], ['dataCache', '版番更新']]))}
    </div>`;
    document.getElementById('g-npmci').onclick = async () => {
        if (!confirm('npm ci を実行しますか？\n\n（依存パッケージのクリーンインストール・数分かかります）')) return;
        const logEl = document.getElementById('g-log');
        const { response: r, job } = await startAndPoll(`/api/games/${gameId}/npm-ci`, {}, logEl);
        if (!job) { document.getElementById('g-result').innerHTML = `<p class="err">${esc(r.error ?? 'npm ci を開始できません')}</p>`; return; }
        if (job.status === 'canceled') return;
        if (job.status === 'failed') reportJobFailure('生成系実行', job, logEl);
        else showPreviousResult(logEl, `依存準備 ${job.result?.ok ? '完了' : '失敗（再実行待ち）'}`);
        document.getElementById('g-result').innerHTML = `<p class="${job.result?.ok ? 'ok' : 'err'}">npm ci → ${job.result?.ok ? '完了' : '失敗'}</p>`;
    };
    document.getElementById('g-treasures').onclick = async () => {
        if (!confirm('generate:treasures を実行しますか？\n\n（宝箱データを再生成して上書きします）')) return;
        const logEl = document.getElementById('g-log');
        const { response: r, job } = await startAndPoll(`/api/games/${gameId}/generate-treasures`, {}, logEl);
        if (!job) { document.getElementById('g-result').innerHTML = `<p class="err">${esc(r.error ?? 'generate:treasures を開始できません')}</p>`; return; }
        if (job.status === 'canceled') return;
        if (job.status === 'failed') reportJobFailure('生成系実行', job, logEl);
        else showPreviousResult(logEl, `宝箱生成 ${job.result?.ok ? '完了' : '失敗（再実行待ち）'}`);
        document.getElementById('g-result').innerHTML = `<p class="${job.result?.ok ? 'ok' : 'err'}">generate:treasures → ${job.result?.ok ? '完了' : '失敗'}</p>`;
    };
    document.getElementById('g-bump').onclick = async () => {
        if (!confirm('dataCache.version を更新しますか？\n\n（マップ設定 JSON の版番を書き換えます）')) return;
        const r = await runBlocking('版番を更新しています…', () => api(`/api/games/${gameId}/bump-data-cache`, { body: { withPlayerModels: document.getElementById('g-pm').checked } }));
        if (r.ok === false) { document.getElementById('g-result').innerHTML = `<p class="err">${esc(r.error ?? '更新に失敗しました')}</p>`; return; }
        showPreviousResult(document.getElementById('g-log'), `版番更新 完了（${(r.changes ?? []).length}件）`);
        document.getElementById('g-result').innerHTML = (r.changes ?? []).map((c) => `<p class="ok">${esc(c.key)}: ${esc(c.from)} → ${esc(c.to)}</p>`).join('')
            + '<p class="muted-note">公開後の JSON 更新は、この版番更新をしないと既存クライアントに配信されません。</p>';
    };
}

function renderDeployTab(el, gameId, game) {
    el.innerHTML = `<div class="card"><h3>公開準備（F9）</h3>
        <div class="actions"><button id="d-build">本番ビルド実行</button><button id="d-plan" class="secondary">公開可否を確認</button></div>
        <div id="d-result"></div>
        ${jobLogBlock('d-log', operationLogState(game, [['build', '公開ビルド']]))}
    </div>`;
    document.getElementById('d-build').onclick = async () => {
        if (!confirm('本番ビルドを実行しますか？（数分かかる場合があります）')) return;
        const logEl = document.getElementById('d-log');
        const { response: r, job } = await startAndPoll(`/api/games/${gameId}/build`, {}, logEl);
        if (!job) { document.getElementById('d-result').innerHTML = `<p class="err">${esc(r.error ?? 'ビルドを開始できません')}</p>`; return; }
        if (job.status === 'canceled') return;
        if (job.status === 'failed') reportJobFailure('ビルド', job, logEl);
        else showPreviousResult(logEl, `公開ビルド ${job.result?.ok ? '完了' : '失敗（再実行待ち）'}`);
        document.getElementById('d-result').innerHTML = `<p class="${job.result?.ok ? 'ok' : 'err'}">build → ${job.result?.ok ? `完了（dist/${esc(gameId)}/）` : '失敗'}</p>`;
    };
    document.getElementById('d-plan').onclick = async () => {
        const r = await api(`/api/games/${gameId}/deploy-plan`);
        const plan = r.plan;
        if (!plan) { document.getElementById('d-result').innerHTML = `<p class="err">${esc(r.error ?? '失敗')}</p>`; return; }
        const availability = plan.publishSupported
            ? `<p class="ok">公開機能を利用できます。予定 URL: <a href="${esc(plan.publicUrl)}" target="_blank" rel="noopener noreferrer">${esc(plan.publicUrl)}</a></p>`
            : `<p class="warn">現在、このツールから本番公開はできません: ${esc(plan.blockedReason)}</p>
               <p>予定 URL: <code>${esc(plan.publicUrl)}</code></p>`;
        document.getElementById('d-result').innerHTML = `${availability}
            <p>dist 生成: ${plan.distReady ? '<span class="ok">✔ 済</span>' : '<span class="err">✖ 未（先にビルド）</span>'}</p>
            <h3>公開チェックリスト</h3>
            <ul class="plain">${plan.checklist.map((c) => `<li>[${c.done ? 'x' : ' '}] ${esc(c.label)}</li>`).join('')}</ul>
            <h3>注意</h3>
            <ul class="plain">${plan.notes.map((n) => `<li class="dim">${esc(n)}</li>`).join('')}</ul>`;
    };
}

function renderCharacterTab(el, gameId, game) {
    const ids = game.characters?.ids ?? [];
    const names = game.characters?.names ?? {};
    el.innerHTML = `<div class="card"><h3>キャラ設定変更（F11）</h3>
        <div class="row">
            <label class="field"><span>モード</span>
                <select id="c-mode">
                    <option value="rename">表示名・説明変更（いつでも可）</option>
                    <option value="re-id">ID 変更（公開前限定）</option>
                    <option value="model">モデル差し替え</option>
                </select></label>
            <label class="field"><span>対象キャラ</span>
                <select id="c-id">${ids.map((id) => `<option>${esc(id)}</option>`).join('')}</select></label>
        </div>
        <div id="c-fields"></div>
        <div class="actions"><button id="c-dry">① ドライラン</button><button id="c-exec" disabled title="ドライランで確認した内容のキャラ設定変更を実行します">② 実行</button></div>
        <div id="c-result"></div>
    </div>`;
    const fieldsEl = document.getElementById('c-fields');
    const renderFields = () => {
        const mode = document.getElementById('c-mode').value;
        const characterId = document.getElementById('c-id').value;
        const currentName = names[characterId] ?? {};
        if (mode === 'rename') {
            fieldsEl.innerHTML = `<div class="row">
                <label class="field"><span>表示名（ja）</span><input type="text" id="c-name-ja" value="${esc(currentName.ja ?? '')}"></label>
                <label class="field"><span>表示名（en）</span><input type="text" id="c-name-en" value="${esc(currentName.en ?? currentName.ja ?? '')}"></label></div>
                <div class="row">
                <label class="field"><span>説明（ja・任意）</span><input type="text" id="c-desc-ja"></label>
                <label class="field"><span>説明（en・任意）</span><input type="text" id="c-desc-en"></label></div>`;
        } else if (mode === 're-id') {
            fieldsEl.innerHTML = `<label class="field"><span>新しいキャラ ID（英小文字。公開後は変更禁止＝セーブ互換が壊れます）</span><input type="text" id="c-to"></label>`;
        } else {
            fieldsEl.innerHTML = `<label class="field"><span>modelPathIdle（../assets/... 形式）</span><input type="text" id="c-idle"></label>
                <label class="field"><span>modelPathWalk</span><input type="text" id="c-walk"></label>
                <label class="field"><span>modelPathAttack</span><input type="text" id="c-attack"></label>`;
        }
        document.getElementById('c-exec').disabled = true;
    };
    renderFields();
    document.getElementById('c-mode').onchange = renderFields;
    document.getElementById('c-id').onchange = renderFields;
    fieldsEl.addEventListener('input', () => { document.getElementById('c-exec').disabled = true; });
    const val = (id) => document.getElementById(id)?.value.trim() || null;
    const buildBody = (dryRun) => {
        const mode = document.getElementById('c-mode').value;
        const characterId = document.getElementById('c-id').value;
        if (mode === 'rename') {
            return { mode, characterId, dryRun,
                name: { ja: val('c-name-ja'), en: val('c-name-en') ?? val('c-name-ja') },
                description: val('c-desc-ja') ? { ja: val('c-desc-ja'), en: val('c-desc-en') ?? val('c-desc-ja') } : null };
        }
        if (mode === 're-id') return { mode, from: characterId, to: val('c-to'), dryRun };
        return { mode, characterId, dryRun, modelPathIdle: val('c-idle'), modelPathWalk: val('c-walk'), modelPathAttack: val('c-attack') };
    };
    const show = (r) => {
        document.getElementById('c-result').innerHTML = r.ok === false ? `<p class="err">${esc(r.error ?? '失敗')}</p>` : `
            <ul class="plain">${(r.changes ?? []).map((c) => `<li>${esc(c)}</li>`).join('')}</ul>
            ${(r.warnings ?? []).map((w) => `<p class="warn">⚠ ${esc(w)}</p>`).join('')}
            ${(r.checklist ?? []).length ? `<h3>確認チェックリスト</h3><ul class="plain">${r.checklist.map((c) => `<li>[ ] ${esc(c)}</li>`).join('')}</ul>` : ''}`;
    };
    document.getElementById('c-dry').onclick = async () => {
        const r = await api(`/api/games/${gameId}/character-config`, { body: buildBody(true) });
        show(r);
        document.getElementById('c-exec').disabled = r.ok === false;
    };
    document.getElementById('c-exec').onclick = async () => {
        if (!confirm('キャラクター設定の変更を実行しますか？\n\n（ドライランで確認した内容でゲームデータを書き換えます）')) return;
        const r = await runBlocking('キャラ設定を変更しています…', () => api(`/api/games/${gameId}/character-config`, { body: buildBody(false) }));
        show(r);
    };
}

function renderWorldTab(el, gameId, game) {
    el.innerHTML = `<div class="card"><h3>ワールド追加（F12）</h3>
        <div class="row">
            <label class="field"><span>タイプ</span>
                <select id="aw-type">
                    <option value="field">通常ワールド（都市・フィールド）</option>
                    <option value="dungeon">ダンジョン型（labyrinth_dungeon 相当）</option>
                    <option value="vault">無限宝物庫型（mapId は eternal_vault 固定）</option>
                </select></label>
            <label class="field"><span>mapId</span><input type="text" id="aw-mapid" placeholder="second_city"></label>
        </div>
        <div class="row">
            <label class="field"><span>マップ名（ja）</span><input type="text" id="aw-name-ja"></label>
            <label class="field"><span>マップ名（en）</span><input type="text" id="aw-name-en"></label>
        </div>
        <div class="row">
            <label class="field"><span id="aw-connect-label">接続元ワールド（通常ワールドでは任意）</span>
                <select id="aw-connect"><option value="">（接続しない）</option>${game.maps.map((m) => `<option>${esc(m)}</option>`).join('')}</select></label>
            <label class="field"><span>既存マップから複製（任意・通常ワールドのみ）</span>
                <select id="aw-copy"><option value="">（空テンプレート）</option>${game.maps.map((m) => `<option>${esc(m)}</option>`).join('')}</select></label>
        </div>
        <div class="actions"><button id="aw-dry">① ドライラン</button><button id="aw-exec" disabled title="ドライランで確認した内容でワールドを追加します">② 実行</button></div>
        <div id="aw-result"></div>
        <p class="muted-note">実行後は「生成・版番」→ generate:treasures、「検証」→ すべて実行、で確認してください（ワープ座標は仮値のため要調整）。</p>
    </div>`;
    const syncWorldType = () => {
        const type = document.getElementById('aw-type').value;
        const mapId = document.getElementById('aw-mapid');
        const copy = document.getElementById('aw-copy');
        const connect = document.getElementById('aw-connect');
        const requiresConnection = type === 'dungeon' || type === 'vault';
        const wasVault = mapId.disabled && mapId.value === 'eternal_vault';
        if (type === 'vault') {
            mapId.value = 'eternal_vault';
            mapId.disabled = true;
            if (!document.getElementById('aw-name-ja').value) document.getElementById('aw-name-ja').value = '無限宝物庫';
            if (!document.getElementById('aw-name-en').value) document.getElementById('aw-name-en').value = 'Eternal Vault';
        } else {
            if (wasVault) {
                mapId.value = '';
                if (document.getElementById('aw-name-ja').value === '無限宝物庫') document.getElementById('aw-name-ja').value = '';
                if (document.getElementById('aw-name-en').value === 'Eternal Vault') document.getElementById('aw-name-en').value = '';
            }
            mapId.disabled = false;
        }
        copy.disabled = type !== 'field';
        if (copy.disabled) copy.value = '';
        connect.required = requiresConnection;
        document.getElementById('aw-connect-label').textContent = requiresConnection
            ? '帰還先ワールド（必須）'
            : '接続元ワールド（任意）';
        if (requiresConnection && !connect.value) {
            const preferred = game.startMapId ?? game.maps[0];
            if (preferred && [...connect.options].some((o) => o.value === preferred)) connect.value = preferred;
        }
        document.getElementById('aw-exec').disabled = true;
    };
    document.getElementById('aw-type').onchange = syncWorldType;
    document.querySelectorAll('#aw-mapid, #aw-name-ja, #aw-name-en, #aw-connect, #aw-copy').forEach((control) => {
        control.addEventListener('input', () => { document.getElementById('aw-exec').disabled = true; });
        control.addEventListener('change', () => { document.getElementById('aw-exec').disabled = true; });
    });
    syncWorldType();
    const buildBody = (dryRun) => ({
        dryRun,
        type: document.getElementById('aw-type').value,
        mapId: document.getElementById('aw-mapid').value.trim(),
        nameJa: document.getElementById('aw-name-ja').value.trim() || document.getElementById('aw-mapid').value.trim(),
        nameEn: document.getElementById('aw-name-en').value.trim() || document.getElementById('aw-mapid').value.trim(),
        connectTo: document.getElementById('aw-connect').value || null,
        copyFrom: document.getElementById('aw-copy').value || null,
    });
    const show = (r) => {
        document.getElementById('aw-result').innerHTML = r.ok === false ? `<p class="err">${esc(r.error ?? '失敗')}</p>` : `
            <ul class="plain">${(r.changes ?? []).map((c) => `<li>${esc(c)}</li>`).join('')}</ul>
            ${(r.warnings ?? []).map((w) => `<p class="warn">⚠ ${esc(w)}</p>`).join('')}
            ${(r.todos ?? []).length ? `<h3>TODO</h3><ul class="plain">${r.todos.map((t) => `<li>[ ] ${esc(t)}</li>`).join('')}</ul>` : ''}`;
    };
    document.getElementById('aw-dry').onclick = async () => {
        const r = await api(`/api/games/${gameId}/add-world`, { body: buildBody(true) });
        show(r);
        document.getElementById('aw-exec').disabled = r.ok === false;
    };
    document.getElementById('aw-exec').onclick = async () => {
        if (!confirm('ワールドの追加を実行しますか？\n\n（ドライランで確認した内容でゲームデータへマップ一式を追加します）')) return;
        const r = await runBlocking('ワールドを追加しています…', () => api(`/api/games/${gameId}/add-world`, { body: buildBody(false) }));
        show(r);
    };
}

// ─────────────────────────────────────────────
// 外部AI依頼文書の作成
// ─────────────────────────────────────────────
async function renderPrompt() {
    const gamesRes = await api('/api/games');
    const games = gamesRes.games ?? [];
    const fields = [
        ['title', '作品タイトル'], ['protagonist', '主人公'], ['playable', 'プレイアブルキャラ'],
        ['maps', '都市・マップ一覧'], ['buildings', '主要建築物'], ['npcs', 'NPC 一覧'],
        ['enemies', '敵一覧'], ['items', 'アイテム一覧'], ['skills', 'スキル一覧'],
        ['events', '章イベント'], ['audio', 'BGM / SE 方針'], ['translation', '翻訳方針'],
    ];
    app.innerHTML = `<h2><span class="h2-ico" aria-hidden="true">📝</span>外部AI依頼文書の作成</h2>
    <div class="card">
        <label class="field"><span>対象ゲーム（選択すると設定済みデータから下の入力欄を自動入力）</span>
            <select id="pr-game"><option value="">新規</option>${games.map((g) => `<option value="${esc(g.gameId)}">${esc(g.gameId)}${g.title?.ja ? `（${esc(g.title.ja)}）` : ''}</option>`).join('')}</select></label>
        <div id="pr-autofill-note"></div>
        <h3>作品要約シート（入力すると依頼文と一緒に整形されます）</h3>
        ${fields.map(([k, label]) => `<label class="field"><span>${label}</span><textarea id="pr-${k}" style="min-height:44px"></textarea></label>`).join('')}
        <div class="actions"><button id="pr-gen">依頼文を生成</button></div>
    </div>
    <div id="pr-result"></div>`;
    // 対象ゲーム選択 → 設定済みデータで全欄を自動入力（物語プロジェクトの解析 summary が第一・無ければゲームのメタ情報）
    document.getElementById('pr-game').onchange = async () => {
        const gameId = document.getElementById('pr-game').value;
        const setField = (k, v) => { document.getElementById(`pr-${k}`).value = v ?? ''; };
        const note = document.getElementById('pr-autofill-note');
        if (!gameId) {
            for (const [k] of fields) setField(k, '');
            note.innerHTML = '';
            return;
        }
        const g = games.find((x) => x.gameId === gameId);
        let summary = null;
        if (g?.storyProjectId) {
            const pr = await api(`/api/projects/${g.storyProjectId}`);
            summary = pr.project?.analysis?.summary ?? pr.analysis?.summary ?? null;
        }
        if (summary) {
            for (const [k] of fields) setField(k, summary[k]);
            note.innerHTML = `<p class="muted-note">✔ 物語プロジェクト ${esc(g.storyProjectId)} の解析データから自動入力しました（編集可能）</p>`;
        } else {
            for (const [k] of fields) setField(k, '');
            setField('title', `${g?.title?.ja ?? gameId} / ${g?.title?.en ?? ''}`.trim());
            const chars = g?.characters ?? {};
            const pn = (c) => (c?.name?.ja ? `${c.name.ja}（${c.id}）` : null);
            setField('playable', [pn(chars.boy), pn(chars.girl)].filter(Boolean).join('、'));
            setField('protagonist', g?.startMapId ? `開始地点: ${g.startMapId}` : '');
            setField('maps', (g?.maps ?? []).join('、'));
            note.innerHTML = '<p class="muted-note">このゲームには物語プロジェクトの解析データが無いため、ゲームのメタ情報のみ自動入力しました（残りは手入力）</p>';
        }
    };
    document.getElementById('pr-gen').onclick = async () => {
        const summary = {};
        for (const [k] of fields) summary[k] = document.getElementById(`pr-${k}`).value;
        const gameId = document.getElementById('pr-game').value || null;
        const r = await api('/api/prompt', { body: { gameId, summary } });
        document.getElementById('pr-result').innerHTML = `
            <div class="card"><h3>依頼文
                <button class="secondary" id="pr-copy-req">コピー</button>
                <button class="secondary" id="pr-download">📥 MD ファイルをダウンロード</button></h3>
            <pre class="log" id="pr-text">${esc(r.requestText)}</pre></div>
            <div class="card"><h3>作品要約シート <button class="secondary" id="pr-copy-sheet">コピー</button></h3>
            <pre class="log" id="pr-sheet">${esc(r.summarySheet)}</pre></div>
            ${(r.attachments ?? []).length > 0 ? `<div class="card"><h3>添付すべき資料</h3><ul class="plain">${r.attachments.map((a) => `<li>${a.exists ? '✔' : '<span class=err>✖</span>'} ${esc(a.path)}</li>`).join('')}</ul></div>` : ''}
            <div class="card"><h3>使い方</h3><ul class="plain">${(r.usage ?? []).map((u) => `<li class="dim">${esc(u)}</li>`).join('')}</ul></div>`;
        // コピー成功のフィードバック（プロンプトカードの「英語プロンプトをコピー」と同じ挙動に統一）
        const wireCopy = (btnId, srcId) => {
            const b = document.getElementById(btnId);
            b.onclick = async () => {
                await navigator.clipboard.writeText(document.getElementById(srcId).textContent);
                b.textContent = 'コピーしました';
                setTimeout(() => { b.textContent = 'コピー'; }, 1200);
            };
        };
        wireCopy('pr-copy-req', 'pr-text');
        wireCopy('pr-copy-sheet', 'pr-sheet');
        // ChatGPT / Gemini 等のフロンティア LLM アプリへそのままアップロードできる Markdown を Blob で保存（§16.13）
        document.getElementById('pr-download').onclick = () => {
            const md = `${r.requestText}\n\n---\n\n${r.summarySheet}\n`;
            const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `AI依頼文_${gameId ?? '新規ゲーム'}_${new Date().toISOString().slice(0, 10)}.md`;
            a.click();
            URL.revokeObjectURL(a.href);
        };
    };
}

// ─────────────────────────────────────────────
// ルーティング
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// API設定（利用者自身の API キー管理。値はこのブラウザの localStorage にのみ保存）
// ─────────────────────────────────────────────
// Google Material Symbols の visibility / visibility_off（Apache 2.0・インライン SVG＝CDN 不要のオフライン動作。
// currentColor で描画されるため CSS の color がそのまま効く）
const ICON_VISIBILITY = '<svg xmlns="http://www.w3.org/2000/svg" height="18" width="18" viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true"><path d="M480-320q75 0 127.5-52.5T660-500q0-75-52.5-127.5T480-680q-75 0-127.5 52.5T300-500q0 75 52.5 127.5T480-320Zm0-72q-45 0-76.5-31.5T372-500q0-45 31.5-76.5T480-608q45 0 76.5 31.5T588-500q0 45-31.5 76.5T480-392Zm0 192q-146 0-266-81.5T40-500q54-137 174-218.5T480-800q146 0 266 81.5T920-500q-54 137-174 218.5T480-200Zm0-300Zm0 220q113 0 207.5-59.5T832-500q-50-101-144.5-160.5T480-720q-113 0-207.5 59.5T128-500q50 101 144.5 160.5T480-280Z"/></svg>';
const ICON_VISIBILITY_OFF = '<svg xmlns="http://www.w3.org/2000/svg" height="18" width="18" viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true"><path d="m644-428-58-58q9-47-27-88t-93-32l-58-58q17-8 34.5-12t37.5-4q75 0 127.5 52.5T660-500q0 20-4 37.5T644-428Zm128 126-58-56q38-29 67.5-63.5T828-500q-50-101-143.5-160.5T480-720q-29 0-57 4t-55 12l-62-62q41-17 84-25.5t90-8.5q151 0 269 83.5T920-500q-23 59-60.5 109.5T772-302Zm20 246L624-222q-35 11-70.5 16.5T480-200q-151 0-269-83.5T40-500q21-53 53-98.5t73-81.5L56-790l56-56 736 736-56 56ZM222-624q-29 26-53 57t-41 67q50 101 143.5 160.5T480-280q20 0 39-2.5t39-5.5l-36-38q-11 3-21 4.5t-21 1.5q-75 0-127.5-52.5T300-500q0-11 1.5-21t4.5-21l-84-82Zm319 93Zm-151 75Z"/></svg>';

async function renderSettings() {
    const modelResponse = await api('/api/settings/models');
    const fallbackModels = {
        analysis: 'deepseek/deepseek-v4-flash-0731',
        json: 'deepseek/deepseek-v4-flash-0731',
        imageCost: 'openai/gpt-image-2',
        imageSpeed: 'black-forest-labs/flux.2-klein-4b',
        imageAdult: 'x-ai/grok-imagine-image-quality',
        imageActiveRole: 'imageCost',
    };
    const modelInfo = modelResponse.ok
        ? modelResponse
        : { defaults: fallbackModels, effective: fallbackModels, imageRoles: [
            { id: 'imageCost', label: 'コスト重視' },
            { id: 'imageSpeed', label: '速度重視' },
            { id: 'imageAdult', label: 'アダルト緩和重視' },
        ] };
    const modelDefaults = { ...fallbackModels, ...(modelInfo.defaults ?? {}) };
    const storedModelSettings = getStoredOpenRouterModelSettings();
    const effectiveModels = { ...modelDefaults, ...(modelInfo.effective ?? {}), ...storedModelSettings };
    const imageRoles = Array.isArray(modelInfo.imageRoles) && modelInfo.imageRoles.length > 0
        ? modelInfo.imageRoles
        : [
            { id: 'imageCost', label: 'コスト重視' },
            { id: 'imageSpeed', label: '速度重視' },
            { id: 'imageAdult', label: 'アダルト緩和重視' },
        ];
    // 旧キャラ生成画面の選択値があれば、初回だけ新しい共通設定へ移行する。
    if (!storedModelSettings.imageActiveRole) {
        const legacyModel = (localStorage.getItem('vn-image-model') ?? '').split('|')[1] ?? '';
        const migratedRole = imageRoles.find((role) => effectiveModels[role.id] === legacyModel)?.id;
        if (migratedRole) {
            storedModelSettings.imageActiveRole = migratedRole;
            saveStoredOpenRouterModelSettings(storedModelSettings);
            effectiveModels.imageActiveRole = migratedRole;
        }
    }
    // 既定の候補枠はサーバ（config/llm.json の image.defaultRole）から受け取る。
    // 保存値・既定値とも不正なときだけ、ここのハードコード既定へ落とす
    const defaultImageRole = OPENROUTER_IMAGE_ROLES.includes(modelDefaults.imageActiveRole)
        ? modelDefaults.imageActiveRole
        : 'imageCost';
    const activeImageRole = OPENROUTER_IMAGE_ROLES.includes(effectiveModels.imageActiveRole)
        ? effectiveModels.imageActiveRole
        : defaultImageRole;
    const imageModels = Array.isArray(modelInfo.imageModels) ? modelInfo.imageModels : [];
    const pricingCache = getStoredOpenRouterPricing();
    const priceNames = {
        prompt: '入力（prompt）', completion: '出力（completion）',
        image: '画像入力（image）', image_output: '画像出力（image_output）',
        input_cache_write: 'キャッシュ書込',
        request: 'リクエスト', internal_reasoning: '内部推論',
    };
    const priceUnits = {
        prompt: '/token', completion: '/token', image: '/image', image_output: '/image token',
        input_cache_write: '/token', internal_reasoning: '/token', request: '/request',
    };
    const formatEstimatedUsd = (value) => Number(value).toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
    const formatEstimatedSeconds = (value) => Number(value).toFixed(1).replace(/\.0$/, '');
    const imageGenerationMetricsHtml = (model) => {
        const info = imageModels.find((entry) => entry?.id === model);
        const estimatedUsdPerImage = Number(info?.estimatedUsdPerImage);
        const estimatedGenerationSeconds = Number(info?.estimatedGenerationSecondsPerImage);
        const cost = Number.isFinite(estimatedUsdPerImage) && estimatedUsdPerImage >= 0
            ? `<b>$${esc(formatEstimatedUsd(estimatedUsdPerImage))}</b>`
            : '<b>推定値未登録</b>';
        const time = Number.isFinite(estimatedGenerationSeconds) && estimatedGenerationSeconds >= 0
            ? `<b><span>${esc(formatEstimatedSeconds(estimatedGenerationSeconds))}</span><span>秒</span></b>`
            : '<b>推定値未登録</b>';
        return `<span class="model-image-metric"><span>画像1枚あたりの生成コスト（推定）:</span> ${cost}</span>
            <span class="model-image-metric"><span>画像1枚あたりの生成時間（推定）:</span> ${time}</span>`;
    };
    const modelPriceHtml = (slot, model) => {
        if (OPENROUTER_IMAGE_ROLES.includes(slot)) return imageGenerationMetricsHtml(model);
        const cached = pricingCache[slot];
        if (!cached || cached.model !== model || !cached.pricing || typeof cached.pricing !== 'object') {
            return '<span class="dim">保存するとOpenRouterからI/O単価を取得します。</span>';
        }
        const order = ['prompt', 'completion', 'image', 'image_output', 'input_cache_write', 'request', 'internal_reasoning'];
        const entries = order.filter((key) => cached.pricing[key] !== undefined).map((key) =>
            `<span><b>${esc(priceNames[key] ?? key)}:</b> $${esc(cached.pricing[key])}${esc(priceUnits[key] ?? '')}</span>`,
        );
        return `<span class="model-price-source">I/O単価（OpenRouter API原値）</span>${entries.length > 0 ? entries.join('') : '<span>単価情報なし</span>'}`;
    };
    // 用途別モデルの並び（2026-07-26 指示）: 文書解析 → JSON生成 →
    // 「キャラ生成で実際に使用する画像モデル」→ その選択肢になる画像候補3つ（1段下げてぶら下げる）。
    // 候補を先に並べると「何を決める設定なのか」が読み取れないため、決定側を先に置く
    const textModelFields = [
        ['analysis', '文書解析', '物語解析・画像プロンプト・ワールド設計に使用'],
        ['json', 'JSON生成', 'マップ・NPC等のJSON生成と英訳に使用'],
    ];
    const imageModelFields = [
        ['imageCost', '画像生成：コスト重視', '画像を1枚ずつ生成する最安候補'],
        ['imageSpeed', '画像生成：速度重視', '生成時間を優先する候補（既定の使用候補）'],
        ['imageAdult', '画像生成：アダルト緩和重視', 'アダルト系表現の規制が比較的緩い候補'],
    ];
    const modelSettingRowHtml = ([slot, label, note]) => `<div class="model-setting-row">
            <label class="field model-setting-field" for="model-${slot}"><span>${label}</span><small>${note}</small>
                <input type="text" id="model-${slot}" value="${esc(effectiveModels[slot])}" autocomplete="off" spellcheck="false" placeholder="author/model-id">
            </label>
            <div class="model-setting-actions">
                <button type="button" data-save-model="${slot}">保存</button>
                <button type="button" class="secondary" data-reset-model="${slot}">デフォルトに戻す</button>
            </div>
            <div class="model-pricing" id="model-price-${slot}">${modelPriceHtml(slot, effectiveModels[slot])}</div>
        </div>`;
    const modelSettingsHtml = `<section class="openrouter-model-settings" aria-labelledby="openrouter-model-settings-title">
        <h4 id="openrouter-model-settings-title">OpenRouter 使用モデル</h4>
        <p class="dim">OpenRouterのモデルID（例: <code>deepseek/deepseek-v4-flash-0731</code>）を直接指定します。設定とコスト情報はこのブラウザに保存されます。</p>
        ${textModelFields.map(modelSettingRowHtml).join('')}
        <div class="model-setting-row model-active-row">
            <label class="field model-setting-field" for="model-image-active"><span>キャラ生成で実際に使用する画像モデル</span>
                <small>ここで選んだ候補を「キャラ生成」画面の既定値と実行モデルにします。</small>
                <select id="model-image-active">${imageRoles.map((role) => `<option value="${esc(role.id)}" ${role.id === activeImageRole ? 'selected' : ''}>${esc(role.label)}: ${esc(effectiveModels[role.id])}</option>`).join('')}</select>
            </label>
            <div class="model-setting-actions">
                <button type="button" data-save-active-model>保存</button>
                <button type="button" class="secondary" data-reset-active-model>デフォルトに戻す</button>
            </div>
            <div class="model-pricing"><span class="ok">現在の使用モデル: ${esc(effectiveModels[activeImageRole])}</span></div>
        </div>
        <div class="model-image-candidates">${imageModelFields.map(modelSettingRowHtml).join('')}</div>
    </section>`;
    const GUIDES = {
        openrouter: `<b>OpenRouter API キーの作成方法:</b><br>
            ① <a href="https://openrouter.ai/" target="_blank" rel="noopener noreferrer">openrouter.ai</a> でアカウントを作成（Google アカウント等でサインアップ可）<br>
            ② 右上メニュー →「Keys」（<a href="https://openrouter.ai/settings/keys" target="_blank" rel="noopener noreferrer">openrouter.ai/settings/keys</a>）→「Create Key」で新しいキーを作成<br>
            ③ 「Credits」（<a href="https://openrouter.ai/credits" target="_blank" rel="noopener noreferrer">openrouter.ai/credits</a>）でクレジットをチャージ（$5〜。解析＋画像一式で ≒$0.1〜0.3/作品）<br>
            ④ <code>sk-or-</code> で始まるキーをコピーして上の欄へ貼り付け →「保存」→「接続テスト」`,
        meshy: `<b>Meshy API キーの作成方法:</b><br>
            ① <a href="https://www.meshy.ai/" target="_blank" rel="noopener noreferrer">meshy.ai</a> でアカウントを作成<br>
            ② 右上のアイコン →「Settings」→「API」（<a href="https://app.meshy.ai/settings/api" target="_blank" rel="noopener noreferrer">app.meshy.ai/settings/api</a>）→「Create API Key」で新しいキーを作成<br>
            ③ <code>msy_</code> で始まるキーをコピーして上の欄へ貼り付け →「保存」→「接続テスト」<br>
            ※ クレジット（P）残高は <a href="https://app.meshy.ai/" target="_blank" rel="noopener noreferrer">app.meshy.ai</a> で確認。3D キャラ 1体 ≒30〜45P（無料枠あり・不足時は有料プラン）`,
    };
    const mask = (k) => (k ? `…${k.slice(-4)}（${k.length}文字）` : null);
    // サービスごとのキー接頭辞。保存・接続テスト・保存済み表示の3箇所で同じ判定を使う
    const KEY_PREFIX = { openrouter: 'sk-or-', meshy: 'msy_' };
    const svc = (id, label, issueUrl, issueLabel) => {
        const saved = getApiKey(id);
        const savedFormatOk = !saved || saved.startsWith(KEY_PREFIX[id]);
        const verified = isApiKeyVerified(id);
        return `
    <div class="card">
        <h3>${label}</h3>
        <div class="dim">状態: ${saved
        ? (savedFormatOk
            ? (verified
                ? `<span class="ok">✔ 設定済み・接続テスト済み</span>（${esc(mask(saved))}）`
                : `<span class="warn">⚠ 設定済み・接続テスト未実施</span>（${esc(mask(saved))}）— 下の「接続テスト」を実行してください`)
            : `<span class="err">⚠ 保存済みキーの形式が不正です（${esc(KEY_PREFIX[id])} で始まっていません）。「キーを削除」してから正しいキーを登録し直してください</span>`)
        : '<span class="err">✖ 未設定</span>'}</div>
        <div class="dim">保存先: このブラウザの localStorage</div>
        <div class="dim" style="margin-bottom:8px">キーの発行: <a href="${issueUrl}" target="_blank" rel="noopener noreferrer">${esc(issueLabel)}</a></div>
        <form data-key-form="${id}">
            <!-- パスワード欄のみのフォームに対する Chrome の a11y 警告対応＋パスワードマネージャが
                 「どのサービスのキーか」を識別できるよう、非表示の username を持たせる -->
            <input type="text" name="username" value="${id}-api-key" autocomplete="username" hidden readonly tabindex="-1" aria-hidden="true">
            <div>
                <label class="field key-field"><span>API キー（ご自身で発行したもの）</span>
                    <span class="key-input-wrap">
                        <!-- autocomplete="new-password": ブラウザのパスワードマネージャが「このサイトの保存済み
                             パスワード」（別サービスのキー等）を自動入力して別のキーにすり替わる事故を防ぐ。
                             password 型の autocomplete="off" は Chrome では無視されるため必ず new-password にする -->
                        <input type="password" id="key-${id}" name="key-${id}" value="${esc(saved)}" placeholder="${esc(KEY_PREFIX[id])}…" autocomplete="new-password" spellcheck="false">
                        <button type="button" class="key-eye" data-eye="${id}" title="キーの表示/非表示を切り替えます（入力ミスの目視確認用）">${ICON_VISIBILITY}</button>
                    </span></label>
            </div>
            ${actionRowsHtml(
        // 2026-07-26 指示による例外: キーの操作は「保存 → 接続テスト → キーを削除」を 1 行に並べる。
        // この 3 つは同じ入力欄に対する一連の操作で、貼り直し（削除→入力→保存）まで含めて
        // 入力欄のすぐ下で完結させたいため、削除だけを 2 行目へ落とさない
        [`<button type="submit" data-save-key="${id}">保存</button>`,
            `<button type="button" class="secondary" data-test-key="${id}">接続テスト</button>`,
            saved ? `<button type="button" class="secondary" data-clear-key="${id}" title="このブラウザに保存されているキーを削除します">キーを削除</button>` : ''],
    )}
        </form>
        <!-- キーの作成方法は入力欄・操作ボタンのすぐ下に置く（モデル設定より前・2026-07-26 指示）。
             読むのは「キーを用意する場面」なので、入力欄から離すと必要なときに見つからない -->
        <p class="muted-note" style="line-height:1.9">${GUIDES[id]}</p>
        ${id === 'openrouter' ? modelSettingsHtml : ''}
    </div>`;
    };
    // 利用開始時に必要な外部接続情報（API キー）は、動作モードによらず全てこの画面で入力・確認できる。
    // 内部構成・別バージョンの存在を示唆する説明は書かない（公開版の絶対厳守事項）
    // .api-settings: この画面のボタンだけを小さくするための目印（2026-07-26 指示。CSS 側で -20%/半分）
    app.innerHTML = `<div class="api-settings"><h2><span class="h2-ico" aria-hidden="true">🔑</span>API設定</h2>
    <p class="dim">本ツールの LLM 解析・JSON 生成・画像生成は <b>OpenRouter</b>、3D キャラ生成は <b>Meshy</b> の API を、<b>利用者自身が発行したキー</b>で直接呼び出します（実費はそれぞれのアカウントに課金。提供元が用意したデフォルトキーはありません）。</p>
    <p><b>⚠ キーはこのブラウザ（localStorage）にのみ保存されます。サーバやファイルには保存されません。共有PCでは利用しないでください。</b></p>
    ${svc('openrouter', 'OpenRouter（LLM 解析・JSON 生成・画像生成）', 'https://openrouter.ai/settings/keys', 'openrouter.ai/settings/keys')}
    ${svc('meshy', 'Meshy（3D キャラ自動生成）', 'https://app.meshy.ai/settings/api', 'app.meshy.ai/settings/api')}</div>`;
    // form はパスワード欄の DOM 警告対策。保存は submit イベント経由で行う（Enter キーでも保存可能）。
    // 既定送信の抑止（preventDefault）を保存処理・再描画より先に行うこと。ボタンの onclick 側で
    // 先に再描画すると form が document から切り離され、ブラウザが既定送信を破棄する際に
    // 「Form submission canceled because the form is not connected」警告が出る（保存自体は成功していても紛らわしい）
    const saveKey = (id) => {
        const key = document.getElementById(`key-${id}`).value.trim();
        if (!key) { alert('キーを入力してください'); return; }
        if (key.length < 16) { alert('キーが短すぎます（コピーミスの可能性）'); return; }
        if (!key.startsWith(KEY_PREFIX[id])) {
            alert(`このサービスのキーは ${KEY_PREFIX[id]} で始まります。キーを確認してください。\n\n（ブラウザのパスワード自動入力で別サービスのキーが入っている場合があります。入力欄を空にして貼り付け直してください）`);
            return;
        }
        localStorage.setItem(API_KEY_STORE[id], key);
        alert('キーをこのブラウザに保存しました。続けて「接続テスト」で確認してください。\n（⚠ 共有PCでは利用しないでください）');
        renderSettings();
    };
    for (const form of app.querySelectorAll('form[data-key-form]')) {
        form.addEventListener('submit', (ev) => {
            ev.preventDefault();
            saveKey(form.dataset.keyForm);
        });
    }
    const syncActiveImageSelection = (settings) => {
        const role = OPENROUTER_IMAGE_ROLES.includes(settings.imageActiveRole)
            ? settings.imageActiveRole
            : modelDefaults.imageActiveRole;
        const model = settings[role] ?? modelDefaults[role];
        if (model) localStorage.setItem('vn-image-model', `openrouter|${model}`);
    };
    const fetchAndCacheModelPricing = async (slot, model, button) => {
        const typedKey = document.getElementById('key-openrouter')?.value.trim() ?? '';
        const response = await runBlocking(
            'OpenRouterからモデル単価を取得しています…',
            () => api('/api/settings/model-pricing', { body: { model, ...(typedKey ? { key: typedKey } : {}) } }),
            button,
        );
        if (!response.ok) return response;
        const cache = getStoredOpenRouterPricing();
        cache[slot] = {
            model,
            id: response.id ?? model,
            name: response.name ?? model,
            pricing: response.pricing ?? {},
            fetchedAt: new Date().toISOString(),
        };
        saveStoredOpenRouterPricing(cache);
        return response;
    };
    for (const saveBtn of app.querySelectorAll('button[data-save-model]')) {
        saveBtn.onclick = async () => {
            const slot = saveBtn.dataset.saveModel;
            const input = document.getElementById(`model-${slot}`);
            const model = input?.value.trim() ?? '';
            if (!validOpenRouterModelId(model)) {
                alert('モデルIDは author/model-id 形式で入力してください。\n例: deepseek/deepseek-v4-flash-0731');
                input?.focus();
                return;
            }
            const pricing = await fetchAndCacheModelPricing(slot, model, saveBtn);
            if (!pricing.ok) {
                alert(`モデルを保存できません:\n${pricing.error ?? '不明なエラー'}`);
                return;
            }
            const settings = getStoredOpenRouterModelSettings();
            settings[slot] = model;
            saveStoredOpenRouterModelSettings(settings);
            if (OPENROUTER_IMAGE_ROLES.includes(slot)) syncActiveImageSelection(settings);
            await renderSettings();
        };
    }
    for (const resetBtn of app.querySelectorAll('button[data-reset-model]')) {
        resetBtn.onclick = async () => {
            const slot = resetBtn.dataset.resetModel;
            const model = modelDefaults[slot];
            const settings = getStoredOpenRouterModelSettings();
            delete settings[slot];
            saveStoredOpenRouterModelSettings(settings);
            if (OPENROUTER_IMAGE_ROLES.includes(slot)) syncActiveImageSelection(settings);
            const pricing = await fetchAndCacheModelPricing(slot, model, resetBtn);
            if (!pricing.ok) {
                alert(`デフォルトに戻しましたが、OpenRouterから単価を取得できませんでした:\n${pricing.error ?? '不明なエラー'}`);
            }
            await renderSettings();
        };
    }
    const saveActiveModelBtn = app.querySelector('button[data-save-active-model]');
    if (saveActiveModelBtn) saveActiveModelBtn.onclick = async () => {
        const role = document.getElementById('model-image-active')?.value;
        if (!OPENROUTER_IMAGE_ROLES.includes(role)) return;
        const settings = getStoredOpenRouterModelSettings();
        settings.imageActiveRole = role;
        saveStoredOpenRouterModelSettings(settings);
        syncActiveImageSelection(settings);
        const model = settings[role] ?? modelDefaults[role];
        const pricing = await fetchAndCacheModelPricing(role, model, saveActiveModelBtn);
        if (!pricing.ok) {
            alert(`使用モデルは保存しましたが、OpenRouterから単価を取得できませんでした:\n${pricing.error ?? '不明なエラー'}`);
        }
        await renderSettings();
    };
    const resetActiveModelBtn = app.querySelector('button[data-reset-active-model]');
    if (resetActiveModelBtn) resetActiveModelBtn.onclick = async () => {
        const settings = getStoredOpenRouterModelSettings();
        delete settings.imageActiveRole;
        saveStoredOpenRouterModelSettings(settings);
        syncActiveImageSelection(settings);
        const role = modelDefaults.imageActiveRole;
        const model = settings[role] ?? modelDefaults[role];
        const pricing = await fetchAndCacheModelPricing(role, model, resetActiveModelBtn);
        if (!pricing.ok) {
            alert(`デフォルトの使用候補に戻しましたが、OpenRouterから単価を取得できませんでした:\n${pricing.error ?? '不明なエラー'}`);
        }
        await renderSettings();
    };
    // 👁 目アイコン: パスワード表示/非表示の切替
    for (const eyeBtn of app.querySelectorAll('button[data-eye]')) {
        eyeBtn.onclick = () => {
            const input = document.getElementById(`key-${eyeBtn.dataset.eye}`);
            const show = input.type === 'password';
            input.type = show ? 'text' : 'password';
            eyeBtn.innerHTML = show ? ICON_VISIBILITY_OFF : ICON_VISIBILITY; // Material Symbols: visibility / visibility_off
        };
    }
    for (const clearBtn of app.querySelectorAll('button[data-clear-key]')) {
        clearBtn.onclick = async () => {
            const id = clearBtn.dataset.clearKey;
            if (!confirm('このブラウザに保存されているキーを削除しますか？')) return;
            const response = await runBlocking('API キーを削除しています…', () => api('/api/settings/clear', { body: { service: id } }), clearBtn);
            if (!response.ok) {
                alert(`キーの削除に失敗しました:\n${response.error ?? '不明なエラー'}`);
                return;
            }
            localStorage.removeItem(API_KEY_STORE[id]);
            renderSettings();
        };
    }
    for (const testBtn of app.querySelectorAll('button[data-test-key]')) {
        testBtn.onclick = async () => {
            const id = testBtn.dataset.testKey;
            const typed = document.getElementById(`key-${id}`).value.trim();
            const key = typed || getApiKey(id);
            if (!key) { alert('キーが未設定です。キーを入力（または保存）してから接続テストしてください。'); return; }
            if (!key.startsWith(KEY_PREFIX[id])) {
                alert(`このサービスのキーは ${KEY_PREFIX[id]} で始まります。キーを確認してください。\n\n（ブラウザのパスワード自動入力で別サービスのキーが入っている場合があります。入力欄を空にして貼り付け直してください）`);
                return;
            }
            testBtn.disabled = true;
            try {
                const resp = await api('/api/settings/test', { body: { service: id, key } });
                if (!resp.ok) { alert(`接続テスト失敗（${id}）:\n${resp.error ?? '不明なエラー'}`); return; }
                // テストに成功したキーの指紋を記録（保存済みキーと一致すれば「接続テスト済み」表示になる）
                markApiKeyVerified(id, key);
                if (id === 'openrouter') {
                    const bal = resp.limitUsd !== null && resp.limitUsd !== undefined
                        ? `残高 $${Number(resp.remainingUsd).toFixed(2)} / 上限 $${resp.limitUsd}` : '上限なしキー';
                    alert(`OpenRouter 接続 OK\n${bal}（使用済み $${Number(resp.usageUsd ?? 0).toFixed(4)}）`);
                } else {
                    alert(`Meshy 接続 OK\n${resp.message ?? ''}`);
                }
                renderSettings(); // 状態表示（接続テスト済み）を最新化
            } finally { testBtn.disabled = false; }
        };
    }
}

// ─────────────────────────────────────────────
// ユーザー認証画面（#/login ほか）。AUTH_ENABLED のときだけルートガードから到達する
// ─────────────────────────────────────────────

/** 認証フォーム共通: submit で action() を実行し、二重送信防止とエラー表示を面倒みる。 */
function wireAuthForm(formId, msgId, action) {
    const form = document.getElementById(formId);
    if (!form) return;
    form.addEventListener('submit', async (ev) => {
        ev.preventDefault();
        const msg = document.getElementById(msgId);
        const buttons = [...form.querySelectorAll('button')];
        const initialDisabled = new Map(buttons.map((button) => [button, button.disabled]));
        const submitButton = ev.submitter instanceof HTMLButtonElement
            ? ev.submitter
            : form.querySelector('button[type="submit"]');
        if (!retainButtonBusy(submitButton, { exclusive: true })) return;
        for (const b of buttons) b.disabled = true;
        if (msg) msg.innerHTML = '<span class="dim">処理中…</span>';
        try {
            const r = await action(new FormData(form));
            if (r && r.ok === false) {
                if (msg) msg.innerHTML = `<span class="err" role="alert">${esc(r.error ?? '処理を完了できませんでした。')}</span>`;
            } else if (r && r.message) {
                if (msg) msg.innerHTML = `<span class="ok" role="status">${esc(r.message)}</span>`;
            } else if (msg) {
                msg.textContent = '';
            }
        } finally {
            for (const b of buttons) {
                if (b !== submitButton) b.disabled = initialDisabled.get(b) === true;
            }
            releaseButtonBusy(submitButton);
        }
    });
}

/** パスワード欄の表示/非表示トグル（API設定画面と同じ挙動）。 */
function wireAuthEyes(rootEl) {
    for (const eyeBtn of rootEl.querySelectorAll('button[data-auth-eye]')) {
        eyeBtn.onclick = () => {
            const input = document.getElementById(eyeBtn.dataset.authEye);
            const show = input.type === 'password';
            input.type = show ? 'text' : 'password';
            eyeBtn.innerHTML = show ? ICON_VISIBILITY_OFF : ICON_VISIBILITY;
        };
    }
}

const authPasswordField = (id, { label = 'パスワード', autocomplete = 'current-password', hint = '' } = {}) => `
    <label class="field"><span>${esc(label)}${hint ? `<span class="dim">（${esc(hint)}）</span>` : ''}</span>
        <span class="key-input-wrap">
            <input type="password" id="${id}" name="${id}" autocomplete="${autocomplete}" required minlength="8" maxlength="72">
            <button type="button" class="key-eye" data-auth-eye="${id}" title="パスワードの表示/非表示を切り替えます">${ICON_VISIBILITY}</button>
        </span></label>`;

// SNS ログインボタンのブランドアイコン（インライン SVG＝CDN 不使用・オフライン動作）。
// Google は公式 4 色 G マーク、Apple は currentColor のアップルマーク（ボタン文字色に追随）
const ICON_GOOGLE = '<svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>';
const ICON_APPLE = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.954 4.45z"/></svg>';

const authOAuthButtons = () => `
    <div class="actions" style="margin-top:10px">
        <button type="button" class="secondary oauth-btn" data-oauth="google" disabled aria-disabled="true" title="Google 認証は現在準備中です">${ICON_GOOGLE}<span>Google でログイン（準備中）</span></button>
        <button type="button" class="secondary oauth-btn" data-oauth="apple" disabled aria-disabled="true" title="Apple 認証は現在準備中です">${ICON_APPLE}<span>Apple でログイン（準備中）</span></button>
    </div>`;

function wireOAuthButtons(msgId) {
    for (const btn of app.querySelectorAll('button[data-oauth]')) {
        if (btn.disabled) continue;
        btn.onclick = async () => {
            btn.disabled = true;
            sessionStorage.setItem(AUTH_RETURN_STORE, AUTH.sanitizeReturnHash(sessionStorage.getItem(AUTH_RETURN_STORE) ?? '#/'));
            const r = await AUTH.signInWithOAuth(btn.dataset.oauth);
            if (r.ok === false) {
                const msg = document.getElementById(msgId);
                if (msg) msg.innerHTML = `<span class="err" role="alert">${esc(r.error)}</span>`;
                btn.disabled = false;
            }
            // 成功時はブラウザごと外部認証へ遷移するため何もしない
        };
    }
}

function renderAuthUnavailable() {
    app.innerHTML = '<div class="card"><p class="dim">アカウント機能はこの環境では利用できません。</p></div>';
}

function renderLogin() {
    if (!AUTH_ENABLED) return renderAuthUnavailable();
    // 表示メッセージ: 明示ログアウト＞セッション切れ の優先で 1 つだけ出す
    const signedOut = sessionStorage.getItem(AUTH_SIGNEDOUT_STORE);
    const expired = !signedOut && sessionStorage.getItem(AUTH_EXPIRED_STORE);
    sessionStorage.removeItem(AUTH_SIGNEDOUT_STORE);
    sessionStorage.removeItem(AUTH_EXPIRED_STORE);
    // 前回ログインしたメールアドレスを初期表示（パスワードはブラウザのパスワードマネージャが
    // ユーザー名=メールに紐づけて補完する。ツール側でパスワードは保存しない）
    const lastEmail = localStorage.getItem(AUTH_LAST_EMAIL_STORE) ?? '';
    app.innerHTML = `<div class="card auth-card">
        <h2><span class="h2-ico" aria-hidden="true">🔐</span>ログイン</h2>
        <p class="dim">本ツールの利用にはログインが必要です。作成したゲームの公開・管理をアカウントに紐付けて安全に行うためです。</p>
        ${signedOut ? '<p class="ok" role="status">ログアウトしました。</p>' : ''}
        ${expired ? '<p class="warn" role="alert">セッションの有効期限が切れました。もう一度ログインしてください。</p>' : ''}
        <form id="login-form">
            <label class="field"><span>メールアドレス</span>
                <input type="email" id="login-email" name="email" autocomplete="username" required maxlength="255" value="${esc(lastEmail)}"></label>
            ${authPasswordField('login-password', { autocomplete: 'current-password' })}
            <div class="auth-subactions"><a href="#/forgot-password">パスワードをお忘れの方</a></div>
            <div class="actions"><button type="submit">ログイン</button></div>
            <div id="login-msg" aria-live="polite"></div>
        </form>
        ${authOAuthButtons()}
        <div class="auth-divider">アカウントをお持ちでない方</div>
        <div class="actions"><button type="button" class="secondary" id="login-goto-signup" title="メールアドレスで新規アカウントを作成します">新規ユーザー登録</button></div>
    </div>`;
    document.getElementById('login-goto-signup').onclick = () => { location.hash = '#/signup'; };
    wireAuthEyes(app);
    wireOAuthButtons('login-msg');
    wireAuthForm('login-form', 'login-msg', async () => {
        const email = document.getElementById('login-email').value;
        const password = document.getElementById('login-password').value;
        const emailError = AUTH.validateEmail(email);
        if (emailError) return { ok: false, error: emailError };
        // ブラウザの自動入力が「以前サイトのパスワードとして誤保存された API キー」を入れてくる事故への
        // ガード（実障害 2026-07-11）。API へ投げる前に検知して、原因と直し方をその場で案内する
        if (/^(sk-or-|msy_)/.test(password.trim())) {
            return {
                ok: false,
                error: 'パスワード欄に API キーが入力されています（ブラウザの自動入力が古い保存内容を補完した可能性があります）。パスワード欄を空にして、アカウントのパスワードを入力してください。ブラウザに保存された古いパスワードの削除もおすすめします。',
            };
        }
        const r = await AUTH.signIn(email, password);
        if (r.ok === false) return r;
        const back = AUTH.sanitizeReturnHash(sessionStorage.getItem(AUTH_RETURN_STORE) ?? '#/');
        sessionStorage.removeItem(AUTH_RETURN_STORE);
        location.hash = back;
        if (location.hash === back) route();
        return { ok: true };
    });
}

function renderSignup() {
    if (!AUTH_ENABLED) return renderAuthUnavailable();
    app.innerHTML = `<div class="card auth-card">
        <h2><span class="h2-ico" aria-hidden="true">👤</span>新規ユーザー登録</h2>
        <p class="dim">登録したメールアドレス宛に確認メールが届きます。メール内のリンクを開くと登録が完了します。</p>
        <form id="signup-form">
            <label class="field"><span>メールアドレス</span>
                <input type="email" id="signup-email" name="email" autocomplete="username" required maxlength="255"></label>
            ${authPasswordField('signup-password', { autocomplete: 'new-password', hint: '8文字以上' })}
            ${authPasswordField('signup-password2', { label: 'パスワード（確認）', autocomplete: 'new-password' })}
            <div class="actions"><button type="submit">登録する</button></div>
            <div id="signup-msg" aria-live="polite"></div>
        </form>
        ${authOAuthButtons()}
        <div class="auth-divider">すでにアカウントをお持ちの方</div>
        <div class="actions"><button type="button" class="secondary" id="signup-goto-login" title="ログイン画面へ戻ります">ログインへ戻る</button></div>
    </div>`;
    document.getElementById('signup-goto-login').onclick = () => { location.hash = '#/login'; };
    wireAuthEyes(app);
    wireOAuthButtons('signup-msg');
    wireAuthForm('signup-form', 'signup-msg', async () => {
        const email = document.getElementById('signup-email').value;
        const password = document.getElementById('signup-password').value;
        const password2 = document.getElementById('signup-password2').value;
        const error = AUTH.validateEmail(email) ?? AUTH.validatePassword(password, password2);
        if (error) return { ok: false, error };
        if (!confirm(`この内容でユーザー登録しますか？\n\n${email.trim()} 宛に確認メールを送信します。`)) return undefined;
        return AUTH.signUp(email, password);
    });
}

function renderForgotPassword() {
    if (!AUTH_ENABLED) return renderAuthUnavailable();
    app.innerHTML = `<div class="card auth-card">
        <h2><span class="h2-ico" aria-hidden="true">✉️</span>パスワード再設定</h2>
        <p class="dim">登録済みのメールアドレスを入力してください。再設定用のリンクをお送りします。</p>
        <form id="forgot-form">
            <label class="field"><span>メールアドレス</span>
                <input type="email" id="forgot-email" name="email" autocomplete="username" required maxlength="255"></label>
            <div class="actions"><button type="submit">再設定メールを送信</button></div>
            <div id="forgot-msg" aria-live="polite"></div>
        </form>
        <div class="actions" style="margin-top:14px"><button type="button" class="secondary" id="forgot-goto-login" title="ログイン画面へ戻ります">ログインへ戻る</button></div>
    </div>`;
    document.getElementById('forgot-goto-login').onclick = () => { location.hash = '#/login'; };
    wireAuthForm('forgot-form', 'forgot-msg', async () => {
        const email = document.getElementById('forgot-email').value;
        const emailError = AUTH.validateEmail(email);
        if (emailError) return { ok: false, error: emailError };
        // メール送信前に宛先を見せて確認をとる（打ち間違い・誤クリック対策）
        if (!confirm(`パスワード再設定メールを送信しますか？\n\n送信先: ${email.trim()}`)) return undefined;
        return AUTH.sendPasswordReset(email);
    });
}

function renderResetPassword() {
    if (!AUTH_ENABLED) return renderAuthUnavailable();
    if (!AUTH.isAuthenticated()) {
        app.innerHTML = `<div class="card auth-card">
            <h2><span class="h2-ico" aria-hidden="true">✉️</span>パスワード再設定</h2>
            <p class="err" role="alert">再設定リンクが無効または期限切れです。もう一度最初から操作してください。</p>
            <p class="dim"><a href="#/forgot-password">再設定メールを送り直す</a> / <a href="#/login">ログインへ戻る</a></p>
        </div>`;
        return;
    }
    app.innerHTML = `<div class="card auth-card">
        <h2><span class="h2-ico" aria-hidden="true">🔑</span>新しいパスワードの設定</h2>
        <form id="reset-form">
            <!-- 非表示 username（メール）: a11y 警告対応＋パスワードマネージャが正しい資格情報を更新できるようにする -->
            <input type="email" name="email" value="${esc(AUTH.state.user?.email ?? '')}" autocomplete="username" hidden readonly tabindex="-1" aria-hidden="true">
            ${authPasswordField('reset-password', { label: '新しいパスワード', autocomplete: 'new-password', hint: '8文字以上' })}
            ${authPasswordField('reset-password2', { label: '新しいパスワード（確認）', autocomplete: 'new-password' })}
            <div class="actions"><button type="submit">パスワードを更新</button></div>
            <div id="reset-msg" aria-live="polite"></div>
        </form>
    </div>`;
    wireAuthEyes(app);
    wireAuthForm('reset-form', 'reset-msg', async () => {
        const password = document.getElementById('reset-password').value;
        const password2 = document.getElementById('reset-password2').value;
        const error = AUTH.validatePassword(password, password2);
        if (error) return { ok: false, error };
        if (!confirm('パスワードを更新しますか？')) return undefined;
        const r = await AUTH.updatePassword(password);
        if (r.ok === false) return r;
        setTimeout(() => { location.hash = '#/'; }, 800);
        return r;
    });
}

function renderAuthCallback() {
    if (!AUTH_ENABLED) return renderAuthUnavailable();
    const returned = (typeof window !== 'undefined' && window.__VN_AUTH_RETURNED__) || null;
    if (AUTH.state.passwordRecovery) { location.hash = '#/reset-password'; return; }
    if (AUTH.isAuthenticated()) {
        const back = AUTH.sanitizeReturnHash(sessionStorage.getItem(AUTH_RETURN_STORE) ?? '#/');
        sessionStorage.removeItem(AUTH_RETURN_STORE);
        location.hash = back;
        if (location.hash === back) route();
        return;
    }
    if (returned?.error) {
        app.innerHTML = `<div class="card auth-card">
            <h2><span class="h2-ico" aria-hidden="true">🔐</span>ログイン処理</h2>
            <p class="err" role="alert">ログインを完了できませんでした。しばらくしてから再度お試しください。</p>
            <p class="dim"><a href="#/login">ログインへ戻る</a></p>
        </div>`;
        return;
    }
    // セッション確立待ち（認証状態変更で自動的に再ルーティングされる）
    app.innerHTML = `<div class="card auth-card"><h2><span class="h2-ico" aria-hidden="true">🔐</span>ログイン処理</h2>
        <p class="dim"><span class="spinner" aria-hidden="true"></span> ログイン処理を進めています。しばらくお待ちください……</p>
        <p class="dim"><a href="#/login">画面が切り替わらない場合はこちら</a></p></div>`;
}

/** アバターURL入力と保存済みURLを、その場で安全に画像プレビューへ反映する。 */
function wireAvatarPreview() {
    const input = document.getElementById('profile-avatar');
    const image = document.getElementById('profile-avatar-preview-image');
    const status = document.getElementById('profile-avatar-preview-status');
    if (!input || !image || !status) return;
    const reset = (message) => {
        image.hidden = true;
        image.removeAttribute('src');
        delete image.dataset.url;
        status.textContent = message;
    };
    image.onload = () => {
        if (!image.dataset.url || image.src !== image.dataset.url) return;
        image.hidden = false;
        status.textContent = 'アバター画像を確認できます。';
    };
    image.onerror = () => reset('画像を読み込めません。URLを確認してください。');
    const sync = () => {
        const value = input.value.trim();
        if (!value) {
            reset('URLを入力すると画像を確認できます。');
            return;
        }
        let url;
        try { url = new URL(value); } catch {
            reset('有効な https:// URLを入力してください。');
            return;
        }
        if (url.protocol !== 'https:') {
            reset('有効な https:// URLを入力してください。');
            return;
        }
        image.dataset.url = url.href;
        image.hidden = false;
        status.textContent = '画像を確認中…';
        image.src = url.href;
    };
    input.addEventListener('input', sync);
    input.addEventListener('change', sync);
    sync();
}

function renderAccount() {
    if (!AUTH_ENABLED) return renderAuthUnavailable();
    const user = AUTH.state.user;
    const profile = AUTH.state.profile;
    if (!user) { location.hash = '#/login'; return; }
    const providers = (user.app_metadata?.providers ?? [user.app_metadata?.provider]).filter(Boolean);
    const providerLabel = { email: currentUiLanguage() === 'en' ? 'Email' : 'メール', google: 'Google', apple: 'Apple' };
    const fmt = (iso) => (iso ? new Date(iso).toLocaleString(currentUiLanguage() === 'en' ? 'en-US' : 'ja-JP') : '-');
    app.innerHTML = `<h2><span class="h2-ico" aria-hidden="true">👤</span>アカウント</h2>
    <div class="card auth-card">
        <h3>登録情報</h3>
        <div class="dim"><span>メールアドレス:</span> ${esc(user.email ?? '-')}
            ${user.email_confirmed_at ? '<span class="ok">（確認済み）</span>' : '<span class="warn">（未確認: 確認メールのリンクを開いてください）</span>'}</div>
        <div class="dim"><span>ログイン方法:</span> ${esc(providers.map((p) => providerLabel[p] ?? p).join(' / ') || '-')}</div>
        <div class="dim">登録日時: ${esc(fmt(user.created_at))} / 最終ログイン: ${esc(fmt(user.last_sign_in_at))}</div>
    </div>
    <div class="card auth-card">
        <h3>プロフィール</h3>
        <form id="profile-form">
            <label class="field"><span>表示名（1〜60文字）</span>
                <input type="text" id="profile-name" name="displayName" maxlength="60" value="${esc(profile?.display_name ?? '')}" autocomplete="nickname"></label>
            <label class="field"><span>アバター URL（任意・https:// のみ）</span>
                <input type="url" id="profile-avatar" name="avatarUrl" maxlength="2048" value="${esc(profile?.avatar_url ?? '')}" placeholder="https://…"></label>
            <div class="profile-avatar-preview" aria-label="アバター画像のプレビュー">
                <img id="profile-avatar-preview-image" class="profile-avatar-preview-image" alt="アバター画像のプレビュー" referrerpolicy="no-referrer" loading="lazy" hidden>
                <span id="profile-avatar-preview-status" class="dim" aria-live="polite">URLを入力すると画像を確認できます。</span>
            </div>
            <div class="actions"><button type="submit">プロフィールを保存</button></div>
            <div id="profile-msg" aria-live="polite"></div>
        </form>
    </div>
    <div class="card auth-card">
        <h3>セキュリティ</h3>
        <p class="dim">パスワードを変更する場合は、登録メールアドレス宛に再設定用リンクを送ります（送信前に確認ダイアログが表示されます）。</p>
        <div class="actions">
            <button id="account-reset" class="secondary" title="確認のうえ、登録メールアドレスへパスワード再設定用のリンクを送ります">パスワード再設定メールを送信</button>
        </div>
        <div id="account-msg" aria-live="polite"></div>
    </div>
    <div class="card auth-card">
        <h3>ログアウト</h3>
        <p class="dim">このブラウザからログアウトします（このブラウザ内の作業データや API キー設定は消えません）。</p>
        <div class="actions">
            <button id="account-logout" class="secondary" title="確認のうえ、このブラウザからログアウトします">ログアウト</button>
        </div>
    </div>
    <div class="card auth-card">
        <h3>アカウントの削除</h3>
        <p class="dim">アカウントと登録情報（プロフィール）を完全に削除します。この操作は取り消せません。削除完了後、登録メールアドレスへお知らせを送信します（このブラウザ内の作業データや API キー設定は消えません）。</p>
        <div class="actions">
            <button id="account-delete" class="danger" title="確認のうえ、アカウントを完全に削除します">アカウントを削除</button>
        </div>
        <div id="account-delete-msg" aria-live="polite"></div>
    </div>`;
    wireAvatarPreview();
    wireAuthForm('profile-form', 'profile-msg', async () => {
        if (!confirm('プロフィールを保存しますか？')) return undefined;
        return AUTH.updateProfile({
            displayName: document.getElementById('profile-name').value,
            avatarUrl: document.getElementById('profile-avatar').value,
        });
    });
    document.getElementById('account-reset').onclick = async (ev) => {
        // メール送信は取り消せない操作のため、実行前に必ず確認をとる（誤クリック対策）
        if (!confirm(`パスワード再設定メールを送信しますか？\n\n送信先: ${user.email}`)) return;
        ev.target.disabled = true;
        const r = await AUTH.sendPasswordReset(user.email);
        document.getElementById('account-msg').innerHTML = r.ok === false
            ? `<span class="err" role="alert">${esc(r.error)}</span>` : `<span class="ok" role="status">${esc(r.message ?? '送信しました。')}</span>`;
        ev.target.disabled = false;
    };
    document.getElementById('account-delete').onclick = async (ev) => {
        // 取り返しのつかない操作のため、実行前に必ず OK / キャンセルの確認をとる
        const confirmText = 'アカウントを削除しますか？\n\n'
            + `対象: ${user.email}\n\n`
            + 'アカウントと登録情報（プロフィール）は完全に削除され、元に戻せません。\n'
            + '（このブラウザ内の作業データや API キー設定は消えません）';
        if (!confirm(confirmText)) return;
        ev.target.disabled = true;
        const msg = document.getElementById('account-delete-msg');
        msg.innerHTML = '<span class="dim" role="status">アカウントを削除しています……</span>';
        try {
            const token = AUTH.state.session?.access_token;
            const res = await fetch('/api/delete-account', {
                method: 'POST',
                headers: { authorization: `Bearer ${token ?? ''}` },
            });
            const body = await res.json().catch(() => null);
            if (!res.ok || body?.ok !== true) {
                throw new Error(body?.error || 'アカウントの削除に失敗しました。時間をおいて再試行してください。');
            }
            // サーバ側は削除済み。ローカルセッションを破棄してログイン画面へ戻す
            sessionStorage.setItem(AUTH_SIGNEDOUT_STORE, '1');
            await AUTH.signOut().catch(() => { /* セッションは既に無効のため失敗しても続行 */ });
            alert('アカウントを削除しました。ご利用ありがとうございました。');
            location.hash = '#/login';
            if (location.hash === '#/login') route();
        } catch (error) {
            msg.innerHTML = `<span class="err" role="alert">${esc(error.message)}</span>`;
            ev.target.disabled = false;
        }
    };
    document.getElementById('account-logout').onclick = async () => {
        if (!confirm('ログアウトしますか？')) return; // 誤クリック対策
        // signOut 完了前にフラグを立てる（onChange が「セッション切れ」扱いにする前に明示ログアウトと分かるように）
        sessionStorage.setItem(AUTH_SIGNEDOUT_STORE, '1');
        await AUTH.signOut();
        location.hash = '#/login';
        if (location.hash === '#/login') route(); // 既に #/login だった場合も再描画してメッセージを出す
    };
}

// ── ⑦ 限定公開・マイ公開ゲーム（Phase D-1） ────────────────────────

const PUBLISH_GAMES_ORIGIN = 'https://games.voxelnovels.com';
const LEGAL_DOC_LABELS = { terms: '利用規約', publishing: '公開ガイドライン', content: 'コンテンツポリシー', privacy: 'プライバシーポリシー' };
// private は「公開データを消さずに一時的に見せない」状態。「非公開」だと作り直しを想像させるため名称を変えている
const VISIBILITY_LABELS = { private: '限定公開の一時停止', unlisted: '限定公開（URLを知っている人のみ）', public: '一般公開' };
const MODERATION_LABELS = { unreviewed: '未申請', pending: '審査中', approved: '承認済み', rejected: '要修正', suspended: '運営停止中' };

/** 公開 API（Netlify Functions）を Supabase セッションつきで呼ぶ。 */
async function fetchPublishApi(method, action, body) {
    const token = AUTH?.state?.session?.access_token ?? '';
    try {
        const res = await fetch(`/api/publish/${action}`, {
            method,
            headers: { authorization: `Bearer ${token}`, ...(body ? { 'content-type': 'application/json' } : {}) },
            body: body ? JSON.stringify(body) : undefined,
        });
        const json = await res.json().catch(() => null);
        if (!res.ok || json?.ok !== true) return { ok: false, error: json?.error ?? `HTTP ${res.status}`, kind: json?.kind };
        // 審査メニュー・「次の作業」の出し分けはこの応答を再利用する（そのための専用リクエストは足さない）
        if (action === 'mine') {
            rememberPublishPlan(json.quota?.plan ?? null);
            rememberPublishedGames(json.games);
        } else if (method !== 'GET') {
            // 公開・取り下げ・公開範囲の変更で「公開中かどうか」が変わる。
            // 記憶を捨てて次の画面で取り直す（古い記憶のまま編集ロックが残る／外れるのを防ぐ）
            publishedGamesLoaded = false;
            invalidateScreenResponseCache();
        }
        return json;
    } catch (e) {
        return { ok: false, error: `サーバへ接続できません（${e?.message ?? e}）` };
    }
}

const mb = (bytes) => (Number(bytes || 0) / 1024 / 1024).toFixed(2);
/** 容量の表示（数値と単位の間は空けない: 12.34MB）。 */
const formatMb = (bytes) => `${mb(bytes)}MB`;
/** 情報表（項目｜内容）の 1 行。「・」「／」で 1 行に詰め込まず 1 項目 1 行で見せる。 */
const infoRow = (label, valueHtml) => `<tr><th>${esc(label)}</th><td>${valueHtml}</td></tr>`;
/** 上限値の表示は整数 MB（仕様として決まっている値なので小数を出さない）。実測値は mb() のまま。 */
const mbLimit = (bytes) => String(Math.round(Number(bytes || 0) / 1024 / 1024));

function quotaMeterHtml(quota, extraBytes = 0) {
    if (!quota || quota.unlimited) return '';   // 管理者（無制限）は上限メーター自体を出さない
    const used = Number(quota.bytesUsed || 0) + Number(extraBytes || 0);
    const max = Math.max(1, Number(quota.maxBytesTotal || 0));
    const pct = Math.min(100, Math.round((used / max) * 100));
    return `<div style="background:#2a3040;border-radius:6px;height:8px;margin:2px 0 6px"><div style="width:${pct}%;height:8px;border-radius:6px;background:${pct > 90 ? 'var(--err)' : '#F39C12'}"></div></div>`;
}

async function renderProjectPublish(el, projectId, d) {
    if (!AUTH_ENABLED) {
        el.innerHTML = '<div class="card"><p class="dim">公開機能はこの環境では利用できません。</p></div>';
        return;
    }
    if (!AUTH.isAuthenticated()) {
        el.innerHTML = `<div class="card"><p class="dim">ゲームの公開にはログインが必要です。公開したゲームをアカウントに紐付けて安全に管理するためです。</p>
        <div class="actions"><button onclick="location.hash='#/login'">ログイン</button></div></div>`;
        return;
    }
    el.innerHTML = '<p class="dim">公開情報を読み込み中……</p>';
    const [plan, mine] = await Promise.all([
        api('/api/publish-plan', { body: { projectId } }),
        fetchPublishApi('GET', 'mine'),
    ]);
    if (!plan.ok) {
        // 説明だけで行き止まりにしない。足りない工程の画面へ直接進める
        el.innerHTML = `<div class="card">
            <p class="err">${esc(plan.error ?? '公開の準備に失敗しました')}</p>
            <div class="actions">
                <button onclick="location.hash='#/project/${esc(projectId)}/generate'" title="② ベースデータ生成の画面へ移動します（公開にはこの工程の完了が必要です）">② ベースデータ生成へ</button>
                <button class="secondary" onclick="location.hash='#/project/${esc(projectId)}'" title="このプロジェクトの工程一覧（①解析）へ戻ります">工程の一覧へ戻る</button>
            </div>
        </div>`;
        return;
    }
    const published = mine.ok ? (mine.games ?? []).find((g) => g.game_id === plan.gameId) : null;
    // 判定の正はサーバ側。UI では「押せるのに必ず失敗する」状態を作らない
    const underReview = published?.moderation_status === 'pending';
    // 公開済みの内容差し替えは禁止（審査済みの内容が入れ替わるのを防ぐ）。変更は取り下げ→再公開。
    // 公開済みはこの画面を「公開パッケージ＋公開済み情報」の閲覧専用にする
    const alreadyPublished = !!published;
    const defaultTitle = published?.title_ja || localizedText(d.analysis?.game?.title, '') || '';
    const lang = currentUiLanguage() === 'en' ? 'en' : 'ja';
    const packageCard = `<div class="card">
        <h3>公開パッケージ</h3>
        <table class="info-table"><tbody>
            ${infoRow('公開URL（gameId）', `${esc(PUBLISH_GAMES_ORIGIN)}/${esc(plan.gameId)}/`)}
            ${infoRow('対象ファイル', `${plan.fileCount}件${plan.skippedCount ? `（対象外 ${plan.skippedCount}件は同梱されません）` : ''}`)}
            ${infoRow('容量', `${mb(plan.totalBytes)}MB`)}
            ${infoRow('生成3Dモデル', `${plan.modelCount ?? 0}件`)}
        </tbody></table>
        ${mine.ok ? quotaMeterHtml(mine.quota, published ? plan.totalBytes - Number(published.bytes_total || 0) : plan.totalBytes) : `<div class="warn">クォータ情報を取得できませんでした: ${esc(mine.error ?? '')}</div>`}
    </div>`;

    // 公開済みは編集させない（内容差し替えによる審査回避を作らない）。
    // 表示は「公開パッケージ」と「公開済み情報」の2つに絞り、申請前なら取り下げだけ行えるようにする
    if (alreadyPublished) {
        const url = `${PUBLISH_GAMES_ORIGIN}/${published.game_id}/`;
        const playable = published.visibility !== 'private' && published.moderation_status !== 'suspended';
        const canWithdraw = published.moderation_status === 'unreviewed';
        // 一時停止中（private）は誰も遊べない。掲載されても遊べないものを審査に回さない
        //（「マイ公開ゲーム」の掲載申請と同じ条件。押せるのに必ず無意味になる操作を作らない）
        const requestBlockedByPaused = canWithdraw && published.visibility === 'private';
        el.innerHTML = `${packageCard}
        <div class="card">
            <h3>公開済み情報</h3>
            <table class="info-table"><tbody>
                ${infoRow('URL', playable ? `<a href="${esc(url)}" target="_blank" rel="noopener noreferrer">${esc(url)}</a>` : esc(url))}
                ${infoRow('タイトル', esc(published.title_ja || published.game_id))}
                ${infoRow('公開状態', `<b>${esc(VISIBILITY_LABELS[published.visibility] ?? published.visibility)}</b>`)}
                ${infoRow('審査状態', esc(MODERATION_LABELS[published.moderation_status] ?? published.moderation_status))}
                ${infoRow('版（rev）', `${published.current_rev}`)}
                ${infoRow('公開中の容量', `${mb(published.bytes_total)}MB`)}
                ${infoRow('初回公開日時', esc(String(published.first_published_at ?? '').replace('T', ' ').slice(0, 16) || '—'))}
                ${published.moderation_note ? infoRow('審査コメント', esc(published.moderation_note)) : ''}
            </tbody></table>
            ${underReview ? '<div class="warn">ギャラリー掲載の審査中です。審査が終わるまで公開の操作はできません（「マイ公開ゲーム」で審査状態の確認・取り下げができます）。</div>' : ''}
            ${published.moderation_status === 'suspended' ? '<div class="err">運営により停止されています。解除は運営が行います。</div>' : ''}
            ${requestBlockedByPaused ? '<div class="warn">一時停止中はギャラリー掲載を申請できません（掲載されても遊べないため）。先に「マイ公開ゲーム」で公開状態を「限定公開（URLを知っている人のみ）」へ戻してください。</div>' : ''}
            <div class="dim">内容を変更するには、公開を取り下げてから、もう一度公開してください（ギャラリー掲載の審査は最初からやり直しになります）。</div>
            ${actionRowsHtml(
        // よく使う: 公開ページ・URL コピー・掲載申請・管理画面への移動
        [playable ? `<a href="${esc(url)}" target="_blank" rel="noopener noreferrer"><button title="公開中のゲームを新しいタブで開いて実際に遊んで確認します">▶ 公開ページを開く</button></a>` : '',
            '<button class="secondary" id="pub-copy-url" title="公開URLをコピーします。SNS やメッセージで共有できます">URLをコピー</button>',
            canWithdraw
                ? (requestBlockedByPaused
                    ? '<button id="pub-request" disabled title="一時停止中は申請できません。「マイ公開ゲーム」で公開状態を「限定公開（URLを知っている人のみ）」へ戻すと申請できます">ギャラリー掲載を申請</button>'
                    : '<button id="pub-request" title="ギャラリーへの掲載を運営へ申請します。承認されると一般公開（ギャラリー掲載）になります。確認ダイアログが表示されます">ギャラリー掲載を申請</button>')
                : '',
            '<button class="secondary" onclick="location.hash=\'#/mygames\'" title="公開状態の変更・ギャラリー掲載の申請・公開取り下げをまとめて行う画面へ移動します">マイ公開ゲームで管理</button>'],
        // たまにしか使わない: 公開の取り下げ
        canWithdraw ? '<button class="danger" id="pub-withdraw" title="公開を取り下げて公開URLを 404 にします（このブラウザ内の作成データは残るので、いつでも公開し直せます）。うっかり公開してしまった場合もここから戻せます。確認ダイアログが表示されます">公開取り下げ</button>' : '',
    )}
            <div id="pub-result" aria-live="polite"></div>
        </div>`;
        document.getElementById('pub-copy-url').onclick = (ev) => copyPublicUrl(url, ev.target);
        const requestBtn = document.getElementById('pub-request');
        if (requestBtn && !requestBlockedByPaused) { // 一時停止中は DOM の disabled と二重で守る
            requestBtn.onclick = () => requestGalleryListing(
                published.game_id, document.getElementById('pub-result'), () => renderProject(projectId, 'publish'),
            );
        }
        const withdrawBtn = document.getElementById('pub-withdraw');
        if (withdrawBtn) {
            withdrawBtn.onclick = async () => {
                if (!confirm(`「${published.game_id}」の公開を取り下げますか？\n\n公開URLは直ちに 404 になり、公開したファイルは削除されます。\nこのブラウザ内の作成データは残るため、あとで同じ URL で公開し直せます。`)) return;
                const res = await fetchPublishApi('POST', 'delete', { gameId: published.game_id });
                const msgEl = document.getElementById('pub-result');
                if (!res.ok) {
                    msgEl.innerHTML = `<span class="err" role="alert">${esc(res.error ?? '公開取り下げに失敗しました')}</span>`;
                    return;
                }
                renderProject(projectId, 'publish');
            };
        }
        return;
    }

    // 押しても必ず失敗する状態を作らない: 容量・件数の超過は事前に判定し、対処ボタンを添える
    const quota = mine.ok ? mine.quota : null;
    const quotaProblems = [];
    if (quota && !quota.unlimited) {   // 管理者は上限なし（サーバ側 assertQuota も免除される）
        if (plan.totalBytes > Number(quota.maxBytesPerGame || 0)) {
            quotaProblems.push(`このゲームの容量 ${mb(plan.totalBytes)}MB が 1ゲームの上限 ${mbLimit(quota.maxBytesPerGame)}MB を超えています`);
        }
        if (Number(quota.bytesUsed || 0) + plan.totalBytes > Number(quota.maxBytesTotal || 0)) {
            quotaProblems.push(`合計容量の上限 ${mbLimit(quota.maxBytesTotal)}MB を超えます（使用中 ${mb(quota.bytesUsed)}MB ＋ 今回 ${mb(plan.totalBytes)}MB）`);
        }
        if (Number(quota.gamesUsed || 0) + 1 > Number(quota.maxGames || 1)) {
            quotaProblems.push(`公開できるゲーム数の上限（${quota.maxGames}件）に達しています`);
        }
    }
    const consentBoxes = Object.entries(LEGAL_DOC_LABELS).map(([slug, label]) => `
        <label class="choice">
            <input type="checkbox" class="pub-consent" data-doc="${esc(slug)}">
            <span><a href="/${lang}/legal/${slug === 'content' ? 'content' : slug}/" target="_blank" rel="noopener noreferrer">${esc(label)}</a> に同意する</span>
        </label>`).join('');
    el.innerHTML = `
    <div class="card">
        <h3>公開の流れ</h3>
        <ol class="dim" style="margin:0 0 0 20px">
            <li>ここで実行すると <b>限定公開</b>（URLを知っている人だけが遊べます。ギャラリーには出ません）</li>
            <li>「マイ公開ゲーム」から <b>ギャラリー掲載を申請</b></li>
            <li>運営の審査に通ると <b>一般公開</b>（ギャラリーに掲載されます）</li>
        </ol>
    </div>
    ${packageCard}
    <div class="card">
        <h3>公開情報</h3>
        <label class="field"><span>タイトル（日本語・1〜120文字）</span>
            <input type="text" id="pub-title" maxlength="120" value="${esc(defaultTitle)}"></label>
        <label class="field"><span>紹介文（任意・ギャラリーと共有リンクに表示・300文字まで）</span>
            <textarea id="pub-desc" rows="3" maxlength="300" placeholder="どんな物語のゲームかを紹介しましょう（先頭100文字ほどが一覧やSNSの要約に出ます）"></textarea></label>
    </div>
    <div class="card">
        <h3>規約への同意</h3>
        <p class="dim">公開には次の文書への同意が必要です（同意記録は文書の版数つきで保存されます）。</p>
        ${consentBoxes}
    </div>
    <div class="card">
        <h3>公開の実行</h3>
        ${quotaProblems.length > 0 ? `<div class="warn">${esc(quotaProblems.join(' / '))}</div>
        <div class="actions"><button class="secondary" onclick="location.hash='#/mygames'" title="公開中のゲームを取り下げて容量・件数の枠を空ける画面へ移動します">マイ公開ゲームで枠を空ける</button></div>` : ''}
        <div class="actions"><button id="pub-run" disabled title="このプロジェクトのゲームを games.voxelnovels.com へ限定公開します（URLを知っている人だけが遊べます）。確認ダイアログが表示されます">🚀 ゲームを公開する</button>
            <span class="dim" id="pub-ready-hint"></span></div>
        <textarea id="pub-log" class="log-box" readonly style="margin-top:10px" placeholder="実行ログがここに表示されます"></textarea>
        <div id="pub-result" aria-live="polite"></div>
    </div>`;
    // 「タイトル」「全文書の同意」が揃った時点で公開できるようにし、足りないものを常に表示する
    const runBtn = document.getElementById('pub-run');
    const titleInput = document.getElementById('pub-title');
    const consentChecks = [...document.querySelectorAll('.pub-consent')];
    const readyHint = document.getElementById('pub-ready-hint');
    const syncReady = () => {
        const missing = [];
        if (titleInput.value.trim().length < 1) missing.push('タイトル');
        if (!consentChecks.every((c) => c.checked)) missing.push('規約への同意');
        runBtn.disabled = missing.length > 0 || quotaProblems.length > 0;
        readyHint.textContent = quotaProblems.length > 0
            ? '容量・件数の上限を解消すると公開できます'
            : (missing.length > 0 ? `あと ${missing.join(' と ')} で公開できます` : '');
    };
    titleInput.oninput = syncReady;
    for (const c of consentChecks) c.onchange = syncReady;
    syncReady();
    runBtn.onclick = async (ev) => {
        const titleJa = document.getElementById('pub-title').value.trim();
        const msgEl = document.getElementById('pub-result');
        if (titleJa.length < 1 || titleJa.length > 120) {
            msgEl.innerHTML = '<span class="err" role="alert">タイトル（日本語）は 1〜120文字で入力してください。</span>';
            return;
        }
        const checks = [...document.querySelectorAll('.pub-consent')];
        if (!checks.every((c) => c.checked)) {
            msgEl.innerHTML = '<span class="err" role="alert">公開には全ての文書への同意が必要です。</span>';
            return;
        }
        if (!confirm(`ゲームを公開しますか？\n\n公開URL: ${PUBLISH_GAMES_ORIGIN}/${plan.gameId}/\n容量: ${mb(plan.totalBytes)}MB\n\n${published ? `更新公開（rev ${published.current_rev + 1}）になります。` : '初回公開は限定公開（URLを知っている人のみ）で確定します。'}`)) return;
        msgEl.innerHTML = '';
        // 版数はサーバ（/api/publish/mine）が返す現行版を正とする。
        // 取得できなかった文書は送らず、サーバ側の未同意チェック（428）に任せる
        const versions = mine.legalVersions ?? {};
        const acceptedDocuments = Object.fromEntries(
            checks.map((c) => [c.dataset.doc, versions[c.dataset.doc]]).filter(([, version]) => !!version),
        );
        // startAndPoll は { response, job } を返す（受け取り方を誤ると、成功しても失敗表示になる）
        const { response: runResponse, job } = await startAndPoll('/api/publish-run', {
            projectId,
            titleJa,
            descriptionJa: document.getElementById('pub-desc').value.trim() || null,
            acceptedDocuments,
            accessToken: AUTH.state.session?.access_token ?? '',
        }, document.getElementById('pub-log'));
        if (job?.status === 'done' && job.result?.playUrl) {
            const url = job.result.playUrl;
            msgEl.innerHTML = `<div class="ok" role="status" style="margin-top:8px">🎉 限定公開が完了しました（URLを知っている人だけが遊べます）</div>
            <p class="dim">ギャラリーに載せるには、次に「マイ公開ゲーム」から<b>掲載申請</b>を行い、運営の審査を通過する必要があります。</p>
            <div class="actions" style="margin-top:6px">
                <a href="${esc(url)}" target="_blank" rel="noopener noreferrer"><button title="公開したゲームを新しいタブで開いて確認します">▶ 公開ページを開く</button></a>
                <button class="secondary" id="pub-copy" title="公開URLをコピーします。SNS やメッセージで共有できます">URLをコピー</button>
                <button id="pub-request-now" title="続けてギャラリーへの掲載を運営へ申請します。承認されると一般公開（ギャラリー掲載）になります。確認ダイアログが表示されます">ギャラリー掲載を申請</button>
                <button class="secondary" onclick="location.hash='#/mygames'" title="公開状態の変更・ギャラリー掲載の申請・公開取り下げをまとめて行う画面へ移動します">マイ公開ゲームで管理</button>
            </div>`;
            document.getElementById('pub-request-now').onclick = () => requestGalleryListing(
                plan.gameId, msgEl, () => renderProject(projectId, 'publish'),
            );
            document.getElementById('pub-copy').onclick = (e2) => copyPublicUrl(url, e2.target);
        } else {
            const reason = job?.error ?? runResponse?.error;
            msgEl.innerHTML = `<span class="err" role="alert">${esc(reason ?? '公開に失敗しました。ログを確認してください。')}</span>`;
        }
    };
}

/**
 * 公開中のゲームが無いときの案内。説明だけ出して終わらせず、⑦ 限定公開の画面へ直接進めるようにする。
 * 公開できるのは ②（ベースデータ生成）まで進んだプロジェクトだけなので、対象を絞って並べる。
 */
/** 公開URLをクリップボードへコピーする（失敗時は prompt で手動コピーへ退避）。 */
async function copyPublicUrl(url, btn) {
    try {
        await navigator.clipboard.writeText(url);
        const label = btn.textContent;
        btn.textContent = 'コピーしました';
        setTimeout(() => { btn.textContent = label; }, 1600);
    } catch {
        prompt('この URL をコピーしてください', url);
    }
}

/** ギャラリー掲載を申請する（確認→API→再描画）。画面をまたいで同じ操作にする。 */
async function requestGalleryListing(gameId, msgEl, onDone) {
    if (!confirm(`「${gameId}」のギャラリー掲載審査を申請しますか？\n\n審査には日数がかかることがあります。承認されると一般公開（ギャラリー掲載）になります。\n申請中は公開範囲の変更・内容の差し替え・取り下げができなくなります（申請の取り下げは可能です）。`)) return;
    const res = await fetchPublishApi('POST', 'request-listing', { gameId });
    if (!res.ok) {
        if (msgEl) msgEl.innerHTML = `<span class="err" role="alert">${esc(res.error ?? '掲載申請に失敗しました')}</span>`;
        return;
    }
    onDone();
}

async function noPublishedGamesHtml() {
    const pr = await api('/api/projects');
    const ready = (pr.projects ?? []).filter((p) => p.gameId);
    if (ready.length === 0) {
        return `<div class="card">
            <p class="dim">公開中のゲームはまだありません。まず物語プロジェクトを作り、②（ベースデータ生成）まで進めると公開できます。</p>
            <div class="actions">
                <button onclick="location.hash='#/story-new'" title="小説などのテキストを登録し、解析からゲーム生成まで進める新しいプロジェクトを作ります">📖 物語から新規作成</button>
                <button class="secondary" onclick="location.hash='#/'" title="作成中のプロジェクト一覧（ホーム）へ移動します">ホーム（プロジェクト一覧）へ</button>
            </div>
        </div>`;
    }
    const label = (p) => {
        const title = localizedText(p.title, p.projectId);
        return title.length > 28 ? `${title.slice(0, 28)}…` : title;
    };
    return `<div class="card">
        <p class="dim">公開中のゲームはまだありません。プロジェクトの「⑦ 限定公開」タブから公開できます。</p>
        <div class="actions">
            ${ready.map((p) => `<button onclick="location.hash='#/project/${esc(p.projectId)}/publish'" title="このプロジェクトの「⑦ 限定公開」画面へ移動します">⑦ 限定公開へ進む: ${esc(label(p))}</button>`).join('')}
        </div>
    </div>`;
}

// ── 通信待ちで画面を止めないための共通処理（2026-07-27 指示）──────────────
// 直近の応答を覚えておき、2 回目以降は先に描いてから裏で取り直す。判定の正はサーバ側で、
// ここは表示を早く出すためだけの記憶。書き込み系 API を通したら必ず捨てる（下の invalidate）。
const SCREEN_RESPONSE_CACHE = new Map();
let screenRenderToken = 0;

/** 公開・審査系の書き込みのあとは記憶を捨て、次の描画で必ず取り直す。 */
function invalidateScreenResponseCache() {
    SCREEN_RESPONSE_CACHE.clear();
}

/**
 * 「記憶で即描画 → 裏で取り直し → 変わっていたときだけ描き直す」。
 * 初回（記憶なし）だけ読み込み中を出して待つ。
 * @param {object} params
 * @param {string} params.key      画面ごとの記憶キー
 * @param {string} params.loading  初回に出す HTML
 * @param {() => Promise<object>} params.load   応答を取りにいく
 * @param {(json: object) => void|Promise<void>} params.paint 応答から描いて配線する
 */
async function renderWithCachedResponse({ key, loading, load, paint }) {
    const token = ++screenRenderToken;
    const cached = SCREEN_RESPONSE_CACHE.get(key);
    if (cached === undefined) {
        app.innerHTML = loading;
        const json = await load();
        if (token !== screenRenderToken) return;
        if (json?.ok === true) SCREEN_RESPONSE_CACHE.set(key, JSON.stringify(json));
        await paint(json);
        return;
    }
    await paint(JSON.parse(cached));
    const json = await load();
    // 別画面へ移ったあと・取り直しに失敗したときは、表示中の内容を壊さない
    if (token !== screenRenderToken || json?.ok !== true) return;
    const next = JSON.stringify(json);
    if (next === cached) return;   // 変わっていなければ描き直さない（ちらつき防止）
    SCREEN_RESPONSE_CACHE.set(key, next);
    if (busyCount > 0) return;     // 処理中の画面は差し替えない
    await paint(json);
}

async function renderMyGames() {
    if (!AUTH_ENABLED) {
        app.innerHTML = '<div class="card"><p class="dim">公開機能はこの環境では利用できません。</p></div>';
        return;
    }
    await renderWithCachedResponse({
        key: 'publish:mine',
        loading: '<h2><span class="h2-ico" aria-hidden="true">🎮</span>マイ公開ゲーム</h2><p class="dim">読み込み中……</p>',
        load: () => fetchPublishApi('GET', 'mine'),
        paint: paintMyGames,
    });
}

/** マイ公開ゲームを描いて操作を配線する（即時描画・再描画の 2 回呼ばれ得る）。 */
async function paintMyGames(r) {
    if (!r.ok) {
        app.innerHTML = `<h2><span class="h2-ico" aria-hidden="true">🎮</span>マイ公開ゲーム</h2>
        <div class="card"><p class="err">${esc(r.error ?? '一覧を取得できませんでした')}</p></div>`;
        return;
    }
    const rows = (r.games ?? []).map((g) => {
        const playable = g.visibility !== 'private' && g.moderation_status !== 'suspended';
        const url = `${PUBLISH_GAMES_ORIGIN}/${g.game_id}/`;
        const visOptions = ['private', 'unlisted', 'public'].map((v) => {
            const disabled = v === 'public' && g.moderation_status !== 'approved';
            return `<option value="${v}" ${g.visibility === v ? 'selected' : ''} ${disabled ? 'disabled' : ''}>${esc(VISIBILITY_LABELS[v])}${disabled ? '（要審査承認）' : ''}</option>`;
        }).join('');
        // 状態の矛盾を UI 側でも作らない（判定の正はサーバ側）:
        //   審査中 = 変更・削除を止める（審査対象とのズレ防止）／停止中 = 作成者操作を一切通さない
        //   非公開 = 掲載申請しても遊べないため申請させない
        const underReview = g.moderation_status === 'pending';
        const suspended = g.moderation_status === 'suspended';
        const isPrivate = g.visibility === 'private';
        const canRequest = ['unreviewed', 'rejected'].includes(g.moderation_status) && !isPrivate;
        const requestBlockedByPrivate = ['unreviewed', 'rejected'].includes(g.moderation_status) && isPrivate;
        const lockAttr = (underReview || suspended) ? ' disabled' : '';
        return `<div class="card" data-game="${esc(g.game_id)}">
        <h3>${esc(g.title_ja || g.game_id)}</h3>
        <table class="info-table"><tbody>
            ${infoRow('URL', playable ? `<a href="${esc(url)}" target="_blank" rel="noopener noreferrer">${esc(url)}</a>` : esc(url))}
            ${infoRow('公開状態', `<b>${esc(VISIBILITY_LABELS[g.visibility] ?? g.visibility)}</b>`)}
            ${infoRow('審査状態', esc(MODERATION_LABELS[g.moderation_status] ?? g.moderation_status))}
            ${infoRow('版（rev）', `${g.current_rev}`)}
            ${infoRow('容量', `${mb(g.bytes_total)}MB`)}
            ${g.moderation_note ? infoRow('審査コメント', esc(g.moderation_note)) : ''}
        </tbody></table>
        ${suspended ? '<div class="err">運営により停止されています。解除は運営が行います（作成者側からの公開範囲の変更・更新公開・削除はできません）。通報・審査の結果をご確認ください。</div>' : ''}
        ${underReview ? '<div class="warn">ギャラリー掲載の審査中です。審査対象のデータが変わらないよう、審査が終わるまで公開範囲の変更・更新公開・削除はできません。</div>' : ''}
        ${requestBlockedByPrivate ? '<div class="warn">非公開のままではギャラリー掲載を申請できません（掲載されても遊べないため）。先に「限定公開」へ変更してください。</div>' : ''}
        ${actionRowsHtml(
        // よく使う: 遊ぶ・URL コピー・申請まわり
        [playable ? `<a href="${esc(url)}" target="_blank" rel="noopener noreferrer"><button title="公開中のゲームを新しいタブで開いて実際に遊んで確認します">▶ 遊ぶ</button></a>` : '',
            '<button class="secondary mg-copy" title="公開URLをコピーします。SNS やメッセージで共有できます">URLをコピー</button>',
            suspended ? '<a href="/ja/legal/report/" target="_blank" rel="noopener noreferrer"><button class="secondary" title="通報・異議申立ての窓口（公開ポリシー）を新しいタブで開きます">問い合わせ・異議申立て</button></a>' : '',
            canRequest ? '<button class="secondary mg-request" title="ギャラリーへの掲載を運営へ申請します。承認されると一般公開（ギャラリー掲載）になります。確認ダイアログが表示されます">ギャラリー掲載を申請</button>' : '',
            underReview ? '<button class="secondary mg-cancel" title="審査（ギャラリー掲載の申請）を取り下げ、審査前の状態へ戻します。取り下げると公開範囲の変更・更新公開・削除が再びできます。確認ダイアログが表示されます">審査を取り下げる</button>' : ''],
        // たまにしか使わない: 公開範囲の切り替えと公開取り下げ
        [`<select class="mg-visibility"${lockAttr}>${visOptions}</select>`,
            `<button class="secondary mg-apply"${lockAttr} title="選んだ公開範囲へ切り替えます（一般公開は審査承認済みのゲームだけ選べます）。確認ダイアログが表示されます">公開状態を変更</button>`,
            `<button class="danger mg-delete"${lockAttr} title="公開を取り下げて公開URLを 404 にします（このブラウザ内の作成データは残るので、いつでも公開し直せます）。ゲーム内容を変更したい場合も、いったん取り下げてから「⑦ 限定公開」で公開し直します（審査は最初からやり直しになります）。確認ダイアログが表示されます">公開取り下げ</button>`],
        { style: 'margin-top:8px' },
    )}
        <div class="mg-msg" aria-live="polite"></div>
        </div>`;
    }).join('');
    app.innerHTML = `<h2><span class="h2-ico" aria-hidden="true">🎮</span>マイ公開ゲーム</h2>
    <div class="card">
        <h3>利用状況（${esc(r.quota?.plan ?? 'free')} プラン）</h3>
        ${quotaMeterHtml(r.quota)}
        <table class="info-table"><tbody>
            ${infoRow('使用中の容量', `${mb(r.quota?.bytesUsed)}MB / ${r.quota?.unlimited ? '無制限' : `${mbLimit(r.quota?.maxBytesTotal)}MB`}`)}
            ${infoRow('公開中のゲーム数', `${r.quota?.gamesUsed ?? 0} / ${r.quota?.unlimited ? '無制限' : `${r.quota?.maxGames ?? 1}件`}`)}
            ${infoRow('1ゲームの上限', r.quota?.unlimited ? '無制限' : `${mbLimit(r.quota?.maxBytesPerGame)}MB`)}
        </tbody></table>
    </div>
    ${rows || await noPublishedGamesHtml()}
`;
    for (const cardEl of document.querySelectorAll('[data-game]')) {
        const gameId = cardEl.dataset.game;
        const msg = cardEl.querySelector('.mg-msg');
        const showError = (m) => { msg.innerHTML = `<span class="err" role="alert">${esc(m)}</span>`; };
        const game = (r.games ?? []).find((g) => g.game_id === gameId);
        cardEl.querySelector('.mg-copy').onclick = (ev) => copyPublicUrl(`${PUBLISH_GAMES_ORIGIN}/${gameId}/`, ev.target);
        // 審査中でも「取り下げ」だけは使えるようにする（ロックから自分で抜けられる手段）
        const cancelBtn = cardEl.querySelector('.mg-cancel');
        if (cancelBtn) {
            cancelBtn.onclick = async () => {
                if (!confirm(`「${gameId}」の審査（ギャラリー掲載の申請）を取り下げますか？\n\n審査前の状態に戻り、公開範囲の変更・更新公開・削除が再びできるようになります。`)) return;
                const res = await fetchPublishApi('POST', 'cancel-listing', { gameId });
                if (!res.ok) return showError(res.error);
                renderMyGames();
            };
        }
        // 審査中・停止中は他の操作を配線しない（DOM の disabled と二重で守る）
        if (['pending', 'suspended'].includes(game?.moderation_status)) continue;
        // 現在と同じ公開範囲を選んでいる間は「公開状態を変更」を押せないようにする（無意味な操作を作らない）
        const visSelect = cardEl.querySelector('.mg-visibility');
        const applyBtn = cardEl.querySelector('.mg-apply');
        const syncApply = () => { applyBtn.disabled = visSelect.value === game?.visibility; };
        visSelect.onchange = syncApply;
        syncApply();
        cardEl.querySelector('.mg-apply').onclick = async () => {
            const visibility = cardEl.querySelector('.mg-visibility').value;
            if (!confirm(`「${gameId}」の公開状態を「${VISIBILITY_LABELS[visibility]}」へ変更しますか？`)) return;
            const res = await fetchPublishApi('POST', 'visibility', { gameId, visibility });
            if (!res.ok) return showError(res.error);
            renderMyGames();
        };
        const requestBtn = cardEl.querySelector('.mg-request');
        if (requestBtn) requestBtn.onclick = () => requestGalleryListing(gameId, msg, renderMyGames);
        cardEl.querySelector('.mg-delete').onclick = async () => {
            if (!confirm(`「${gameId}」の公開を取り下げますか？\n\n公開URLは直ちに 404 になり、公開したファイルは削除されます。\nこのブラウザ内の作成データは残るため、あとで同じ URL で公開し直せます（ギャラリー掲載の審査はやり直しになります）。`)) return;
            const res = await fetchPublishApi('POST', 'delete', { gameId });
            if (!res.ok) return showError(res.error);
            renderMyGames();
        };
    }
}

// ── 公開審査（管理者のみ・Phase D-1）────────────────────────────
// 管理者判定はサーバ（user_entitlements.plan='admin'）が正。画面の出し分けは補助にすぎない。

/** 審査 API（Netlify Functions）を Supabase セッションつきで呼ぶ。 */
async function fetchAdminApi(method, action, body) {
    const token = AUTH?.state?.session?.access_token ?? '';
    try {
        const res = await fetch(`/api/admin/${action}`, {
            method,
            headers: { authorization: `Bearer ${token}`, ...(body ? { 'content-type': 'application/json' } : {}) },
            body: body ? JSON.stringify(body) : undefined,
        });
        const json = await res.json().catch(() => null);
        if (!res.ok || json?.ok !== true) return { ok: false, error: json?.error ?? `HTTP ${res.status}`, status: res.status };
        // 審査の承認・却下・停止解除で一覧が変わる。記憶を捨てて次の描画で取り直す
        if (method !== 'GET') invalidateScreenResponseCache();
        return json;
    } catch (e) {
        return { ok: false, error: `サーバへ接続できません（${e?.message ?? e}）` };
    }
}

const PUBLISH_PLAN_STORE = 'vn-publish-plan';

// 公開中ゲーム（gameId → 行）の記憶。mine の応答を各画面で使い回すためのキャッシュで、
// 判定の正はサーバ側。表示の出し分け（「次の作業」ラベル等）にだけ使う。
const PUBLISHED_GAME_STATE = new Map();
// 一度でも mine を取得できたか（プロジェクト画面を直接開いたときの初回取得に使う）
let publishedGamesLoaded = false;

function rememberPublishedGames(games) {
    PUBLISHED_GAME_STATE.clear();
    for (const g of games ?? []) if (g?.game_id) PUBLISHED_GAME_STATE.set(g.game_id, g);
    publishedGamesLoaded = true;
}

/**
 * 公開中ゲームの一覧をまだ一度も取得していなければ取得する。
 * ホーム（renderDashboard）を経由せずプロジェクト画面を直接開いた場合でも
 * 「公開中かどうか」で操作を出し分けられるようにするための初回ロード。
 */
async function ensurePublishedGamesLoaded() {
    if (!AUTH_ENABLED || !AUTH?.isAuthenticated()) {
        PUBLISHED_GAME_STATE.clear();
        publishedGamesLoaded = false;
        return;
    }
    if (publishedGamesLoaded) return;
    await fetchPublishApi('GET', 'mine'); // 成功すれば rememberPublishedGames が記憶する
}

/** 公開中（限定公開・一般公開・公開後の一時停止のいずれか）なら公開情報を返す。未公開は null。 */
function publishedGameOf(gameId) {
    return gameId ? (PUBLISHED_GAME_STATE.get(gameId) ?? null) : null;
}

/**
 * 公開 API（mine）の応答で分かったプランを記憶する。
 * 審査メニューの出し分けはこの記憶だけで行い、**起動時に API を叩かない**
 * （Functions が動かないローカル dev:web で 404 を出さないため。判定の正はサーバ側）。
 */
function rememberPublishPlan(plan) {
    try {
        if (plan) localStorage.setItem(PUBLISH_PLAN_STORE, String(plan));
        else localStorage.removeItem(PUBLISH_PLAN_STORE);
    } catch { /* 保存できなくても表示が変わるだけ */ }
    applyAdminNav();
}

/** 記憶しているプランに合わせて審査メニューの表示を更新する（通信はしない）。 */
function applyAdminNav() {
    const btn = document.getElementById('sidebar-admin');
    if (!btn) return;
    let plan = null;
    try { plan = localStorage.getItem(PUBLISH_PLAN_STORE); } catch { /* 取得できなければ非表示のまま */ }
    btn.hidden = !(AUTH_ENABLED && AUTH.isAuthenticated() && plan === 'admin');
    if (!btn.hidden && !btn.dataset.authNav) {
        btn.dataset.authNav = '1';
        btn.addEventListener('click', () => {
            location.hash = '#/admin';
            closeSidebarAfterNavigation();
        });
    }
}

function adminGameCardHtml(g, gamesOrigin) {
    const url = `${gamesOrigin}/${g.game_id}/`;
    return `<div class="card" data-admin-game="${esc(g.game_id)}">
        <h3>${esc(g.title_ja || g.game_id)}</h3>
        <table class="info-table"><tbody>
            ${infoRow('ゲームID', esc(g.game_id))}
            ${infoRow('URL', `<a href="${esc(url)}" target="_blank" rel="noopener noreferrer">${esc(url)}</a>`)}
            ${infoRow('公開状態', `<b>${esc(VISIBILITY_LABELS[g.visibility] ?? g.visibility)}</b>`)}
            ${infoRow('審査状態', esc(MODERATION_LABELS[g.moderation_status] ?? g.moderation_status))}
            ${infoRow('版（rev）', `${g.current_rev}`)}
            ${infoRow('容量', `${mb(g.bytes_total)}MB（${g.file_count}ファイル）`)}
            ${Number.isInteger(g.featured_rank) ? infoRow('現在の特集順位', `${g.featured_rank}`) : ''}
            ${g.description_ja ? infoRow('紹介文', esc(g.description_ja.slice(0, 300))) : ''}
            ${g.moderation_note ? infoRow('前回の審査コメント', esc(g.moderation_note)) : ''}
        </tbody></table>
        <div class="actions">
            <a href="${esc(url)}" target="_blank" rel="noopener noreferrer"><button class="secondary" title="公開ページを新しいタブで開き、起動・表示・内容を確認します">▶ ゲームを確認</button></a>
        </div>
        <table class="info-table"><tbody>
            <tr><th>審査コメント</th><td>
                <textarea class="adm-note" rows="2" maxlength="2000"></textarea>
                <small>却下・停止では必須。作成者の画面に表示されます</small></td></tr>
            <tr><th>特集順位</th><td class="cell-inline">
                <input type="number" class="adm-rank" min="1" max="999" style="width:90px" value="${Number.isInteger(g.featured_rank) ? g.featured_rank : ''}">
                <small>空欄で解除</small></td></tr>
        </tbody></table>
        ${actionRowsHtml(
        // よく使う: 審査の判定（承認・却下・差し戻し）
        ['<button class="adm-act" data-status="approved" title="掲載申請中のゲームをそのまま一般公開（ギャラリー掲載）にします。確認ダイアログが表示されます">承認する</button>',
            '<button class="secondary adm-act" data-status="rejected" title="却下して作成者へ差し戻します。審査コメントが作成者に表示されます。確認ダイアログが表示されます">却下する</button>',
            '<button class="secondary adm-act" data-status="unreviewed" title="審査前（未申請）の状態へ戻します。作成者が再申請できるようになります。確認ダイアログが表示されます">審査前に戻す</button>'],
        // たまにしか使わない: 規約違反・通報対応の公開停止
        '<button class="danger adm-act" data-status="suspended" title="規約違反・通報対応として公開を停止します。プレイもギャラリー掲載も止まります。確認ダイアログが表示されます">公開を停止する</button>',
    )}
        <div class="adm-msg" aria-live="polite"></div>
    </div>`;
}

async function renderAdminReview() {
    if (!AUTH_ENABLED) {
        app.innerHTML = '<div class="card"><p class="dim">公開機能はこの環境では利用できません。</p></div>';
        return;
    }
    const heading = '<h2><span class="h2-ico" aria-hidden="true">🛡️</span>公開審査</h2>';
    await renderWithCachedResponse({
        key: 'admin:queue',
        loading: `${heading}<p class="dim">読み込み中……</p>`,
        load: () => fetchAdminApi('GET', 'queue'),
        paint: paintAdminReview,
    });
}

/** 公開審査を描いて操作を配線する（即時描画・再描画の 2 回呼ばれ得る）。 */
function paintAdminReview(r) {
    const heading = '<h2><span class="h2-ico" aria-hidden="true">🛡️</span>公開審査</h2>';
    if (!r.ok) {
        app.innerHTML = `${heading}<div class="card"><p class="err">${esc(r.error ?? '審査キューを取得できませんでした')}</p>
        ${r.status === 403 ? '<p class="dim">この画面は管理者アカウントでのみ利用できます。</p>' : ''}
        <div class="actions"><button class="secondary" onclick="location.hash='#/'" title="ホーム画面へ戻ります">ホームへ戻る</button></div></div>`;
        return;
    }
    const gamesOrigin = r.gamesOrigin ?? PUBLISH_GAMES_ORIGIN;
    const counts = r.counts ?? {};
    app.innerHTML = `${heading}
    <div class="card">
        <h3>審査キュー</h3>
        <table class="info-table"><tbody>
            ${infoRow('申請中', `${counts.pending ?? 0}件`)}
            ${infoRow('承認済み', `${counts.approved ?? 0}件`)}
            ${infoRow('未申請', `${counts.unreviewed ?? 0}件`)}
            ${infoRow('要修正', `${counts.rejected ?? 0}件`)}
            ${infoRow('運営停止中', `${counts.suspended ?? 0}件`)}
            ${infoRow('公開ゲーム総数', `${r.total ?? 0}件`)}
        </tbody></table>
        ${r.truncated ? '<div class="warn">件数が上限に達したため一部のみ表示しています。</div>' : ''}
        <div class="actions">
            <button class="secondary" id="adm-refresh-index" title="ギャラリーの掲載一覧（index/home.json・gallery.json）を台帳から作り直します">ギャラリーを再生成</button>
            <a href="${esc(gamesOrigin)}/gallery/" target="_blank" rel="noopener noreferrer"><button class="secondary" title="公開中のギャラリーを新しいタブで開きます">ギャラリーを開く</button></a>
            <button class="secondary" id="adm-history-btn" title="過去に審査したゲームの履歴（いつ・どのゲームを・どの状態にしたか）を表示します">審査履歴を表示</button>
        </div>
        <div id="adm-index-msg" aria-live="polite"></div>
        <div id="adm-history" hidden></div>
    </div>
    <h3 style="margin:18px 0 8px">申請中（${r.pending.length}件）</h3>
    ${r.pending.length === 0 ? '<div class="card"><p class="dim">審査待ちの申請はありません。</p></div>'
        : r.pending.map((g) => adminGameCardHtml(g, gamesOrigin)).join('')}`;

    document.getElementById('adm-refresh-index').onclick = async (ev) => {
        const msg = document.getElementById('adm-index-msg');
        ev.target.disabled = true;
        const res = await fetchAdminApi('POST', 'refresh-index', {});
        ev.target.disabled = false;
        msg.innerHTML = res.ok
            ? `<span class="ok" role="status">ギャラリーを再生成しました（掲載 ${res.count}件）。</span>`
            : `<span class="err" role="alert">${esc(res.error ?? '再生成に失敗しました')}</span>`;
    };
    document.getElementById('adm-history-btn').onclick = (ev) => showAdminHistory(ev.target);

    for (const cardEl of document.querySelectorAll('[data-admin-game]')) {
        const gameId = cardEl.dataset.adminGame;
        const msg = cardEl.querySelector('.adm-msg');
        for (const btn of cardEl.querySelectorAll('.adm-act')) {
            btn.onclick = async () => {
                const moderationStatus = btn.dataset.status;
                const label = MODERATION_LABELS[moderationStatus] ?? moderationStatus;
                const note = cardEl.querySelector('.adm-note').value.trim();
                if (['rejected', 'suspended'].includes(moderationStatus) && note === '') {
                    msg.innerHTML = '<span class="err" role="alert">却下・停止には理由（審査コメント）が必要です。</span>';
                    return;
                }
                if (!confirm(`「${gameId}」の審査状態を「${label}」にしますか？`)) return;
                const rankRaw = cardEl.querySelector('.adm-rank').value.trim();
                const res = await fetchAdminApi('POST', 'moderate', {
                    gameId,
                    moderationStatus,
                    moderationNote: note,
                    featuredRank: rankRaw === '' ? null : Number(rankRaw),
                });
                if (!res.ok) {
                    msg.innerHTML = `<span class="err" role="alert">${esc(res.error ?? '審査結果を反映できませんでした')}</span>`;
                    return;
                }
                renderAdminReview();
            };
        }
    }
}

/** 審査履歴（publish_log の審査操作）を必要なときだけ表示する。 */
async function showAdminHistory(btn) {
    const panel = document.getElementById('adm-history');
    if (!panel) return;
    if (!panel.hidden) { panel.hidden = true; btn.textContent = '審査履歴を表示'; return; }
    panel.hidden = false;
    panel.innerHTML = '<p class="dim">読み込み中……</p>';
    btn.textContent = '審査履歴を閉じる';
    const res = await fetchAdminApi('GET', 'history');
    if (!res.ok) {
        panel.innerHTML = `<span class="err" role="alert">${esc(res.error ?? '審査履歴を取得できませんでした')}</span>`;
        return;
    }
    if ((res.history ?? []).length === 0) {
        panel.innerHTML = '<p class="dim">審査の履歴はまだありません。</p>';
        return;
    }
    panel.innerHTML = `<table class="info-table history-table"><thead>
        <tr><th>日時</th><th>ゲーム</th><th>変更</th><th>特集</th><th>審査コメント</th></tr></thead><tbody>
        ${res.history.map((h) => `<tr>
            <td>${esc(String(h.at ?? '').replace('T', ' ').slice(0, 16))}</td>
            <td>${esc(h.game_id ?? '')}</td>
            <td>${esc(MODERATION_LABELS[h.detail?.from] ?? h.detail?.from ?? '')} → <b>${esc(MODERATION_LABELS[h.detail?.to] ?? h.detail?.to ?? '')}</b></td>
            <td>${Number.isInteger(h.detail?.featuredRank) ? h.detail.featuredRank : '—'}</td>
            <td>${esc(h.detail?.note ?? '')}</td>
        </tr>`).join('')}
    </tbody></table>`;
}

let sidebarSetOpen = () => {};

function isDesktopSidebar() {
    return window.matchMedia('(min-width: 769px)').matches;
}

function closeSidebarAfterNavigation() {
    if (!isDesktopSidebar()) sidebarSetOpen(false);
}

function syncSidebarActive(hash = location.hash || '#/') {
    for (const btn of document.querySelectorAll('.tool-sidebar button[data-nav]')) {
        const target = btn.dataset.nav;
        const active = target === '#/' ? hash === '#/' : hash.startsWith(target);
        btn.classList.toggle('is-active', active);
        if (active) btn.setAttribute('aria-current', 'page');
        else btn.removeAttribute('aria-current');
    }
}

/** 公式サイトと同じ PC 固定／モバイルオーバーレイ型サイドバーを初期化する。 */
function setupSidebar() {
    const toggle = document.getElementById('sidebar-toggle');
    const close = document.getElementById('sidebar-close');
    const backdrop = document.getElementById('sidebar-backdrop');
    if (!toggle || !close || !backdrop) return;
    const desktop = window.matchMedia('(min-width: 769px)');
    const isOpen = () => desktop.matches
        ? !document.body.classList.contains('sidebar-closed')
        : document.body.classList.contains('sidebar-open');
    const setOpen = (open) => {
        if (desktop.matches) {
            document.body.classList.toggle('sidebar-closed', !open);
            document.body.classList.remove('sidebar-open');
        } else {
            document.body.classList.toggle('sidebar-open', open);
            document.body.classList.remove('sidebar-closed');
        }
        toggle.setAttribute('aria-expanded', String(open));
        backdrop.setAttribute('aria-hidden', String(!open));
    };
    sidebarSetOpen = setOpen;
    toggle.addEventListener('click', () => setOpen(!isOpen()));
    close.addEventListener('click', () => { setOpen(false); toggle.focus(); });
    backdrop.addEventListener('click', () => { setOpen(false); toggle.focus(); });
    document.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape' && isOpen()) {
            setOpen(false);
            toggle.focus();
        }
    });
    desktop.addEventListener('change', () => setOpen(desktop.matches));
    setOpen(desktop.matches);
    syncSidebarActive();
}

/** ヘッダ右上とサイドバーのアカウントボタン（ログイン状態に追随）。 */
function updateAuthNav() {
    if (!AUTH_ENABLED) return;
    const nav = document.querySelector('.tool-header .header-actions');
    if (!nav) return;
    let btn = document.getElementById('nav-account');
    if (!btn) {
        btn = document.createElement('button');
        btn.id = 'nav-account';
        nav.appendChild(btn);
    }
    const sidebarBtn = document.getElementById('sidebar-account');
    if (sidebarBtn) sidebarBtn.hidden = false;
    for (const authBtn of [btn, sidebarBtn].filter(Boolean)) {
        if (!authBtn.dataset.authNav) {
            authBtn.dataset.authNav = '1';
            authBtn.addEventListener('click', () => {
                location.hash = AUTH.isAuthenticated() ? '#/account' : '#/login';
                closeSidebarAfterNavigation();
            });
        }
    }
    // マイ公開ゲーム（⑦ 限定公開で公開したゲームの管理画面）はログイン中のみ表示する
    const myGamesBtn = document.getElementById('sidebar-mygames');
    if (myGamesBtn) {
        myGamesBtn.hidden = !AUTH.isAuthenticated();
        if (!myGamesBtn.dataset.authNav) {
            myGamesBtn.dataset.authNav = '1';
            myGamesBtn.addEventListener('click', () => {
                location.hash = '#/mygames';
                closeSidebarAfterNavigation();
            });
        }
    }
    // 公開審査は管理者プランのときだけ表示する（記憶済みプランに追随。通信はしない）
    applyAdminNav();
    const name = AUTH.state.profile?.display_name || AUTH.state.user?.email || '';
    // ヘッダ統一レイアウト（1行目アイコン・2行目タイトル）。保存済みアバターがあれば
    // 人物アイコンと同じ大きさの丸型画像にし、URL未設定・読込失敗時は人物アイコンへ戻す。
    // 未ログインは textContent のみにして共通のアイコン自動付与（login アイコン）に任せる。
    const renderButton = (target) => {
        if (!target) return;
        if (AUTH.isAuthenticated()) {
            const avatarUrl = AUTH.state.profile?.avatar_url;
            const avatarHtml = typeof avatarUrl === 'string' && /^https:\/\//.test(avatarUrl)
                ? `<img class="nav-account-avatar" src="${esc(avatarUrl)}" alt="" aria-hidden="true" referrerpolicy="no-referrer" decoding="async">`
                : '<span class="nav-ico" aria-hidden="true">👤</span>';
            target.innerHTML = `${avatarHtml}<span>${esc(name)}</span>`;
            const avatar = target.querySelector('.nav-account-avatar');
            if (avatar) {
                avatar.onerror = () => {
                    const fallback = document.createElement('span');
                    fallback.className = 'nav-ico';
                    fallback.setAttribute('aria-hidden', 'true');
                    fallback.textContent = '👤';
                    avatar.replaceWith(fallback);
                };
            }
        } else {
            target.textContent = 'ログイン';
        }
        target.title = AUTH.isAuthenticated() ? 'アカウント設定（プロフィール・パスワード・ログアウト）を開きます' : 'ログイン画面を開きます';
        delete target.dataset.ico; // ラベルが変わったのでアイコンを付け直す（MutationObserver が再適用）
    };
    renderButton(btn);
    renderButton(sidebarBtn);
}

function route() {
    const hash = location.hash || '#/';
    // 実行中はブラウザ戻る/進む等での画面切替を無効化（元の画面へ戻す。クリックはオーバーレイが遮断済み）。
    // 差し戻しで hash が一致した 2 回目の hashchange でも再描画しない
    //（再描画するとジョブログの表示先 DOM が差し替わり、実行中のログが画面から消えるため）
    if (busyCount > 0) {
        if (busyHash && hash !== busyHash) location.hash = busyHash;
        return;
    }
    syncSidebarActive(hash);
    // ── 認証ガード（Web 版のみ）──────────────────────
    // セッション復元は tool-bundle 起動時に完了済みのため、ここでの判定にちらつきは無い。
    if (AUTH_ENABLED) {
        // パスワード再設定リンク経由のセッションは、他画面より先に再設定画面へ誘導する
        if (AUTH.state.passwordRecovery && !hash.startsWith('#/reset-password')) {
            location.hash = '#/reset-password';
            return;
        }
        if (!AUTH.isAuthenticated() && !AUTH.isAuthRoute(hash)) {
            // 元の遷移先を保持してログインへ（復帰先はツール内ハッシュのみ許可）
            sessionStorage.setItem(AUTH_RETURN_STORE, AUTH.sanitizeReturnHash(hash));
            location.hash = '#/login'; // hash は必ず非ログイン系なので hashchange → route が再実行される
            return;
        }
        if (AUTH.isAuthenticated() && (hash.startsWith('#/login') || hash.startsWith('#/signup'))) {
            const back = AUTH.sanitizeReturnHash(sessionStorage.getItem(AUTH_RETURN_STORE) ?? '#/');
            sessionStorage.removeItem(AUTH_RETURN_STORE);
            location.hash = back; // sanitizeReturnHash はログイン系を返さないため必ず遷移する
            return;
        }
    }
    if (hash.startsWith('#/login')) return renderLogin();
    if (hash.startsWith('#/signup')) return renderSignup();
    if (hash.startsWith('#/forgot-password')) return renderForgotPassword();
    if (hash.startsWith('#/reset-password')) return renderResetPassword();
    if (hash.startsWith('#/auth/callback')) return renderAuthCallback();
    if (hash.startsWith('#/account')) return renderAccount();
    if (hash.startsWith('#/mygames')) return renderMyGames();
    if (hash.startsWith('#/admin')) return renderAdminReview();
    const m = hash.match(/^#\/game\/([a-z0-9_-]+)(?:\/([a-z-]+))?/);
    if (m) return renderGame(m[1], m[2] ?? 'validate');
    const pm = hash.match(/^#\/project\/([a-z0-9-]+)(?:\/([a-z-]+))?/);
    if (pm) return renderProject(pm[1], pm[2] ?? 'analyze');
    if (hash.startsWith('#/settings')) return renderSettings();
    if (hash.startsWith('#/story-new')) return renderStoryNew();
    if (hash.startsWith('#/new')) return renderNewWizard();
    if (hash.startsWith('#/prompt')) return renderPrompt();
    return renderDashboard();
}
window.addEventListener('hashchange', route);

// 認証状態の変化に追随: ヘッダ表示を更新し、ログアウト・セッション失効時は保護画面から退避する。
// （実行中ジョブがある間は route() 側の busy ガードが画面を維持する）
if (AUTH_ENABLED) {
    let wasAuthenticated = AUTH.isAuthenticated();
    AUTH.onChange(() => {
        updateAuthNav();
        const nowAuthenticated = AUTH.isAuthenticated();
        // 次回ログイン画面の初期表示用にメールアドレスを記憶（パスワードは保存しない）
        if (nowAuthenticated && AUTH.state.user?.email) {
            localStorage.setItem(AUTH_LAST_EMAIL_STORE, AUTH.state.user.email);
        }
        if (wasAuthenticated && !nowAuthenticated) {
            rememberPublishPlan(null); // 別ユーザーへ切り替わっても審査メニューが残らないようにする
            PUBLISHED_GAME_STATE.clear(); // 前のユーザーの公開状態を次のユーザーの表示に使わない
            publishedGamesLoaded = false; // 次のユーザーでは取り直す
            invalidateScreenResponseCache(); // 前のユーザーの一覧を次のユーザーへ見せない
            // 明示ログアウト以外（トークン失効等）でもログイン画面へ。理由はログイン画面で表示
            //（明示ログアウト時は「セッション切れ」ではなく「ログアウトしました」を出す）
            if (!sessionStorage.getItem(AUTH_SIGNEDOUT_STORE)) sessionStorage.setItem(AUTH_EXPIRED_STORE, '1');
            if (!AUTH.isAuthRoute(location.hash)) location.hash = '#/login';
        } else if (!wasAuthenticated && nowAuthenticated && AUTH.isAuthRoute(location.hash)) {
            route(); // ログイン完了 → ガードが元ページへ戻す
        }
        wasAuthenticated = nowAuthenticated;
    });
    updateAuthNav();
}

// ヘッダとサイドバーは同じ遷移処理を共有する。
for (const btn of document.querySelectorAll('button[data-nav]')) {
    btn.addEventListener('click', () => {
        location.hash = btn.dataset.nav;
        closeSidebarAfterNavigation();
    });
}

// ── 全ボタンへのツールチップ自動付与（2026-07-07 UI 改善）─────────
// ラベル→説明の対応表。動的ラベル（「再解析を実行（…）」等）は前方一致で解決する。
// 新しいボタンを追加したらここへ 1 行足す（テンプレート側に title を書いた場合はそちらが優先）。
const BUTTON_TIPS = [
    ['📖 物語から新規作成', '小説などのテキストを登録し、解析→ゲーム生成→画像→3D→JSON 取り込みまで自動で進める新規プロジェクトを作成します'],
    ['＋ 新規ゲーム作成（手動）', 'ゲーム ID やタイトルを自分で入力して、空のゲーム環境（スキャフォールド）を作成します'],
    ['削除', 'このゲーム環境（newgames/ 配下のフォルダ一式）を削除します。実行前に確認ダイアログが表示されます'],
    ['プロジェクトを作成して解析へ', '入力した物語テキストでプロジェクトを作成し、① 解析画面へ進みます'],
    ['解析を実行', '物語テキストを LLM で解析し、ゲーム企画（タイトル・キャスト・ワールド案）を抽出します（OpenRouter に実費が発生）'],
    ['再解析を実行', '物語テキストを再度 LLM 解析し、現在の解析結果（analysis.json）を上書きします（OpenRouter に実費が発生）'],
    ['ファイルの読み込み', '解析元の物語文書を別のテキストファイルへ差し替え、解析以降の工程を初期化します'],
    ['解析結果を保存', '画面上で編集した解析結果（タイトル・キャスト等）を analysis.json に保存します'],
    ['① 解析（ドライラン）', 'ゲーム生成で作られるファイル一覧を実行せずに確認します（ファイルは変更されません）'],
    ['② 実行', 'スキャフォールドを実行し、newgames/ 配下にゲーム環境一式を生成します'],
    ['① ドライラン（実行内容の確認）', '実行内容（作成されるファイル一覧）を事前確認します（ファイルは変更されません）'],
    ['① ドライラン', '実行内容を事前確認します（ファイルは変更されません）'],
    ['プロンプトを生成', '全キャストの画像生成用プロンプト（A字型/シーンポーズ）を LLM で作成します（OpenRouter に実費が発生）'],
    ['全GLBの再生成', '全キャスト分の GLB 一式を Meshy でまとめて再生成します（画像を変更したキャストのみ実費・変更なしはキャッシュ再利用で追加消費 0P）'],
    ['プロンプト再生成', 'このキャストの画像プロンプトを LLM で作り直します（既存は上書き・実費が発生）'],
    ['生成ログ表示', 'このキャストの画像生成・GLB生成の保存済みログ履歴を表示します'],
    ['画像再生成', 'このキャストの画像をプロンプトから再生成します（実費が発生）'],
    ['GLB再生成', 'このキャストに必要な GLB 一式（プレイアブル3点・敵2点・NPC1点）を Meshy で再生成します（クレジット消費・数分〜30分）'],
    ['英語プロンプトをコピー', 'この英語プロンプトをクリップボードへコピーします（外部の画像生成 AI に貼り付けて使えます）'],
    ['画像を自動生成', 'このキャストの画像を OpenRouter（FLUX）で自動生成します（1枚 ≒$0.014 の実費が発生）'],
    ['全画像の再生成', '全キャスト分の画像をまとめて再生成します（生成済みの画像も作り直し・枚数 × ≒$0.014 の実費が発生）'],
    ['🚀 未作成を一括実行', '未完了の工程（① 解析〜⑥ 取り込み）を順番に自動実行します。開始前に工程一覧と実費の目安を確認でき、ESC キーで中断できます'],
    ['▶ プレビューを開く', 'ブラウザ内の作業データでゲームを起動します（新しいタブ）'],
    ['画面を更新', 'ステップ表示を最新の状態に更新します'],
    ['再読み込み', 'ページを再読み込みします'],
    ['画像変更', '手元の画像ファイルをアップロードして、このキャストの画像を差し替えます'],
    ['GLB変更', '待機・移動・バトル等の GLB ファイルを種類ごとに個別変更します'],
    ['このGLBを変更', 'この種類に選択した GLB ファイルを上書き保存します'],
    ['再生成', 'この対象を LLM でもう一度生成し直します（実費が発生）'],
    ['3Dキャラを自動生成', 'キャスト画像から Meshy API で 3D モデル（GLB）を自動生成して推奨配置先へ保存します（Meshy クレジット消費・数分〜30分）'],
    ['プレビュー', 'この GLB モデルを 3D ビューアで表示します（ドラッグで回転・ホイールで拡大縮小）'],
    ['ワールド設計を実行', '解析結果と全テンプレート・ブロック・地形・天候仕様から、物語の縮尺・自然・生活環境・生息地を LLM で設計します（OpenRouter に実費が発生）'],
    ['再設計を実行', 'ワールド設計をやり直して上書きします（OpenRouter に実費が発生）'],
    ['設計を保存', '画面上で編集したワールド設計を保存します'],
    ['依頼文を生成', 'ゲームデータ作成を AI に依頼するための依頼文（Markdown）を生成します'],
    ['依頼文を再生成', '依頼文を作り直します（画面での編集内容は破棄されます）'],
    ['編集内容を保存', '画面上で編集した内容を保存します'],
    ['選択ユニットを生成', 'チェックしたユニット（マップ・NPC 等の JSON）を LLM で生成します（OpenRouter に実費が発生）'],
    ['取り込み実行', '差分・スキーマの事前確認後、F2配置、i18nマージ、treasures生成、整合・翻訳・アセット検証まで連続実行します'],
    ['② 配置を実行', '生成された内容をゲームデータへ配置します'],
    ['すべて実行（schema + 整合 + 翻訳 + アセット）', '4種類の検証（JSON スキーマ・データ整合・翻訳の欠落・アセット実在）をまとめて実行します'],
    ['validate:maps 実行', 'マップ JSON のスキーマ検証を実行します'],
    ['整合チェック実行', 'ID 参照や座標などのデータ整合チェックを実行します'],
    ['generate:treasures 実行', '宝箱データの自動生成スクリプトを実行します'],
    ['dataCache.version 更新', 'ブラウザキャッシュを更新させるためにデータキャッシュのバージョン番号を上げます（maps JSON 変更後に必須）'],
    ['npm ci', 'ゲームの依存パッケージをクリーンインストールします（初回起動前に必要・数分かかります）'],
    ['本番ビルド実行', 'ゲームの本番用ビルド（vite build）を実行します'],
    ['起動', 'このゲームのローカル開発サーバを起動します（ブラウザで動作確認できます）'],
    ['停止', 'ローカル開発サーバを停止します'],
    ['ローカル assets/ を確認', 'ゲームが参照するアセット（GLB・画像）がローカルに存在するか確認します'],
    ['本番 CDN も確認（HEAD）', '本番 CDN（R2）にアセットが配信済みか HEAD リクエストで確認します'],
    ['公開可否を確認', '現行の共有ランタイム方式で公開可能か、必要な検証と未完了項目を確認します'],
    ['ゲーム詳細へ', 'このゲームの管理画面（検証・生成・起動・3D モデル）を開きます'],
    ['プロンプト表示', 'この対象のプロンプト全文を表示します'],
    ['コピー', 'この内容をクリップボードへコピーします'],
    ['保存', '入力したキーをこのブラウザの localStorage に保存します'],
    ['接続テスト', '保存済みのキーで実際に API へ接続し、残高や疎通状態を確認します'],
    ['📥 MD ファイルをダウンロード', '依頼文と作品要約シートを1つのMarkdownファイルとして保存します（ChatGPT / Gemini アプリへそのままアップロードできます）'],
    ['ゲームシナリオ文書作成', '読み込んだ小説全文を LLM で読み、ゲーム生成に必要な情報（開始地点・登場人物・敵・イベント等）をまとめたゲームシナリオ文書を作成します（OpenRouter に実費が発生）'],
    ['実行', 'この処理を実行します'],
];
function applyButtonTips(rootEl) {
    for (const btn of rootEl.querySelectorAll('button:not([title])')) {
        const label = btn.textContent.trim();
        if (!label) continue;
        const hit = BUTTON_TIPS.find(([k]) => label === k) ?? BUTTON_TIPS.find(([k]) => label.startsWith(k));
        if (hit) btn.title = hit[1];
    }
}

// ── 全ボタンの「アイコン＋ボタン名」統一 ─────────────────────
// Google Material Symbols 相当の自作インライン SVG（CDN 不使用・オフライン動作）。
// ラベル前方一致（ja/en 両対応・上から先勝ち）でアイコンを自動付与する。
// 絵文字（📖🚀▶＋ 等）や SVG（目アイコン・SNS ロゴ）を既に持つボタンはそのまま。
const ico = (d) => `<svg class="btn-ico" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${d}"/></svg>`;
const BTN_ICONS = {
    play: ico('M8 5v14l11-7z'),
    stop: ico('M6 6h12v12H6z'),
    plus: ico('M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6z'),
    refresh: ico('M17.65 6.35A8 8 0 1 0 20 12h-2a6 6 0 1 1-1.76-4.24L13 11h7V4z'),
    save: ico('M17 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7zm-5 16a3 3 0 1 1 3-3 3 3 0 0 1-3 3zm3-10H5V5h10z'),
    trash: ico('M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6zm3-9h2v8H9zm4 0h2v8h-2zM15.5 4l-1-1h-5l-1 1H5v2h14V4z'),
    copy: ico('M16 1H4a2 2 0 0 0-2 2v12h2V3h12zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h11z'),
    check: ico('M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z'),
    clipboard: ico('M19 3h-4.2a2.8 2.8 0 0 0-5.6 0H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm-7 0a1 1 0 1 1-1 1 1 1 0 0 1 1-1zm7 16H5V5h2v3h10V5h2z'),
    import: ico('M19 9h-4V3H9v6H5l7 7zM5 18v2h14v-2z'),
    upload: ico('M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z'),
    mail: ico('M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4-8 5-8-5V6l8 5 8-5z'),
    key: ico('M12.65 10a6 6 0 1 0 0 4H15v4h4v-4h2v-4zM7 14a2 2 0 1 1 2-2 2 2 0 0 1-2 2z'),
    bolt: ico('M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66L13 3h1l-1 7h3.5c.49 0 .56.33.47.51z'),
    gear: ico('M19.14 12.94a7 7 0 0 0 0-1.88l2.03-1.58-2-3.46-2.39.96a7 7 0 0 0-1.62-.94L14.8 3.5h-4l-.36 2.54a7 7 0 0 0-1.62.94l-2.39-.96-2 3.46 2.03 1.58a7 7 0 0 0 0 1.88L4.43 14.5l2 3.46 2.39-.96a7 7 0 0 0 1.62.94l.36 2.56h4l.36-2.56a7 7 0 0 0 1.62-.94l2.39.96 2-3.46zM12.8 15.5a3.5 3.5 0 1 1 3.5-3.5 3.5 3.5 0 0 1-3.5 3.5z'),
    grid: ico('M3 3h8v8H3zm10 0h8v8h-8zM3 13h8v8H3zm10 0h8v8h-8z'),
    home: ico('M12 3l9 8h-3v9h-5v-6h-2v6H6v-9H3z'),
    doc: ico('M6 2h8l6 6v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm7 7h5.5L13 3.5z'),
    globe: ico('M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm7 9h-3a15 15 0 0 0-1-5 8 8 0 0 1 4 5zM12 4a13 13 0 0 1 1.5 7h-3A13 13 0 0 1 12 4zM5 13h3a15 15 0 0 0 1 5 8 8 0 0 1-4-5zm3-2H5a8 8 0 0 1 4-5 15 15 0 0 0-1 5zm4 9a13 13 0 0 1-1.5-7h3A13 13 0 0 1 12 20zm3-2a15 15 0 0 0 1-5h3a8 8 0 0 1-4 5z'),
    image: ico('M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zM8.5 8A1.5 1.5 0 1 1 7 9.5 1.5 1.5 0 0 1 8.5 8zM5 19l3.5-4.5 2.5 3 3.5-4.5L19 19z'),
    cube: ico('M12 2 3 7v10l9 5 9-5V7zm0 2.3L18.6 8 12 11.7 5.4 8zM5 9.7l6 3.4v6.2l-6-3.3zm8 9.6v-6.2l6-3.4v6.3z'),
    sparkle: ico('M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8zM19 14l.9 2.6 2.6.9-2.6.9L19 21l-.9-2.6-2.6-.9 2.6-.9z'),
    person: ico('M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm0 2c-3.3 0-8 1.7-8 5v3h16v-3c0-3.3-4.7-5-8-5z'),
    login: ico('M11 7l-1.4 1.4L12.2 11H3v2h9.2l-2.6 2.6L11 17l5-5zm9-4h-8v2h8v14h-8v2h8a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z'),
    logout: ico('M13 7l1.4 1.4L11.8 11H21v2h-9.2l2.6 2.6L13 17l-5-5zM4 5h8V3H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8v-2H4z'),
    arrowR: ico('M10 6l-1.4 1.4L13.2 12l-4.6 4.6L10 18l6-6z'),
    arrowL: ico('M14 6l1.4 1.4L10.8 12l4.6 4.6L14 18l-6-6z'),
    search: ico('M15.5 14h-.8l-.3-.3a6.5 6.5 0 1 0-.7.7l.3.3v.8l5 5 1.5-1.5zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14z'),
    hammer: ico('M13.7 3.3l7 7-2.4 2.4-7-7zM3 17.3 11.3 9l3.7 3.7L6.7 21H3z'),
    eye: ico('M12 5C7 5 2.7 8.1 1 12c1.7 3.9 6 7 11 7s9.3-3.1 11-7c-1.7-3.9-6-7-11-7zm0 11.5A4.5 4.5 0 1 1 16.5 12 4.5 4.5 0 0 1 12 16.5zm0-7A2.5 2.5 0 1 0 14.5 12 2.5 2.5 0 0 0 12 9.5z'),
    cloud: ico('M19.4 10a7.5 7.5 0 0 0-14-2A6 6 0 0 0 6 20h13a5 5 0 0 0 .4-10z'),
    info: ico('M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 15h-2v-6h2zm0-8h-2V7h2z'),
};
// ラベル → アイコン（前方一致・上から先勝ち。en 表記は i18n 辞書から自動展開）
const BUTTON_ICON_RULES = [
    ['ログインへ戻る', 'arrowL'], ['ログアウト', 'logout'], ['ログイン', 'login'],
    ['新規ユーザー登録', 'person'], ['登録する', 'person'], ['キャラ設定', 'person'],
    ['パスワードを更新', 'key'], ['再設定メールを送信', 'mail'], ['パスワード再設定メールを送信', 'mail'],
    ['プロフィールを保存', 'save'], ['解析結果を保存', 'save'], ['設計を保存', 'save'], ['編集内容を保存', 'save'],
    ['キーを削除', 'trash'], ['削除', 'trash'],
    ['接続テスト', 'bolt'],
    ['API設定を開く', 'gear'], ['API設定', 'gear'],
    ['ホーム', 'home'], ['ダッシュボード', 'grid'],
    ['外部AI依頼', 'doc'], ['AI 依頼文', 'doc'], ['依頼文を再生成', 'refresh'], ['依頼文を生成', 'doc'], ['プロンプト表示', 'doc'], ['生成ログ表示', 'doc'], ['使い方', 'info'],
    ['ファイルの読み込み', 'upload'], ['再解析を実行', 'refresh'], ['解析を実行', 'search'], ['解析（ドライラン）', 'clipboard'], ['解析', 'search'],
    ['全GLBの再生成', 'cube'], ['再設計を実行', 'refresh'], ['再生成', 'refresh'],
    ['画面を更新', 'refresh'], ['再読み込み', 'refresh'], ['dataCache.version 更新', 'refresh'],
    ['全画像の再生成', 'image'], ['画像再生成', 'image'], ['画像を自動生成', 'image'], ['GLB再生成', 'cube'], ['GLB変更', 'cube'], ['このGLBを変更', 'upload'], ['プロンプト再生成', 'refresh'], ['キャラ生成へ', 'arrowR'], ['画像変更', 'upload'],
    ['3Dキャラを自動生成', 'cube'], ['モデル', 'cube'], ['npm ci', 'cube'], ['アセット', 'cube'],
    ['ワールド設計を実行', 'globe'], ['ワールド追加', 'globe'], ['翻訳', 'globe'],
    ['プロンプトを生成', 'sparkle'], ['選択ユニットを生成', 'sparkle'], ['generate:treasures 実行', 'sparkle'], ['生成・版番', 'sparkle'], ['ゲーム生成', 'sparkle'],
    ['取り込み実行', 'import'], ['取り込みと検証', 'import'], ['取り込み', 'import'], ['配置を実行', 'import'],
    ['ドライラン', 'clipboard'], ['公開可否を確認', 'clipboard'],
    ['validate:maps 実行', 'check'], ['整合チェック実行', 'check'], ['すべて実行', 'check'], ['検証', 'check'], ['ローカル assets/ を確認', 'check'],
    ['本番 CDN も確認', 'cloud'], ['公開準備', 'cloud'],
    ['本番ビルド実行', 'hammer'],
    ['起動', 'play'], ['停止', 'stop'],
    ['プロジェクトを作成して解析へ', 'plus'],
    ['ゲーム詳細へ', 'arrowR'], ['次の作業', 'arrowR'],
    ['作品要約シート コピー', 'copy'], ['英語プロンプトをコピー', 'copy'], ['コピー', 'copy'],
    ['プレビュー', 'eye'],
    ['保存', 'save'],
    ['実行', 'play'],
    ['EN', 'globe'], ['日本語', 'globe'], ['English', 'globe'],
];
let buttonIconLookup = null;
function resolveButtonIcon(label) {
    if (!buttonIconLookup) {
        // en 表示中でも同じ規則が効くよう、i18n 辞書の訳語でも引けるように展開する
        const en = globalThis.VN_I18N?._dict?.EXACT ?? {};
        buttonIconLookup = [];
        for (const [ja, iconKey] of BUTTON_ICON_RULES) {
            buttonIconLookup.push([ja, iconKey]);
            if (en[ja]) buttonIconLookup.push([en[ja], iconKey]);
        }
    }
    const normalized = label.replace(/^[①-⑳]+[\s\-0-9]*/, '').trim(); // 「① ドライラン」→「ドライラン」
    for (const [prefix, iconKey] of buttonIconLookup) {
        if (label.startsWith(prefix) || normalized.startsWith(prefix)) return iconKey;
    }
    return null;
}
function applyButtonIcons(rootEl) {
    for (const btn of rootEl.querySelectorAll('button:not([data-ico])')) {
        btn.dataset.ico = '1';
        const label = btn.textContent.trim();
        if (!label || btn.querySelector('svg')) continue; // アイコン既設（目アイコン・SNS ロゴ等）
        const first = Array.from(label)[0];
        if (/\p{Extended_Pictographic}/u.test(first) || first === '＋' || first === '▶') continue; // 絵文字が既にアイコンの役割
        const iconKey = resolveButtonIcon(label);
        if (iconKey && BTN_ICONS[iconKey]) btn.insertAdjacentHTML('afterbegin', BTN_ICONS[iconKey]);
    }
}

// ── 有料コスト発生ボタンの統一配色（黒背景＋黄色文字 = class "cost"）─────────
// 押すと LLM・画像生成・Meshy 等の実費が発生する処理のラベル（前方一致・ja/en 両対応）。
// コスト非発生のボタンは既定の水色背景＋黒文字（2026-07-12 配色統一指示）。
const COST_BUTTON_LABELS = [
    'ゲームシナリオ文書作成', '解析を実行', '再解析を実行', 'プロンプトを生成', 'プロンプト再生成',
    '全画像の再生成', '全GLBの再生成', '画像を自動生成', '画像再生成',
    'GLB再生成', '3Dキャラを自動生成', 'ワールド設計を実行', '再設計を実行',
    '選択ユニットを生成', '未作成を一括実行',
    // ⑤ 開始画面設計（画像生成の実費が発生。無償の「足元の透過を補正」は対象外）
    '背景を生成', '背景を再生成', '立ち絵を生成', '立ち絵を再生成',
    '未生成分を自動生成', 'すべて再生成',
];
let costLabelLookup = null;
function applyButtonCostStyles(rootEl) {
    if (!costLabelLookup) {
        // en 表示中でも同じ規則が効くよう、i18n 辞書の訳語でも引けるように展開する
        const en = globalThis.VN_I18N?._dict?.EXACT ?? {};
        costLabelLookup = COST_BUTTON_LABELS.flatMap((ja) => (en[ja] ? [ja, en[ja]] : [ja]));
    }
    for (const btn of rootEl.querySelectorAll('button:not([data-cost])')) {
        btn.dataset.cost = '1';
        // 絵文字アイコン（🚀 等）付きラベルにも効くよう先頭の絵文字・空白を外して判定する
        const label = btn.textContent.trim().replace(/^[\p{Extended_Pictographic}️\s]+/u, '');
        if (costLabelLookup.some((prefix) => label.startsWith(prefix))) btn.classList.add('cost');
    }
}

// ヘッダ（言語・アカウントボタンの動的追加/変更）も対象にするため、監視は body 全体
new MutationObserver(() => {
    guardButtonOnclickHandlers(document.body);
    applyButtonTips(document.body);
    applyButtonIcons(document.body);
    applyButtonCostStyles(document.body);
})
    .observe(document.body, { childList: true, subtree: true });

setupSidebar();
route();
guardButtonOnclickHandlers(document.body);
applyButtonTips(document.body);
applyButtonIcons(document.body);
applyButtonCostStyles(document.body);
